import { User, GPSCoordinate, Talk, type Tag, type KnownPerson, type IpfsAttachment, InteractionKind } from '../../shared/types';
import { backgroundAnswerRulesFromPreferences } from '../../shared/background-talk-agent';
import {
  BLOCK_SIGNAL_PAYLOAD_KIND,
  recordBlockSignal,
  recordSharedBlockSignal as mergeSharedBlockSignal,
  resolveSharedSignalsForContact,
  type BlockSignalPayload,
} from '../../shared/block-signal';
import { restoreLastTab } from '../ui/app-shell-controls';
import {
  deriveBackendApiBaseFromLocation,
  KEY_CUSTODY_DEVICE_SECRET_STORAGE,
  KEY_CUSTODY_STORAGE,
  WebGunService,
} from '../services/web-gun-service';
import { WebUserService } from '../services/web-user-service';
import { WebIdentityLinkService } from '../services/web-identity-link-service';
import { WebDeviceHandoffService } from '../services/web-device-handoff-service';
import { WebDeviceSyncService } from '../services/web-device-sync-service';
import { WebAttestationService } from '../services/web-attestation-service';
import { OFFICIAL_APPLICATION_ID, OFFICIAL_SIGNING_IDENTITY_HASH } from '../../shared/official-build-credential';
import { addSyncedMessage, mergeMessagesWithSynced, type SyncedMessage } from '../services/web-device-sync-message-cache';
import { showDeviceSyncConflictDialog } from '../ui/device-sync-conflict-dialog';
import { showMatchCollectionReviewDialog } from '../ui/match-collection-review-dialog';
import { readLinkedDeviceRecords } from '../ui/linked-devices-dialog';
import {
  WebChatroomService,
  chatroomCapacityTestOverride,
} from '../services/web-chatroom-service';
import { PlaceChatClient } from '../services/place-chat-client';
import { WebTalkService } from '../services/web-talk-service';
import { fetchActiveRooms, getRoomListSort } from '../services/active-rooms-client';
import { BlockStatusCache } from '../services/block-status-cache';
import { isPageHidden, throttleWhileHidden } from '../services/throttle-while-hidden';
import { NearbyV3Runtime, FetchNearbyV3Hub } from '../services/nearby-v3-runtime';
import type { NearbyCountIdentity } from '../../shared/nearby-v3-card';
import { nearbyV3AttestorKeysFromManifest, nearbyV3ConfigFromManifest } from '../../shared/nearby-v3-config';
import { IncomingTalkBudget } from '../../shared/incoming-talk-budget';
import { placesMissingFromCatalogue } from '../ui/room-list-view';
import { ACTIVE_ROOMS_PAGE } from '../../shared/active-rooms';
import { loadQuietSettings, QUIET_SETTINGS_EVENT } from '../services/quiet-settings-store';
import { QUIET_GPS_REFRESH_MS, quietStateFor, shouldRefreshQuietGps, type QuietState } from '../../shared/quiet-places';
import { setCachedLocation } from '../services/location-cache';
import { boundsContain, type MapBounds } from '../../shared/map-dots';
import { publicLocationFor, publicLocationWithHysteresis, snapPublicLocation, publicLocationPrecisionMeters, type PublicLocation } from '../../shared/public-location';
import { isNearbyAudienceCandidate } from '../../shared/nearby-v3-audience';
import { restoreReceivedTalkHistory } from '../services/talk-history-restorer';
import { GunChatbotMemoryRepository } from '../services/gun-chatbot-memory-repository';
import { getAnsweredTalkByContent, getExactChatbotMemory, getFlattenedAnswerPreferences, setExactChatbotMemory } from '../ui/answer-preferences-storage';
import { runAnswerContextMigration } from '../ui/answer-context-migration';
import { getMyTalks, setMyTalks } from '../ui/my-talks-storage';
import { applyTalkRevisionToAnsweredCopy } from '../ui/talk-completion';
import { computeTalkRevisionHash } from '../../shared/talk-revision';
import { recordTalkRevisionSent, talkRevisionOwedToPeer } from '../services/talk-revision-sent-store';
import { recordTalksBroadcastInRoom, talksBroadcastInRoom } from '../ui/broadcast-room-record';
import { getUnsentBroadcastTalkIdsForReceiver } from '../ui/broadcast-delivery-selection';
import { loadConnectivitySettings, routePreferencesFromSettings, saveConnectivitySettings, takeLegacyPrivacyMigration, type ConnectivitySettings } from '../ui/connectivity-settings';
import { ConnectionManager, type NetworkInterface } from '../../shared/connection-manager';
import { WebConversationService } from '../services/web-conversation-service';
import { WebContentNodeService, type WebContentNode } from '../services/web-content-node-service';
import { WebLedgerService } from '../services/web-ledger-service';
import { MapMatchView } from '../services/map-match-view';
import { exchangeMapMatchSource } from '../services/map-match-source';
import type { MapMatchSource } from '../../shared/map-matches';
import { UIManager } from '../ui/ui-manager';
import type { BroadcastAudiencePreview } from '../ui/broadcast-audience-preview';
import { LocationPrivacy } from '../../shared/location';
import { isPlaceRoomId, parseAnchorCell } from '../../shared/place-rooms';
import { MAX_LIVE_PLACES_PER_CELL } from '../../shared/place-lifecycle';
import { createSignedPlaceDescriptor } from '../../shared/place-descriptor';
import { createSignedFriendMark } from '../../shared/friend-mark';
import {
  ROOM_CREATE_MAX_BLOCKS_EXCLUSIVE,
  ROOM_CREATE_MIN_FRIEND_MARKS,
} from '../../shared/room-creation-credit';
import {
  CONTACTS_ONLY_SCOPE_ID,
  isLocalOnlyRoomScope,
  NEARBY_V3_ROOM_ID,
  hasNoHubRoster,
  hasRoomChat,
  isPersonalListScope,
  isCatchUpRecipient,
  isNearbyPublicMode,
  isNearbyRoomId,
  type NearbyPrivacyMode,
} from '../../shared/nearby-rooms';
import { applyPublicChatroomHierarchy, getAllChatroomIds } from '../../shared/chatroom-hierarchy';
import {
  isRenderableSystemAnnouncement,
  isVerifiedTechSupportIdentity,
  readVerifiedTechSupportIdentity,
  type TechSupportIdentity,
} from '../../shared/system-announcements';
import { pickLatestTalkIdFromIncomingCluster } from '../../shared/incoming-talk-ids';
import { FlowCapture, TalkAutofix, buildRevisedTalkDraft, checkIfMatch } from '../../shared/talk-engine';
import { computeTalkIdFromTalkData, computeResponseId, canonicalSerialize, computeCIDv1 } from '../../shared/cid';
import { getDevStageZeroMaxGlobalMembers, isDevStageZero, isDevStageZeroSelfHeal } from '../dev-stage-env';
import { purgeDevStageZeroGraph } from '../dev-stage-seeds';
import {
  isTechSupportUser,
  TECHSUPPORT_ANNOUNCEMENT_TRUST_ANCHORS,
  TECHSUPPORT_ROOT_USER_ID,
  TECHSUPPORT_STAGE_NAME,
} from '../../shared/techsupport';
import {
  renderGreeting,
  verifyTechSupportGreeting,
  verifySupportAck,
  type GreetingLocale,
  type SignedGreeting,
  type SupportAckLocale,
  type SignedSupportAck,
} from '../../shared/techsupport-greeting';
import techsupportGreetingBundle from '../../shared/techsupport-greeting.signed.json';
import techsupportSupportAckBundle from '../../shared/techsupport-support-ack.signed.json';
import {
  buildSupportFaqEntry,
  lookupSupportAnswer,
  supportAutoAnswerMessageId,
  supportHumanAnswerMessageId,
  supportQuestionKey,
  type SupportInboxEntry,
} from '../../shared/techsupport-faq';
import { signFaqEntry, faqEntryOf, type SignedFaqEntry } from '../../shared/techsupport-faq-entry';
import {
  applyRawFaqEntry,
  fetchFaqEntryFromServer,
  fetchRecentFaqEntriesFromServer,
  readSeedFaqBundle,
} from '../services/techsupport-faq-cache';
import {
  subscribeToRecoveryAnchor,
  fetchRecoveryAnchorFromServer,
  readCachedRecoveryAnchor,
} from '../services/techsupport-recovery-cache';
import { isTrustedDmPubWithRecovery } from '../../shared/techsupport-recovery';
import {
  signDelegateGrant,
  verifyValidDelegateGrant,
  isValidDelegateGrant,
  delegateGrantPath,
  DELEGATE_GRANT_DEFAULT_TTL_MS,
  DELEGATE_GRANT_MAX_TTL_MS,
  type TechSupportDelegateGrant,
} from '../../shared/techsupport-delegate';
import {
  readCachedDelegateGrants,
  subscribeToDelegateGrants,
  fetchGrantLive,
  fetchDelegateGrantsFromServer,
  applyRawDelegateGrant,
} from '../services/techsupport-delegate-cache';
import {
  createDelegateInvite,
  encodeDelegateInviteCode,
  decodeDelegateInviteCode,
  isDelegateInviteExpired,
  buildDelegateRequest,
  verifyDelegateRequest,
  delegateRequestMatchesInvite,
  delegateRequestPath,
  signTargetedDelegateInvite,
  verifyTargetedDelegateInvite,
  delegateRequestMatchesTargetedInvite,
  targetedDelegateInvitePath,
  type DelegateInvitePayload,
  type TargetedDelegateInvite,
  type TechSupportDelegateRequest,
} from '../../shared/techsupport-delegate-invite';
import { uiLanguageFromProfile, uiText, type UiTranslationKey } from '../ui/ui-translations';
import { getChatbotTemplates, getMatchCollectionEnabled, getNearbyBluetoothEnabled, getNearbyProviderEnabled, getNearbyWifiDirectEnabled, getUiLanguagePreference, NEARBY_SETTINGS_EVENT, setMatchCollectionEnabled } from '../ui/ui-settings-storage';
import { discoveryPolicyTimingFromSearch, hubDiscoveryPathAllowed, type DiscoveryNetworkPath } from '../../shared/nearby-discovery-policy';
import { CONFIG } from '../../shared/config';
import { buildTalkWakePayload, TALK_WAKE_TTL_MS, TalkWakeThrottle, talkWakeEnvelopeId } from '../../shared/talk-wake';
import { assignRandomEncounterLocation, deviceHasGps, readDeviceLocation, ENCOUNTER_LOCATION_EVENT, encounterLocationCoordinate, getEncounterLocation, isGpsFixUnavailable, setEncounterLocation, setGpsFixUnavailable, usesSelfDefinedEncounterLocation, type EncounterLocation } from '../services/encounter-location';
import {
  endMatchCollectionSession,
  getActiveMatchCollectionSession,
  recordMatchCollectionDelivery,
  recordMatchCollectionExchange,
  startMatchCollectionSession,
} from '../services/match-collection-session';
import { showEncounterLocationPicker } from '../ui/encounter-location-picker';
import { resolveP2PRuntimeFlags, usesMeshTalkDelivery, type P2PRuntimeFlags, type ConversationTransportMode } from '../../shared/p2p-runtime';
import { filterTalkPeersByContactPolicy, talkContactPolicyAllowsPeer } from '../../shared/talk-contact-policy';
import { getTalkIntakeFilters, setTalkIntakeFilters, setTalkIntakeFiltersOwner } from '../ui/talk-intake-filters';
import { P2PPresenceClient } from '../services/p2p-presence-client';
import { P2PLocalNodeBridgeClient } from '../services/p2p-local-node-bridge-client';
import { PeerMeshService } from '../services/peer-mesh-service';
import { WebMailboxClient } from '../services/web-mailbox-client';
import { getOrCreateLibp2pMeshSession } from '../services/p2p-libp2p-mesh-session';
import { eraseDevice } from '../services/device-wipe';
import { parseLinkFragmentPayload, clearLinkFragmentFromUrl } from '../services/identity-link-fragment';
import { getOrCreateP2PSession, listP2PSessions, onP2PSessionCreated, onP2PVersionMismatch, setLocalLinkIceServer, setP2PContactIdentityResolver, type P2PVersionMismatchEvent } from '../services/p2p-webrtc-session';
import { WifiDirectLinkService } from '../services/wifi-direct-link-service';
import { AndroidWifiDirectNative, EmbeddedNodeLocalRelay, readAndroidWifiDirectBridge, resolveWifiDirectLinkFlag } from '../services/android-wifi-direct-native';
import { NearbyOfflineService, readNearbyOfflineBridge, type NearbyReadinessGap, type NearbyStatus } from '../services/nearby-offline-service';
import {
  ActiveExchangeRoomController,
  BASELINE_ROOM_PROTOCOL_CHECKPOINT,
  roomDeliveryMatchesActiveRoom,
  type ActiveExchangeRoom,
  type RoomProtocolCheckpoint,
} from '../../shared/active-exchange-room';
import { ProtocolManifestController } from '../../shared/protocol-manifest-controller';
import {
  BUNDLED_PROTOCOL_MANIFEST_HISTORY,
  PRODUCTION_PROTOCOL_MANIFEST_ANCHOR,
  PRODUCTION_PROTOCOL_MANIFEST_RUNTIME,
} from '../../shared/production-protocol-manifest';
import { createFallbackMeshSession } from '../services/p2p-mesh-session-fallback';
import { P2PRoomDiscoveryService } from '../services/p2p-room-discovery';
import type { P2PMeshTalkBodyPayload, P2PMeshTalkResponsePayload, P2PMeshTalkRetractedPayload } from '../../shared/p2p-mesh-protocol';
import {
  collectLocalIncomingTalkClusters,
  mirrorIncomingTalkClustersToLocalGun,
  subscribeLocalIncomingTalkClusters,
  upsertLocalIncomingTalkCluster,
  upsertLocalIncomingTalkClusters,
} from '../../core/client-incoming-talk-mirror';
import { getSEA, type GunPair } from '../sea-gun';
import {
  applyTalkLedgerEvent,
  applyEdgeGateForPeer,
  shouldSuppressForPeer,
  getTalkLedgerDoc,
  connectTalkLedgerCore,
  commitTalkResponse,
  recordAuthorAnswer,
  getResponderVersionForTalk,
  writeResponderExchangedEntry,
  getResponderTargetsForIdentity,
  getResponderLastResponseId,
  markTalkSentToPeer,
} from '../services/web-talk-ledger-store';
import { buildTalkIdentityKey } from '../../shared/cid';
import { applyContactsPrune, getContactsCap, planContactsPrune, unprunePeer } from '../services/contacts-prune';
import { sharedTagCounter } from '../ui/tag-graph-view';
import { deriveLocalPeers } from '../services/local-peer-derivation';
import { connectExchangeCore, upsertExchange } from '../services/exchange-view';
import type { CoreRequest } from '../../core/protocol';
import type { CoreCollection } from '../../core/collections';
import { decideResponseIngest } from '../../core/response-ingest';
import { decideIncomingTalk, talkExpiresAtMs } from '../../core/talk-intake';
import { chatbotWillAct, decideChatbotReply, type ChatbotReplyDecision } from '../../core/chatbot-reply';
import { planDealFinalization } from '../../core/deal-finalization';
import { classifyMailboxPayload, mailboxSenderId } from '../../core/mailbox-envelope';
import { authorLedgerWritesFor, exchangeRecordFor } from '../../core/exchange-recording';
import { attachmentShareKeyPayload, attachmentShareMessageId, attachmentSharePayload, parseAttachmentShareMessageText, type AttachmentShareMessagePayload } from '../../core/attachment-share';
import { decodePairResponse, encryptPairResponse, firstKeyHint, pairSecret, type PairResponseContent, type SeaPort } from '../../core/pair-response-crypto';
import type { SeaSigningPair } from '../../shared/p2p-runtime';
import { answeredBeforeFor, isDealEligibleTalk, isPassedTag, matchScoreParamsFor, selectedTalkIdentityKeys, talkRevisionPairKey } from '../../core/talk-rules';
import { isIgnoreAnswerSet, isResponseComplete, nextResponseVersion } from '../../core/response-rules';
import { conversationReferencesTalk, isDealMutuallyConfirmed, planRetractionReceipt, retractionRecipients } from '../../core/retraction';
import { appStorage, settingsStorage } from '../services/core-storage';
import { MIGRATION_MARKS, migrateWorkerCoreToNode, resolveNativeNode, type CoreRequester, type NativeNode } from '../services/node-core-link';
import { restoreCreatorReplyFilterState } from '../ui/creator-replies-view';
import { connectCoreCollections, dealTalkByConversationView, needKeyByConversationView } from '../services/core-views';
import { getPinnedIds } from '../ui/list-pins';
import { markStartupPhase } from '../performance/startup-metrics';
import {
  buildTagIdentityKeys,
  filterTalkForRecipient,
  setTalkLedgerQuotaUnlimited,
  retractedKey as ledgerRetractedKey,
} from '../../shared/talk-ledger';
import { getMyConversations } from '../ui/my-conversations-storage';

/** OPEN-38: how long after its last heartbeat a still-joined member keeps receiving queued Talks. */
const LAPSED_MEMBER_DELIVERY_MS = 12 * 60 * 60_000;

type MailboxAttachmentSharePayload = {
  kind: 'ipfs-conversation-share-v1';
  conversationId: string;
  messageId: string;
  senderId: string;
  senderName: string;
  recipientId: string;
  recipientName: string;
  text: string;
  timestamp: string;
};

/**
 * Phase 4: an ordinary DM queued to the recipient's offline mailbox because WebRTC
 * delivery failed. The `wire` is already SEA-ECDH-encrypted between the two users
 * (the mailbox additionally wraps it); on drain the recipient writes it idempotently
 * into local Gun via `conversationService.upsertMessageRecord`.
 */
type MailboxConversationMessagePayload = {
  kind: 'conversation-message-v1';
  conversationId: string;
  senderId: string;
  recipientUserId: string;
  senderName?: string;
  wire: import('../services/gun-message-store').ConversationMessageWire;
};

/**
 * docs/TODO.md K5, design note §Item 3 (decision K5-A): a new question reaches the TechSupport
 * device as an encrypted mailbox envelope — never a server route — so the relay holds no
 * support-inbox data. The TechSupport device ingests this into its OWN local Gun
 * (`techsupport-inbox/<questionKey>`) on drain.
 */
type MailboxSupportQuestionPayload = {
  kind: 'support-question-v1';
  questionKey: string;
  question: string;
  askedBy: string;
  conversationId: string;
  askedAt: string;
};

/** Debounce for automatic same-room catch-up to one peer (link flaps, roster heartbeats). */
const ROOM_CATCHUP_MIN_INTERVAL_MS = 10_000;
/** How often online dots (Contacts, conversations, room members) are asked again. */
const PRESENCE_REFRESH_MS = 60_000;
/** On a phone the native mailbox watch wakes a drain; the page's own drain is a safety net. */
const NATIVE_MAILBOX_SAFETY_DRAIN_MS = 15_000;

/**
 * The room protocol baseline. Outside production a local-debug/E2E capacity (`?e2e_capacity=`)
 * replaces the baseline capacity so a full Nearby list or Place can be exercised with a few users;
 * production always uses the bundled manifest's capacity.
 */
function baselineRoomCheckpoint(): RoomProtocolCheckpoint {
  const capacity = chatroomCapacityTestOverride();
  return capacity === null
    ? BASELINE_ROOM_PROTOCOL_CHECKPOINT
    : { ...BASELINE_ROOM_PROTOCOL_CHECKPOINT, chatroomCapacity: capacity };
}

/**
 * The checkpoint actually used for rooms. An activated bundled manifest supplies its own capacity
 * (498) and would silently replace the local-debug/E2E capacity, so a test capacity is re-applied
 * on top. `chatroomCapacityTestOverride()` is always null in production.
 */
function manifestRoomCheckpoint(controller: { roomCheckpoint(baseline: RoomProtocolCheckpoint): RoomProtocolCheckpoint }): RoomProtocolCheckpoint {
  const checkpoint = controller.roomCheckpoint(baselineRoomCheckpoint());
  const capacity = chatroomCapacityTestOverride();
  return capacity === null ? checkpoint : { ...checkpoint, chatroomCapacity: capacity };
}

export class IinPublicApp {
  private gunService: WebGunService;
  private userService: WebUserService;
  private chatroomService: WebChatroomService;
  private placeChatClient: PlaceChatClient;
  private placeChatUnsubscribe: (() => void) | null = null;
  private talkService: WebTalkService;
  private conversationService: WebConversationService;
  private contentNodeService: WebContentNodeService;
  private identityLinkService: WebIdentityLinkService;
  private deviceHandoffService: WebDeviceHandoffService;
  private deviceSyncService: WebDeviceSyncService;
  /** Scenario 2 (§16): this device's official-build attestation. Never on the hot path — see
   * WebAttestationService's own doc comment. */
  private attestationService: WebAttestationService;
  private deviceSyncTimer: ReturnType<typeof setInterval> | undefined;
  /** Interaction ledger (Phase E). Initialized lazily after SEA keypair is ready. */
  private ledgerService: WebLedgerService | null = null;
  private uiManager: UIManager;
  private readonly mapMatchView = new MapMatchView(
    (counts) => this.uiManager.setMapMatchCounts(counts),
    (talks) => this.uiManager.setMapTalkConnections(talks),
  );
  private currentUser?: User;
  private currentLocation?: GPSCoordinate;
  private currentChatroomId?: string;
  /** Gun .map().on may replay the same response node; avoid duplicate match UI/conversations. */
  private processedTalkResponseKeys = new Set<string>();
  /** One auto chatbot reply per announcer for the same content-hash talk id (same qa_* = same talk; keys are not author-based talk identity). */
  private chatbotAutoReplySentForPair = new Set<string>();
  /** Bounded retries for template races (announcement can arrive before manual answer persistence finishes). */
  private chatbotAutoReplyRetryCountByPair = new Map<string, number>();
  private subscribedMemberCountRoomIds = new Set<string>();
  private stageZeroLastMemberCounts = new Map<string, number>();
  private stageZeroRepairInFlight = false;
  private stageZeroWatchdogTimer: ReturnType<typeof setInterval> | undefined;
  private stageZeroBootedAt = 0;
  private incomingApiRefreshTimer: ReturnType<typeof setTimeout> | undefined = undefined;
  private travelModeActive: boolean = false;
  private presenceRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private travelHomeChatroomId: string | undefined = undefined;
  private travelChatroomId: string | undefined = undefined;
  private travelNearbyPoint: (GPSCoordinate & { placeId: string; placeName: string }) | undefined;
  private activeRoomsTimer: ReturnType<typeof setInterval> | undefined;
  private activeRoomsLimit = ACTIVE_ROOMS_PAGE;
  /** Places seen in the room list that already triggered a catalogue refresh (one try each). */
  private catalogueRefreshedForPlaces = new Set<string>();
  private activeRoomsRefreshTimer: ReturnType<typeof setTimeout> | undefined;
  /** Quiet places / schedule / "Quiet now" (shared/quiet-places.ts), re-checked every minute. */
  private quietState: QuietState = { active: false };
  private quietTimer: ReturnType<typeof setInterval> | undefined;
  private quietGpsTimer: ReturnType<typeof setInterval> | undefined;
  private quietGpsInFlight = false;
  /** Room members marked as Travelers (for the "Residents only" Talk audience). */
  private roomTravelerIds: ReadonlySet<string> = new Set();
  private nearbyPointShownKey = '';
  /** Nearby (§3.7): my personal list, the audience of `NEARBY_V3_ROOM_ID`. */
  private nearbyV3: NearbyV3Runtime | null = null;
  /** Verified direct-link proof when this device shares another device's Nearby count key. */
  private nearbyCountIdentity: NearbyCountIdentity | null = null;
  /** The latest Nearby broadcast, for "Sent to N of M". */
  private nearbyBroadcast: { talkIds: Set<string>; total: number; delivered: Set<string> } | null = null;
  /** Last published home square (hysteresis memory for `publicHomeLocation`). */
  private lastPublicHomeLocation: PublicLocation | null = null;
  /** A Nearby-at-a-pin visit waiting for `chatroomChanged` to commit it. */
  private pendingNearbyTravel: { location: GPSCoordinate; placeId: string; placeName: string } | undefined;
  private supportBootstrapChecked = false;
  /** `${roomId}|${peerId}` → last automatic catch-up send (see catchUpPeerInActiveRoom). */
  private readonly roomCatchupSentAt = new Map<string, number>();
  /** Talk id → deliberate broadcasts still resolving receivers/delivering; catch-up waits for them. */
  private readonly talkBroadcastsInFlight = new Map<string, number>();
  /** Peers whose catch-up skipped an in-flight Talk; re-checked when that broadcast settles. */
  private readonly catchUpDeferredByInFlight = new Map<string, { roomId: string; stageName?: string }>();
  /** When this session rendered the welcome greeting; null until then. */
  private supportGreetingTimestamp: string | null = null;
  private supportGreetingRendered: string | null = null;
  private presenceClient: P2PPresenceClient | null = null;
  /** The hub's either-way block answer per peer, for a minute (every incoming Talk asks). */
  private readonly blockStatusCache = new BlockStatusCache({ fetchStatus: (peerId) => this.fetchBlockStatusEitherWay(peerId) });
  private conversationPreviewUnsubscribers = new Map<string, () => void>();
  private peerEpubByUserId = new Map<string, string>();
  private talkLedgerSuppressionDisabledForE2e = false;
  private mailboxFallbackDisabledForE2e = false;
  private localNodeBridge: P2PLocalNodeBridgeClient | null = null;
  private peerMeshService: PeerMeshService | null = null;
  private wifiDirectLinkService: WifiDirectLinkService | null = null;
  private nearbyOfflineService: NearbyOfflineService | null = null;
  /** The hub count revision the last stats poll saw (OPEN-86 E4). */
  private nearbyCountRevisionSeen: number | null = null;
  private activeRoomRoster: { roomId: string; count: number; pubs: string[] } | null = null;
  private readonly talkWakeThrottle = new TalkWakeThrottle();
  private activeExchangeRoomController: ActiveExchangeRoomController | null = null;
  private protocolManifestController: ProtocolManifestController | null = null;
  private protocolActivationTimer: ReturnType<typeof setTimeout> | null = null;
  private mailboxClient: WebMailboxClient | null = null;
  private mailboxPollTimer: ReturnType<typeof setInterval> | undefined;
  private mailboxChangedListener: (() => void) | undefined;
  private readonly incomingTalkBudget = new IncomingTalkBudget(() => this.nearbyV3Config().incomingTalksPerMinute);
  /** docs/TODO.md K5 — live subscription that keeps the local FAQ-bundle cache verified/fresh. */
  /** docs/TODO.md OPEN-29 — live subscription that keeps the local recovery-anchor cache fresh. */
  private techSupportRecoveryAnchorUnsubscribe: (() => void) | null = null;
  /** docs/TODO.md K7 — live subscription that keeps the local delegate-grant cache verified/fresh. */
  private techSupportDelegateGrantsUnsubscribe: (() => void) | null = null;
  /** docs/TODO.md K7 — this session's OWN currently-valid delegate grant, if any (never the master's). */
  private techSupportDelegateGrant: TechSupportDelegateGrant | null = null;
  /** docs/TODO.md K7 — whether the delegate has opted in to acting as a support operator (persisted). */
  private techSupportDelegateOptedIn = false;
  private static readonly DELEGATE_OPT_IN_STORAGE_KEY = 'iinpublic_techsupport_delegate_optin_v1';
  /** K7 follow-on — the master's one outstanding invite-code, if a dialog generated one this session. */
  private pendingDelegateInvite: DelegateInvitePayload | null = null;
  /** K7 follow-on — live subscription to the delegate-request roster (master session only). */
  private techSupportDelegateRequestsUnsubscribe: (() => void) | null = null;
  /** K7 follow-on — verified requests matching this session's outstanding invite, shown for approval. */
  private delegateRequestsCache: TechSupportDelegateRequest[] = [];
  /** Targeted (remote) invites this master has sent, keyed by requestId (persisted — they last days, not minutes). */
  private targetedDelegateInvites: Map<string, TargetedDelegateInvite> = this.loadTargetedDelegateInvites();
  private static readonly TARGETED_DELEGATE_INVITES_STORAGE_KEY = 'iinpublic_techsupport_targeted_invites_v1';
  /** Candidate side — requestIds of targeted invites already accepted or declined on this device. */
  private static readonly HANDLED_DELEGATE_INVITES_STORAGE_KEY = 'iinpublic_techsupport_handled_invites_v1';
  /** Candidate side — a verified invite addressed to this identity, awaiting Accept/Decline. */
  private incomingTargetedDelegateInvite: TargetedDelegateInvite | null = null;
  /** K7 follow-on — tracks which grant pubs an expiry-soon warning has already fired for this session, so a re-render doesn't re-notify. */
  private delegateExpiryWarned: Set<string> = new Set();
  /** Guards `subscribeToSupportInboxIfTechSupport` against double-subscribing — it is now called both at boot and when a delegate opts in later in the session. */
  private supportInboxSubscribed = false;
  /** docs/TODO.md OPEN-31: every inbox row this session knows, and which pending ones already have a published answer elsewhere. */
  private supportInboxRows = new Map<string, SupportInboxEntry>();
  private supportAnsweredKeys = new Set<string>();
  private supportAnsweredCheckInFlight = false;
  private techSupportAdminPanelTick = 0;
  private mailboxDrainPromise: Promise<void> | null = null;
  private nativeBackgroundSyncQueued = false;
  private nativeBackgroundSyncRunning = false;
  private nativeBackgroundReplayQueued = false;
  private nativeBackgroundReplayRunning = false;
  /**
   * GUN/SEA signs writes in the authenticated user namespace. Overlapping talk completions
   * from the same device can race that signer and produce `Unverified data` acknowledgements,
   * aborting delivery before local exchange bookkeeping. Keep the per-device completion
   * pipeline ordered while allowing failures to be reported without poisoning later work.
   */
  private talkCompletionQueue: Promise<void> = Promise.resolve();
  private attachmentShareSentIds = new Set<string>();
  private fetchedAttachmentBytesByCid = new Map<string, Uint8Array>();
  /** Cached object URLs for decrypted shared attachments, keyed by cid (L5 image preview). */
  private attachmentObjectUrlByCid = new Map<string, string>();
  /** cids with an in-flight fetch loop, so overlapping message-update callbacks don't stack. */
  private attachmentFetchInFlight = new Set<string>();
  private static readonly ATTACHMENT_SHARE_SENT_KEY = 'iinpublic_ipfs_share_sent_ids';
  public initialized = false;
  /**
   * Durable mesh-ping diagnostics record updated by onPing/onPong callbacks.
   * Exposed via getApp() for E2E assertion (design §6, R5).
   */
  public meshPingDiagnostics: {
    pingedOrigins: string[];
    pongedOrigins: string[];
    lastPingFrom: string | null;
    lastPongFrom: string | null;
  } = {
    pingedOrigins: [],
    pongedOrigins: [],
    lastPingFrom: null,
    lastPongFrom: null,
  };

  /**
   * Step 2: durable mesh announce-receipt diagnostics updated by onTalkAnnounce callback.
   * Each entry records a talkId + authorId pair received via a talk-announce mesh frame.
   * Exposed via getApp() for E2E assertion without requiring the full body pull to complete.
   */
  public meshAnnounceDiagnostics: {
    /** Announcements received: each entry is { talkId, authorId } */
    received: Array<{ talkId: string; authorId: string }>;
  } = {
    received: [],
  };
  public meshDiscoveryDiagnostics: {
    roomId: string | null;
    providerPeerIds: string[];
    discoveredUserIds: string[];
    bootstrapPeers: string[];
    updatedAt: string | null;
  } = {
    roomId: null,
    providerPeerIds: [],
    discoveredUserIds: [],
    bootstrapPeers: [],
    updatedAt: null,
  };
  private readonly e2eSeededIncomingClusters: any[] = [];
  private readonly e2eSeededTagTalks = new Map<string, any>();
  private incomingTalkClusterUnsubscribe: (() => void) | null = null;
  private roomDiscoveryService: P2PRoomDiscoveryService | null = null;
  private readonly roomDiscoveredUserIds = new Map<string, Set<string>>();
  /** Room members already scheduled for receiver-scoped historical broadcast catch-up. */
  private readonly p2pRuntimeFlags: P2PRuntimeFlags = resolveP2PRuntimeFlags(
    typeof process !== 'undefined'
      ? {
          P2P_NODE_ENABLED: process.env.P2P_NODE_ENABLED,
          RELAY_ONLY_HUB: process.env.RELAY_ONLY_HUB,
          P2P_CLIENT_TALK_MIRROR: process.env.P2P_CLIENT_TALK_MIRROR,
        }
      : {},
  );

  /** Initialize the interaction ledger after the SEA keypair is available. */
  private initLedger(): void {
    if (this.isLedgerDisabledForRun()) return;
    try {
      const pair = this.gunService.getStoredPair();
      const userId = this.currentUser?.id || '';
      const pubkey = pair?.pub || '';
      if (!userId || !pubkey) return; // not ready yet — will re-init after user is created
      this.ledgerService = new WebLedgerService(this.gunService, userId, pubkey);
      void this.ledgerService.loadOwnFeedHead()
        .then(() => {
          this.initLedgerTransportHooks();
        })
        .catch(() => {/* non-fatal */});
    } catch {/* non-fatal */}
  }

  /**
   * The ledger (Phase E+F) is disabled for the whole `DISABLE_HMR=true` E2E run by
   * default (bundled into a broader background-service-quieting sweep, not a deliberate
   * "ledger is broken in E2E" decision — see git blame on this check). TODO §S Item 7
   * needs the ledger genuinely active to prove checkpoint/prune/delta-sync end to end in
   * a real browser, so `IINPUBLIC_E2E_ENABLE_LEDGER=1` narrowly re-enables it for that one
   * spec's run without changing behavior for the hundreds of other E2E specs that never
   * touch the ledger and don't set this var.
   */
  private isLedgerDisabledForRun(): boolean {
    return process.env.DISABLE_HMR === 'true' && process.env.IINPUBLIC_E2E_ENABLE_LEDGER !== '1';
  }

  /**
   * TODO §J — how long the sender waits for the receiver's handoff acknowledgement
   * before giving up (erase-device-dialog.ts then shows an error, never enables Done —
   * see setDeviceHandoffSync's own doc comment). Overridable so an E2E spec proving a
   * real send→ack round trip isn't forced to wait the full production timeout; unset,
   * this is the production default. Static `process.env.KEY` access, no runtime guard —
   * see web-ledger-service.ts's own doc comment for why a guard would silently defeat
   * webpack's DefinePlugin/EnvironmentPlugin substitution here.
   */
  private handoffAckTimeoutMs(): number {
    return parseInt(process.env.IINPUBLIC_E2E_HANDOFF_ACK_TIMEOUT_MS || '', 10) || 60_000;
  }

  /** Wire P2P transport fallback UI + WebRTC LEDGER_STATE hooks (REQ-LEDGER-06). */
  private initLedgerTransportHooks(): void {
    this.conversationService.setTransportFallbackHandler(({ conversationId, mode, fallbackReason }) => {
      this.uiManager.updateConversationTransportMode(conversationId, mode, fallbackReason);
    });
    // Phase 4: queue undeliverable DMs to the recipient's offline mailbox.
    this.conversationService.setMessageUndeliverableHandler((wire, conversationId, recipientUserId) => {
      void this.postConversationMessageToMailbox(wire, conversationId, recipientUserId).catch(() => {});
    });
    if (!this.ledgerService) return;
    const ledger = this.ledgerService;
    this.conversationService.setLedgerHandshakeHooks({
      getLedgerState: () => ledger.getState(),
      getLedgerDelta: (state) => ledger.buildDeltaForPeer(state),
      onRemoteLedgerEvents: (otherUserId, events) => ledger.ingestRemoteEvents(otherUserId, events),
    });
    // P2P media (no server/gateway): serve our own shared-file bytes to a peer on request,
    // and store bytes a peer streams to us so the conversation card can render them.
    this.conversationService.setAttachmentHooks({
      getAttachmentBytesForCid: async (cid: string) => {
        try {
          return await this.contentNodeService.readLocalBlock(cid);
        } catch {
          return null;
        }
      },
      onAttachmentBytes: (cid: string, bytes: Uint8Array) => {
        if (!cid || !bytes?.length) return;
        this.fetchedAttachmentBytesByCid.set(String(cid).trim(), bytes);
        this.uiManager.refreshOpenConversationForAttachment();
      },
    });
  }

  /**
   * Fire-and-forget ledger event emission.
   * Errors are swallowed so that ledger failures never block the main flow.
   */
  private ledgerEmit(kind: InteractionKind, content: Record<string, unknown>): void {
    if (this.isLedgerDisabledForRun()) return;
    if (!this.ledgerService) return;
    void this.ledgerService.appendEvent(kind, content as any).catch((err) => {
      console.warn('[Ledger] appendEvent failed (non-fatal):', kind, err);
    });
  }

  /** Room headcount for the status bar; TechSupport is a Contact and never a room member. */
  private countRoomMembers(members: Array<{ userId: string }>): number {
    return new Set(members.map((member) => member.userId)).size;
  }

  /**
   * The active room's other members drive the staged discovery policy: LAN NSD stays silent for a
   * lone user, and roster pubs bind rotating radio ids to SEA identities.
   */
  private observeNearbyRoomRoster(roomId: string, members: Array<{ userId: string; pub?: string; isTraveler?: boolean }>): void {
    if (roomId !== this.currentChatroomId) return;
    this.roomTravelerIds = new Set(members.filter((member) => member.isTraveler === true).map((member) => member.userId));
    const others = members.filter((member) => !!member.userId && member.userId !== this.currentUser?.id);
    const wasAlone = this.activeRoomRoster?.roomId !== roomId || this.activeRoomRoster.count === 0;
    this.activeRoomRoster = {
      roomId,
      count: others.length,
      pubs: others.map((member) => String(member.pub || '').trim()).filter(Boolean),
    };
    if (wasAlone && others.length > 0) this.nearbyOfflineService?.resetDiscoveryBackoff();
  }

  /**
   * The Internet discovery switch means cellular: off, discovery never uses cellular data. Wi-Fi
   * and Ethernet uplinks are unaffected; browsers (no native path data) always may.
   */
  private hubDiscoveryAllowedNow(): boolean {
    const bridge = readNearbyOfflineBridge();
    if (!bridge?.networkPathState) return true;
    let path: DiscoveryNetworkPath | null = null;
    try {
      path = JSON.parse(bridge.networkPathState()) as DiscoveryNetworkPath;
    } catch {
      return true;
    }
    return hubDiscoveryPathAllowed(
      { ...(Array.isArray(path?.transports) ? { transports: path.transports } : {}) },
      getNearbyProviderEnabled('cellular'),
    );
  }

  private loadAttachmentShareSentIds(): void {
    this.attachmentShareSentIds.clear();
    try {
      const raw = appStorage.getItem(IinPublicApp.ATTACHMENT_SHARE_SENT_KEY);
      if (!raw) return;
      const values = JSON.parse(raw);
      if (!Array.isArray(values)) return;
      for (const value of values) {
        const id = String(value || '').trim();
        if (id) this.attachmentShareSentIds.add(id);
      }
    } catch {
      /* best-effort local idempotency cache */
    }
  }

  private persistAttachmentShareSentIds(): void {
    try {
      appStorage.setItem(
        IinPublicApp.ATTACHMENT_SHARE_SENT_KEY,
        JSON.stringify([...this.attachmentShareSentIds]),
      );
    } catch {
      /* best-effort local idempotency cache */
    }
  }

  private getTalkAttachmentsForShare(talkData: any): IpfsAttachment[] {
    return this.contentNodeService.normalizeIpfsAttachments((talkData as any)?.ipfsAttachments);
  }

  /**
   * Upload any file's bytes to IPFS and return its attachment descriptor. The bytes are
   * stored public (enc 'none') so the shared link is openable by the recipient — we share
   * the link, not the bytes, over the DM/talk channel. Accepts photos, docs, video, audio,
   * or anything else.
   */
  private async publishMediaFileToIpfs(file: File, talkId: string): Promise<IpfsAttachment> {
    await this.ensureContentNodeInitialized();
    const bytes = new Uint8Array(await file.arrayBuffer());
    return this.contentNodeService.publishAttachmentBytes({
      talkId,
      attachment: {
        cid: 'pending',
        name: file.name || 'attachment',
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: bytes.length,
        enc: 'none',
      },
      bytes,
      publicOptIn: true,
    });
  }

  private async buildAttachmentSharePayload(params: {
    conversationId: string;
    talkId: string;
    authorId: string;
    recipientId: string;
    attachment: IpfsAttachment;
  }): Promise<AttachmentShareMessagePayload> {
    // The share message is the core's (src/core/attachment-share.ts); the key goes encrypted.
    const keyCiphertext = await this.encryptPairTalkResponsePayload(
      params.recipientId,
      attachmentShareKeyPayload(params.talkId, params.authorId, params.attachment),
    );
    return attachmentSharePayload(params, keyCiphertext, new Date().toISOString());
  }

  private formatAttachmentShareMessageText(payload: AttachmentShareMessagePayload): string {
    return `IPFS_SHARE:${JSON.stringify(payload)}`;
  }

  private async maybeFetchSharedAttachmentBytes(
    sharePayload: AttachmentShareMessagePayload,
    senderUserId: string,
    attempts = 6,
  ): Promise<void> {
    const cid = String(sharePayload.cid || '').trim();
    if (!cid || this.fetchedAttachmentBytesByCid.has(cid)) return;

    // De-dupe overlapping fetches: loadConversation re-fires per message update, so several
    // callbacks race for the same cid; only run one fetch loop per cid at a time.
    if (this.attachmentFetchInFlight.has(cid)) return;
    this.attachmentFetchInFlight.add(cid);

    // Same block gate as talk delivery (resolveBlockStatusEitherWay) — a blocked-by peer's
    // shared attachment is never decrypted/ingested. See that method's doc comment.
    if (await this.resolveBlockStatusEitherWay(senderUserId)) {
      this.attachmentFetchInFlight.delete(cid);
      return;
    }

    const pair = this.gunService.getStoredPair();
    if (!pair?.priv) { this.attachmentFetchInFlight.delete(cid); return; }

    try {
      const senderEpub = await this.resolvePeerEpub(senderUserId);
      if (sharePayload.enc === 'sea-pair') {
        const secret = await this.getPairTalkResponseSecret(senderUserId, senderEpub);
        const keyPayloadRaw = await getSEA().decrypt(sharePayload.keyCiphertext, secret);
        if (!keyPayloadRaw) {
          throw new Error('Attachment share key decrypt failed');
        }
      }

      // Primary retrieval (no server/gateway): pull the bytes from the sender over the DM
      // WebRTC DataChannel. The recipient's DM session can start cold (they never sent in this
      // conversation), so retry each pass — the request connects the session, the sender serves
      // its own block, and onAttachmentBytes stores the result. The content-node read is a
      // secondary attempt for the same cid.
      for (let i = 0; i < attempts && !this.fetchedAttachmentBytesByCid.has(cid); i += 1) {
        if (this.currentUser?.id && senderUserId && sharePayload.conversationId) {
          await this.conversationService
            .requestAttachment(sharePayload.conversationId, this.currentUser.id, senderUserId, cid)
            .catch(() => { /* session not ready yet — the next pass retries */ });
        }
        if (this.fetchedAttachmentBytesByCid.has(cid)) break;
        let bytes: Uint8Array | null = null;
        try {
          bytes = await this.contentNodeService.fetchAttachmentBytes({
            cid,
            enc: sharePayload.enc,
            senderEpub,
            recipientPair: pair as GunPair,
          });
        } catch {
          /* unreachable yet — retry below */
        }
        if (bytes) {
          this.fetchedAttachmentBytesByCid.set(cid, bytes);
          // The share message likely already rendered as a card; re-render so the now-available
          // bytes appear as an image preview / working Download in the open conversation.
          this.uiManager.refreshOpenConversationForAttachment();
          return;
        }
        if (i < attempts - 1) await new Promise((resolve) => setTimeout(resolve, 1500 * (i + 1)));
      }
    } finally {
      this.attachmentFetchInFlight.delete(cid);
    }
  }

  private async postAttachmentShareToMailbox(payload: MailboxAttachmentSharePayload): Promise<void> {
    if (!this.currentUser?.id) return;
    const mailbox = this.ensureMailboxClient();
    const pair = this.gunService.getStoredPair();
    if (!pair?.priv) return;
    try {
      const recipientEpub = await this.resolvePeerEpub(payload.recipientId);
      if (!recipientEpub) return;
      const ciphertext = await mailbox.encryptForRecipient(
        recipientEpub,
        pair as import('../sea-gun').GunPair,
        payload,
      );
      const envelopeId = `mbx_share_${payload.messageId}`;
      await mailbox.postEnvelope({
        id: envelopeId,
        recipientId: payload.recipientId,
        ciphertext,
      });
    } catch (err) {
      console.warn('[Mailbox] Failed to post attachment-share envelope:', err);
    }
  }

  private async autoShareMatchedTalkAttachments(params: {
    conversationId: string;
    talkId: string;
    authorId: string;
    responderId: string;
    responderName: string;
    talkData: any;
  }): Promise<void> {
    if (!this.currentUser?.id || this.currentUser.id !== params.authorId) return;
    const attachments = this.getTalkAttachmentsForShare(params.talkData);
    if (attachments.length === 0) return;
    // Courtesy/efficiency check, not the security boundary (that's the receive-side gate in
    // maybeFetchSharedAttachmentBytes/ingestAttachmentShareFromMailbox) — skip encrypting and
    // publishing to IPFS for a peer we already know is blocked either-way.
    if (await this.resolveBlockStatusEitherWay(params.responderId)) return;

    for (const attachment of attachments) {
      try {
        const messageId = await attachmentShareMessageId(
          params.conversationId,
          params.talkId,
          params.authorId,
          attachment.cid,
        );
        if (this.attachmentShareSentIds.has(messageId)) continue;

        const sharePayload = await this.buildAttachmentSharePayload({
          conversationId: params.conversationId,
          talkId: params.talkId,
          authorId: params.authorId,
          recipientId: params.responderId,
          attachment,
        });
        const messageText = this.formatAttachmentShareMessageText(sharePayload);
        const timestamp = new Date().toISOString();
        await this.conversationService.sendMessage(
          params.conversationId,
          params.authorId,
          messageText,
          {
            otherUserId: params.responderId,
            messageId,
            channel: 'known',
          },
        );
        await this.postAttachmentShareToMailbox({
          kind: 'ipfs-conversation-share-v1',
          conversationId: params.conversationId,
          messageId,
          senderId: params.authorId,
          senderName: this.currentUser.stageName,
          recipientId: params.responderId,
          recipientName: params.responderName,
          text: messageText,
          timestamp,
        });
        this.attachmentShareSentIds.add(messageId);
      } catch (err) {
        console.warn('[L5] autoShareMatchedTalkAttachments failed:', err);
      }
    }

    this.persistAttachmentShareSentIds();
  }

  private async ingestAttachmentShareFromMailbox(payload: MailboxAttachmentSharePayload): Promise<void> {
    if (!this.currentUser?.id) return;
    if (payload.kind !== 'ipfs-conversation-share-v1') return;
    const messageId = String(payload.messageId || '').trim();
    if (!messageId) return;
    if (this.attachmentShareSentIds.has(messageId)) return;
    // The actual enforcement boundary (a modified sender client could skip the send-side check
    // in autoShareMatchedTalkAttachments) — never create a message record for, or fetch bytes
    // from, a blocked-either-way sender.
    if (await this.resolveBlockStatusEitherWay(payload.senderId)) return;

    await this.conversationService.upsertMessageRecord(
      payload.conversationId,
      {
        id: messageId,
        senderId: payload.senderId,
        text: payload.text,
        timestamp: payload.timestamp,
        channel: 'pair',
        transport: this.conversationService.getTransportMode(),
      },
      { otherUserId: this.currentUser.id },
    );
    const sharePayload = parseAttachmentShareMessageText(payload.text);
    if (sharePayload) {
      void this.maybeFetchSharedAttachmentBytes(sharePayload, payload.senderId).catch((err) => {
        console.warn('[L5] mailbox attachment fetch failed:', err);
      });
    }
    this.attachmentShareSentIds.add(messageId);
    this.persistAttachmentShareSentIds();
  }

  /**
   * Receive side of the friend-circle block signal (src/shared/block-signal.ts). Records only
   * the opaque signalId, never the sender's identity — recordReceivedBlockSignal's on-device
   * tally is what user-detail-view.ts's threshold check reads. Rides the existing mailbox
   * drain loop, no new polling.
   */
  private async ingestBlockSignalFromMailbox(payload: BlockSignalPayload): Promise<void> {
    if (!this.currentUser?.id) return;
    const targetIdentity = String(payload.targetIdentity || '').trim();
    const signalId = String(payload.signalId || '').trim();
    if (!targetIdentity || !signalId) return;
    await this.userService.recordReceivedBlockSignal(this.currentUser.id, targetIdentity, signalId);
    // Reflect immediately in the in-memory currentUser so a profile view opened right after
    // this drain sees the updated tally without waiting for a separate reload.
    this.currentUser.receivedBlockSignals = recordBlockSignal(
      this.currentUser.receivedBlockSignals,
      targetIdentity,
      signalId,
    );
  }

  /** One pairwise mailbox-encrypted send — the atomic unit both fanOutBlockSignal and
   *  resendSharedBlockSignalsToNewContact loop over. No one-to-many encryption primitive
   *  exists in this codebase (see web-mailbox-client.ts's doc comment), so every "notify N
   *  contacts" case is N calls to this. Best-effort: caller decides whether one failure stops
   *  the rest (it doesn't, in both current callers). */
  private async sendBlockSignalPayload(payload: BlockSignalPayload, recipientId: string): Promise<void> {
    const pair = this.gunService.getStoredPair();
    if (!pair?.priv) return;
    const recipientEpub = await this.resolvePeerEpub(recipientId);
    if (!recipientEpub) return;
    const mailbox = this.ensureMailboxClient();
    const ciphertext = await mailbox.encryptForRecipient(
      recipientEpub,
      pair as import('../sea-gun').GunPair,
      payload,
    );
    await mailbox.postEnvelope({
      id: `blocksig_${payload.signalId}_${recipientId}`,
      recipientId,
      ciphertext,
    });
  }

  /**
   * Send side of the friend-circle block signal (src/shared/block-signal.ts) — fans out to an
   * already-resolved recipient list (the UI layer picked 'all contacts' or one contact-group,
   * see contacts-view.ts's block-notify modal; resolveContactGroupUserIds already excludes
   * blocked users). Also PERSISTS the share as this account's current status for that target
   * (recordSharedBlockSignal) — a block-share is a status, not a one-time push: when a contact
   * is later added or relabeled into a matching group, resendSharedBlockSignalsToNewContact
   * catches them up using this same persisted signalId.
   */
  private async fanOutBlockSignal(targetIdentity: string, groupId: string, recipientUserIds: string[]): Promise<void> {
    if (!this.currentUser?.id) return;
    const signalId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const payload: BlockSignalPayload = {
      kind: BLOCK_SIGNAL_PAYLOAD_KIND,
      targetIdentity,
      at: new Date().toISOString(),
      signalId,
    };
    // Persist the status FIRST, unconditionally — a group can legitimately have zero CURRENT
    // members (e.g. shared with 'coworker' before any contact is labeled that yet). The status
    // must still be recorded so resendSharedBlockSignalsToNewContact can catch up whoever
    // qualifies later; only the immediate send loop below is skippable when there's no one to
    // send to right now.
    await this.userService.recordSharedBlockSignal(this.currentUser.id, targetIdentity, groupId, signalId);
    this.currentUser.sharedBlockSignals = mergeSharedBlockSignal(
      this.currentUser.sharedBlockSignals, targetIdentity, groupId, signalId,
    );
    if (recipientUserIds.length === 0) return;
    for (const recipientId of recipientUserIds) {
      try {
        await this.sendBlockSignalPayload(payload, recipientId);
      } catch (err) {
        console.warn('[BlockSignal] fan-out to contact failed (best-effort):', recipientId, err);
      }
    }
  }

  /**
   * "Block is a status, not a one-time notification": called from the 'saveKnownPerson'
   * handler whenever a contact is added or relabeled. Checks this account's persisted share
   * scopes (sharedBlockSignals) against the contact's CURRENT labels and re-sends any match —
   * e.g. Tom relabeled 'coworker' now receives a block-share that was scoped to 'coworker'
   * before Tom was ever added, using the ORIGINAL signalId (so if Tom happens to already have
   * it from some other path, his recordBlockSignal dedup is a no-op, not a double-count).
   */
  private async resendSharedBlockSignalsToNewContact(contact: KnownPerson): Promise<void> {
    const matches = resolveSharedSignalsForContact(this.currentUser?.sharedBlockSignals, contact);
    for (const { targetIdentity, signalId } of matches) {
      try {
        await this.sendBlockSignalPayload(
          { kind: BLOCK_SIGNAL_PAYLOAD_KIND, targetIdentity, at: new Date().toISOString(), signalId },
          contact.userId,
        );
      } catch (err) {
        console.warn('[BlockSignal] catch-up resend failed (best-effort):', contact.userId, targetIdentity, err);
      }
    }
  }

  private async markSharedAttachmentLinksDead(params: {
    talkId: string;
    authorId: string;
    conversationPeerId: string;
    conversationId: string;
    attachments: IpfsAttachment[];
    retractedAt: number;
  }): Promise<void> {
    if (!this.currentUser?.id || params.attachments.length === 0) return;

    for (const attachment of params.attachments) {
      try {
        const messageId = await computeCIDv1({
          kind: 'ipfs-auto-share-retracted-v1',
          conversationId: params.conversationId,
          talkRef: `${params.talkId}::${params.authorId}`,
          cid: attachment.cid,
        });
        if (this.attachmentShareSentIds.has(messageId)) continue;
        const messageText = `IPFS_SHARE_RETRACTED:${JSON.stringify({
          kind: 'ipfs-auto-share-retracted-v1',
          talkId: params.talkId,
          authorId: params.authorId,
          cid: attachment.cid,
          link: `ipfs://${attachment.cid}`,
          retractedAt: params.retractedAt,
          note: 'Link is now marked dead in this conversation. Remote copies may still exist.',
        })}`;
        await this.conversationService.sendMessage(
          params.conversationId,
          this.currentUser.id,
          messageText,
          {
            otherUserId: params.conversationPeerId,
            messageId,
            channel: 'known',
          },
        );
        this.attachmentShareSentIds.add(messageId);
      } catch (err) {
        console.warn('[L5] markSharedAttachmentLinksDead failed:', err);
      }
    }

    this.persistAttachmentShareSentIds();
  }

  constructor() {
    this.gunService = new WebGunService();
    this.userService = new WebUserService(this.gunService);
    this.chatroomService = new WebChatroomService(this.gunService);
    this.placeChatClient = new PlaceChatClient(() => this.getBackendApiBase());
    // Membership heartbeats must always carry the CURRENT stage name — a captured snapshot
    // clobbers renames back to the old name on every beat (see startMembershipHeartbeat).
    this.chatroomService.setMembershipStageNameResolver(() => this.currentUser?.stageName || '');
    // Travel is room-membership metadata, not a private UI decoration: peers in the room must
    // agree that a user deliberately visiting away from their saved home is a traveler.
    this.chatroomService.setMembershipTravelerResolver((chatroomId) => {
      // A chosen-point user (every website user) is always a Traveler.
      if (usesSelfDefinedEncounterLocation() && (isNearbyRoomId(chatroomId) || isPlaceRoomId(chatroomId))) return true;
      if (isNearbyRoomId(chatroomId)) return this.pendingNearbyTravel != null || (this.travelModeActive && this.travelNearbyPoint != null);
      if (!isPlaceRoomId(chatroomId)) return false;
      return !this.placeIsAtMyGps(chatroomId);
    });
    this.talkService = new WebTalkService(this.gunService, this.getBackendApiBase(), {
      meshLocalFirst: usesMeshTalkDelivery(this.p2pRuntimeFlags),
    });
    this.conversationService = new WebConversationService(this.gunService);
    this.contentNodeService = new WebContentNodeService();
    this.attestationService = new WebAttestationService(this.getBackendApiBase());
    // Scenario 2 (§16): every direct-p2p handshake evaluates a remote peer's presented
    // buildTrustCredential against our own pinned official identity, using whatever this
    // device's own attestationService currently has cached — see WebAttestationService and
    // computeBuildTrust (p2p-webrtc-session.ts) for what "currently has cached" means and why
    // this is never a blocking call.
    this.conversationService.setBuildTrustHooks({
      getBuildTrustCredential: () => {
        const localPub = this.currentUser?.pub || this.gunService.getStoredPair()?.pub;
        return localPub ? this.attestationService.getCachedCredential(localPub) ?? undefined : undefined;
      },
      officialApplicationId: OFFICIAL_APPLICATION_ID,
      officialSigningIdentityHash: OFFICIAL_SIGNING_IDENTITY_HASH,
      lookupVerifierKey: (verifierKeyId) => this.attestationService.lookupVerifierKey(verifierKeyId),
    });
    this.identityLinkService = new WebIdentityLinkService(this.gunService, undefined, this.getBackendApiBase());
    this.deviceHandoffService = new WebDeviceHandoffService(this.gunService, this.getBackendApiBase());
    this.deviceSyncService = new WebDeviceSyncService(this.gunService, this.getBackendApiBase());
    this.deviceSyncService.setHandlers({
      onPreferencesApplied: (filters) => {
        if (!this.currentUser) return;
        this.currentUser.talkFilters = filters;
        this.writeCachedUser(this.currentUser);
        // settings-view.ts deliberately prefers localStorage's cached talkFilters over
        // user.talkFilters when it's owned by this same user (docs/e2e
        // 31-intake-filters-persist, 2026-08-09: a Gun round-trip caught mid-drain can hand back
        // a partial/stale user.talkFilters, so this device's own last-known-good local copy wins
        // instead). Without writing through here too, an incoming synced value would update
        // user.talkFilters but the Settings page would keep rendering the stale local copy.
        setTalkIntakeFilters(filters);
        setTalkIntakeFiltersOwner(this.currentUser.id);
        this.uiManager.refreshSettingsViewIfActive(this.currentUser);
        void this.userService.updateTalkFilters(this.currentUser.id, filters);
      },
      onConflict: (conflicts) => showDeviceSyncConflictDialog({
        conflicts,
        text: (key, fallback) => this.uiManager.translateWithFallback(key, fallback ?? key),
      }),
      onConversationApplied: (conversation) => {
        this.uiManager.addNewConversation(conversation as any);
      },
      onMessageApplied: (conversationId, message) => {
        addSyncedMessage(conversationId, message);
        const merged = mergeMessagesWithSynced(conversationId, []);
        const currentUserId = this.currentUser?.id;
        if (currentUserId) this.uiManager.syncConversationMessageSummary(conversationId, merged, currentUserId);
        // No-ops unless this conversation's thread happens to already be open —
        // displayConversationMessages self-guards on getCurrentConversationId().
        void this.uiManager.displayConversationMessages(conversationId, merged);
      },
      // supportChannel conversations excluded: TechSupport is a per-device relationship (every
      // device has its OWN, independently valid support conversation — CLAUDE.md's "TechSupport
      // counts as exactly 1 in every headcount"), not something to copy from one linked device
      // onto another. Found via the real-hardware device-sync test: without this filter, backfill
      // also shipped each device's own TechSupport conversation to its peer as if it were a third
      // person, inflating the peer's conversation count.
      getLocalConversationsSnapshot: () =>
        Object.values(this.uiManager.getMyConversations())
          .filter((conversation: any) => conversation?.supportChannel !== true) as Array<Record<string, unknown> & { conversationId: string; otherUserId?: string }>,
      getLocalMessagesSnapshot: (conversationId, otherUserId) => this.snapshotLocalMessagesForSync(conversationId, otherUserId),
    });
    this.userService.setPrivateUserDataChangeListener((user) => {
      if (!user.talkFilters) return;
      void this.deviceSyncService.enqueuePreferencesChange(user.pub || '', user.talkFilters);
    });
    this.uiManager = new UIManager();
    // A link change can move my Nearby count key; that audience refresh runs in the background so
    // the linked-devices dialog never waits on the hub (stage2/73 timed out on "Removed").
    this.uiManager.setIdentityLinkHooks({
      createLinkCode: (now) => this.identityLinkService.createLinkCode(now),
      readIncomingRequest: () => this.identityLinkService.readIncomingLinkRequest(),
      approveIncomingRequest: async (pub) => {
        const approved = await this.identityLinkService.confirmIncomingLink(pub);
        if (approved) {
          this.deviceSyncService.watchPeerForSync(pub);
          void this.refreshNearbyCountIdentity();
        }
        return approved;
      },
      cancelPendingRequest: (requestId) => this.identityLinkService.cancelPendingLink(requestId),
      refreshRecords: async () => {
        await this.identityLinkService.refreshLocalRecords();
        void this.refreshNearbyCountIdentity();
      },
      completeFromCode: async (code) => {
        const result = await this.identityLinkService.completeLinkFromCode(code);
        if (result.ok) {
          this.deviceSyncService.watchPeerForSync(result.peerPub);
          void this.refreshNearbyCountIdentity();
        }
        return result.ok ? null : result.error;
      },
      unlink: async (pub) => {
        const result = await this.identityLinkService.unlink(pub);
        void this.refreshNearbyCountIdentity();
        return result;
      },
      isLinked: (pub) => this.identityLinkService.isLinked(pub),
    });
    this.uiManager.setSupportDelegateInviteHooks({
      createInvite: () => this.handleCreateTechSupportDelegateInvite(),
      submitInviteCode: (code) => this.handleSubmitTechSupportDelegateRequest(code),
      previewInviteTarget: (userId) => this.previewTargetedDelegateInviteTarget(userId),
      sendTargetedInvite: (userId) => this.handleSendTargetedDelegateInvite(userId),
      acceptTargetedInvite: () => this.handleRespondToTargetedDelegateInvite(true),
      declineTargetedInvite: () => this.handleRespondToTargetedDelegateInvite(false),
    });
    this.uiManager.setDeviceSyncHooks({
      stateFor: (pub) => this.deviceSyncService.peerState(pub),
      enable: (pub) => this.deviceSyncService.enableSyncWithPeer(pub),
    });
    // The reviewed password-custody implementation is available to development/E2E
    // builds for staged verification. Production can still unlock an existing v2
    // record, but Settings mutation controls remain hidden until the security review's
    // cross-platform release conditions are complete.
    const identityPasswordSettingsEnabled = process.env.NODE_ENV !== 'production';
    this.uiManager.setIdentityPasswordHooks({
      getStatus: async () => {
        const status = await this.gunService.getIdentityPasswordProtectionStatus();
        return { state: status.state };
      },
      ...(identityPasswordSettingsEnabled
        ? {
            setPassword: (password: string) => this.gunService.setIdentityPassword(password),
            changePassword: (currentPassword: string, newPassword: string) =>
              this.gunService.changeIdentityPassword(currentPassword, newPassword),
            removePassword: (currentPassword: string) =>
              this.gunService.removeIdentityPassword(currentPassword),
            lockNow: async () => {
              await this.gunService.lockIdentityNow();
              window.location.reload();
            },
          }
        : {}),
    });
    // Scenario 1 fix (2026-09-29): a P2P peer's handshake revealed a different app version.
    // Remind the older side to upgrade, and the newer side that this particular contact may not
    // understand brand-new features yet. Subscribed once for the app's lifetime; the session
    // layer already dedupes to one notification per live connection (see
    // maybeNotifyVersionMismatch in p2p-webrtc-session.ts).
    onP2PVersionMismatch((event: P2PVersionMismatchEvent) => {
      const message = event.direction === 'local-older'
        ? this.uiManager.formatP2PVersionMismatchOlder(event.localVersion, event.remoteVersion)
        : this.uiManager.formatP2PVersionMismatchNewer(event.localVersion, event.remoteVersion);
      this.uiManager.showNotification(message, 'warning', { peerId: event.otherUserId });
    });
    setP2PContactIdentityResolver(async (localUserId, otherUserId) => {
      if (this.currentUser?.id !== localUserId) return undefined;
      return this.userService.getPinnedIdentityPub(otherUserId);
    });
  }

  /**
   * True once `currentLocation` reflects a real fix (cached from a previous real GPS read, or a
   * freshly-resolved one) rather than the neutral placeholder `index.ts` boots with while the
   * real location resolves in the background (cache-first UI — see updateCurrentLocation below).
   * Gates the one-time location-room suggestion: firing it against the placeholder would consume
   * its per-user "already shown" flag with a location that was never real, permanently
   * suppressing the correct suggestion once the real fix comes in.
   */
  private locationConfirmed = true;

  async initialize(location: GPSCoordinate, options: { locationConfirmed?: boolean } = {}): Promise<void> {
    this.initialized = false;
    this.mapMatchView.reset();
    this.currentLocation = location;
    this.locationConfirmed = options.locationConfirmed ?? true;

    // Paint the cached shell immediately. Nearby identity, presence and counts hydrate below.
    this.uiManager.initialize();
    this.uiManager.setCurrentLocation(location, this.locationConfirmed);
    this.uiManager.showStartupInterface();
    markStartupPhase('firstUsableNavigation');

    // Initialize services (stage-zero server wipe happens in index.ts before init; do not purge
    // here — clearing the graph before SEA auth breaks gun.user().auth()).
    // Native (OPEN-91 P5): the app's node holds the device's one Gun and its core; the WebView runs
    // no Gun, and keeps its worker only until what an earlier version stored there is copied over.
    const nativeNode = await resolveNativeNode().catch(() => null);
    if (nativeNode) {
      const marks = await nativeNode.core({ name: 'collection.snapshot', collection: 'appData' }).catch(() => ({})) as Record<string, unknown>;
      this.gunService.useNativeNode({ ...nativeNode, keepWorker: !marks[MIGRATION_MARKS.collections] || !marks[MIGRATION_MARKS.privates] });
    }
    await this.gunService.initialize();
    // The exchange log lives in the device core (OPEN-91 P1): load it before the first screen reads it.
    await this.resolveCoreHost(nativeNode);
    await this.connectExchangeLog();
    // Use the same core-backed view as Contacts, including late hydration and removals.
    this.setMapMatchSource(exchangeMapMatchSource);
    // Kept in the device core, readable only once it has loaded.
    this.loadAttachmentShareSentIds();
    restoreCreatorReplyFilterState();
    const passwordStatus = await this.gunService.getIdentityPasswordProtectionStatus();
    if (passwordStatus.state === 'locked') {
      await this.uiManager.showIdentityUnlock(
        passwordStatus.publicIdentity.pub,
        async (password) => {
          await this.gunService.unlockIdentity(password);
        },
        async () => {
          await this.gunService.lockIdentityNow();
          await eraseDevice();
        },
      );
    } else {
      await this.gunService.ensureKeypairAndAuth();
    }
    await this.finishMoveToNativeNode();
    this.gunService.getGun().get('public').get('chatroom-hierarchy').on((raw: unknown) => {
      applyPublicChatroomHierarchy(raw);
    });
    void this.identityLinkService.flushPendingRevocations().catch(() => {});
    // getPrivate() (used by loadState()) has a hardcoded 4s wait for a lone peer with nothing to
    // answer it locally — this was blocking every single boot for 4s to restore an auto-reply
    // memory that has no bearing on whether the app can be used yet. Fire-and-forget: arrives
    // like email whenever (if) it lands, and updates chatbot memory in place at that point.
    const durableChatbotMemory = new GunChatbotMemoryRepository(this.gunService);
    void durableChatbotMemory.loadState().then((restored) => {
      if (restored) setExactChatbotMemory(restored);
    }).catch(() => {});
    await this.syncConversationTransportFromServer();
    this.initLedgerTransportHooks();

    // Phase E: initialize ledger after SEA keypair is established
    this.initLedger();

    // Finish wiring the already-visible UI.
    this.uiManager.setApiBase(this.getBackendApiBase());
    void this.uiManager.renderAppDownloadBanner();
    void this.uiManager.renderAppUpdateReminderBanner();
    this.uiManager.setPublicProfileFoundationReader(async (userId: string) => {
      const [data, reputation] = await Promise.all([
        this.gunService.get(`user-public-profile/${userId}`),
        this.gunService.get(`users/${userId}/reputation`).catch(() => null),
      ]);
      if (!data || typeof data !== 'object') return null;
      return { ...(data as object), ...(reputation && typeof reputation === 'object' ? { reputation } : {}) } as {
        headshot?: string | null;
        languagesJson?: string;
        profileJson?: string;
        interestsJson?: string;
      } & { reputation?: { questionsAnswered?: number; matchesFound?: number; blockCount?: number; isHidden?: boolean } };
    });
    this.uiManager.setContactPreRenderSync(async () => {
      await this.syncDirectPairTalkExchangesForContacts();
    });
    // Scenario 2 (§16): lets the peer-detail view read a live buildTrust label — see
    // conversationService.getHandshakeDiagnostics's own doc comment (P2P-Y).
    this.uiManager.setHandshakeDiagnosticsReader((conversationId, localUserId) =>
      this.conversationService.getHandshakeDiagnostics(conversationId, localUserId),
    );
    // L5: turn decrypted shared-attachment bytes into a viewable object URL so the
    // conversation view can render the shared photo instead of the raw IPFS_SHARE payload.
    this.uiManager.setSharedAttachmentResolver(async (cid: string, mimeType: string) => {
      const key = String(cid || '').trim();
      if (!key) return null;
      let bytes = this.fetchedAttachmentBytesByCid.get(key);
      if (!bytes) {
        // The SENDER holds their own published block locally (they never "fetch" it), and
        // the content node caches fetched blocks — read it so the author's own shared photo
        // previews instead of showing a broken/forever-loading chip.
        try {
          const local = await this.contentNodeService.readLocalBlock(key);
          if (local && local.length) {
            bytes = local;
            this.fetchedAttachmentBytesByCid.set(key, local);
          }
        } catch { /* not available locally */ }
      }
      if (!bytes) return null;
      let url = this.attachmentObjectUrlByCid.get(key);
      if (!url) {
        // Copy into a fresh ArrayBuffer-backed view so Blob's typing is satisfied.
        const copy = new Uint8Array(bytes);
        url = URL.createObjectURL(new Blob([copy.buffer], { type: mimeType || 'application/octet-stream' }));
        this.attachmentObjectUrlByCid.set(key, url);
      }
      return url;
    });
    // Sync-before-erase (redesign §11.2, item J): build the handoff archive from local
    // sources, encrypt it to the linked device's pub, publish it, then wait for the
    // receiver's signed acknowledgement. §11.3's safety invariant — "erase stays disabled
    // until the archive is acknowledged by the receiving device" — depends entirely on
    // this promise only resolving on a REAL verified ack; `sendHandoffArchive`'s
    // non-'sent' results and `waitForHandoffAck`'s timeout both throw/return false here,
    // which erase-device-dialog.ts's own doc comment treats as "show an error, never
    // silently enable Done."
    this.uiManager.setDeviceHandoffSync(async (toPub, progress) => {
      const { buildHandoffArchive } = await import('../../shared/device-handoff');
      const pair = this.gunService.getStoredPair?.();
      const sources: Parameters<typeof buildHandoffArchive>[0] = { fromPub: String(pair?.pub || '') };
      const collect: Array<[import('../../shared/device-handoff').HandoffCategory, () => void]> = [
        ['profile', () => {
          if (this.currentUser) {
            sources.profile = {
              stageName: this.currentUser.stageName,
              languages: this.currentUser.languages,
              profile: this.currentUser.profile,
              interests: this.currentUser.interests,
            } as any;
          }
        }],
        ['contacts', () => {
          const people = this.currentUser?.knownPeople;
          if (Array.isArray(people)) {
            sources.contacts = people.map((p: any) => ({ ...p, id: String(p.userId || p.id || '') }));
          }
        }],
        ['talkFilters', () => { sources.talkFilters = this.currentUser?.talkFilters as any; }],
        ['answerPreferences', () => { sources.answerPreferences = getExactChatbotMemory() as any; }],
        ['myTalks', () => { sources.myTalks = getMyTalks(); }],
        ['conversations', () => { sources.conversations = getMyConversations(); }],
      ];
      for (const [category, fill] of collect) {
        try {
          fill();
        } catch {
          /* category unavailable locally — still counts as collected */
        }
        progress(category);
      }
      const archive = buildHandoffArchive(sources);
      const result = await this.deviceHandoffService.sendHandoffArchive(toPub, archive);
      if (result !== 'sent') {
        throw new Error(`handoff send failed: ${result}`);
      }
      const acked = await this.deviceHandoffService.waitForHandoffAck(toPub, this.handoffAckTimeoutMs());
      if (!acked) {
        throw new Error('handoff not acknowledged within the timeout');
      }
    });
    // TODO §J receiver side: check for an archive from any of THIS device's own
    // verified-linked pubs (never a general "who sent me something" discovery — see
    // web-device-handoff-service.ts's own doc comment on why no `.map()` is needed) and,
    // on explicit user Import, merge it and publish the signed ack the sender is waiting
    // on. Import is never automatic — the archive only becomes real local data once the
    // receiving user has reviewed and pressed Import (spec §11.2's "importable item").
    this.uiManager.setDeviceHandoffReceive({
      checkIncoming: async () => {
        let linked: Array<{ pub: string; stageName: string }> = [];
        try {
          const arr = JSON.parse(appStorage.getItem('iinpublic_linked_devices') || '[]');
          linked = Array.isArray(arr) ? arr.filter((r: any) => r?.state === 'linked') : [];
        } catch {
          linked = [];
        }
        for (const row of linked) {
          const archive = await this.deviceHandoffService.readIncomingHandoff(row.pub).catch(() => null);
          if (archive) return { fromPub: row.pub, fromName: row.stageName || '', archive };
        }
        return null;
      },
      importArchive: async (fromPub, archive) => {
        if (!this.currentUser) return;
        const { mergeHandoffArchive } = await import('../../shared/device-handoff');
        const read = (key: string): any => {
          try {
            return JSON.parse(appStorage.getItem(key) || 'null') ?? undefined;
          } catch {
            return undefined;
          }
        };
        const write = (key: string, value: unknown): void => {
          try {
            appStorage.setItem(key, JSON.stringify(value));
          } catch {
            /* best effort */
          }
        };
        const merged = mergeHandoffArchive(archive as any, {
          contacts: (this.currentUser.knownPeople as any) || [],
          talkFilters: this.currentUser.talkFilters as any,
          answerPreferences: getExactChatbotMemory() as any,
          myTalks: getMyTalks(),
        });
        this.currentUser.knownPeople = merged.contacts as any;
        this.currentUser.talkFilters = merged.talkFilters as any;
        this.writeCachedUser(this.currentUser);
        await this.userService.importHandoffData(this.currentUser.id, {
          knownPeople: merged.contacts as any,
          talkFilters: merged.talkFilters as any,
        });
        setExactChatbotMemory(merged.answerPreferences as any);
        setMyTalks(merged.myTalks as any);
        // Imported thread history is read-only (spec §11.2) — never merged into the
        // live conversation store, kept under its own key, additive across imports
        // rather than clobbering a prior import from a different linked device.
        const importedKey = 'importedHandoffConversations';
        const existingImported = read(importedKey) || {};
        write(importedKey, { ...existingImported, ...merged.readOnlyConversations });
        await this.deviceHandoffService.acknowledgeHandoff(fromPub);
      },
    });
    this.uiManager.setPeerLocationReader(async (peerId: string) => {
      const data = await new Promise<unknown>((resolve) => {
        this.gunService.getGun()
          .get('users')
          .get(peerId)
          .get('location')
          .once((locData: unknown) => resolve(locData));
      });
      const region = data && typeof data === 'object' ? (data as { region?: unknown }).region : undefined;
      return typeof region === 'string'
        ? LocationPrivacy.coordinateFromRegion(region) ?? undefined
        : undefined;
    });
    // Two first-ever tabs can finish SEA custody together, then both observe no app user id.
    // Keep the read/create/cache operation under one origin lock so the second tab adopts the
    // first one's user record. Storage ownership alone cannot serialize these GUI-side effects.
    if (!nativeNode && typeof navigator !== 'undefined' && navigator.locks) {
      await navigator.locks.request('iinpublic-device-user-initialization', () => this.initializeUser());
    } else {
      await this.initializeUser();
    }
    // Contextual chatbot memory (docs/design/contextual-chatbot-memory.md): one-time, no-
    // user-interaction resync of the chatbot's answer memory whenever ANSWER_CONTEXT_VERSION
    // bumps — see answer-context-migration.ts's own header for why this is a full rebuild from
    // myTalks history rather than an in-place hash conversion. Purely local/synchronous (no Gun
    // dependency), so it runs right after currentUser is known and before anything reads
    // flattenedAnswerPreferences; no-ops instantly once the version marker is current.
    try {
      const { migratedTalks } = runAnswerContextMigration(this.currentUser?.id);
      if (migratedTalks > 0) {
        console.log(`🔄 Chatbot answer memory resynced for ${migratedTalks} saved talk(s) after an answer-context version upgrade.`);
      }
    } catch (error) {
      console.warn('Answer-context migration failed (non-fatal — chatbot will re-ask as needed):', error);
    }
    // Scenario 2 (§16): kick off this device's build-attestation credential + the verifier's
    // published keys, both fire-and-forget — WebAttestationService's own doc comment explains
    // why this never gates app usability (native bridge call + two network round trips, and the
    // P2P handshake path only ever reads whatever's already cached by the time it needs it).
    const localPubForAttestation = this.currentUser?.pub || this.gunService.getStoredPair()?.pub;
    if (localPubForAttestation) {
      void this.attestationService.ensureCredential(localPubForAttestation).catch(() => {});
    }
    void this.attestationService.ensureVerifierKeys().catch(() => {});
    // Rebuilds previously-received Talk UI from a Gun read that, for a lone peer with nothing
    // local to answer it, pays its own multi-second timeout before giving up (gun-talk-repository
    // .ts's listReceived → WebGunService.get()). None of this gates whether the app is usable —
    // it only back-fills a list the user can already see is there once it arrives. Fire-and-forget.
    void restoreReceivedTalkHistory(
      this.talkService,
      (talk) => this.uiManager.displayIncomingTalk(talk),
    ).catch(() => {});

    // Join appropriate chatroom
    await this.initializeChatrooms();

    // Setup event handlers
    this.setupEventHandlers();
    this.ensureMatchCollectionSession(this.chatroomService.getCurrentChatroomId());

    // Show main interface
    this.uiManager.showMainInterface(this.currentUser!);
    restoreLastTab();
    this.uiManager.showFirstRunWalkthroughIfNeeded();
    this.checkForPendingIdentityLinkFragment();
    // TODO §J — publish this identity's signed pub→epub binding on every boot so a
    // linked device can encrypt a handoff *to* this identity without needing to already
    // know its app-level userId (see web-device-handoff-service.ts's own doc comment).
    // Best-effort, never a boot blocker.
    void this.deviceHandoffService.publishEpub().catch(() => {});
    this.subscribeToPublicAnnouncements();
    // Subscribe to member counts for all chatrooms (real-time updates)
    this.subscribeToAllChatroomMemberCounts();
    void this.refreshCustomChatroomsFromServer().then(() => {
      // Custom rooms may introduce additional IDs not present at startup.
      this.subscribeToAllChatroomMemberCounts();
    });
    if (isDevStageZeroSelfHeal()) {
      this.stageZeroBootedAt = Date.now();
      this.startStageZeroHeadcountWatchdog();
    }
    this.initialized = true;
    if (takeLegacyPrivacyMigration() === 'contacts-only' && this.currentUser) {
      // "Contacts only" moved from the location list to the Talk audience.
      const filters = { ...getTalkIntakeFilters(), contactsOnlyTalks: true };
      setTalkIntakeFilters(filters);
      setTalkIntakeFiltersOwner(this.currentUser.id);
      this.currentUser.talkFilters = filters;
    }
    this.startQuietWatch();
    await this.startNearbyV3();
    this.refreshNearbyPoint();
    this.startActiveRooms();
    this.startPresenceRefresh();
    if (this.locationConfirmed) {
      void this.applyNearbyPrivacyMode(loadConnectivitySettings()).catch(() => {});
    }
    // Registered for everyone: a phone can switch to Traveler later in Settings.
    window.addEventListener(ENCOUNTER_LOCATION_EVENT, (event) => {
      if (!usesSelfDefinedEncounterLocation()) return;
      const detail = (event as CustomEvent<EncounterLocation | null>).detail ?? null;
      // A phone without a GPS fix whose owner picks a point keeps it: a later fix must not move them.
      if (detail && !detail.auto && isGpsFixUnavailable() && loadConnectivitySettings().locationSource !== 'traveler') {
        saveConnectivitySettings({ ...loadConnectivitySettings(), locationSource: 'traveler' });
      }
      this.applyEncounterLocationChange(detail);
    });
    if (this.forcedGpsFallbackPending) {
      this.forcedGpsFallbackPending = false;
      this.handleGpsFixUnavailable({ force: true });
    } else {
      this.placeLocationlessDeviceOnMap();
    }
    markStartupPhase('initialSyncComplete');
  }

  /**
   * TODO §I — URL-fragment same-device linking shortcut (spec §10.3): a `#link=<code>`
   * fragment opened on this device (e.g. from "Link this device's browser"/"Open in app
   * to link", or a loopback affordance's `window.open`) never reaches any server — it's
   * consumed entirely client-side. Structurally validated before popping any UI, so an
   * unrelated/garbage `#...` hash on the page is silently ignored rather than surfacing a
   * confusing linking prompt. The fragment is cleared immediately either way: it carries
   * a one-time secret that must not linger in the URL bar, browser history, or survive a
   * reload (a stale fragment must not re-trigger the prompt on every refresh).
   */
  private checkForPendingIdentityLinkFragment(): void {
    try {
      const parsed = parseLinkFragmentPayload(window.location.hash || '');
      if (!parsed) return;
      clearLinkFragmentFromUrl();
      this.uiManager.openLinkedDevicesWithCode(parsed.code);
    } catch {
      /* non-fatal — same-device shortcut is a convenience, never a boot blocker */
    }
  }

  private subscribeToPublicAnnouncements(): void {
    const publicGun = this.gunService.getGun().get('public');
    publicGun.get('techsupport-identity').on((raw: unknown) => {
      void isVerifiedTechSupportIdentity(raw, TECHSUPPORT_ANNOUNCEMENT_TRUST_ANCHORS).then((valid) => {
        if (raw && !valid) this.uiManager.showNotification('TechSupport identity verification failed.', 'warning');
      });
    });
    publicGun.get('announcements').map().on((raw: unknown) => {
      void isRenderableSystemAnnouncement(raw, TECHSUPPORT_ANNOUNCEMENT_TRUST_ANCHORS).then((valid) => {
        if (valid) {
          const announcement = raw as { id: string; text: string };
          this.uiManager.showSystemAnnouncement(announcement);
        }
      });
    });
  }

  /**
   * Read the self-signed TechSupport identity advertised through local Gun.
   * This deliberately does not use the compiled public-key pin: callers can
   * discover an offline/bootstrap peer's advertised identity, while normal app
   * startup continues to enforce the pin in `subscribeToPublicAnnouncements`.
   */
  public async discoverTechSupportIdentityFromGun(): Promise<TechSupportIdentity | null> {
    const raw = await new Promise<unknown>((resolve) => {
      this.gunService.getGun().get('public').get('techsupport-identity').once((value: unknown) => resolve(value));
    });
    return readVerifiedTechSupportIdentity(raw);
  }

  /**
   * Cache-first UI (index.ts): called once the real GPS fix resolves in the background, after
   * boot already painted with a cached-or-placeholder location so first paint never waits on it.
   * A no-op before initialization has established a pending first-room assignment.
   */
  /**
   * Website/desktop encounter location changed in Settings. Setting it re-resolves the shared Nearby
   * room at that point (travel, if active, keeps its own point); clearing it returns to Contacts only.
   */
  private applyEncounterLocationChange(value: { latitude: number; longitude: number } | null): void {
    if (value) {
      this.updateCurrentLocation(encounterLocationCoordinate(value));
      return;
    }
    this.locationConfirmed = false;
    if (this.currentUser && this.initialized) {
      void this.applyNearbyPrivacyMode(loadConnectivitySettings()).catch((error) => {
        console.warn('Nearby room refresh after clearing the encounter location failed:', error);
      });
    }
  }

  /**
   * Everyone shows up on the map (OPEN-53). A device without real GPS — website/desktop, a phone
   * Traveler, or a phone whose fix was denied or never came — appears at its chosen point as a
   * Traveler; with no point yet it is placed at a random city (ENCOUNTER_LOCATION_EVENT above moves
   * it into that Nearby room), told so, and asked to pick its own. TechSupport stays Contacts only.
   */
  private placeLocationlessDeviceOnMap(options: { askToPick?: boolean } = {}): void {
    if (!this.currentUser || isTechSupportUser(this.currentUser)) return;
    if (!usesSelfDefinedEncounterLocation()) return;
    const existing = getEncounterLocation();
    if (existing) {
      if (!this.locationConfirmed) this.applyEncounterLocationChange(existing);
      if (existing.auto) this.uiManager.showNotification(this.uiText('encounterLocationRandomHelp'), 'info');
      return;
    }
    const placed = assignRandomEncounterLocation();
    this.uiManager.showNotification(this.uiText('encounterLocationRandomHelp'), 'info');
    if (options.askToPick === false) return;
    showEncounterLocationPicker({
      t: (key) => this.uiText(key),
      initial: { latitude: placed.latitude, longitude: placed.longitude },
      onConfirm: (point) => { setEncounterLocation(point.latitude, point.longitude); },
    });
  }

  /**
   * A phone's GPS fix was denied, failed or timed out (index.ts). Unless a real fix is already in
   * hand, the device becomes a Traveler at its encounter point instead of staying in Contacts only.
   */
  public handleGpsFixUnavailable(options: { askToPick?: boolean; force?: boolean } = {}): void {
    if (this.locationConfirmed && !isGpsFixUnavailable() && !options.force) return;
    setGpsFixUnavailable(true);
    // Before initialize() finishes, its own placeLocationlessDeviceOnMap() call picks this up; a
    // forced fallback (location denied while a saved fix is in use) is replayed there in full.
    if (!this.initialized) {
      if (options.force) this.forcedGpsFallbackPending = true;
      return;
    }
    const existing = getEncounterLocation();
    if (options.force && existing) this.applyEncounterLocationChange(existing);
    this.placeLocationlessDeviceOnMap(options);
  }

  private forcedGpsFallbackPending = false;

  /** True when the current location is a real device fix, not an encounter point. */
  private hasRealGpsFix(): boolean {
    if (!this.locationConfirmed || !this.currentLocation) return false;
    const encounter = getEncounterLocation();
    return !encounter
      || encounter.latitude !== this.currentLocation.latitude
      || encounter.longitude !== this.currentLocation.longitude;
  }

  /** A real GPS fix arrived: a device that was a Traveler only for lack of GPS becomes a Resident. */
  public applyRealGpsFix(location: GPSCoordinate): void {
    setCachedLocation(location);
    setGpsFixUnavailable(false);
    if (usesSelfDefinedEncounterLocation()) return;
    document.getElementById('encounter-location-modal')?.remove();
    this.updateCurrentLocation(location);
  }

  updateCurrentLocation(location: GPSCoordinate): void {
    this.currentLocation = location;
    this.locationConfirmed = true;
    this.uiManager.setCurrentLocation(location);
    if (this.currentUser) {
      this.refreshQuietState();
      void this.userService.updateUserLocation(this.currentUser.id, this.publicHomeLocation()).catch(() => {});
      this.refreshNearbyPoint();
      if (this.initialized) {
        void this.applyNearbyPrivacyMode(loadConnectivitySettings()).catch((error) => {
          console.warn('Nearby room refresh after location update failed:', error);
        });
      }
    }
  }

  /**
   * Blurred author location for a new talk — only once a real fix has resolved. A device that never
   * got one (location permission denied, no GPS) boots on a placeholder (index.ts, New York City);
   * stamping that on talks made receivers' distance filter reject them as thousands of miles away —
   * seen on hardware with two phones side by side in OPEN-36 offline mode.
   */
  private confirmedLocationForTalks(): { latitude: number; longitude: number } | undefined {
    const location = this.publicHomeLocation();
    return location ? { latitude: location.latitude, longitude: location.longitude } : undefined;
  }

  /**
   * The only public form of the home location (users/<id>/location, Talk authorLocation): the
   * snapped square for the user's privacy setting, or null for Contacts only / Location off.
   */
  private publicHomeLocation(): PublicLocation | null {
    if (!this.currentLocation || !this.locationConfirmed) return null;
    const next = publicLocationWithHysteresis(
      this.currentLocation,
      this.effectiveNearbyMode(),
      this.lastPublicHomeLocation,
    );
    this.lastPublicHomeLocation = next;
    return next;
  }

  private uiText(key: Parameters<typeof uiText>[1]): string {
    return uiText(getUiLanguagePreference(uiLanguageFromProfile(this.currentUser?.languages)), key);
  }

  /**
   * The area size actually used right now: the user's setting, unless a quiet place / schedule /
   * "Quiet now" applies. Blur → Regional (~50 km). Hide → out of Nearby; Contacts still work.
   */
  private effectiveNearbyMode(): NearbyPrivacyMode {
    const quiet = this.quietState;
    if (quiet?.active) return quiet.action === 'hide' ? 'contacts-only' : 'regional';
    return loadConnectivitySettings().nearbyPrivacyMode;
  }

  /**
   * A Traveler is a cyber traveler: someone who does not appear at their own real GPS location
   * (product definition 2026-10-24). A Resident with a real fix is at home wherever that fix is —
   * moving with the phone is not travel. Travelers are: no usable GPS or the Traveler setting
   * (every website user), Nearby at a point picked on the map, and a Place that is not where the
   * real GPS is.
   */
  private appearsAsTraveler(): boolean {
    if (usesSelfDefinedEncounterLocation()) return true;
    const roomId = this.currentChatroomId;
    if (roomId && isPlaceRoomId(roomId)) return !this.placeIsAtMyGps(roomId);
    return this.travelModeActive && this.travelNearbyPoint != null;
  }

  /** The Place's pin is in the same blurred area as my real GPS fix. */
  private placeIsAtMyGps(placeId: string): boolean {
    const place = this.uiManager.getCustomChatroomMeta(placeId)?.location;
    if (!place || !this.currentLocation || !this.locationConfirmed) return false;
    return LocationPrivacy.blurLocation(this.currentLocation).region
      === LocationPrivacy.blurLocation({ ...this.currentLocation, ...place }).region;
  }

  /** Quiet places use the device's own GPS, so they only apply to a Resident with a real fix. */
  private refreshQuietState(): boolean {
    const residentGps = !usesSelfDefinedEncounterLocation() && this.locationConfirmed && this.currentLocation
      ? this.currentLocation
      : null;
    const next = quietStateFor(loadQuietSettings(), residentGps, new Date());
    const before = JSON.stringify(this.quietState);
    this.quietState = next;
    if (JSON.stringify(next) === before) return false;
    if (this.initialized) {
      this.uiManager.showNotification(
        !next.active
          ? this.uiText('quietEnded')
          : next.action === 'hide' ? this.uiText('quietHidden') : this.uiText('quietBlurred'),
        'info',
      );
    }
    return true;
  }

  /** Re-apply rooms, the map dot and the public location when the quiet state changes. */
  private applyQuietStateIfChanged(): void {
    if (!this.refreshQuietState() || !this.initialized) return;
    void this.applyNearbyPrivacyMode(loadConnectivitySettings()).catch((error) => {
      console.warn('Applying the quiet state failed (non-fatal):', error);
    });
  }

  private startQuietWatch(): void {
    if (this.quietTimer) return;
    this.refreshQuietState();
    this.quietTimer = setInterval(() => this.applyQuietStateIfChanged(), 60_000);
    window.addEventListener(QUIET_SETTINGS_EVENT, () => this.applyQuietStateIfChanged());
    // Arriving at a quiet place while the app stays open: re-read GPS every few minutes, and
    // at once when the app returns to the foreground (e.g. unlocked at home).
    this.quietGpsTimer = setInterval(() => { void this.refreshGpsForQuietPlaces(); }, QUIET_GPS_REFRESH_MS);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.refreshGpsForQuietPlaces();
    });
  }

  private async refreshGpsForQuietPlaces(): Promise<void> {
    if (this.quietGpsInFlight || !shouldRefreshQuietGps({
      resident: !usesSelfDefinedEncounterLocation(),
      // Only a real phone GPS; dev/test builds keep their injected test location.
      hasRealGps: deviceHasGps(undefined, false),
      quietPlaceCount: loadQuietSettings().places.length,
      visible: document.visibilityState === 'visible',
    })) return;
    this.quietGpsInFlight = true;
    try {
      const location = await readDeviceLocation();
      setCachedLocation(location);
      this.updateCurrentLocation(location);
    } catch (error) {
      console.warn('Quiet-place GPS refresh failed (keeping the last fix):', error);
    } finally {
      this.quietGpsInFlight = false;
    }
  }

  /** My card's point: the travel point while traveling (same precision as home), else home. */
  private nearbyPublicPoint(): PublicLocation | null {
    const home = this.publicHomeLocation();
    const travel = this.travelModeActive ? this.travelNearbyPoint : undefined;
    if (!travel) return home;
    const precision = publicLocationPrecisionMeters(this.effectiveNearbyMode());
    return precision === null ? null : snapPublicLocation(travel, precision);
  }

  /** Default-on collection is ambient. Lazily restore it if storage was cleared or a session ended. */
  private ensureMatchCollectionSession(
    scopeIdInput?: string | null,
  ): ReturnType<typeof getActiveMatchCollectionSession> {
    if (!getMatchCollectionEnabled()) return null;
    const active = getActiveMatchCollectionSession();
    if (active) return active;
    const scopeId = String(scopeIdInput || this.chatroomService.getCurrentChatroomId() || '').trim();
    if (!scopeId) return null;
    const point = this.nearbyPublicPoint();
    return startMatchCollectionSession({
      label: this.getChatroomDisplayName(scopeId) || this.uiText('matchCollectionDefaultLabel'),
      scopeId,
      talkIds: this.uiManager.getBroadcastableTalkIds(),
      ...(point ? { point } : {}),
    });
  }

  /**
   * OPEN-81 G3: capture the outing's location when a send actually happens. Exact GPS and its raw
   * accuracy never enter the collection store; only the normal public grid cell is retained.
   */
  private async matchCollectionSendLocation(chatroomId: string): Promise<{
    source: 'gps' | 'traveler' | 'place' | 'unavailable';
    point?: PublicLocation;
  }> {
    if (isPlaceRoomId(chatroomId)) {
      const pin = this.uiManager.getCustomChatroomMeta(chatroomId)?.location;
      const latitude = Number(pin?.latitude);
      const longitude = Number(pin?.longitude);
      return Number.isFinite(latitude) && Number.isFinite(longitude)
        ? { source: 'place', point: snapPublicLocation({ latitude, longitude }, 1_000) }
        : { source: 'unavailable' };
    }
    if (usesSelfDefinedEncounterLocation() || this.travelModeActive) {
      const point = this.nearbyPublicPoint();
      return point ? { source: 'traveler', point } : { source: 'unavailable' };
    }
    try {
      const fix = await readDeviceLocation();
      const point = publicLocationFor(fix, this.effectiveNearbyMode());
      return point ? { source: 'gps', point } : { source: 'unavailable' };
    } catch {
      return { source: 'unavailable' };
    }
  }

  private async startNearbyV3(): Promise<void> {
    const user = this.currentUser;
    if (this.nearbyV3 || !user || isTechSupportUser(user)) return;
    const countIdentity = await this.identityLinkService.nearbyCountIdentity().catch(() => null);
    if (this.nearbyV3 || this.currentUser !== user) return;
    this.nearbyCountIdentity = countIdentity;
    const mesh = this.ensurePeerMeshService();
    if (!mesh) return;
    const extensions = BUNDLED_PROTOCOL_MANIFEST_HISTORY.at(-1)?.body.extensions;
    this.nearbyV3 = new NearbyV3Runtime({
      gunService: this.gunService,
      mesh,
      apiBase: () => this.getBackendApiBase(),
      userId: user.id,
      stageName: () => this.currentUser?.stageName || user.id,
      countIdentity: () => this.nearbyCountIdentity,
      config: () => this.nearbyV3Config(extensions),
      pinnedAttestorKeys: () => nearbyV3AttestorKeysFromManifest(extensions ? { extensions } : null),
      position: () => {
        const roomId = this.currentChatroomId;
        // In a Place my card is scoped to it and sits at its public pin, so every member is "at"
        // the Place and the order is the hash order alone (§3.7 "Places in v3").
        if (roomId && isPlaceRoomId(roomId)) {
          const pin = this.uiManager.getCustomChatroomMeta(roomId)?.location;
          const lat = Number(pin?.latitude);
          const lng = Number(pin?.longitude);
          const point = Number.isFinite(lat) && Number.isFinite(lng)
            ? snapPublicLocation({ latitude: lat, longitude: lng }, 1_000)
            : this.nearbyPublicPoint();
          return point ? { point, traveler: this.appearsAsTraveler(), scope: `place:${roomId}` as const } : null;
        }
        if (!isNearbyRoomId(roomId)) return null;
        const point = this.nearbyPublicPoint();
        return point ? { point, traveler: this.appearsAsTraveler() } : null;
      },
      isPaused: isPageHidden,
      onCounts: ({ nearby, online, scope }) => {
        this.uiManager.setNearbyCount(nearby, scope === 'nearby' ? NEARBY_V3_ROOM_ID : scope.slice('place:'.length));
        this.uiManager.setNearbyOnline(online);
      },
      onRoster: (members) => {
        const scopeId = this.currentChatroomId;
        if (!scopeId || !isPersonalListScope(scopeId)) return;
        // Like a shared room's roster, the roster includes me.
        const me = this.currentUser;
        const roster = me ? [...members, { userId: me.id, stageName: me.stageName || me.id, isTraveler: this.appearsAsTraveler() }] : members;
        this.uiManager.setNearbyGroup(this.nearbyV3?.service.groupInfo() ?? null);
        this.chatroomService.pushPersonalRoster(roster, me?.id, scopeId);
        // "N near you" is my list without me (§3.7); a Place counts everyone in it, me included.
        const people = new Set(members.map((member) => member.userId)).size;
        this.applyChatroomMemberCount(scopeId, isPlaceRoomId(scopeId) ? people + 1 : people);
      },
    });
    const runtime = this.nearbyV3;
    (window as unknown as { __iinpublicNearbyV3?: () => Record<string, unknown> }).__iinpublicNearbyV3 = () => runtime.diagnostics();
    runtime.start();
  }

  /** Reissue immediately when linking or unlinking changes this device's count key. */
  private async refreshNearbyCountIdentity(): Promise<void> {
    const next = await this.identityLinkService.nearbyCountIdentity().catch(() => null);
    const previousKey = this.nearbyCountIdentity?.countKey ?? null;
    this.nearbyCountIdentity = next;
    if (previousKey !== (next?.countKey ?? null)) await this.nearbyV3?.service.refresh();
  }

  private nearbyV3Config(extensions = BUNDLED_PROTOCOL_MANIFEST_HISTORY.at(-1)?.body.extensions) {
    return nearbyV3ConfigFromManifest({
      chatroomCapacity: this.activeExchangeRoomController?.getActiveRoom()?.chatroomCapacity
        ?? baselineRoomCheckpoint().chatroomCapacity,
      ...(extensions ? { extensions } : {}),
    });
  }

  /** Online dots refresh once a minute while the app is in front (and at once on start). */
  private startPresenceRefresh(): void {
    if (this.presenceRefreshTimer) return;
    void this.refreshConversationPresence();
    this.presenceRefreshTimer = setInterval(() => {
      if (!isPageHidden()) void this.refreshConversationPresence();
    }, PRESENCE_REFRESH_MS);
  }

  /** Room list: every Place with ≥ 1 person, refreshed every 20 s and on demand. */
  private startActiveRooms(): void {
    if (this.activeRoomsTimer) return;
    this.activeRoomsTimer = setInterval(() => { void this.refreshActiveRooms(); }, 20_000);
    // Coalesce bursts of roster changes.
    this.uiManager.on('activeRoomsRefresh', () => {
      if (this.activeRoomsRefreshTimer) clearTimeout(this.activeRoomsRefreshTimer);
      this.activeRoomsRefreshTimer = setTimeout(() => { void this.refreshActiveRooms(); }, 1_500);
    });
    this.uiManager.on('activeRoomsQueryChanged', (change: { showMore?: boolean } | undefined) => {
      this.activeRoomsLimit = change?.showMore ? this.activeRoomsLimit + ACTIVE_ROOMS_PAGE : ACTIVE_ROOMS_PAGE;
      void this.refreshActiveRooms();
    });
    void this.refreshActiveRooms();
  }

  private async refreshActiveRooms(): Promise<void> {
    if (!this.currentUser) return;
    // "M online" (§3.7): the hub's count of everyone heartbeating a Nearby card.
    const runtime = this.nearbyV3;
    void runtime?.hub.stats().then((stats) => {
      this.uiManager.setNearbyOnline(stats.online);
      // Someone joined, moved or left since my last heartbeat: refresh my own Nearby count too.
      if (typeof stats.countRevision === 'number' && stats.countRevision !== this.nearbyCountRevisionSeen) {
        this.nearbyCountRevisionSeen = stats.countRevision;
        void runtime.service.refreshCounts();
      }
    }).catch(() => undefined);
    const origin = this.nearbyPublicPoint();
    try {
      const view = await fetchActiveRooms(this.getBackendApiBase(), {
        ...(origin ? { origin: { latitude: origin.latitude, longitude: origin.longitude } } : {}),
        ...(this.currentChatroomId ? { currentRoomId: this.currentChatroomId } : {}),
        sort: getRoomListSort(),
        limit: this.activeRoomsLimit,
      });
      this.uiManager.setActiveRooms(view);
      // The Place catalogue loads at boot and after our own create. A Place someone else made
      // since then reaches us first in this list, while it has people; fetch the catalogue
      // then, so it stays under "Places near you" once it empties (J-PLACE-05).
      const unknownPlaces = placesMissingFromCatalogue(
        view.rooms.map((room) => room.roomId),
        new Set(this.uiManager.getCustomChatroomIds()),
        this.catalogueRefreshedForPlaces,
      );
      if (unknownPlaces.length > 0) {
        unknownPlaces.forEach((roomId) => this.catalogueRefreshedForPlaces.add(roomId));
        void this.refreshCustomChatroomsFromServer().then(() => this.subscribeToAllChatroomMemberCounts());
      }
    } catch (error) {
      console.warn('Room list refresh failed (non-fatal):', error);
    }
  }

  private stopNearbyV3(): void {
    this.nearbyV3?.stop();
    this.nearbyV3 = null;
  }

  /** My point or travel state changed: reissue my card and redraw me (★) on the map. */
  private refreshNearbyPoint(): void {
    if (!this.initialized) return; // boot ends with one refresh (startNearbyV3)
    this.nearbyV3?.refresh();
    void this.refreshActiveRooms(); // Place distances follow where I now appear
    const location = this.nearbyPublicPoint();
    const traveler = this.appearsAsTraveler();
    // Only the local map receives this fix. Presence, Talks and other people's dots stay snapped.
    const selfGpsPoint = location && !traveler && this.locationConfirmed && this.currentLocation
      ? { latitude: this.currentLocation.latitude, longitude: this.currentLocation.longitude }
      : undefined;
    const key = JSON.stringify({ location, traveler, selfGpsPoint });
    if (key === this.nearbyPointShownKey) return;
    this.nearbyPointShownKey = key;
    this.uiManager.setMapUserDotSource({
      ...(location ? { selfPoint: location } : {}),
      ...(selfGpsPoint ? { selfGpsPoint } : {}),
      selfTraveler: traveler,
      listPoints: () => this.nearbyV3?.service.entries().map((entry) => entry.card.point) ?? [],
      isCandidate: (dot) => isNearbyAudienceCandidate(this.nearbyV3?.service.audienceInfo() ?? null, dot),
      load: (bounds) => this.loadNearbyMapDots(bounds),
    });
  }

  /**
   * The people on my list in view, then every other live Nearby identity the hub has in a
   * city-sized view (OPEN-86 E3); the map colours them green or yellow. Heat: the hub's count per
   * cell.
   */
  private async loadNearbyMapDots(bounds: MapBounds): Promise<{
    dots: Array<PublicLocation & { userId: string; traveler: boolean }>;
    density: Array<{ latitude: number; longitude: number; count: number }>;
  }> {
    const runtime = this.nearbyV3;
    if (!runtime) return { dots: [], density: [] };
    const dots = runtime.service.entries()
      .filter((entry) => boundsContain(bounds, entry.card.point))
      .map((entry) => ({ ...entry.card.point, userId: entry.card.userId, traveler: entry.card.traveler }));
    const stats = await runtime.hub.stats(bounds).catch(() => null);
    return { dots: [...dots, ...(stats?.dots ?? [])], density: stats?.heat ?? [] };
  }

  /**
   * Subscribe to member count updates for all chatrooms in the UI list
   * This allows showing real-time headcount badges that update when users join/leave
   */
  /** A refused Place creation (docs/design/nearby-and-place-chatrooms.md §4.2), in words. */
  private showPlaceCreateRefusal(status: number, text: string): void {
    let body: {
      error?: string;
      code?: string;
      existingId?: string;
      existingName?: string;
      requiredFriendMarks?: number;
      maximumAllowedBlocks?: number;
      friendMarks?: number;
      blockCount?: number;
    } = {};
    try {
      body = JSON.parse(text) as typeof body;
    } catch {
      body = {};
    }
    const language = getUiLanguagePreference(uiLanguageFromProfile(this.currentUser?.languages));
    const name = String(body.existingName || '');
    if (status === 409 && body.code === 'PLACE_NAME_TAKEN' && body.existingId) {
      this.uiManager.showNotification(uiText(language, 'chatroomCreateNameTaken').replace('{name}', name), 'info');
      void this.refreshCustomChatroomsFromServer().then(() => this.uiManager.showChatroomDetail(body.existingId!));
      return;
    }
    if (status === 409 && body.code === 'PLACE_CREATOR_LIMIT') {
      this.uiManager.showNotification(uiText(language, 'chatroomCreateCreatorLimit').replace('{name}', name), 'error');
      return;
    }
    if (status === 409 && body.code === 'PLACE_CELL_FULL') {
      this.uiManager.showNotification(
        uiText(language, 'chatroomCreateCellFull').replace('{count}', String(MAX_LIVE_PLACES_PER_CELL)),
        'error',
      );
      return;
    }
    if (status === 403 && body.code === 'PLACE_REPUTATION_REQUIRED') {
      this.uiManager.showNotification(
        uiText(language, 'chatroomCreateNeedsCredit')
          .replace('{requiredFriends}', String(body.requiredFriendMarks ?? ROOM_CREATE_MIN_FRIEND_MARKS))
          .replace('{maximumBlocks}', String(body.maximumAllowedBlocks ?? ROOM_CREATE_MAX_BLOCKS_EXCLUSIVE - 1))
          .replace('{friends}', String(body.friendMarks ?? 0))
          .replace('{blocks}', String(body.blockCount ?? 0)),
        'error',
      );
      return;
    }
    this.uiManager.showNotification(this.uiManager.formatChatroomCreateFailed(body.error || undefined), 'error');
  }

  private async refreshCustomChatroomsFromServer(): Promise<void> {
    try {
      const base = this.getBackendApiBase();
      // Our own key keeps our expired Place listed for us.
      const creatorPub = this.gunService.getStoredPair()?.pub;
      const res = await fetch(`${base}/api/chatrooms${creatorPub ? `?creatorPub=${encodeURIComponent(creatorPub)}` : ''}`);
      if (!res.ok) return;
      const rows = (await res.json()) as Array<{
        id: string;
        name: string;
        type: string;
        description?: string;
        createdBy?: string;
        createdAt?: string;
        businessInfo?: { headline?: string };
        location?: { latitude: number; longitude: number };
      }>;
      if (!Array.isArray(rows)) return;
      this.uiManager.setCustomChatroomsFromServer(rows);
    } catch (e) {
      console.warn('refreshCustomChatroomsFromServer failed:', e);
    }
  }

  private subscribeToAllChatroomMemberCounts(): void {
    // Subscribe only to the active scope and visible Places. The retired Global/tree catalog must
    // not create background Gun listeners or discovery traffic.
    const chatroomIds: string[] = [];
    const currentChatroomId = this.chatroomService.getCurrentChatroomId();
    if (currentChatroomId && !chatroomIds.includes(currentChatroomId)) chatroomIds.push(currentChatroomId);
    if (currentChatroomId) {
      console.log(`📍 Including current chatroom path in live counts: ${currentChatroomId}`);
    }

    for (const id of this.uiManager.getCustomChatroomIds()) {
      if (id && !chatroomIds.includes(id)) {
        chatroomIds.push(id);
      }
    }

    console.log('📊 Subscribing to member counts for all chatrooms...');
    console.log(`   Total chatrooms: ${chatroomIds.length}`);

    chatroomIds.forEach((chatroomId) => {
      if (!chatroomId || this.subscribedMemberCountRoomIds.has(chatroomId)) return;
      this.subscribedMemberCountRoomIds.add(chatroomId);
      this.chatroomService.subscribeToMemberCount(chatroomId, (count) => {
        console.log(`  - ${chatroomId}: ${count} members`);
        this.applyChatroomMemberCount(chatroomId, count);
      });
      this.chatroomService.subscribeToVisitCounts(chatroomId, (counts) => {
        this.uiManager.setChatroomVisitCounts(chatroomId, counts);
      });
    });

    console.log('✅ Subscribed to all chatroom member counts');
  }

  private async initializeUser(): Promise<void> {
    if (isDevStageZero()) {
      // Preserve the TechSupport root id (K3: only the dev:multi ?devRole=techsupport driver
      // window sets it now — plain stage-zero/empty boots as an ordinary user) so that window
      // stays logged in as root. Ordinary dev windows have no stored id here and get a fresh
      // identity as before.
      if (localStorage.getItem('iinpublic_user_id') !== TECHSUPPORT_ROOT_USER_ID) {
        localStorage.removeItem('iinpublic_user_id');
        localStorage.removeItem(KEY_CUSTODY_STORAGE);
        localStorage.removeItem(KEY_CUSTODY_DEVICE_SECRET_STORAGE);
        localStorage.removeItem('gun/');
        if ((this.gunService.isBridgeReady() || this.gunService.isNativeNode())) {
          void this.gunService.putDeviceLocal(IinPublicApp.DEVICE_LOCAL_USER_ID_PATH, null).catch(() => {});
          void this.gunService.putDeviceLocal(IinPublicApp.DEVICE_LOCAL_CACHED_USER_PATH, null).catch(() => {});
        }
      }
    }
    // Check for existing user in local storage. A missing value here does not necessarily mean
    // "no identity was ever created on this device" — see the recovery check below.
    let existingUserId = localStorage.getItem('iinpublic_user_id');
    if (!existingUserId) {
      // localStorage being empty does not necessarily mean "no identity was ever created on this
      // device" — a real process SIGKILL can lose a localStorage write that returned successfully
      // moments earlier (see persistUserId's doc comment), and empirically which specific
      // localStorage keys survive a given hard kill is not consistent from crash to crash (a
      // SEA-identity custody record surviving is not a reliable enough signal to gate on).
      // Checking the IndexedDB-backed mirror unconditionally is the only robust option — a
      // genuinely new device's read comes back empty near-instantly (a local-only Gun `.once()`
      // never waits on a network peer), so this adds no meaningful latency to normal cold boot.
      if ((this.gunService.isBridgeReady() || this.gunService.isNativeNode())) {
        try {
          const recovered = await this.gunService.getDeviceLocal(IinPublicApp.DEVICE_LOCAL_USER_ID_PATH);
          const recoveredId = recovered && typeof recovered === 'object' ? recovered.id : undefined;
          if (typeof recoveredId === 'string' && recoveredId) {
            existingUserId = recoveredId;
            localStorage.setItem('iinpublic_user_id', existingUserId);
            console.log('👤 Recovered user id from IndexedDB-backed mirror after apparent localStorage loss:', existingUserId);
          }
        } catch {
          // Best-effort recovery only — fall through to the normal existingUserId handling below.
        }
      }
    }
    let isNewUser = false;

    if (existingUserId) {
      const cached = await this.readNewestCachedUser(existingUserId);
      if (cached) {
        // Instant path: render from the last-known copy of this identity's own record — no
        // network wait at all. A decentralized app has no excuse to be slower to open than a
        // centralized one just because the network is slow; the network's job is to keep this
        // local copy fresh, not to gate the door. Refresh happens in the background below and
        // arrives like email — asynchronously, whenever it lands — never blocking this boot.
        this.currentUser = cached;
        console.log('👤 Existing user loaded from local cache (instant):', this.currentUser.stageName);
        void this.userService.getUser(existingUserId).then(async (freshRecord) => {
          const pair = this.gunService.getStoredPair();
          // Merge onto the cached copy: a partial refresh (fields missing) must not erase them.
          const fresh = mergeUserRecord(cached, freshRecord);
          let merged = fresh;
          if (pair && !fresh.pub) {
            merged = { ...fresh, pub: pair.pub, epub: pair.epub };
            await this.userService.publishIdentityKeys(fresh.id, pair).catch(() => {});
          }
          this.currentUser = merged;
          this.writeCachedUser(merged);
          this.uiManager.adoptSessionUser(merged);
        }).catch((error) => {
          console.warn('Background user refresh failed (non-fatal, using cached identity):', error);
        });
      } else {
        try {
          this.currentUser = withUsableStageName(await this.userService.getUser(existingUserId));
          const pair = this.gunService.getStoredPair();
          if (pair && !this.currentUser.pub) {
            const merged: User = { ...this.currentUser, pub: pair.pub, epub: pair.epub };
            await this.userService.publishIdentityKeys(this.currentUser.id, pair);
            this.currentUser = merged;
          }
          console.log('👤 Existing user loaded:', this.currentUser.stageName);
        } catch (error) {
          console.log('🆕 Existing user not found, creating new user');
          this.currentUser = await this.createNewUser({
            rootTechSupport: existingUserId === TECHSUPPORT_ROOT_USER_ID,
          });
          isNewUser = true;
        }
      }
    } else {
      // The built-in TechSupport Contact is verified from compiled trust anchors. Browsers
      // never mint the root identity or create a room presence for it.
      this.currentUser = await this.createNewUser();
      isNewUser = true;
    }

    // Store whether this is a new user for welcome banner
    (this as any).isNewUser = isNewUser;

    // Hand identity to the UI as soon as it's known — do not make it wait for the slower
    // location-publish/relay-sync calls below, or for the rest of the boot chain
    // (restoreReceivedTalkHistory, initializeChatrooms) that still has to run before
    // showMainInterface fires. A tab clicked immediately on cold launch (e.g. Settings) can
    // then paint from this identity right away instead of sitting blank for the full boot.
    this.uiManager.adoptSessionUser(this.currentUser);
    this.writeCachedUser(this.currentUser);
    this.conversationService.setLocalUser(this.currentUser.id, this.currentUser.stageName);

    // Phase E: re-initialize ledger now that currentUser is known (userId was not available earlier)
    this.initLedger();

    // Update user location and sync the public record to the relay — both fire-and-forget.
    // Neither result changes what's already on screen: the in-memory location is already used
    // for room selection, and the local Gun graph already has this user's public record from
    // createUser/getUser above. Waiting here just to observe a relay ack (which a relay-only
    // production hub may never send at all — see WebGunService.put's doc comment) used to make
    // every boot pay for a network round trip it didn't actually need to open the app.
    if (this.currentLocation && this.locationConfirmed) {
      void this.userService.updateUserLocation(this.currentUser.id, this.publicHomeLocation()).catch((error) => {
        console.warn('Boot-time location publication skipped (non-fatal):', error);
      });
    }
    const pair = this.gunService.getStoredPair();
    void this.userService.syncPublicUserForRelay({
      ...this.currentUser,
      ...(pair?.pub ? { pub: pair.pub } : {}),
      ...(pair?.epub ? { epub: pair.epub } : {}),
    }).catch((error) => {
      console.warn('Boot-time relay sync skipped (non-fatal):', error);
    });
  }

  private static readonly CACHED_USER_STORAGE = 'iinpublic_cached_user_v1';

  /** Last-known copy of this device's own user record, for an instant cold-boot render. */
  /** Path of the IndexedDB-backed mirror of the cached user record — see writeCachedUser. */
  private static readonly DEVICE_LOCAL_CACHED_USER_PATH = 'device-local/cached-user';

  /**
   * The newer of the two copies of my own record: localStorage and its IndexedDB mirror. A hard
   * kill can lose the localStorage write of a recent change (a new stage name) while an older
   * copy survives there, so the cache alone would bring the old name back.
   */
  private async readNewestCachedUser(expectedUserId: string): Promise<User | null> {
    let mirrored: string | null = null;
    if ((this.gunService.isBridgeReady() || this.gunService.isNativeNode())) {
      try {
        const node = await this.gunService.getDeviceLocal(IinPublicApp.DEVICE_LOCAL_CACHED_USER_PATH);
        mirrored = node && typeof node === 'object' && typeof node.json === 'string' ? node.json : null;
      } catch {
        // Best-effort: the localStorage copy alone, as before.
      }
    }
    const local = (() => {
      try { return localStorage.getItem(IinPublicApp.CACHED_USER_STORAGE); } catch { return null; }
    })();
    const cachedAt = (raw: string | null): number => {
      try { return raw ? Number(JSON.parse(raw)?.cachedAt) || 0 : -1; } catch { return -1; }
    };
    const newest = cachedAt(mirrored) > cachedAt(local) ? mirrored : local;
    if (newest && newest !== local) {
      try { localStorage.setItem(IinPublicApp.CACHED_USER_STORAGE, newest); } catch { /* best effort */ }
    }
    return this.readCachedUser(expectedUserId, newest);
  }

  private readCachedUser(expectedUserId: string, rawRecord?: string | null): User | null {
    try {
      const raw = rawRecord !== undefined ? rawRecord : localStorage.getItem(IinPublicApp.CACHED_USER_STORAGE);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      delete parsed.cachedAt;
      if (!parsed || parsed.id !== expectedUserId || typeof parsed.stageName !== 'string' || !parsed.stageName) return null;
      if (typeof parsed.createdAt === 'string') parsed.createdAt = new Date(parsed.createdAt);
      if (typeof parsed.lastActive === 'string') parsed.lastActive = new Date(parsed.lastActive);
      return parsed as User;
    } catch {
      return null;
    }
  }

  private writeCachedUser(user: User): void {
    // A partial record cached here crashed the next boot (no stageName — seen on the C10 tablet).
    if (!user?.id || typeof user.stageName !== 'string' || !user.stageName) return;
    const json = JSON.stringify({ ...user, cachedAt: Date.now() });
    try {
      localStorage.setItem(IinPublicApp.CACHED_USER_STORAGE, json);
    } catch {
      // Best-effort only — a full cache means the next cold boot waits on the network instead.
    }
    // Mirrored where a hard kill does not lose it (as persistUserId does for the id): a stage
    // name changed seconds before a crash came back as the old one (e2e stage2/37 under load).
    if ((this.gunService.isBridgeReady() || this.gunService.isNativeNode())) {
      void this.gunService.putDeviceLocal(IinPublicApp.DEVICE_LOCAL_CACHED_USER_PATH, { json }).catch(() => {});
    }
  }

  /** Gun path for the IndexedDB-backed durable mirror of `iinpublic_user_id` — see persistUserId. */
  private static readonly DEVICE_LOCAL_USER_ID_PATH = 'device-local/current-user-id';

  /**
   * A genuine hard process kill (SIGKILL — no graceful shutdown, no chance to flush) can lose a
   * `localStorage.setItem()` that returned successfully moments earlier: Chromium's localStorage
   * backend doesn't necessarily commit every write to disk immediately, and a kill before its own
   * internal flush cycle fires loses that write permanently, regardless of how long anything waits
   * afterward. Confirmed via e2e (37-hard-crash-recovery): after a real SIGKILL + relaunch from the
   * same on-disk profile, `iinpublic_user_id` came back empty and the app created a brand-new
   * identity instead of recovering the real one.
   *
   * Mirrors the id into the worker's IndexedDB-backed Gun instance (getBridge()) too — already the
   * durable persistence layer for the Gun graph itself (see prepareDirectGunBrowserStorage's doc
   * comment), so this piggybacks on infrastructure with stronger commit guarantees than plain
   * localStorage, instead of introducing a new storage mechanism. Best-effort and fire-and-forget:
   * a failed mirror write never blocks or fails user creation, since the localStorage copy remains
   * the fast path for every normal (non-crash) boot.
   */
  private persistUserId(id: string): void {
    localStorage.setItem('iinpublic_user_id', id);
    if ((this.gunService.isBridgeReady() || this.gunService.isNativeNode())) {
      void this.gunService.putDeviceLocal(IinPublicApp.DEVICE_LOCAL_USER_ID_PATH, { id }).catch(() => {});
    }
  }

  private async createNewUser(options: { rootTechSupport?: boolean } = {}): Promise<User> {
    // Show user creation UI
    const userData = await this.uiManager.showUserCreationDialog();

    const blurredLocation = this.locationConfirmed
      ? LocationPrivacy.blurLocation(this.currentLocation!)
      : { region: '', chatrooms: [] };
    const pair = this.gunService.getStoredPair();

    if (options.rootTechSupport) {
      const rootUser: Partial<User> = {
        headshot: userData.headshot,
        location: blurredLocation,
        languages: userData.languages || ['en'],
        interests: userData.interests || [],
        profile: userData.profile || [],
        ...(pair?.pub ? { pub: pair.pub } : {}),
        ...(pair?.epub ? { epub: pair.epub } : {}),
      };
      const user = await this.userService.createTechSupportRoot(rootUser);
      this.persistUserId(user.id);
      console.log('✨ TechSupport root user created:', user.stageName);
      return user;
    }

    const newUser: Partial<User> = {
      // stageName will be auto-generated in userService.createUser()
      headshot: userData.headshot,
      location: blurredLocation,
      languages: userData.languages || ['en'],
      interests: userData.interests || [],
      profile: [],
      ...(pair?.pub ? { pub: pair.pub } : {}),
      ...(pair?.epub ? { epub: pair.epub } : {}),
    };

    const user = await this.userService.createUser(newUser);
    this.persistUserId(user.id);

    console.log('✨ New user created:', user.stageName);
    return user;
  }

  private async initializeChatrooms(): Promise<void> {
    if (!this.currentUser || !this.currentLocation) return;

    // Get last chatroom from localStorage (for re-entry logic)
    const lastChatroomId = settingsStorage.getItem('iinpublic_last_chatroom') || undefined;
    this.loadTravelModeStateFromStorage();

    // Find optimal chatroom using hierarchical assignment
    let chatroomId = await this.chatroomService.findOptimalChatroomHierarchical(
      this.currentLocation,
      this.currentUser.id,
      lastChatroomId,
      this.locationConfirmed,
      this.effectiveNearbyMode(),
    );
    if (isTechSupportUser(this.currentUser)) chatroomId = CONTACTS_ONLY_SCOPE_ID;

    this.currentChatroomId = chatroomId; // Track current chatroom

    console.log('🎯 Assigned to chatroom:', chatroomId);
    console.log('📍 Based on location:', this.currentLocation);

    // Join the assigned chatroom. Awaited: unlike
    // the other boot-time network calls below (location publish, chatbot-memory restore, talk
    // history — pure back-fill this client never blocks on), other peers' room rosters depend on
    // this write actually reaching the shared graph before their own UIs can show this user.
    // Briefly made fire-and-forget for boot speed, but that let the join silently race (or lose
    // to) the multi-second Gun ack/propagation delay that's common under real network conditions
    // with no visible failure — reproduced via a 3-browser chatroom-roster e2e test where the
    // write never landed within a 15s window. joinChatroom's own local Gun put still lands
    // synchronously on this user's own client the instant it's called, so this await's cost is
    // only the network's ack/retry/propagation-delay budget, not a duplicate of the write itself.
    try {
      await this.chatroomService.joinChatroom(
        chatroomId,
        this.currentUser.id,
        this.currentUser.stageName,
      );
    } catch (error) {
      if (isPlaceRoomId(chatroomId)) {
        const rejectedPlace = chatroomId;
        chatroomId = await this.resolveAutomaticNearbyRoom();
        this.currentChatroomId = chatroomId;
        this.travelModeActive = false;
        this.travelChatroomId = undefined;
        this.travelHomeChatroomId = chatroomId;
        this.persistTravelModeStateToStorage();
        await this.chatroomService.joinChatroom(
          chatroomId,
          this.currentUser.id,
          this.currentUser.stageName,
        );
        const language = getUiLanguagePreference(uiLanguageFromProfile(this.currentUser.languages));
        this.uiManager.showNotification(uiText(language, 'chatroomPlaceStartupUnavailable'), 'warning');
        console.warn(`Saved Place ${rejectedPlace} could not be entered; opened ${chatroomId}:`, error);
      } else {
        console.warn('Chatroom join encountered an error (non-fatal):', error);
      }
    }

    // Store current chatroom in localStorage for next time
    settingsStorage.setItem('iinpublic_last_chatroom', chatroomId);

    console.log('🏠 Joined chatroom:', chatroomId);

    // Subscribe to chatroom members and update UI. `joinChatroom` above is an awaited Gun write
    // (with retries) that can take a while — if the user has already navigated to a different
    // room in the meantime (e.g. a fast subsequent click during this boot-time assignment), this
    // callback's closure-captured `chatroomId` is stale. Without this guard it would win the
    // single-slot `subscribeToMembers` race against the newer room's subscription and silently
    // re-scope the live mesh session (and any broadcast issued from it) back to the boot-time
    // room, even though the UI/currentChatroomId correctly show the room the user navigated to.
    if (this.currentChatroomId !== chatroomId) {
      console.log(`⏭️  Skipping stale boot-time chatroom subscription for ${chatroomId}; user is now in ${this.currentChatroomId}`);
    } else {
      this.chatroomService.subscribeToMembers(chatroomId, (members) => {
        if (this.currentChatroomId !== chatroomId) return;
        this.harvestRosterEpubs(members);
        this.observeNearbyRoomRoster(chatroomId, members);
        console.log('👥 Chatroom members updated:', members);
        this.uiManager.updateChatroomMembers(members, this.currentUser!.id);
        this.syncPeerMeshRoom(chatroomId, members);

        // Update status bar with current chatroom info (real-time)
        const chatroomName = this.getChatroomDisplayName(chatroomId);
        this.uiManager.updateStatusBar(
          this.currentUser!.stageName,
          chatroomName,
          this.countRoomMembers(members),
          this.uiManager.getTotalMatches(),
        );
      });
    }

    // Contacts-only has no public room graph.
    this.subscribeToMessages(chatroomId);

    // Subscribe to chatroom talks

    // Subscribe to user's conversations (for matches)
    this.subscribeToUserConversations();
    // OPEN-87: keep the Contacts list within its cap from the start.
    this.scheduleContactsPrune();
    
    // Subscribe to backend-driven incoming talk clusters
    this.subscribeToIncomingTalks();

    // Update chatroom info
    this.uiManager.updateChatroomInfo({ id: chatroomId, name: `Chatroom: ${chatroomId}` });

    // If travel mode is active, switch once to the selected travel chatroom (single-room presence).
    if (this.travelModeActive && this.travelChatroomId && this.travelChatroomId !== chatroomId) {
      this.travelHomeChatroomId = chatroomId;
      this.persistTravelModeStateToStorage();
      this.uiManager.setTravelModeState({
        active: true,
        ...(this.travelHomeChatroomId ? { homeChatroomId: this.travelHomeChatroomId } : {}),
      });
      this.refreshNearbyPoint();
      await this.chatroomService.switchChatroom(this.currentUser.id, this.travelChatroomId, this.currentUser.stageName);
      this.currentChatroomId = this.travelChatroomId;
      settingsStorage.setItem('iinpublic_last_chatroom', this.currentChatroomId);
      this.subscribeToMessages(this.currentChatroomId);
      this.uiManager.setCurrentChatroomId(this.currentChatroomId);
      this.chatroomService.subscribeToMembers(this.currentChatroomId, (members) => {
        this.harvestRosterEpubs(members);
        if (this.currentChatroomId) this.observeNearbyRoomRoster(this.currentChatroomId, members);
        this.uiManager.updateChatroomMembers(members, this.currentUser!.id);
        this.syncPeerMeshRoom(this.currentChatroomId!, members);
        const chatroomName = this.getChatroomDisplayName(this.currentChatroomId!);
        this.uiManager.updateStatusBar(
          this.currentUser!.stageName,
          chatroomName,
          this.countRoomMembers(members),
          this.uiManager.getTotalMatches(),
        );
      });
    } else {
      this.uiManager.setTravelModeState({
        active: this.travelModeActive,
        ...(this.travelHomeChatroomId ? { homeChatroomId: this.travelHomeChatroomId } : {}),
      });
      this.refreshNearbyPoint();
    }

    await this.ensureSupportBootstrapForCurrentUser().catch((error) => {
      console.warn('Support bootstrap failed:', error);
    });
    this.subscribeToTechSupportRecoveryAnchor();
    this.subscribeToTechSupportDelegateGrants();
    this.startTechSupportDelegateRelayPolling();
    this.subscribeToSupportInboxIfTechSupport();
    if (this.currentUser?.id === TECHSUPPORT_ROOT_USER_ID) {
      this.refreshDelegateAdminPanel();
    } else {
      void this.checkOwnDelegateEligibility();
    }
    await this.activateRoomExchange(this.currentChatroomId || chatroomId);
    this.initDirectTalkDeliverySubscriptions();
    // Step 6: drain mailbox on app boot + retry any failed mailbox POSTs.
    void this.drainMailbox().catch(() => {});
    this.startMailboxPolling();
    void this.retryFailedMailboxPosts().catch(() => {});
    this.resumeDeviceSyncForLinkedPeers();
    this.startDeviceSyncLoop();
  }

  /** K7-adjacent WP5 wiring: resumes watching every already-linked peer for a mutual device-sync
   * handshake (this device's own half may have been published in an earlier session, or the peer
   * may approve later while this device stays online) — see WebDeviceSyncService.watchPeerForSync. */
  private resumeDeviceSyncForLinkedPeers(): void {
    for (const row of readLinkedDeviceRecords(true)) {
      if (row.state === 'linked') this.deviceSyncService.watchPeerForSync(row.pub);
    }
  }

  private startDeviceSyncLoop(): void {
    if (this.deviceSyncTimer) return;
    this.deviceSyncTimer = setInterval(() => {
      void this.deviceSyncService.tick().catch(() => {});
    }, 5_000);
  }

  /**
   * P0: apply the same intake gates as POST /received (filters, age, block list). Also the
   * shared block gate for IPFS attachment transfer (send: autoShareMatchedTalkAttachments,
   * shareConversationMedia handler; receive: maybeFetchSharedAttachmentBytes,
   * ingestAttachmentShareFromMailbox) — call this directly from a new call site rather than
   * re-deriving block status, same as the talk-delivery path already does.
   */
  private async resolveBlockStatusEitherWay(peerId: string): Promise<boolean> {
    const me = this.currentUser;
    if (!me?.id || !peerId) return false;
    if ((me.blockedUserIds ?? []).includes(peerId)) return true;
    return this.blockStatusCache.isBlocked(peerId);
  }

  /** The hub's answer, or null when it could not be reached (not remembered). */
  private async fetchBlockStatusEitherWay(peerId: string): Promise<boolean | null> {
    const me = this.currentUser;
    if (!me?.id) return null;
    try {
      const base = this.getBackendApiBase();
      const res = await fetch(
        `${base}/api/users/${encodeURIComponent(me.id)}/block-status/${encodeURIComponent(peerId)}`,
        { cache: 'no-store' },
      );
      if (!res.ok) return null;
      const status = (await res.json()) as { eitherBlocked?: boolean };
      return !!status.eitherBlocked;
    } catch {
      return null;
    }
  }

  private async resolveAgeVerifiedForIntake(): Promise<boolean> {
    if (this.currentUser?.reputation?.ageVerified) return true;
    if (!this.currentUser?.id) return false;
    try {
      const base = this.getBackendApiBase();
      const res = await fetch(
        `${base}/api/users/${encodeURIComponent(this.currentUser.id)}?viewerId=${encodeURIComponent(this.currentUser.id)}`,
        { cache: 'no-store' },
      );
      if (!res.ok) return false;
      const user = await res.json();
      const verified = !!user.reputation?.ageVerified;
      if (verified) {
        if (!this.currentUser.reputation) {
          this.currentUser.reputation = user.reputation;
        } else {
          this.currentUser.reputation.ageVerified = true;
        }
        this.writeCachedUser(this.currentUser);
      }
      return verified;
    } catch {
      return false;
    }
  }

  private async shouldAcceptIncomingTalkAsync(offer: {
    senderId: string;
    talkData: Record<string, unknown>;
    deliveryChatroomId?: string;
    directPeerSend?: boolean;
  }): Promise<boolean> {
    const me = this.currentUser;
    if (!me?.id) return false;
    // The rule is the core's (src/core/talk-intake.ts); this supplies the context and the lookups.
    const decision = await decideIncomingTalk(offer, {
      myId: me.id,
      filters: getTalkIntakeFilters(),
      knownPeople: me.knownPeople,
      travelerIds: this.roomTravelerIds,
      ...(this.currentChatroomId ? { currentChatroomId: this.currentChatroomId } : {}),
      ...(this.locationConfirmed && this.currentLocation ? { location: this.currentLocation } : {}),
      now: Date.now(),
    }, {
      isBlockedEitherWay: (peerId) => this.resolveBlockStatusEitherWay(peerId),
      ageVerified: () => this.resolveAgeVerifiedForIntake(),
      isRoomMember: async (roomId, peerId) => {
        // Gun's member map lags; the server's list is asked first.
        const serverIds = await this.chatroomService.fetchMemberIdsFromServer(roomId);
        if (serverIds.includes(peerId)) return true;
        return (await this.chatroomService.getActiveMembers(roomId)).includes(peerId);
      },
    });
    return decision.accept;
  }

  private isTalkExpiredForDelivery(talkData: unknown): boolean {
    const expiresAt = talkExpiresAtMs(talkData);
    return Number.isFinite(expiresAt) && Date.now() > expiresAt;
  }

  public async warmMeshConnectionToPeer(peerId: string, peerName = 'Unknown'): Promise<boolean> {
    const me = this.currentUser;
    const mesh = this.ensurePeerMeshService();
    if (!me?.id || !peerId || !mesh) return false;
    const chatroomId = this.chatroomService.getCurrentChatroomId?.() || this.currentChatroomId || CONTACTS_ONLY_SCOPE_ID;
    // Nearby: the mesh runs on my list; open just this link instead of replacing the roster.
    if (isPersonalListScope(chatroomId)) return mesh.openPersonalLink(peerId);
    for (let attempt = 0; attempt < 4; attempt += 1) {
      try {
        const activeIds = await this.chatroomService.getActiveMembers(chatroomId);
        const members = Array.from(new Set([...activeIds, me.id, peerId]))
          .filter(Boolean)
          .map((userId) => ({
            userId,
            stageName: userId === me.id ? me.stageName : userId === peerId ? peerName : userId,
          }));
        await mesh.joinRoom(chatroomId, members);
        if (await mesh.waitForConnectedNeighbor(peerId, 5_000)) return true;
      } catch (error) {
        console.warn('Mesh peer warm-up failed:', error);
      }
      await new Promise((resolve) => setTimeout(resolve, 300 + attempt * 250));
    }
    return false;
  }

  /**
   * Directed peer send (Send My Talks) over the mesh-talk transport.
   *
   * docs/TODO.md §W Gap 2 — this used to call `mesh.broadcastTalk` directly, bypassing
   * `deliverTalkToReceiversOverMesh` (and therefore the ledger) entirely: the only thing
   * stopping a resend was the peer-detail view's own separate `localTalkExchanges` check,
   * inconsistent with every other send path. Now routes through the same chokepoint room
   * broadcast and contact-group broadcast already use, so a talk already sent to this peer via
   * *any* path (room, group, or a prior direct send) is suppressed here too, and this send
   * writes the same `sent` ledger entry theirs do.
   */
  public async sendDirectTalkToPeer(
    talkId: string,
    talkData: Talk | Record<string, unknown>,
    peerId: string,
    peerName: string,
  ): Promise<void> {
    const me = this.currentUser;
    if (!me?.id) throw new Error('Not signed in');
    const talk = talkData as Talk;
    if (this.isTalkExpiredForDelivery(talk)) {
      throw new Error('Talk expired');
    }
    // R-f step 7: author-side Gun talks/* mirror removed; body cached in PeerMeshService directly
    // (deliverTalkToReceiversOverMesh does this itself via mesh.cacheTalkBody).
    const ready = await this.warmMeshConnectionToPeer(peerId, peerName);
    if (!ready) {
      console.warn('Directed mesh send proceeding before peer link reported ready');
    }
    const ok = await this.deliverTalkToReceiversOverMesh(talkId, talk, [{ userId: peerId, stageName: peerName }]);
    if (!ok) throw new Error('Mesh talk delivery is not available');
  }

  /** Subscribe to the receiver-owned local incoming-talk index used by mesh delivery. */
  private initDirectTalkDeliverySubscriptions(): void {
    if (!this.currentUser?.id) return;
    void this.refreshIncomingTalkClustersFromLocalGun();
  }

  /** P2P-I / P2P-O: register live presence and probe local node bridge (stack only). */
  private async initP2PPresenceAndBridge(): Promise<void> {
    if (!this.currentUser?.id || isTechSupportUser(this.currentUser)) return;
    const pair = this.gunService.getStoredPair();
    if (!pair?.pub || !pair.priv) return;
    const base = this.getBackendApiBase();
    try {
      const heartbeatMs = process.env.DISABLE_HMR === 'true' ? 300_000 : 30_000;
      this.presenceClient = new P2PPresenceClient({ apiBase: base, heartbeatMs });
      await this.presenceClient.startHeartbeat({
        userId: this.currentUser.id,
        pub: String(pair.pub),
        pair,
        getRoomScope: () => this.activeExchangeRoomController?.getScope() ?? null,
        getRoomEnteredAt: () => this.activeExchangeRoomController?.getActiveRoom()?.enteredAt ?? null,
        isDiscoveryAllowed: () => this.hubDiscoveryAllowedNow(),
        ...(pair.epub ? { epub: String(pair.epub) } : {}),
      });
      const peers = !this.hubDiscoveryAllowedNow() ? [] : await this.presenceClient.fetchNearby(
        this.currentUser.id,
        20,
        this.activeExchangeRoomController?.getScope() ?? undefined,
      );
      // K1 item 3: TechSupport's online/away indicator is real peer presence, not a fake heartbeat —
      // this reuses the same registry an eventual TechSupport client (K3) would heartbeat into by
      // running the ordinary client in TechSupport mode, same as any other peer.
      this.uiManager.setTechSupportOnlineStatus(peers.some((peer) => peer.userId === TECHSUPPORT_ROOT_USER_ID));
      for (const peer of peers) {
        if (peer.epub) this.peerEpubByUserId.set(peer.userId, peer.epub);
        try {
          await this.presenceClient.acknowledgePeer({
            fromUserId: this.currentUser.id,
            fromPub: String(pair.pub),
            toUserId: peer.userId,
            toPub: peer.pub,
            pair,
          });
        } catch {
          /* best-effort peer ack */
        }
      }
    } catch (err) {
      console.warn('P2P presence init failed (non-fatal):', err);
    }
    if (this.p2pRuntimeFlags.p2pNodeEnabled) {
      this.localNodeBridge = new P2PLocalNodeBridgeClient(true);
      void this.localNodeBridge.probe(base);
    }
    this.initWifiDirectLink(String(pair.pub));
    this.initNearbyOffline(String(pair.pub));
  }

  private async stopRoomExchangeRuntime(_room?: ActiveExchangeRoom): Promise<void> {
    this.presenceClient?.stopHeartbeat();
    this.presenceClient = null;
    this.peerMeshService?.leaveRoom();
    this.nearbyOfflineService?.dispose();
    this.nearbyOfflineService = null;
  }

  private async ensureProtocolManifestController(): Promise<ProtocolManifestController> {
    if (this.protocolManifestController) return this.protocolManifestController;
    const controller = await ProtocolManifestController.create({
      anchor: PRODUCTION_PROTOCOL_MANIFEST_ANCHOR,
      runtimePolicy: PRODUCTION_PROTOCOL_MANIFEST_RUNTIME,
      storage: localStorage,
      bundledManifests: BUNDLED_PROTOCOL_MANIFEST_HISTORY,
    });
    this.protocolManifestController = controller;
    this.peerMeshService?.setProtocolManifestController(controller);
    this.scheduleProtocolActivation();
    return controller;
  }

  private currentRoomProtocolCheckpoint(): RoomProtocolCheckpoint {
    return this.protocolManifestController
      ? manifestRoomCheckpoint(this.protocolManifestController)
      : baselineRoomCheckpoint();
  }

  private scheduleProtocolActivation(): void {
    if (this.protocolActivationTimer) clearTimeout(this.protocolActivationTimer);
    this.protocolActivationTimer = null;
    const activation = this.protocolManifestController?.nextActivationAt();
    if (!activation) return;
    const delay = Math.max(0, Math.min(activation.getTime() - Date.now() + 25, 2_147_000_000));
    this.protocolActivationTimer = setTimeout(() => {
      this.protocolActivationTimer = null;
      void this.syncActiveRoomProtocolCheckpoint();
    }, delay);
  }

  private async syncActiveRoomProtocolCheckpoint(): Promise<void> {
    const controller = this.protocolManifestController;
    const activeController = this.activeExchangeRoomController;
    const active = activeController?.getActiveRoom();
    if (!controller || !activeController || !active) return;
    try {
      await activeController.transitionTo({
        roomId: active.roomId,
        neighborLimit: active.neighborLimit,
        ...manifestRoomCheckpoint(controller),
      });
      this.scheduleProtocolActivation();
    } catch (error) {
      await activeController.pause().catch(() => undefined);
      this.uiManager.showNotification(
        `Room exchange paused: ${error instanceof Error ? error.message : String(error)}. Update IinPublic to reconnect.`,
        'warning',
      );
    }
  }

  private async activateRoomExchange(roomId: string): Promise<void> {
    if (!roomId || isLocalOnlyRoomScope(roomId) || !this.currentUser || isTechSupportUser(this.currentUser)) return;
    let checkpoint: RoomProtocolCheckpoint;
    try {
      const manifestController = await this.ensureProtocolManifestController();
      checkpoint = manifestRoomCheckpoint(manifestController);
    } catch (error) {
      await this.activeExchangeRoomController?.pause().catch(() => undefined);
      this.uiManager.showNotification(
        `Room exchange is unavailable: ${error instanceof Error ? error.message : String(error)}. Update IinPublic to reconnect. Settings, export, and identity recovery remain available.`,
        'warning',
      );
      return;
    }
    if (!this.activeExchangeRoomController) {
      this.activeExchangeRoomController = new ActiveExchangeRoomController(localStorage, {
        stopRoom: (room) => this.stopRoomExchangeRuntime(room),
        startRoom: async () => this.initP2PPresenceAndBridge(),
      });
      this.chatroomService.configureActiveExchangeRoom(
        this.activeExchangeRoomController,
        (nextRoomId) => ({ roomId: nextRoomId, ...this.currentRoomProtocolCheckpoint() }),
      );
    }
    await this.activeExchangeRoomController.activate({
      roomId,
      ...checkpoint,
    });
  }

  /** Apply a saved audience choice immediately; no new OS prompt is raised mid-exchange. */
  private async applyNearbyPrivacyMode(_settings: ConnectivitySettings): Promise<void> {
    if (!this.currentUser || !this.currentLocation) return;
    this.refreshNearbyPoint();
    void this.userService.updateUserLocation(this.currentUser.id, this.publicHomeLocation()).catch(() => {});
    const effectiveMode = this.effectiveNearbyMode();
    const publicNearbyMode = isNearbyPublicMode(effectiveMode) ? effectiveMode : undefined;
    let selectedRoomId: string;
    if (this.travelModeActive && this.travelNearbyPoint && publicNearbyMode) {
      selectedRoomId = NEARBY_V3_ROOM_ID; // the card moves with the travel point (refreshNearbyPoint)
    } else {
      if (this.travelNearbyPoint && !publicNearbyMode) {
        this.travelModeActive = false;
        this.travelChatroomId = undefined;
        this.travelNearbyPoint = undefined;
        this.persistTravelModeStateToStorage();
        this.uiManager.setTravelModeState({ active: false });
        this.refreshNearbyPoint();
      }
      selectedRoomId = await this.chatroomService.findOptimalChatroomHierarchical(
        this.currentLocation,
        this.currentUser.id,
        this.currentChatroomId,
        this.locationConfirmed,
        effectiveMode,
      );
    }
    const nextRoomId = isTechSupportUser(this.currentUser) ? CONTACTS_ONLY_SCOPE_ID : selectedRoomId;
    if (nextRoomId === this.currentChatroomId) return;
    await this.chatroomService.switchChatroom(
      this.currentUser.id,
      nextRoomId,
      this.currentUser.stageName,
    );
    this.currentChatroomId = nextRoomId;
    settingsStorage.setItem('iinpublic_last_chatroom', nextRoomId);
    this.uiManager.setCurrentChatroomId(nextRoomId);
    this.chatroomService.subscribeToMembers(nextRoomId, (members) => {
      this.harvestRosterEpubs(members);
      this.observeNearbyRoomRoster(nextRoomId, members);
      this.uiManager.updateChatroomMembers(members, this.currentUser!.id);
      this.syncPeerMeshRoom(nextRoomId, members);
      this.uiManager.updateStatusBar(
        this.currentUser!.stageName,
        this.getChatroomDisplayName(nextRoomId),
        this.countRoomMembers(members),
        this.uiManager.getTotalMatches(),
      );
    });
    if (!isLocalOnlyRoomScope(nextRoomId)) {
      this.subscribeToMessages(nextRoomId);
      await this.activateRoomExchange(nextRoomId);
    }
  }

  /** Recompute the automatic home scope; a selected Place is never treated as home. */
  private async resolveAutomaticNearbyRoom(): Promise<string> {
    if (!this.currentUser || isTechSupportUser(this.currentUser)) return CONTACTS_ONLY_SCOPE_ID;
    // Nearby is my personal list wherever I publish a point (§3.7); nothing to resolve.
    if (this.nearbyPublicPoint()) return NEARBY_V3_ROOM_ID;
    if (!this.currentLocation) return CONTACTS_ONLY_SCOPE_ID;
    return this.chatroomService.findOptimalChatroomHierarchical(
      this.currentLocation,
      this.currentUser.id,
      undefined,
      this.locationConfirmed,
      this.effectiveNearbyMode(),
    );
  }

  /** A deliberate Nearby visit at a map point: my card moves there; the audience stays my list. */
  private beginNearbyTravel(location: GPSCoordinate, placeId: string, placeName: string): void {
    if (!this.currentUser) return;
    if (this.effectiveNearbyMode() === 'contacts-only') {
      this.uiManager.showNotification(this.uiText('quietHiddenNoTravel'), 'warning');
      return;
    }
    this.pendingNearbyTravel = { location, placeId, placeName };
    this.uiManager.emit('chatroomChanged', NEARBY_V3_ROOM_ID);
  }

  /**
   * OPEN-36 offline mode: find other phones without the hub — NSD on a shared LAN (always), and
   * Wi-Fi Direct service discovery while the hub is unreachable. Android shell only.
   */
  private initNearbyOffline(localPub: string): void {
    if (this.nearbyOfflineService) return;
    const connectivity = loadConnectivitySettings();
    const bridge = readNearbyOfflineBridge();
    const wifiDirectBridge = readAndroidWifiDirectBridge();
    const port = Number(window.location.port);
    if (!bridge || !wifiDirectBridge || !Number.isInteger(port) || port <= 0) return;
    const activeScope = this.activeExchangeRoomController?.getScope();
    if (!activeScope) return;
    bridge.setNearbyExchangeMode?.(connectivity.nearbyMode, activeScope.roomId);
    if (connectivity.nearbyMode === 'off') return;
    const apiBase = this.getBackendApiBase();
    const gapMessages: Record<NearbyReadinessGap, UiTranslationKey> = {
      permission: 'nearbyOfflinePermissionDenied',
      wifi: 'nearbyOfflineWifiOff',
      location: 'nearbyOfflineLocationOff',
      bluetooth: 'nearbyOfflineBluetoothOff',
    };
    const discoveryTiming = discoveryPolicyTimingFromSearch(window.location.search);
    const service = new NearbyOfflineService({
      localPub,
      getRoomScope: () => this.activeExchangeRoomController?.getScope() ?? null,
      port,
      bridge,
      native: new AndroidWifiDirectNative(wifiDirectBridge),
      localRelay: new EmbeddedNodeLocalRelay(apiBase),
      addGunPeer: (url) => this.gunService.addPeer(url),
      setLocalLinkIceServer,
      hubReachable: async () => {
        const res = await fetch(`${apiBase}/api/local-link/hub-status`, { cache: 'no-store' });
        if (!res.ok || ((await res.json()) as { reachable?: unknown }).reachable !== true) return false;
        let nativePath: { connected?: unknown; validated?: unknown; metered?: unknown; transports?: unknown } = {};
        try {
          nativePath = bridge.networkPathState ? JSON.parse(bridge.networkPathState()) as typeof nativePath : {};
        } catch {
          // Old native shells have no path metadata; preserve their reachable-hub behavior.
          return true;
        }
        if (nativePath.connected === false || nativePath.validated === false) return false;
        const transports = Array.isArray(nativePath.transports) ? nativePath.transports.map(String) : [];
        // Cellular switch off: a cellular-only path never serves discovery; the radios take over.
        if (!hubDiscoveryPathAllowed({ transports }, getNearbyProviderEnabled('cellular'))) return false;
        const networkInterface: NetworkInterface = transports.includes('wifi')
          ? 'wifi'
          : transports.includes('ethernet')
            ? 'ethernet'
            : transports.includes('cellular')
              ? 'cellular'
              : 'unknown';
        const settings = loadConnectivitySettings();
        const routes = new ConnectionManager(
          settings.meteredPermission,
          undefined,
          routePreferencesFromSettings(settings),
        );
        routes.register({
          path: {
            pathId: 'internet-hub',
            transport: 'gun-websocket',
            interface: networkInterface,
            directness: 'relay',
            metered: nativePath.metered === true,
            latencyMs: 100,
            bandwidthKbps: 1_000,
            batteryClass: 'low',
            stability: 80,
            health: 'healthy',
          },
          send: async () => undefined,
        });
        // This is a policy read, not a mid-exchange prompt. If the saved policy disallows the
        // active metered route, nearby Wi-Fi Direct remains eligible automatically.
        return routes.select('background-sync').selected !== null;
      },
      requestPermission: () => new Promise<boolean>((resolve) => {
        window.addEventListener('iinpublic-nearby-permission', (event) => {
          resolve(!!(event as CustomEvent<{ granted?: boolean }>).detail?.granted);
        }, { once: true });
        bridge.requestOfflineNearbyPermission();
      }),
      wifiDirectAllowed: getNearbyWifiDirectEnabled(),
      lanDiscovery: getNearbyProviderEnabled('lan') && new URLSearchParams(window.location.search).get('nearby_lan') !== '0',
      getRoomPeers: () => {
        const roster = this.activeRoomRoster;
        const roomId = this.activeExchangeRoomController?.getScope()?.roomId;
        return roster && roster.roomId === roomId ? { count: roster.count, pubs: roster.pubs } : null;
      },
      blePresence: getNearbyBluetoothEnabled() && new URLSearchParams(window.location.search).get('nearby_ble') !== '0',
      ...(discoveryTiming ? { discoveryTiming } : {}),
      // A missing permission was the user's answer to the one first-launch batch; Settings → App
      // permissions shows it. Only radio/service switches that are off get a hint.
      onReadinessGap: (gap) => gap === 'permission' ? undefined : this.uiManager.showNotification(uiText(getUiLanguagePreference(uiLanguageFromProfile(this.currentUser?.languages)), gapMessages[gap]), 'warning', {
        retry: () => bridge.openNearbySettings(gap),
      }),
      onEvent: (event) => console.info('[nearby-offline]', JSON.stringify(event)),
    });
    this.nearbyOfflineService = service;
    (window as unknown as { __iinpublicNearbyOffline?: NearbyOfflineService }).__iinpublicNearbyOffline = service;
    // A new nearby Gun peer: pull the room roster right away instead of waiting for the next 60 s
    // membership heartbeat (and once more after Gun's handshake settles).
    this.gunService.on('peer-added', () => {
      const room = this.chatroomService.getCurrentChatroomId?.() || this.currentChatroomId;
      if (!room) return;
      this.chatroomService.pullMembersFromPeers(room);
      this.chatroomService.announceMembershipNow();
      setTimeout(() => {
        this.chatroomService.pullMembersFromPeers(room);
        this.chatroomService.announceMembershipNow();
      }, 2_000);
    });
    // Settings switches (both on by default) apply immediately.
    window.addEventListener(NEARBY_SETTINGS_EVENT, () => {
      service.updateSettings({
        wifiDirect: getNearbyWifiDirectEnabled(),
        bluetooth: getNearbyBluetoothEnabled() && new URLSearchParams(window.location.search).get('nearby_ble') !== '0',
        lan: getNearbyProviderEnabled('lan') && new URLSearchParams(window.location.search).get('nearby_lan') !== '0',
      });
      this.syncNativeBackgroundAgent();
    });
    // Awareness: the Settings screen shows what nearby mode is doing right now.
    const statusTimer = setInterval(() => {
      const el = document.getElementById('settings-nearby-status');
      if (el) el.textContent = this.nearbyStatusText(service.getStatus());
    }, 2_000);
    (statusTimer as ReturnType<typeof setInterval> & { unref?: () => void }).unref?.();
    void service.start().catch((error) => console.warn('[nearby-offline] start failed', error));
  }

  /**
   * OPEN-36: upgrade non-LAN WebRTC sessions between two Android app users to a Wi-Fi Direct
   * link. Flag-gated (`?wifi_direct_link=1`, persisted) and only on the Android shell.
   */
  private initWifiDirectLink(localPub: string): void {
    if (this.wifiDirectLinkService) return;
    let storage: Storage | null = null;
    try { storage = window.localStorage; } catch { /* storage blocked */ }
    if (!resolveWifiDirectLinkFlag(window.location.search, storage)) return;
    const bridge = readAndroidWifiDirectBridge();
    if (!bridge) return;
    const native = new AndroidWifiDirectNative(bridge);
    if (!native.capabilities().wifiDirect) { native.dispose(); return; }
    const service = new WifiDirectLinkService({
      localPub,
      native,
      localRelay: new EmbeddedNodeLocalRelay(this.getBackendApiBase()),
      onEvent: (event) => console.info('[wifi-direct-link]', JSON.stringify(event)),
    });
    this.wifiDirectLinkService = service;
    // Flag-on builds only: lets adb/devtools inspect link state on a physical device.
    (window as unknown as { __iinpublicWifiDirectLink?: WifiDirectLinkService }).__iinpublicWifiDirectLink = service;
    for (const session of listP2PSessions()) service.attach(session);
    onP2PSessionCreated((session) => service.attach(session));
  }

  private nearbyStatusText(status: NearbyStatus): string {
    const language = getUiLanguagePreference(uiLanguageFromProfile(this.currentUser?.languages));
    const text = (key: UiTranslationKey, count?: number) => uiText(language, key).replace('{count}', String(count ?? ''));
    const main = status.kind === 'wifi-direct'
      ? (status.online
        ? (status.hosting ? text('settingsNearbyStatusBridging', status.phones ?? 0) : text('settingsNearbyStatusBridgingJoined'))
        : (status.hosting ? text('settingsNearbyStatusHosting', status.phones ?? 0) : text('settingsNearbyStatusJoined')))
      : status.kind === 'quiet'
        ? text('settingsNearbyStatusQuiet', Math.max(1, Math.ceil(status.nextScanInSeconds / 60)))
        : text(status.kind === 'searching' ? 'settingsNearbyStatusSearching' : status.kind === 'off' ? 'settingsNearbyStatusOff' : 'settingsNearbyStatusStandby');
    return status.lanPhones > 0 ? `${main} ${text('settingsNearbyStatusLan', status.lanPhones)}` : main;
  }

  private ensurePeerMeshService(): PeerMeshService | null {
    if (!usesMeshTalkDelivery(this.p2pRuntimeFlags) || !this.currentUser?.id) return null;
    if (this.peerMeshService) return this.peerMeshService;
    this.peerMeshService = new PeerMeshService(this.gunService, {
      apiBase: this.getBackendApiBase(),
      localUserId: this.currentUser.id,
      localStageName: this.currentUser.stageName || this.currentUser.id,
      onNeighborConnected: (userId, roomId) => {
        // Warm the block-status answer now, so this peer's first Talk does not wait ~1 s on the hub.
        void this.resolveBlockStatusEitherWay(userId).catch(() => undefined);
        this.catchUpPeerInActiveRoom(userId, roomId);
      },
      knockPeer: async (peer) => { await this.nearbyV3?.knock(peer); },
      onNearbyGossip: async (fromUserId, fromPub, message) => this.nearbyV3?.handleGossip(fromUserId, fromPub, message) ?? null,
      personalConcurrency: () => {
        const config = this.nearbyV3Config();
        return { initial: config.broadcastInitialConcurrency, max: config.broadcastMaxConcurrency };
      },
      onPersonalPeerUnreachable: (_userId, pub) => this.nearbyV3?.unreachable(pub),
      admitTalkOffer: (authorUserId) => {
        const contacts = this.currentUser?.knownPeople;
        if (Array.isArray(contacts) && contacts.some((known) => known?.userId === authorUserId)) return true;
        return this.incomingTalkBudget.tryAdmit(Date.now());
      },
      ...(this.protocolManifestController
        ? { protocolManifestController: this.protocolManifestController }
        : {}),
      authoritativeDirectTalkDelivery: this.p2pRuntimeFlags.p2pDirectTalkDelivery,
      onTalkDeliveryAck: (payload, recipientUserId) => {
        this.noteNearbyDelivery(payload.talkId, recipientUserId);
        const deliveredTalk = payload.talkData as unknown as Talk;
        const wholeIdentityKey = buildTalkIdentityKey(deliveredTalk);
        const deliveredIdentityKeys = buildTagIdentityKeys(deliveredTalk, wholeIdentityKey);
        const sentAt = new Date().toISOString();
        for (const identityKey of deliveredIdentityKeys) {
          void markTalkSentToPeer({
            peerId: recipientUserId,
            identityKey,
            talkId: payload.talkId,
            sentAt,
          }).catch((error) => console.warn('[ledger] delivery acknowledgement commit failed:', error));
        }
        recordTalkRevisionSent(
          recipientUserId,
          payload.talkId,
          computeTalkRevisionHash(deliveredTalk),
        );
      },
      onProtocolManifestCompatibility: async () => {
        await this.syncActiveRoomProtocolCheckpoint();
      },
      forwardingSettings: loadConnectivitySettings().forwarding,
      ...(this.p2pRuntimeFlags.p2pNodeEnabled
        ? {
            createSession: (params) =>
              createFallbackMeshSession({
                primaryFactory: () =>
                  getOrCreateLibp2pMeshSession({
                    conversationId: params.conversationId,
                    localUserId: params.localUserId,
                    localPub: params.localPub,
                    localPair: params.localPair,
                    otherUserId: params.otherUserId,
                    otherPub: params.otherPub,
                    gunService: this.gunService,
                    ensureLibp2pNode: () => this.ensureContentLibp2pInitialized(),
                    onRemoteMeshFrame: params.onRemoteMeshFrame,
                  }),
                fallbackFactory: () =>
                  getOrCreateP2PSession({
                    apiBase: this.getBackendApiBase(),
                    conversationId: params.conversationId,
                    localUserId: params.localUserId,
                    localPub: params.localPub,
                    localPair: params.localPair,
                    otherUserId: params.otherUserId,
                    otherPub: params.otherPub,
                    isInitiator: params.isInitiator,
                    // S2: Gun pub/sub signaling for the mesh fallback session too.
                    gun: this.gunService.getGun(),
                    onRemoteMeshFrame: params.onRemoteMeshFrame,
                  }),
                onFallback: (cause, error) => {
                  console.warn(`[Mesh] falling back to WebRTC session after libp2p ${cause} failure`, error);
                },
              }),
          }
        : {}),
      onTalkBody: (payload) => this.handleMeshTalkBody(payload),
      onTalkResponse: (payload) => void this.handleMeshTalkResponse(payload).catch((err) => {
        // The mesh layer has already ACKed the frame by the time this runs — a swallowed
        // throw here is a silently lost response (observed: author-side decrypt failures
        // were invisible in every log). Never let ingest failures vanish.
        console.warn('[MeshResponse] ingest failed for', payload?.responderId, err);
      }),
      onPing: (fromUserId, _frame) => {
        const diag = this.meshPingDiagnostics;
        diag.lastPingFrom = fromUserId;
        if (!diag.pingedOrigins.includes(fromUserId)) diag.pingedOrigins.push(fromUserId);
      },
      onPong: (fromUserId, _frame) => {
        const diag = this.meshPingDiagnostics;
        diag.lastPongFrom = fromUserId;
        if (!diag.pongedOrigins.includes(fromUserId)) diag.pongedOrigins.push(fromUserId);
      },
      // Step 2: record announce receipt for durable E2E assertion (meshAnnounceDiagnostics).
      // Fires before body pull, enabling the test to assert announce reachability without
      // waiting for the full talk-body-request/talk-body round-trip.
      onTalkAnnounce: async (payload, _frame) => {
        const diag = this.meshAnnounceDiagnostics;
        const alreadyRecorded = diag.received.some(
          (r) => r.talkId === payload.talkId && r.authorId === payload.authorId,
        );
        if (!alreadyRecorded) {
          diag.received.push({ talkId: payload.talkId, authorId: payload.authorId });
        }
        return this.shouldAcceptIncomingTalkAsync({
          senderId: payload.authorId,
          talkData: {
            id: payload.talkId,
            title: payload.title,
            type: payload.type,
            isAdult: payload.isAdult === true,
            language: payload.language,
            tags: payload.tags ?? [],
            questions: [],
          },
          ...(this.currentChatroomId ? { deliveryChatroomId: this.currentChatroomId } : {}),
        });
      },
      // OPEN-38/G6: mailbox the encrypted body for every pending receiver and send a separate
      // content-free wake only to peers that look asleep; the authoritative body stays queued.
      ...(this.p2pRuntimeFlags.p2pDirectTalkDelivery
        ? {
            onDirectDeliveryPending: async (
              payload: P2PMeshTalkBodyPayload,
              recipientUserIds: string[],
              wakeRecipientUserIds: string[],
            ) => {
              await Promise.all([
                this.postTalkWakeToMailbox(wakeRecipientUserIds),
                this.postTalkBodyToMailboxForRecipients(payload, recipientUserIds),
              ]);
            },
          }
        : {}),
      // Mixed-release escape hatch only. The authoritative direct scheduler retains ordinary
      // Talk bodies in the author's encrypted local outbox until the end receiver ACKs.
      ...(!this.p2pRuntimeFlags.p2pDirectTalkDelivery
        ? {
            onMailboxFallback: (payload: P2PMeshTalkBodyPayload, recipientUserIds: string[]) =>
              this.postTalkBodyToMailboxForRecipients(payload, recipientUserIds),
          }
        : {}),
      // Step 10: responder side — handle incoming retraction flood frames.
      onTalkRetracted: (payload) => this.handleMeshTalkRetracted(payload),
    });
    (this as any).peerMeshService = this.peerMeshService;
    return this.peerMeshService;
  }

  private async resolveDiscoveredUserIds(
    providerPeerIds: string[],
    members: Array<{ userId: string; stageName?: string }>,
  ): Promise<string[]> {
    if (!Array.isArray(providerPeerIds) || providerPeerIds.length === 0) return [];
    const peerSet = new Set(providerPeerIds.map((peerId) => String(peerId || '').trim()).filter(Boolean));
    if (peerSet.size === 0) return [];
    const discovered = new Set<string>();
    await Promise.all(members.map(async (member) => {
      const userId = String(member.userId || '').trim();
      if (!userId) return;
      try {
        const binding = await this.gunService.get(`p2p-peer-bindings/${userId}`) as { peerId?: unknown } | null;
        const peerId = String(binding?.peerId || '').trim();
        if (peerId && peerSet.has(peerId)) {
          discovered.add(userId);
        }
      } catch {
        // best-effort
      }
    }));
    return [...discovered];
  }

  private ensureRoomDiscoveryService(): P2PRoomDiscoveryService | null {
    if (!this.p2pRuntimeFlags.p2pNodeEnabled) return null;
    if (this.roomDiscoveryService) return this.roomDiscoveryService;
    const discoveryConfig = this.contentNodeService.getDiscoveryConfig();
    this.roomDiscoveryService = new P2PRoomDiscoveryService(
      () => this.ensureContentLibp2pInitialized(),
      discoveryConfig.bootstrapPeers,
    );
    this.meshDiscoveryDiagnostics.bootstrapPeers = this.roomDiscoveryService.getBootstrapPeers();
    return this.roomDiscoveryService;
  }

  /**
   * Peers that can reach each other in the same room exchange Talks automatically: when a link to
   * a room peer comes up (LAN, Wi-Fi Direct or relay) or a peer appears in the roster, send it the
   * Talks this device deliberately broadcast in THIS room that it has not received yet. The
   * per-peer delivery ledger keeps this idempotent; Talks never follow the user to another room.
   */
  /**
   * Mark Talks whose deliberate room broadcast is still resolving receivers or delivering (~10 s on
   * a phone). Catch-up skips them, so a roster change meanwhile does not send the same Talk in
   * parallel (OPEN-51). Returns the release to call when that broadcast settles.
   */
  private markTalkBroadcastsInFlight(talkIds: readonly string[]): () => void {
    for (const talkId of talkIds) this.talkBroadcastsInFlight.set(talkId, (this.talkBroadcastsInFlight.get(talkId) ?? 0) + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      for (const talkId of talkIds) {
        const remaining = (this.talkBroadcastsInFlight.get(talkId) ?? 1) - 1;
        if (remaining > 0) this.talkBroadcastsInFlight.set(talkId, remaining);
        else this.talkBroadcastsInFlight.delete(talkId);
      }
      // A peer that connected mid-broadcast was skipped above; the broadcast's own flood may have
      // gone out before that link was live. Re-check it now: the per-peer ledger only still lists
      // the Talk if the broadcast did not reach that peer.
      const deferred = [...this.catchUpDeferredByInFlight.entries()];
      this.catchUpDeferredByInFlight.clear();
      for (const [peerId, { roomId, stageName }] of deferred) {
        this.catchUpPeerInActiveRoom(peerId, roomId, stageName, { ignoreInterval: true });
      }
    };
  }

  private catchUpPeerInActiveRoom(
    userId: string,
    roomId: string,
    stageName?: string,
    options: { ignoreInterval?: boolean } = {},
  ): void {
    const peerId = String(userId || '').trim();
    if (!peerId || !this.currentUser || isTechSupportUser(this.currentUser) || peerId === this.currentUser.id) return;
    if (peerId === TECHSUPPORT_ROOT_USER_ID) return;
    const scope = this.activeExchangeRoomController?.getScope();
    if (!scope || scope.roomId !== roomId || this.currentChatroomId !== roomId) return;
    // A link opened from the other side is only a route: in Nearby and Places my Talks catch up
    // only with people on my current page (found by stage5/14: "page1" reached someone whose own
    // page held me, although mine did not hold them).
    const currentList = new Set(this.nearbyV3?.service.entries().map((entry) => entry.card.userId) ?? []);
    if (!isCatchUpRecipient(roomId, peerId, currentList)) return;
    const key = `${roomId}|${peerId}`;
    const now = Date.now();
    if (!options.ignoreInterval && now - (this.roomCatchupSentAt.get(key) ?? 0) < ROOM_CATCHUP_MIN_INTERVAL_MS) return;
    const broadcastHere = talksBroadcastInRoom(roomId);
    // A Talk already queued for this peer is retried by the outbox itself (OPEN-51).
    const candidates = getUnsentBroadcastTalkIdsForReceiver(peerId).filter((talkId) =>
      broadcastHere.has(talkId) && !this.peerMeshService?.hasQueuedDirectTalk(peerId, talkId));
    // A Talk still being broadcast is left to that broadcast; re-check this peer when it settles.
    if (candidates.some((talkId) => this.talkBroadcastsInFlight.has(talkId))) {
      this.catchUpDeferredByInFlight.set(peerId, { roomId, ...(stageName ? { stageName } : {}) });
    }
    const talkIds = candidates.filter((talkId) => !this.talkBroadcastsInFlight.has(talkId));
    if (talkIds.length === 0) return;
    this.roomCatchupSentAt.set(key, now);
    this.uiManager.emit('broadcastTalk', {
      chatroomId: roomId,
      members: [{ userId: peerId, stageName: stageName || peerId }],
      talkIds,
      automatic: true,
      catchUp: true,
    });
  }

  private syncPeerMeshRoom(
    chatroomId: string,
    members: Array<{ userId: string; stageName?: string; pub?: string }>,
  ): void {
    if (isLocalOnlyRoomScope(chatroomId)) {
      this.peerMeshService?.leaveRoom();
      return;
    }
    const mesh = this.ensurePeerMeshService();
    if (!mesh || !this.currentUser?.id || !chatroomId) return;
    const withSelf = members.some((member) => member.userId === this.currentUser!.id)
      ? members
      : [
          ...members,
          { userId: this.currentUser.id, stageName: this.currentUser.stageName },
        ];
    // The Nearby runtime notices a new scope itself (it watches its position) and reissues my card.
    const personal = isPersonalListScope(chatroomId);
    void mesh.joinRoom(chatroomId, withSelf, { personal, replace: personal })
      .then(() => {
        for (const member of members) this.catchUpPeerInActiveRoom(member.userId, chatroomId, member.stageName);
      })
      .catch((error) => {
        console.warn('Peer mesh room join failed:', error);
      });
    const discovery = !personal && this.hubDiscoveryAllowedNow() ? this.ensureRoomDiscoveryService() : null;
    if (discovery) {
      void discovery.announceRoom(chatroomId).catch((error) => {
        console.warn('Room discovery provide failed:', error);
      });
      void discovery.findRoomProviderPeerIds(chatroomId, { timeoutMs: 2_500, limit: 20 })
        .then(async (peers) => {
          const discoveredUserIds = await this.resolveDiscoveredUserIds(peers, withSelf);
          const cached = this.roomDiscoveredUserIds.get(chatroomId) || new Set<string>();
          const sameRoomUserIds = withSelf
            .map((member) => String(member.userId || '').trim())
            .filter((userId) => !!userId && userId !== this.currentUser?.id);
          for (const userId of sameRoomUserIds) cached.add(userId);
          for (const userId of discoveredUserIds) cached.add(userId);
          this.roomDiscoveredUserIds.set(chatroomId, cached);
          this.meshDiscoveryDiagnostics = {
            roomId: chatroomId,
            providerPeerIds: peers,
            discoveredUserIds: [...cached],
            bootstrapPeers: discovery.getBootstrapPeers(),
            updatedAt: new Date().toISOString(),
          };
        })
        .catch((error) => {
          console.warn('Room discovery findProviders failed:', error);
        });
    }
    // Step 6: drain the mailbox whenever roster changes (any newly-present member
    // may be a sender for whom we have queued envelopes in the mailbox).
    // Also retry failed mailbox POSTs now that network may be available.
    void this.drainMailbox().catch(() => {});
    void this.retryFailedMailboxPosts().catch(() => {});
  }

  // ─── Encrypted offline mailbox — drain-on-connect (Step 6) ───────────────
  //
  // On every roster join / app boot, fetch and decrypt all pending envelopes
  // addressed to the current user.  Each envelope carries a P2PMeshTalkResponsePayload
  // encrypted with SEA ECDH (sender's epub, recipient's keypair).  After successful
  // dispatch through handleMeshTalkResponse (which dedupes on responseId), delete
  // the envelope from the server (drain-then-delete: a crash before delete causes
  // re-delivery, which is safe because the handler is idempotent).

  private ensureMailboxClient(): WebMailboxClient {
    if (!this.mailboxClient) {
      this.mailboxClient = new WebMailboxClient(this.getBackendApiBase());
    }
    return this.mailboxClient;
  }

  private startMailboxPolling(): void {
    this.registerNativeMailboxWatch();
    this.syncNativeBackgroundAgent();
    void this.replayNativeBackgroundEvents();
    if (this.mailboxPollTimer) return;
    // On a phone the native mailbox watch keeps a long-poll open and wakes a drain the moment an
    // envelope arrives ('iinpublic-mailbox-changed'), so the page polls only as a safety net
    // (OPEN-73: every 3 s was ~20 requests a minute through the embedded node while idle). The
    // page does not hold a long-poll itself: a browser allows six connections per host, and one
    // more held open starved WebRTC signaling (stage2/88's deal confirmation never arrived).
    // While hidden it polls once a minute.
    const native = this.hasNativeMailboxWatch();
    if (native && !this.mailboxChangedListener) {
      this.mailboxChangedListener = () => { void this.drainMailbox().catch(() => {}); };
      window.addEventListener('iinpublic-mailbox-changed', this.mailboxChangedListener);
    }
    this.mailboxPollTimer = setInterval(throttleWhileHidden(() => {
      void this.drainMailbox().catch(() => {});
      void this.retryFailedMailboxPosts().catch(() => {});
    }, 60_000), native ? NATIVE_MAILBOX_SAFETY_DRAIN_MS : 3_000);
  }

  /**
   * OPEN-38: Android 12+ freezes this page while the phone dozes, so this 3 s poll stops. Tell
   * the always-on native service whose mailbox to watch meanwhile — it only counts envelope ids
   * and posts a "new activity" notification; this page still does every decrypt/drain on open.
   * A no-op in browsers and on builds without the bridge method.
   */
  private hasNativeMailboxWatch(): boolean {
    const bridge = (window as unknown as { IinPublicNearby?: { setMailboxWatch?: unknown } }).IinPublicNearby;
    return typeof bridge?.setMailboxWatch === 'function';
  }

  private registerNativeMailboxWatch(): void {
    const userId = this.currentUser?.id;
    const bridge = (window as unknown as { IinPublicNearby?: { setMailboxWatch?: (id: string) => void } }).IinPublicNearby;
    if (!userId || typeof bridge?.setMailboxWatch !== 'function') return;
    try {
      bridge.setMailboxWatch(userId);
    } catch {
      /* native shell without the watcher — nothing to register */
    }
  }

  /**
   * G6: give the app-owned foreground processor only the user's already-approved exact rules and
   * current room/session state. The SEA pair never crosses this JS bridge; Android reads it from
   * Keystore custody and provisions the loopback-only embedded node itself.
   */
  private syncNativeBackgroundAgent(): void {
    if (this.nativeBackgroundSyncRunning || !this.currentUser?.id) {
      this.nativeBackgroundSyncQueued = true;
      return;
    }
    const bridge = (window as unknown as {
      IinPublicBackground?: { configure?: (configJson: string) => boolean };
    }).IinPublicBackground;
    if (typeof bridge?.configure !== 'function') return;
    this.nativeBackgroundSyncRunning = true;
    const flattened = getFlattenedAnswerPreferences();
    const answerRules = backgroundAnswerRulesFromPreferences(flattened);
    const settings = loadConnectivitySettings();
    const myTalks = getMyTalks();
    const authoredTalks = Object.fromEntries(Object.entries(myTalks)
      .filter(([, entry]) => entry.role === 'created'));
    try {
      bridge.configure(JSON.stringify({
        version: 1,
        enabled: settings.nearbyMode === 'always',
        userId: this.currentUser.id,
        stageName: this.currentUser.stageName,
        activeRoomId: this.chatroomService.getCurrentChatroomId?.() || this.currentChatroomId || '',
        autoAnswerEnabled: this.uiManager.getChatbotEnabled(),
        answerRules,
        chatbotTemplates: getChatbotTemplates(),
        authoredTalks,
        matchCollectionStore: appStorage.getItem('iinpublic_match_collection_sessions_v1') || undefined,
        intakeFilters: getTalkIntakeFilters(),
        knownContactIds: (this.currentUser.knownPeople || []).map((person) => person.userId),
        travelerIds: [...this.roomTravelerIds],
        ageVerified: !!this.currentUser.reputation?.ageVerified,
      }));
    } catch {
      // Older native shells and a full native policy store are both non-fatal to the foreground UI.
    } finally {
      this.nativeBackgroundSyncRunning = false;
      if (this.nativeBackgroundSyncQueued) {
        this.nativeBackgroundSyncQueued = false;
        queueMicrotask(() => this.syncNativeBackgroundAgent());
      }
    }
  }

  /** Reconcile the encrypted native journal through the same idempotent UI handlers. */
  private async replayNativeBackgroundEvents(): Promise<void> {
    if (this.nativeBackgroundReplayRunning) {
      this.nativeBackgroundReplayQueued = true;
      return;
    }
    const bridge = (window as unknown as {
      IinPublicBackground?: {
        events?: () => string;
        acknowledge?: (idsJson: string) => boolean;
      };
    }).IinPublicBackground;
    if (!this.currentUser?.id || typeof bridge?.events !== 'function') return;
    this.nativeBackgroundReplayRunning = true;
    try {
      let events: any[] = [];
      try {
        const parsed = JSON.parse(bridge.events());
        events = Array.isArray(parsed?.events) ? parsed.events : [];
      } catch { return; }
      const acknowledged: string[] = [];
      for (const event of events) {
        try {
          if (event?.kind === 'incoming-talk' && event.payload) {
            const accepted = await this.handleMeshTalkBody(event.payload, { suppressAutoReply: true });
            if (accepted && Array.isArray(event.answers) && event.answers.length > 0) {
              const talkData = { ...event.payload.talkData, id: event.payload.talkId, authorId: event.payload.authorId };
              const outcome = checkIfMatch(talkData, event.answers) ? 'match' : 'mismatch';
              this.uiManager.recordChatbotAnsweredTalk(talkData, event.answers, outcome);
              await this.submitTalkResponsePairDirect({
                talkId: event.payload.talkId,
                talkData,
                answers: event.answers,
                isChatbotResponse: true,
                authorId: event.payload.authorId,
                authorName: event.payload.authorName || event.payload.authorId,
                isAutoResponse: true,
              });
            }
          } else if (event?.kind === 'talk-response' && event.payload) {
            await this.handleMeshTalkResponse(event.payload);
          } else {
            continue;
          }
          if (event.id) acknowledged.push(String(event.id));
        } catch (error) {
          console.warn('[BackgroundAgent] Native event retained for retry:', error);
        }
      }
      if (acknowledged.length > 0 && typeof bridge.acknowledge === 'function') {
        try { bridge.acknowledge(JSON.stringify(acknowledged)); } catch { /* retry on next resume */ }
      }
    } finally {
      this.nativeBackgroundReplayRunning = false;
      if (this.nativeBackgroundReplayQueued) {
        this.nativeBackgroundReplayQueued = false;
        queueMicrotask(() => { void this.replayNativeBackgroundEvents(); });
      }
    }
  }

  /**
   * Drain all envelopes addressed to the current user from the server mailbox.
   * Decrypt each and dispatch:
   *   - talk-body payloads (R-a step 7 fallback) → handleMeshTalkBody
   *   - talk-response payloads → handleMeshTalkResponse
   * Then delete each envelope (drain-then-delete; re-delivery on crash is safe — handlers dedup).
   */
  private async drainMailbox(): Promise<void> {
    if (this.mailboxDrainPromise) return this.mailboxDrainPromise;
    this.mailboxDrainPromise = this.drainMailboxOnce().finally(() => {
      this.mailboxDrainPromise = null;
    });
    return this.mailboxDrainPromise;
  }

  private async drainMailboxOnce(): Promise<void> {
    if (!this.currentUser?.id) return;
    const mailbox = this.ensureMailboxClient();
    const pair = this.gunService.getStoredPair();
    if (!pair?.priv) return; // keypair not ready

    const envelopes = await mailbox.fetchEnvelopes(this.currentUser.id);
    if (!envelopes.length) return;

    console.log(`[Mailbox] Draining ${envelopes.length} envelope(s) for user ${this.currentUser.id}`);
    for (const envelope of envelopes) {
      try {
        let senderEpubHint = '';
        try {
          const wrapper = JSON.parse(envelope.ciphertext) as { senderEpub?: unknown };
          senderEpubHint = typeof wrapper.senderEpub === 'string' ? wrapper.senderEpub.trim() : '';
        } catch {
          /* malformed wrappers are handled by decryptEnvelope below */
        }
        const payload = await mailbox.decryptEnvelope<
          | P2PMeshTalkResponsePayload
          | import('../../shared/p2p-mesh-protocol').P2PMeshTalkBodyPayload
          | MailboxAttachmentSharePayload
          | MailboxConversationMessagePayload
          | MailboxSupportQuestionPayload
          | BlockSignalPayload
        >(
          envelope.ciphertext,
          pair as import('../sea-gun').GunPair,
        );
        if (senderEpubHint) {
          const senderId = mailboxSenderId(payload);
          if (senderId) this.peerEpubByUserId.set(senderId, senderEpubHint);
        }
        // The kind is the core's call (src/core/mailbox-envelope.ts); each goes to its handler.
        switch (classifyMailboxPayload(payload)) {
          case 'wake':
            // OPEN-38: the direct scheduler delivers the queued Talk now that this page runs.
            break;
          case 'conversation-message':
            await this.ingestConversationMessageFromMailbox(payload as MailboxConversationMessagePayload);
            break;
          case 'attachment-share':
            await this.ingestAttachmentShareFromMailbox(payload as MailboxAttachmentSharePayload);
            break;
          case 'support-question':
            await this.ingestSupportQuestionFromMailbox(payload as MailboxSupportQuestionPayload);
            break;
          case 'block-signal':
            await this.ingestBlockSignalFromMailbox(payload as BlockSignalPayload);
            break;
          case 'talk-body':
            await this.handleMeshTalkBody(payload as import('../../shared/p2p-mesh-protocol').P2PMeshTalkBodyPayload);
            break;
          case 'talk-retraction':
            await this.handleMeshTalkRetracted(payload as unknown as P2PMeshTalkRetractedPayload);
            break;
          case 'talk-response':
            await this.handleMeshTalkResponse(payload as P2PMeshTalkResponsePayload);
            break;
        }
        // Delete only after handler completes without throwing.
        await mailbox.deleteEnvelope(this.currentUser.id, envelope.id);
        console.log('[Mailbox] Drained and deleted envelope', envelope.id);
      } catch (err) {
        console.warn('[Mailbox] Failed to drain envelope', envelope.id, '— will retry on next connect:', err);
        // Leave envelope on server for re-delivery.
      }
    }
  }

  /**
   * OPEN-38: wake receivers whose page is frozen asleep. One content-free encrypted envelope per
   * receiver per 30 minutes; it reveals only that this sender has something for them.
   */
  private async postTalkWakeToMailbox(recipientUserIds: string[]): Promise<void> {
    const me = this.currentUser?.id;
    const pair = this.gunService.getStoredPair();
    if (!me || !pair?.priv || this.mailboxFallbackDisabledForE2e) return;
    const mailbox = this.ensureMailboxClient();
    const nowMs = Date.now();
    for (const recipientId of recipientUserIds) {
      if (!recipientId || recipientId === me || !this.talkWakeThrottle.shouldWake(recipientId, nowMs)) continue;
      try {
        const recipientEpub = await this.resolvePeerEpub(recipientId);
        if (!recipientEpub) continue;
        const ciphertext = await mailbox.encryptForRecipient(
          recipientEpub,
          pair as import('../sea-gun').GunPair,
          buildTalkWakePayload(me, new Date(nowMs)),
        );
        await mailbox.postEnvelope({
          id: talkWakeEnvelopeId(me, recipientId, nowMs),
          recipientId,
          ciphertext,
          ttlMs: TALK_WAKE_TTL_MS,
        });
      } catch (error) {
        console.warn('[Mailbox] Failed to post Talk wake ping:', error);
      }
    }
  }

  /**
   * Post a talk response to the mailbox as a ciphertext-only envelope.
   * Falls back to localStorage queue only when the mailbox POST itself fails.
   */
  private async postToMailbox(payload: P2PMeshTalkResponsePayload): Promise<boolean> {
    if (this.mailboxFallbackDisabledForE2e) return true;
    if (!this.currentUser?.id) return false;
    const mailbox = this.ensureMailboxClient();
    const pair = this.gunService.getStoredPair();
    if (!pair?.priv) return false;

    const authorEpub = await this.resolvePeerEpub(payload.authorId);
    if (!authorEpub) {
      console.warn('[Mailbox] Cannot post — author epub not found for', payload.authorId);
      return false;
    }

    try {
      const ciphertext = await mailbox.encryptForRecipient(
        authorEpub,
        pair as import('../sea-gun').GunPair,
        payload,
      );
      const envelopeId = `mbx_${payload.responseId}`;
      const result = await mailbox.postEnvelope({
        id: envelopeId,
        recipientId: payload.authorId,
        ciphertext,
      });
      if (result.stored) {
        console.log('[Mailbox] Posted envelope', envelopeId, 'for author', payload.authorId);
        return true;
      }
      console.warn('[Mailbox] Server rejected envelope:', result.error);
      return false;
    } catch (err) {
      console.warn('[Mailbox] postToMailbox failed:', err);
      return false;
    }
  }

  /**
   * Phase 4: queue an undeliverable DM to the recipient's encrypted offline mailbox.
   * Fired by the direct transport when WebRTC delivery fails (peer offline). The wire
   * is already SEA-ECDH-encrypted between the two users; the mailbox wraps it again.
   * Idempotent: the envelope id is derived from the message id, and drain → upsert is
   * keyed by message id, so re-delivery is safe.
   */
  private async postConversationMessageToMailbox(
    wire: import('../services/gun-message-store').ConversationMessageWire,
    conversationId: string,
    recipientUserId: string,
  ): Promise<void> {
    if (this.mailboxFallbackDisabledForE2e) return;
    if (!this.currentUser?.id || !recipientUserId || recipientUserId === this.currentUser.id) return;
    const mailbox = this.ensureMailboxClient();
    const pair = this.gunService.getStoredPair();
    if (!pair?.priv) return;
    try {
      const recipientEpub = await this.resolvePeerEpub(recipientUserId);
      if (!recipientEpub) {
        console.warn('[Mailbox] Cannot post DM — epub not found for', recipientUserId);
        return;
      }
      const payload: MailboxConversationMessagePayload = {
        kind: 'conversation-message-v1',
        conversationId,
        senderId: wire.senderId,
        recipientUserId,
        senderName: this.currentUser.stageName,
        wire,
      };
      const ciphertext = await mailbox.encryptForRecipient(recipientEpub, pair as import('../sea-gun').GunPair, payload);
      const result = await mailbox.postEnvelope({
        id: `mbx_dm_${conversationId}_${wire.id}`,
        recipientId: recipientUserId,
        ciphertext,
      });
      if (result.stored) {
        console.log('[Mailbox] Posted DM envelope', wire.id, 'for', recipientUserId);
      } else {
        console.warn('[Mailbox] Server rejected DM envelope:', result.error);
      }
    } catch (err) {
      console.warn('[Mailbox] postConversationMessageToMailbox failed:', err);
    }
  }

  /**
   * Phase 4: drain handler for an offline DM — writes the wire idempotently into the
   * recipient's local Gun (same pair-private path the live transport uses), so the UI
   * renders it via the existing conversation subscription.
   */
  private async ingestConversationMessageFromMailbox(payload: MailboxConversationMessagePayload): Promise<void> {
    if (!this.currentUser?.id) return;
    if (payload.kind !== 'conversation-message-v1' || !payload.wire?.id) return;
    await this.conversationService.ensureIncomingConversation({
      ownerUserId: this.currentUser.id,
      conversationId: payload.conversationId,
      senderUserId: payload.senderId,
      ...(payload.senderName ? { senderUserName: payload.senderName } : {}),
      createdAt: payload.wire.timestamp,
    });
    await this.conversationService.upsertMessageRecord(
      payload.conversationId,
      payload.wire,
      { otherUserId: payload.senderId },
    );
    console.log('[Mailbox] Ingested offline DM', payload.wire.id, 'in', payload.conversationId);
  }

  /**
   * docs/TODO.md K5, design note §Item 3 (decision K5-A). A new question reaches TechSupport as
   * an encrypted mailbox envelope, never a server route, so the relay holds no support-inbox
   * data — the same pattern `postConversationMessageToMailbox` uses for ordinary DMs. Called
   * only from the asker's client (§Item 2's miss branch). The envelope id is deterministic on
   * `questionKey` + `askedBy`, so a repeat ask overwrites rather than duplicating.
   *
   * Deliberately does NOT use `resolvePeerEpub` (generic presence/`users/<id>` lookup): that
   * path reads whatever epub is currently on `users/TECHSUPPORT_ROOT_USER_ID`, which any session
   * that has ever adopted the reserved TechSupport id — including test fixtures built by an
   * ordinary (non-K3-mode) bootstrap — can have overwritten with an unrelated device keypair.
   * Encrypting to that would silently produce an envelope the real TechSupport device can never
   * decrypt. `discoverTechSupportIdentityFromGun()` reads the signed `public/techsupport-identity`
   * record and verifies it against the compiled TechSupport trust-anchor overlap list, so its
   * `epub` is guaranteed to belong to an authorized DM key — the same guarantee K1/K3 already
   * rely on elsewhere, including during staged key rotation.
   */
  private async postSupportQuestionToMailbox(entry: SupportInboxEntry): Promise<void> {
    if (this.mailboxFallbackDisabledForE2e) return;
    if (!this.currentUser?.id) return;
    const mailbox = this.ensureMailboxClient();
    const pair = this.gunService.getStoredPair();
    if (!pair?.priv) return;

    const payload: MailboxSupportQuestionPayload = {
      kind: 'support-question-v1',
      questionKey: entry.questionKey,
      question: entry.question,
      askedBy: entry.askedBy,
      conversationId: entry.conversationId,
      askedAt: entry.askedAt,
    };

    // docs/TODO.md K7 fan-out: master + every currently-valid delegate each get their own
    // envelope, so any of their devices independently materializes the same pending row in
    // their OWN local `techsupport-inbox/*` (design note "Delivery: keeping the inbox visible
    // to N devices"). One recipient's failure must not block another's.
    const recipients: Array<{ id: string; epub: string }> = [];
    try {
      const identity = await this.discoverTechSupportIdentityFromGun();
      if (identity?.epub) recipients.push({ id: TECHSUPPORT_ROOT_USER_ID, epub: identity.epub });
      else console.warn('[Mailbox] Cannot post support question to master — verified TechSupport identity not found');
    } catch (err) {
      console.warn('[Mailbox] discoverTechSupportIdentityFromGun failed:', err);
    }
    // K7 follow-on: don't trust the ambient background poll's timing here — a fast session
    // (found on the packaged Electron app, real-hardware test 09-android-techsupport-delegate-
    // answers) can ask its very first question before that poll's first tick has even resolved,
    // silently fanning out to master only. A synchronous fetch right before addressing removes
    // the race outright, independent of any poll interval.
    //
    // Real-device regression (2026-09-23, Huawei phone): the original fix here awaited the fetch
    // but then discarded its result and re-derived `validDelegates` via `readCachedDelegateGrants()`
    // — a second, independent round trip through localStorage. Confirmed live: the fetch got a
    // fresh 200 with the correct grant, `verifyDelegateGrant` accepted it, and the delegate's epub
    // was resolvable — yet no mailbox envelope was ever posted for the delegate, only for the
    // master. The verified, already-in-memory list from the fetch itself is used directly now,
    // removing that unnecessary write-then-read-back dependency entirely (still fine to also let
    // the fetch populate the cache for its other readers, e.g. the master's own admin panel).
    const freshDelegateGrants = await fetchDelegateGrantsFromServer(this.getBackendApiBase()).catch(() => []);
    const now = new Date();
    const validDelegates = freshDelegateGrants.filter((g) => isValidDelegateGrant(g, now));
    // Diagnostic (2026-09-23, Huawei-phone fan-out investigation): the two prior fixes to this
    // function (the synchronous pre-fetch, then removing the localStorage round-trip) both looked
    // correct in isolation and both failed to fix the actual real-device symptom — the delegate
    // never received an envelope, master always did. Every failure in this loop was previously
    // silently swallowed (by design, so one delegate's failure can't block another's — see the
    // comment above), which made it impossible to tell from server-side logs alone whether
    // validDelegates was empty, or non-empty but epub resolution failed. This makes both
    // observable without changing the swallow-and-continue behavior itself.
    console.log(`🔍 [Mailbox] fan-out: fetched ${freshDelegateGrants.length} grant(s), ${validDelegates.length} currently valid`);
    for (const grant of validDelegates) {
      try {
        const epub = await this.resolvePeerEpub(grant.delegateUserId);
        console.log(`🔍 [Mailbox] resolvePeerEpub(${grant.delegateUserId}) ->`, epub ? epub.slice(0, 12) + '…' : '(empty)');
        if (epub) recipients.push({ id: grant.delegateUserId, epub });
      } catch (err) {
        console.warn(`🔍 [Mailbox] resolvePeerEpub(${grant.delegateUserId}) threw:`, err);
        /* one delegate's unresolved epub must not block the rest */
      }
    }

    await Promise.all(recipients.map(async ({ id, epub }) => {
      try {
        const ciphertext = await mailbox.encryptForRecipient(epub, pair as import('../sea-gun').GunPair, payload);
        const result = await mailbox.postEnvelope({
          id: `mbx_support_${entry.questionKey}_${entry.askedBy}_${id}`,
          recipientId: id,
          ciphertext,
        });
        if (result.stored) {
          console.log('[Mailbox] Posted support question envelope for', entry.questionKey, 'to', id);
        } else {
          console.warn('[Mailbox] Server rejected support question envelope for', id, ':', result.error);
        }
      } catch (err) {
        console.warn('[Mailbox] postSupportQuestionToMailbox failed for', id, ':', err);
      }
    }));
  }

  /**
   * docs/TODO.md K5, design note §Item 3. Device-side ingest: only materializes a pending inbox
   * entry when THIS session is authenticated as TechSupport (an ordinary user must never build
   * someone else's inbox). Writes to TechSupport-LOCAL Gun only (`techsupport-inbox/<key>`) —
   * never a `public/` path, so it does not replicate as a relay-held authority record. Idempotent
   * by questionKey: a duplicate ask overwrites the same row rather than creating a second one.
   */
  private async ingestSupportQuestionFromMailbox(payload: MailboxSupportQuestionPayload): Promise<void> {
    if (!this.currentUser?.id || !this.isTechSupportOperatorSession()) return;
    if (payload.kind !== 'support-question-v1' || !payload.questionKey) return;
    const gun = this.gunService.getGun();
    const existing = await new Promise<any>((resolve) => {
      const timer = setTimeout(() => resolve(null), 700);
      gun.get('techsupport-inbox').get(payload.questionKey).once((data: any) => {
        clearTimeout(timer);
        resolve(data);
      });
    });
    // A question already answered must not regress to pending on a re-ask/replay.
    if (existing?.status === 'answered') return;
    const entry: SupportInboxEntry = {
      questionKey: payload.questionKey,
      question: payload.question,
      askedBy: payload.askedBy,
      conversationId: payload.conversationId,
      askedAt: payload.askedAt,
      status: 'pending',
    };
    gun.get('techsupport-inbox').get(payload.questionKey).put(entry);
    // The operator answers in the ordinary DM overlay, which needs the conversation record; since
    // TechSupport is Contacts-only it never meets the asker through a room member list.
    if (payload.askedBy && payload.askedBy !== TECHSUPPORT_ROOT_USER_ID) this.ensureSupportConversationRecord(payload.askedBy);
    console.log('[Support] Ingested pending question', payload.questionKey, 'from', payload.askedBy);
  }

  /**
   * docs/TODO.md K5, design note §Item 4. Live view of TechSupport-LOCAL Gun
   * (`techsupport-inbox/*`) for the support-inbox UI — only subscribed when this session is
   * authenticated as TechSupport itself; an ordinary user has nothing to read here (K1's
   * relay-light presence invariant: the relay/other sessions hold no support data).
   */
  private subscribeToSupportInboxIfTechSupport(): void {
    if (!this.currentUser || !this.isTechSupportOperatorSession()) return;
    if (this.supportInboxSubscribed) return;
    this.supportInboxSubscribed = true;
    const gun = this.gunService.getGun();
    gun
      .get('techsupport-inbox')
      .map()
      .on((data: any, questionKey: string) => {
        if (!questionKey || questionKey.startsWith('_')) return;
        if (!data || typeof data !== 'object') {
          this.supportInboxRows.delete(questionKey);
        } else {
          this.supportInboxRows.set(questionKey, { ...data, questionKey } as SupportInboxEntry);
        }
        this.renderSupportInbox();
        void this.refreshSupportAnsweredKeys();
      });
  }

  /**
   * docs/TODO.md K7: with fan-out delivery, more than one device can hold the same pending row
   * (master + every delegate each got their own envelope). No cross-device "claim" lock —
   * whichever device answers first publishes the signed per-entry record
   * (`techsupport-faq-entries/<key>`); treat that as "someone already answered this" and hide the
   * row rather than inviting a redundant answer.
   */
  private renderSupportInbox(): void {
    const visible = Array.from(this.supportInboxRows.values()).filter(
      (entry) => entry.status !== 'pending' || !this.supportAnsweredKeys.has(entry.questionKey),
    );
    this.uiManager.updateSupportInboxEntries(visible);
  }

  /**
   * docs/TODO.md OPEN-31: one targeted per-key read for each pending row (the old code diffed
   * against a locally cached whole bundle). Re-run on every inbox change and every relay-poll tick,
   * because a competing delegate's answer arrives over the relay, not through this inbox.
   */
  private async refreshSupportAnsweredKeys(): Promise<void> {
    if (this.supportAnsweredCheckInFlight) return;
    const apiBase = this.getBackendApiBase();
    if (!apiBase) return;
    const pending = Array.from(this.supportInboxRows.values()).filter(
      (entry) => entry.status === 'pending' && !this.supportAnsweredKeys.has(entry.questionKey),
    );
    if (pending.length === 0) return;
    this.supportAnsweredCheckInFlight = true;
    try {
      let changed = false;
      for (const row of pending) {
        const found = await fetchFaqEntryFromServer(apiBase, this.gunService.getGun(), row.questionKey);
        if (found) {
          this.supportAnsweredKeys.add(row.questionKey);
          changed = true;
        }
      }
      if (changed) this.renderSupportInbox();
    } catch {
      /* best-effort — the row simply stays visible */
    } finally {
      this.supportAnsweredCheckInFlight = false;
    }
  }

  /**
   * docs/TODO.md K5, design note §Item 5. Runs in a session authenticated as TechSupport itself
   * (holds the DM pair via K3) OR — docs/TODO.md K7 — an opted-in delegate holding a currently-
   * valid grant. One action does all four steps: sign + publish the updated FAQ bundle, deliver
   * the answer to the asker, and flip the inbox entry to answered.
   */
  private async handleAnswerSupportQuestion(input: {
    questionKey: string;
    question: string;
    answer: string;
    conversationId: string;
    askedBy: string;
  }): Promise<void> {
    if (!this.currentUser) return;
    const isMaster = this.currentUser.id === TECHSUPPORT_ROOT_USER_ID;
    const pair = this.gunService.getStoredPair();
    if (!pair?.priv || !pair.pub) {
      console.warn('[Support] Cannot answer — no keypair available on this session');
      return;
    }
    if (!isMaster) {
      // K7: re-verify live rather than trusting `this.techSupportDelegateGrant` — a stale
      // cache must never authorize an actual answer (design note "not trusted from a stale
      // cache"). Opt-in alone (without a still-valid grant) is also not enough.
      if (!this.techSupportDelegateOptedIn) return;
      const grant = await this.fetchOwnGrantLiveOrRelay(pair.pub);
      if (!grant) {
        console.warn('[Support] Cannot answer — delegate grant is missing, expired, or revoked');
        return;
      }
    }

    // The operator may have edited the question text (privacy — never publish the asker's raw
    // wording verbatim if it carries personal detail); the PUBLISHED entry is keyed on the
    // edited text, not the original asked text. The original questionKey is kept only for
    // flipping the right inbox row below.
    const entry = buildSupportFaqEntry({
      question: input.question,
      answer: input.answer,
      ...(isMaster ? {} : { answeredByDelegate: pair.pub }),
    });
    if (!entry) return;

    const gun = this.gunService.getGun();
    // docs/TODO.md OPEN-31: sign and publish exactly this ONE entry (O(1)) — no read-modify-write
    // of a whole-history bundle. The record goes to the hub's durable support store via HTTP: a
    // native (embedded-node) device has no generic Gun peering to the hub, and the relay is also
    // where every other delegate/asker reads it from. Awaited (not fire-and-forget) so a failed
    // publish is reported instead of silently telling the asker an unpublished answer.
    const signedEntry = await signFaqEntry(entry, pair as import('../sea-gun').GunPair & { pub: string; priv: string });
    const apiBaseForFaq = this.getBackendApiBase();
    if (apiBaseForFaq) {
      try {
        const res = await fetch(`${apiBaseForFaq}/api/support/faq-entries`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(signedEntry),
        });
        if (!res.ok) console.warn('[Support] FAQ entry publish rejected:', res.status);
      } catch (err) {
        console.warn('[Support] FAQ entry publish failed — the answer is still delivered to the asker', err);
      }
    }
    await applyRawFaqEntry(gun, signedEntry);
    this.supportAnsweredKeys.add(input.questionKey);
    this.supportAnsweredKeys.add(entry.questionKey);
    const faqEntryJson = JSON.stringify(signedEntry);

    await this.conversationService.sendMessage(
      input.conversationId,
      TECHSUPPORT_ROOT_USER_ID,
      input.answer,
      {
        otherUserId: input.askedBy,
        messageId: supportHumanAnswerMessageId(input.questionKey),
        isFromChatbot: false,
      },
    );
    if (!isMaster) {
      // K7: a delegate is authenticated as their OWN account, not TechSupport's — unlike the
      // master's case (a genuinely-authenticated TechSupport session needs no extra proof), the
      // asker's client must independently verify this delivered answer, so it needs the same
      // signed provenance the auto-answer path attaches (`filterVerifiedSupportMessages`'s
      // `isFaqAnswer` branch already checks exactly these fields against the delegate-aware
      // `verifyFaqBundle`). Belt pattern matching `handleSupportQuestion`'s hit branch: merge
      // onto the message node after the transport send, rather than threading new fields through
      // `SendMessageOptions`.
      try {
        gun
          .get(`conversations/${input.conversationId}`)
          .get('messages')
          .get(supportHumanAnswerMessageId(input.questionKey))
          .put({
            faqQuestionKey: entry.questionKey,
            faqAuthorPub: signedEntry.authorPub,
            faqSignature: signedEntry.signature,
            faqEntryJson,
          });
      } catch {
        /* local Gun write failure is non-fatal — the message text itself already delivered */
      }
    }

    gun.get('techsupport-inbox').get(input.questionKey).put({ status: 'answered' });
    if (this.currentUser.id === TECHSUPPORT_ROOT_USER_ID) this.refreshDelegateAdminPanel();
    console.log('[Support] Answered and published', input.questionKey);
  }

  /**
   * R-a step 7: mailbox fallback for talk-body delivery when DataChannel overlay cannot
   * guarantee full room coverage. Posts an encrypted talk-body envelope per unreachable
   * recipient. Recipients drain the mailbox on reconnect → flows through the same
   * intake-filtered `handleMeshTalkBody` accept path.
   * Best-effort: failures are logged but do not propagate.
   */
  private async postTalkBodyToMailboxForRecipients(
    payload: import('../../shared/p2p-mesh-protocol').P2PMeshTalkBodyPayload,
    recipientUserIds: string[],
  ): Promise<void> {
    if (this.mailboxFallbackDisabledForE2e) return;
    if (!this.currentUser?.id || recipientUserIds.length === 0) return;
    const mailbox = this.ensureMailboxClient();
    const pair = this.gunService.getStoredPair();
    if (!pair?.priv) return;
    const recipients = [...new Set(recipientUserIds)].filter(
      (recipientId) => recipientId && recipientId !== this.currentUser?.id,
    );
    // The body mailbox post is the delivery backstop when the mesh overlay can't reach a
    // recipient. Under saturation (20-browser M4) a single epub read or POST can transiently
    // fail; unlike talk *responses* there was no retry, so that recipient was permanently
    // skipped and never received the talk. Retry per recipient with backoff. The envelope id is
    // deterministic, so re-posting is idempotent (server dedupes; receiver dedupes on drain).
    // This whole method is invoked fire-and-forget from the mesh fallback, so the retries never
    // delay the broadcast's own completion. The budget must survive a saturation boot storm:
    // with ~12 browsers starting at once (mass/ specs, busy real deployments), a receiver can
    // take 20-30s to publish its SEA keys, and the old ~8s budget (6 attempts) gave up forever
    // — receivers permanently missed the talk. ~45s of capped backoff rides out the storm.
    const MAX_POST_ATTEMPTS = 12;
    const concurrency = 5;
    for (let i = 0; i < recipients.length; i += concurrency) {
      await Promise.all(recipients.slice(i, i + concurrency).map(async (recipientId) => {
        for (let attempt = 0; attempt < MAX_POST_ATTEMPTS; attempt += 1) {
          try {
            const recipientEpub = await this.resolvePeerEpub(recipientId);
            if (recipientEpub) {
              const ciphertext = await mailbox.encryptForRecipient(
                recipientEpub,
                pair as import('../sea-gun').GunPair,
                payload,
              );
              const envelopeId = `mbx_body_${payload.talkId}_${this.currentUser?.id}_${recipientId}`;
              const result = await mailbox.postEnvelope({
                id: envelopeId,
                recipientId,
                ciphertext,
              });
              if (result.stored) {
                console.log('[Mesh/Mailbox] Posted talk-body envelope for', recipientId);
                return;
              }
              console.warn('[Mesh/Mailbox] Server rejected talk-body envelope for', recipientId, ':', result.error);
            } else {
              console.warn('[Mesh/Mailbox] epub not found for', recipientId, `(attempt ${attempt + 1})`);
            }
          } catch (err) {
            console.warn('[Mesh/Mailbox] post talk-body failed for', recipientId, `(attempt ${attempt + 1}):`, err);
          }
          if (attempt < MAX_POST_ATTEMPTS - 1) {
            await new Promise((resolve) => setTimeout(resolve, Math.min(5_000, 400 + attempt * 600)));
          }
        }
        console.warn('[Mesh/Mailbox] gave up posting talk-body for', recipientId, `after ${MAX_POST_ATTEMPTS} attempts`);
      }));
    }
  }

  // ─── localStorage fallback queue (POST-to-mailbox failure only) ──────────
  //
  // The STEP-6-REPLACE localStorage queue is now narrowed to a "mailbox POST
  // itself failed" buffer only — it retries on the next roster-change drain
  // via retryFailedMailboxPosts.  This covers transient network errors during the
  // mailbox POST without requiring a new server endpoint or Gun path.

  private static readonly PENDING_MESH_RESPONSES_KEY = 'pendingMeshTalkResponses';

  private enqueueFailedMailboxPost(payload: P2PMeshTalkResponsePayload): void {
    try {
      const raw = appStorage.getItem(IinPublicApp.PENDING_MESH_RESPONSES_KEY);
      const queue: Record<string, P2PMeshTalkResponsePayload> = raw ? JSON.parse(raw) : {};
      const queueKey = `${payload.talkId}::${payload.authorId}::${payload.responseId}`;
      queue[queueKey] = payload;
      appStorage.setItem(IinPublicApp.PENDING_MESH_RESPONSES_KEY, JSON.stringify(queue));
      console.log('[Mailbox] Queued failed-POST response for author', payload.authorId, 'key:', queueKey);
    } catch (err) {
      console.warn('[Mailbox] Failed to enqueue failed-POST response:', err);
    }
  }

  /** Retry pending mailbox POSTs on roster change (transient network error recovery). */
  private async retryFailedMailboxPosts(): Promise<void> {
    let queue: Record<string, P2PMeshTalkResponsePayload>;
    try {
      const raw = appStorage.getItem(IinPublicApp.PENDING_MESH_RESPONSES_KEY);
      if (!raw) return;
      queue = JSON.parse(raw);
    } catch {
      return;
    }
    const succeeded: string[] = [];
    for (const [queueKey, payload] of Object.entries(queue)) {
      const posted = await this.postToMailbox(payload);
      if (posted) succeeded.push(queueKey);
    }
    if (succeeded.length > 0) {
      for (const k of succeeded) delete queue[k];
      try {
        appStorage.setItem(IinPublicApp.PENDING_MESH_RESPONSES_KEY, JSON.stringify(queue));
      } catch {/* ignore */}
    }
  }

  private async resolveMeshTalkData(talkId: string): Promise<any | null> {
    const cached = this.peerMeshService?.getCachedTalkBody(talkId);
    if (cached) return cached;
    try {
      const entry: any = getMyTalks()[talkId];
      if (entry?.fullTalk) return entry.fullTalk;
      if (entry) return entry;
    } catch {
      /* fallback below */
    }
    // 3 attempts x 150ms (450ms total) was far too short against real Gun read latency — every
    // finding tonight showed legitimate reads on real devices taking anywhere from ~800ms to
    // several seconds (see WebGunService.get()'s own 8s timeout, and the boot-path fixes this
    // session). A receiver opening a talk moments after it mesh-delivered would frequently lose
    // this race and surface "Could not load this talk yet" even though the data was genuinely
    // still in flight, not actually missing. 12 x 300ms (3.6s) gives real local Gun propagation
    // a realistic chance without hanging the UI indefinitely. The deadline keeps that budget now
    // that each local miss itself takes ~0.5 s (12 attempts alone stretched to ~11 s).
    return this.talkService.getTalkWithRetry(talkId, { attempts: 12, gapMs: 300, deadlineMs: 4_000 });
  }

  private async handleMeshTalkBody(
    payload: P2PMeshTalkBodyPayload,
    options: { suppressAutoReply?: boolean } = {},
  ): Promise<boolean> {
    if (!this.currentUser?.id || payload.authorId === this.currentUser.id) return false;
    const activeRoom = this.activeExchangeRoomController?.getActiveRoom();
    const currentRoomId = this.chatroomService.getCurrentChatroomId?.() || this.currentChatroomId;
    // The encrypted mailbox is pair-private but deliberately route-agnostic. Carry the room
    // boundary in the signed/encrypted Talk body too. Sender-side room switching never
    // auto-rebroadcasts pending Talks; a broadcast remains scoped to its named origin room.
    if (!roomDeliveryMatchesActiveRoom(payload, activeRoom, currentRoomId)) return false;
    // Mesh delivery deliberately re-floods the same talk-body frame to the same recipient for
    // reliability (broadcastTalk's flood-retry, plus a separate mailbox-fallback copy), so this
    // handler routinely runs several times for one logical delivery. Short-circuit once a
    // (talkId, authorId) pair has actually been ACCEPTED (see the `accepted` gate below) —
    // without this, every redundant re-delivery re-ran the full ingest+cache Gun-write path.
    // Measured under a 6-browser saturation test: ~7x redundant writes for the same 20 logical
    // deliveries, which cascaded into Gun's 12s put-ack "relaxed mode" timeout
    // (web-gun-service.ts) piling up and blew isolated-02-mixed-saturation.spec.ts's 60s budget.
    // Only marked on acceptance, not on entry: a REJECTED delivery (e.g. filtered by
    // talkFilters) keeps re-checking on each redundant copy, since eligibility could change
    // between flood attempts.
    // The content identity is part of the key: an author's in-place edit keeps the talkId, and
    // that revised body must be ingested (and offered to the chatbot) as a new talk.
    const pairKey = talkRevisionPairKey(payload.talkId, payload.authorId, payload.talkData);
    const bodyDedupeKey = `mesh-talk-body::${pairKey}`;
    if (this.processedTalkResponseKeys.has(bodyDedupeKey)) return true;
    if (payload.authorEpub) this.peerEpubByUserId.set(payload.authorId, payload.authorEpub);
    const talkData = {
      ...payload.talkData,
      id: payload.talkId,
      authorId: payload.authorId,
      authorName: payload.authorName,
      ...(payload.authorEpub ? { authorEpub: payload.authorEpub } : {}),
    };
    if (this.isTalkExpiredForDelivery(talkData)) return false;
    // R-f step 7: Gun talks/* mirror removed — receiver talk bodies are cached in
    // PeerMeshService.talkBodies (mesh body cache) and in the incoming-talk cluster
    // local store. The Gun mirror was the only remaining talks/* write on the receiver side.
    // Apply intake/age filtering BEFORE surfacing the talk in the UI. Mesh room
    // broadcasts reach every member, so age-gated/filtered talks must be rejected
    // here or they leak into the incoming-cluster UI (e.g. an adult talk shown to a
    // not-yet-age-verified receiver). Pass the current room as the delivery hint so
    // the receiver-side membership poll (flaky under Gun lag in e2e) is skipped.
    const accepted = await this.ingestIncomingTalkAnnouncement(
      payload.talkId,
      payload.authorId,
      payload.authorName || 'Unknown',
      talkData,
      this.currentChatroomId ? { deliveryChatroomId: this.currentChatroomId } : {},
    );
    if (!accepted) return false;
    this.processedTalkResponseKeys.add(bodyDedupeKey);
    this.peerMeshService?.cacheTalkBody(payload.talkId, talkData);
    // Delivery is ACK-eligible only after the receiver's local Gun commit and
    // repository read-back verification complete.
    await this.talkService.cacheReceivedTalk(payload.talkId, talkData);
    // OPEN-45: same content already answered from this author → refresh that copy in place.
    const silentUpdate = applyTalkRevisionToAnsweredCopy(talkData, payload.authorId);
    this.uiManager.displayIncomingTalk({
      silentUpdate,
      autoAnsweredByChatbot: !silentUpdate && this.chatbotWillAnswer(payload.talkId, talkData, payload.authorId),
      id: payload.talkId,
      title: String(payload.title || (talkData as any).title || 'Talk'),
      authorName: payload.authorName || 'Unknown',
      type: (talkData as any).type,
      questionCount: Array.isArray((talkData as any).questions) ? (talkData as any).questions.length : payload.questionCount,
      timestamp: new Date().toISOString(),
      isOwnTalk: false,
      fullTalk: talkData,
    });
    if (!options.suppressAutoReply) {
      this.maybeAutoChatbotReplyToAnnouncer(
        payload.talkId,
        talkData,
        payload.authorId,
        payload.authorName || 'Unknown',
      );
    }
    return true;
  }

  private async handleMeshTalkResponse(payload: P2PMeshTalkResponsePayload): Promise<void> {
    if (!this.currentUser?.id || payload.authorId !== this.currentUser.id) return;
    if (!talkContactPolicyAllowsPeer(
      payload.responderId,
      getTalkIntakeFilters(),
      this.currentUser.knownPeople,
      this.roomTravelerIds,
    )) return;
    console.log(`[RESP-INGEST] talk=${String(payload.talkId).slice(-8)} from=${String(payload.responderId).slice(0, 8)} v=${payload.version ?? 1}`);
    const dedupeKey = `mesh-response::${payload.talkId}::${payload.responseId}::v${payload.version ?? 1}`;
    if (this.processedTalkResponseKeys.has(dedupeKey)) return;
    const talkData = await this.resolveMeshTalkData(payload.talkId);
    if (!talkData) return;
    const decrypted = await this.decryptPairTalkResponsePayload(payload);

    // The decision is the core's (src/core/response-ingest.ts); this method carries it out.
    const decision = decideResponseIngest({
      talkData,
      answers: decrypted.answers,
      ledger: getTalkLedgerDoc(),
      identityKey: buildTalkIdentityKey(talkData),
      responderId: payload.responderId,
      authorId: this.currentUser.id,
      talkId: payload.talkId,
      responseId: payload.responseId,
      ...(payload.version !== undefined ? { version: payload.version } : {}),
      ...(payload.respondedAt ? { respondedAt: payload.respondedAt } : {}),
      submittedAt: payload.submittedAt,
      now: new Date().toISOString(),
    });
    if (decision.kind === 'reject') {
      console.debug(`[RESP-INGEST] discarded (${decision.reason}) from ${payload.responderId} v=${payload.version ?? 1}`);
      return;
    }
    const incomingVersion = decision.version;
    const incomingRespondedAt = decision.respondedAt;

    if (decision.kind !== 'first') {
      const isMatch = decision.isMatch;
      // Accepted: add to dedupeKey set, record exchange, and handle outcome flip.
      this.processedTalkResponseKeys.add(dedupeKey);
      this.recordLocalTalkExchange(
        payload.responderId,
        decrypted.responderName,
        payload.talkId,
        talkData,
        isMatch ? 'match' : 'mismatch',
        {
          responseId: payload.responseId,
          version: incomingVersion,
          respondedAt: incomingRespondedAt,
          answers: decrypted.answers,
        },
      );

      const changedAt = incomingRespondedAt;

      if (decision.kind === 'now-match') {
        // ignore → match: create conversation. No auto-disable here — matches aren't
        // exclusive; the talk only disables once a Deal is mutually confirmed (see the
        // deal-confirmation flow).
        this.uiManager.showNotification(
          `${decrypted.responderName} changed their answer — now a match · ${new Date(changedAt).toLocaleTimeString()}`,
          'success',
        );
        const conversationId = await this.conversationService.createConversation({
          userId1: this.currentUser.id,
          userName1: this.currentUser.stageName,
          userId2: payload.responderId,
          userName2: decrypted.responderName,
          talkId: payload.talkId,
          respondedByBotForUser1: !!decrypted.isChatbotResponse,
          respondedByBotForUser2: false,
          dealEligible: isDealEligibleTalk(talkData),
          ...matchScoreParamsFor(talkData, decrypted.answers),
        });
        this.recordMyDealTalkForConversation(conversationId, payload.talkId, talkData);
        this.recordConversationNeedKey(conversationId, talkData);
        this.uiManager.maybeShowMatchSafetyToast();
        this.uiManager.addNewConversation({
          conversationId,
          otherUserId: payload.responderId,
          otherUserName: decrypted.responderName,
          talkId: payload.talkId,
          respondedByBot: !!decrypted.isChatbotResponse,
          transportMode: this.conversationService.getTransportMode(),
          changeOfMindAt: changedAt,
          dealEligible: isDealEligibleTalk(talkData),
          ...matchScoreParamsFor(talkData, decrypted.answers),
        });
        await this.autoShareMatchedTalkAttachments({
          conversationId,
          talkId: payload.talkId,
          authorId: this.currentUser.id,
          responderId: payload.responderId,
          responderName: decrypted.responderName,
          talkData,
        });
        this.uiManager.setMemberMatched(payload.responderId);
      } else if (decision.kind === 'no-longer-match') {
        // match → ignore: mark conversation ended with status 'ignored'
        this.uiManager.showNotification(
          `${decrypted.responderName} changed their answer — no longer a match · ${new Date(changedAt).toLocaleTimeString()}`,
          'info',
        );
        this.uiManager.markConversationEnded(
          payload.responderId,
          payload.talkId,
          changedAt,
        );
      } else {
        // Outcome unchanged (both match or both not-match) — version bumped but no flip
        console.debug(`[Step9] Version bump from ${payload.responderId}: v${decision.priorVersion}→v${incomingVersion}, outcome unchanged`);
      }
      return;
    }

    // No prior entry — first receipt (original flow from step 8)
    this.processedTalkResponseKeys.add(dedupeKey);
    const isMatch = decision.isMatch;
    // R-2: pass responseId/version/respondedAt for forward-compat (steps 8–11)
    this.recordLocalTalkExchange(
      payload.responderId,
      decrypted.responderName,
      payload.talkId,
      talkData,
      isMatch ? 'match' : 'mismatch',
      {
        responseId: payload.responseId,
        version: incomingVersion,
        respondedAt: incomingRespondedAt,
        answers: decrypted.answers,
      },
    );
    if (!isMatch) return;
    // No auto-disable here — matches aren't exclusive; multiple candidates can all form
    // conversations for the same talk. The talk only disables once a Deal is mutually
    // confirmed (see the deal-confirmation flow).
    const conversationId = await this.conversationService.createConversation({
      userId1: this.currentUser.id,
      userName1: this.currentUser.stageName,
      userId2: payload.responderId,
      userName2: decrypted.responderName,
      talkId: payload.talkId,
      respondedByBotForUser1: !!decrypted.isChatbotResponse,
      respondedByBotForUser2: false,
      dealEligible: isDealEligibleTalk(talkData),
      ...matchScoreParamsFor(talkData, decrypted.answers),
    });
    this.recordMyDealTalkForConversation(conversationId, payload.talkId, talkData);
    this.recordConversationNeedKey(conversationId, talkData);
    this.uiManager.maybeShowMatchSafetyToast();
    this.uiManager.addNewConversation({
      conversationId,
      otherUserId: payload.responderId,
      otherUserName: decrypted.responderName,
      talkId: payload.talkId,
      respondedByBot: !!decrypted.isChatbotResponse,
      transportMode: this.conversationService.getTransportMode(),
      dealEligible: isDealEligibleTalk(talkData),
      ...matchScoreParamsFor(talkData, decrypted.answers),
    });
    // Match! toast after the conversation exists so clicking it navigates there (rule N6).
    this.uiManager.showNotification(
      this.uiManager.formatTalkMatched(decrypted.responderName, talkData.title),
      'success',
      { conversationId },
    );
    await this.autoShareMatchedTalkAttachments({
      conversationId,
      talkId: payload.talkId,
      authorId: this.currentUser.id,
      responderId: payload.responderId,
      responderName: decrypted.responderName,
      talkData,
    });
    this.uiManager.setMemberMatched(payload.responderId);
  }

  /**
   * Local-only, conversationId-keyed record of which of MY OWN deal-eligible talks a given
   * conversation actually formed/updated through — written synchronously, on this device only,
   * at the exact moment `handleMeshTalkResponse` (as the talk's AUTHOR) turns an incoming answer
   * into a match and creates/refreshes the conversation for it. Deliberately NOT derived from the
   * conversation record's `talkId`/`relatedTalkIds`: those are recomputed by
   * `WebConversationService.createConversation` from a fresh read of the SHARED Gun conversation
   * node, and bidirectional exchange means both participants can call `createConversation` for
   * the same pair within moments of each other — whichever call's Gun write lands last silently
   * overwrites the other's contribution, so `relatedTalkIds` can end up missing my own talkId
   * entirely even on my own device. This map has no such race: it's a plain synchronous
   * localStorage write made right where `payload.talkId` is already known to be MY OWN talk.
   *
   * docs/TODO.md §JJ residual gap, closed: also called from `submitTalkResponsePairDirect` (as
   * the talk's RESPONDER, including via the chatbot's auto-reply), where `talkId` is instead
   * resolved indirectly via `resolveResponderSourceTalkIdForAnswers` — chatbot memory taught by
   * self-answering that specific one of my own talks — rather than being already known the way
   * the author-side call sites above know it.
   */
  private recordMyDealTalkForConversation(conversationId: string, talkId: string, talkData: any): void {
    if (!conversationId || !talkId || !isDealEligibleTalk(talkData)) return;
    // Kept in the device core (OPEN-91 P3). When it is missing, maybeFinalizeConfirmedDeal falls
    // back to disabling every outstanding deal-eligible talk.
    dealTalkByConversationView.set({ [conversationId]: talkId });
  }

  private myDealTalkForConversation(conversationId: string): string | undefined {
    const talkId = dealTalkByConversationView.get(conversationId);
    return typeof talkId === 'string' && talkId ? talkId : undefined;
  }

  /**
   * Records the content-hash "need" a deal-eligible conversation formed through
   * (`buildTalkIdentityKey`, the same content-hash already used elsewhere for talk dedup) —
   * local-only, keyed by conversationId, mirroring `recordMyDealTalkForConversation` above.
   *
   * This is what lets `maybeFinalizeConfirmedDeal` group conversations across DIFFERENT
   * authors' talkIds (docs/TODO.md §JJ "known gap": two drivers with independently-authored but
   * identically-worded listings both reach the same passenger — my conversation with each has a
   * different `talkId`, but the SAME content-hash, since `buildTalkIdentityKey` normalizes and
   * hashes question/answer text, not identity). Recorded for every deal-eligible conversation
   * regardless of which side of the match I was on (author or responder), since the "need" this
   * describes is the matched CONTENT, not who authored it.
   */
  private recordConversationNeedKey(conversationId: string, talkData: any): void {
    if (!conversationId || !isDealEligibleTalk(talkData)) return;
    // Kept in the device core (OPEN-91 P3); when missing, maybeFinalizeConfirmedDeal has nothing
    // to group by, the same fallback as the sibling deal-talk map.
    needKeyByConversationView.set({ [conversationId]: buildTalkIdentityKey(talkData) });
  }

  /** Every conversationId → needKey pair recorded so far (see recordConversationNeedKey). */
  private allConversationNeedKeys(): Record<string, string> {
    return needKeyByConversationView.all();
  }

  /**
   * Runs once a conversation's `dealConfirmedBy` includes BOTH participants — called both from
   * the local `confirmDeal` click handler and (idempotently) whenever a synced conversation
   * record shows the same thing, since "both confirmed" can become true on EITHER participant's
   * device depending on who clicks second, and each side only has the context to run its own
   * half of the finalization (only the talk's own author has a talk to disable).
   *
   * Narrows to the specific talk of mine `recordMyDealTalkForConversation` recorded for this
   * conversationId, so confirming one deal leaves my OTHER simultaneous listings untouched.
   * Falls back to disabling ALL of my outstanding created deal-eligible talks only when this
   * conversation never went through that recording at all — the case where my own broadcast was
   * never the one that produced this specific match (see that method's doc comment), so there is
   * no more specific candidate to narrow to.
   *
   * Landed 2026-09-14 (docs/TODO.md §JJ "known gap"): once my own talk(s) are disabled above,
   * also mark OTHER of my open conversations as "no longer available" — LOCAL-only, on THIS
   * device (cross-device notification to those other candidates' own devices isn't wired yet,
   * a separate, still-real gap). Two grouping rules, since "other candidates" can come from
   * either side of a match:
   *   1. Same talkId (`markOtherDealConversationsEnded`) — several responders matched the ONE
   *      talk I authored; disabling it above doesn't retroactively flag their already-open
   *      conversations.
   *   2. Same content-hash "need" (`markConversationsSupersededByIds`, `recordConversationNeedKey`)
   *      — the cross-author case (e.g. two drivers with independently-authored but identically-
   *      worded listings both reaching me): different talkIds, so rule 1 can't find them, but
   *      `buildTalkIdentityKey` hashes the matched CONTENT, which is identical either way.
   */
  /**
   * Conversations this app instance has observed as mutually confirmed. Keep the protection for
   * the rest of the session: disabling either participant's talk emits retractions over several
   * asynchronous paths (local cascade, mesh, and mailbox), so a temporary synchronous guard can
   * disappear before a delayed retraction arrives. The persisted conversation record remains the
   * cross-session source of truth; this set closes stale-record races within the active session.
   */
  private readonly mutuallyConfirmedConversationIds = new Set<string>();

  private maybeFinalizeConfirmedDeal(dealConfirmedBy: string[], otherUserId?: string, conversationId?: string): void {
    if (!this.currentUser?.id || !otherUserId) return;
    // The plan is the core's (src/core/deal-finalization.ts); this carries it out. A repeat call
    // (the click, then its sync echo) plans the same recorded Talk again and changes nothing more.
    const plan = planDealFinalization({
      myId: this.currentUser.id,
      otherUserId,
      dealConfirmedBy,
      ...(conversationId ? { conversationId } : {}),
      myTalks: getMyTalks(),
      ...(conversationId && this.myDealTalkForConversation(conversationId) ? { recordedTalkId: this.myDealTalkForConversation(conversationId)! } : {}),
      needKeys: this.allConversationNeedKeys(),
      isDealEligible: (talk) => isDealEligibleTalk(talk),
    });
    if (!plan) return;
    const talkIdsToDisable = plan.talkIdsToDisable;

    // `setTalkDisabled(talkId, true)` floods a hard-retraction event
    // (talk-broadcast-toggle.ts emits 'retractTalk'), which `handleRetractTalk` handles by
    // RE-READING `getMyConversations()` and re-deriving "is this conversation's deal already
    // mutually confirmed" from `conv.dealConfirmedByJson` — a fresh read of the exact
    // localStorage snapshot `applyDealConfirmedBy` just wrote, moments ago, in the same
    // synchronous call stack. That re-derivation is a real gap: an interleaved Gun-sync echo
    // for the SAME conversation record (e.g. an unrelated lastActivity heartbeat) landing
    // between that write and this read can transiently overwrite dealConfirmedByJson with a
    // stale value, silently withdrawing the very conversation just finalized — reproduced once
    // in real `test:all` runs under heavy concurrent load. Registering this conversationId as
    // mutually confirmed up front removes the dependency on that re-read for both the immediate
    // cascade and delayed mesh/mailbox retractions. The guard deliberately lasts for the session:
    // a finalized successful deal must never be retroactively invalidated by its source talk's
    // later retraction.
    if (conversationId) this.mutuallyConfirmedConversationIds.add(conversationId);
    for (const talkId of talkIdsToDisable) {
      this.uiManager.setTalkDisabled(talkId, true);
    }

    const changedAt = new Date().toISOString();
    // Rule 1: other responders who matched the SAME talk of mine.
    for (const talkId of talkIdsToDisable) {
      this.uiManager.markOtherDealConversationsEnded(talkId, otherUserId, changedAt);
    }
    // Rule 2: other of my conversations matched on the SAME content-hash "need", regardless of
    // which different author's talkId produced them.
    if (plan.supersededConversationIds.length > 0) {
      this.uiManager.markConversationsSupersededByIds(plan.supersededConversationIds, changedAt);
    }
  }

  /**
   * Step 10 (author side): hard retraction on delete or tag-uncheck.
   *
   * 1. Write local tombstone via applyEvent.
   * 2. Emit TALK_RETRACTED to the audit ledger.
   * 3. Flood the `talk-retracted` mesh frame to all connected neighbors.
   * 4. Mailbox-fanout to all known responders so offline holders are reached.
   * 5. Tear down the author's own conversation view (if matched).
   */
  private async handleRetractTalk(talkId: string, retractedAt: number): Promise<void> {
    if (!this.currentUser?.id) return;
    const authorId = this.currentUser.id;
    const talkData = await this.resolveMeshTalkData(talkId);
    const attachments = this.getTalkAttachmentsForShare(talkData);
    this.contentNodeService.unpinTalkAttachments(talkId);

    // 1. Write local tombstone (clears outcomes + exchanged for this talkId::authorId).
    await applyTalkLedgerEvent({
      kind: 'TALK_RETRACTED',
      talkId,
      authorId,
      retractedAt,
    });

    // 2. Emit audit ledger event.
    this.ledgerEmit(InteractionKind.TALK_RETRACTED, { talkId, retractedAt });

    // 3. Flood the mesh frame.
    const mesh = this.ensurePeerMeshService();
    if (mesh) {
      const retractionPayload: P2PMeshTalkRetractedPayload = { talkId, authorId, retractedAt };
      try {
        await mesh.sendTalkRetraction(retractionPayload);
      } catch (err) {
        console.warn('[Retraction] Mesh flood failed (non-fatal):', err);
      }
    }

    // 4. Mailbox fanout to known responders.
    // The ledger already cleared outcomes, so we read from a pre-retraction snapshot:
    // use the conversations store to enumerate responders.
    void this.postRetractionToKnownResponders(talkId, authorId, retractedAt).catch(() => {});

    // 5. Author side: tear down any conversations derived from this talkId.
    //
    // Skips a conversation whose deal is ALREADY mutually confirmed (§JJ): this same
    // retraction fires as a SIDE EFFECT of maybeFinalizeConfirmedDeal's fallback disabling
    // ALL of my active deal-eligible talks when I confirmed as a RESPONDER (no specific talk
    // was recorded to narrow to — see that method's doc comment). Reproduced directly: a
    // passenger who also authored her own "looking for a ride" request, confirming a deal
    // with one driver, would have THAT OWN request disabled by the fallback — and without this
    // guard, tearing it down here would immediately withdraw the very conversation she just
    // confirmed, undoing the deal on her own device seconds after finalizing it. A finalized,
    // mutually-confirmed match is a successful outcome, not an abandoned one; retracting the
    // broadcast that produced it must never retroactively invalidate it.
    const allConversations = this.uiManager.getMyConversations() as Record<string, any>;
    for (const [conversationId, conv] of Object.entries(allConversations)) {
      if (conversationReferencesTalk(conv, talkId)) {
        const otherUserId = String((conv as any).otherUserId || '');
        if (this.mutuallyConfirmedConversationIds.has(conversationId)) continue;
        if (isDealMutuallyConfirmed(conv, authorId, otherUserId)) continue;
        this.uiManager.markConversationWithdrawn(
          otherUserId,
          talkId,
          retractedAt,
        );
        if (conversationId && otherUserId && otherUserId !== authorId) {
          await this.markSharedAttachmentLinksDead({
            talkId,
            authorId,
            conversationPeerId: otherUserId,
            conversationId,
            attachments,
            retractedAt,
          });
        }
      }
    }
  }

  /**
   * Step 10 (responder side): handle an incoming `talk-retracted` mesh frame.
   *
   * 1. Write tombstone to local ledger.
   * 2. Surface "match gone" notice to UI.
   * 3. Mark any conversations for this talkId::authorId as withdrawn.
   */
  private async handleMeshTalkRetracted(payload: P2PMeshTalkRetractedPayload): Promise<void> {
    if (!this.currentUser?.id) return;
    // authorId check is already done in PeerMeshService.handleLocalFrame before firing this callback.

    // What it changes is the core's (src/core/retraction.ts); read before the tombstone is written.
    const receipt = planRetractionReceipt({
      ledger: getTalkLedgerDoc(),
      talkId: payload.talkId,
      authorId: payload.authorId,
      retractedAt: payload.retractedAt,
      myId: this.currentUser.id,
      conversations: this.uiManager.getMyConversations() as Record<string, any>,
      confirmedThisSession: this.mutuallyConfirmedConversationIds,
    });

    // 1. Write tombstone to local ledger.
    await applyTalkLedgerEvent({
      kind: 'TALK_RETRACTED',
      talkId: payload.talkId,
      authorId: payload.authorId,
      retractedAt: payload.retractedAt,
    });

    // 2. Surface each semantic retraction once. Mesh and mailbox delivery can replay the same
    // frame; the durable ledger timestamp is the idempotency boundary across reloads too.
    if (receipt.isNew) {
      this.uiManager.showNotification(
        `${payload.authorId} removed a talk — the match is gone · ${new Date(payload.retractedAt).toLocaleTimeString()}`,
        'info',
      );
      this.contentNodeService.unpinTalkAttachments(payload.talkId);
    }

    // 3. Mark any conversation involving this talkId as withdrawn — unless our own deal on it
    // is already mutually confirmed (see handleRetractTalk's matching guard/comment: a
    // finalized match is a successful outcome, never retroactively invalidated by the
    // retraction that produced it).
    if (receipt.withdraw) {
      this.uiManager.markConversationWithdrawn(payload.authorId, payload.talkId, payload.retractedAt);
    }
  }

  /**
   * Step 10: post encrypted retraction envelopes to known responders via the mailbox.
   * Reads the myConversations store for talkId matches — these are the peers who need
   * the tombstone but may be offline at the moment of retraction.
   */
  private async postRetractionToKnownResponders(
    talkId: string,
    authorId: string,
    retractedAt: number,
  ): Promise<void> {
    if (!this.currentUser?.id) return;
    const mailbox = this.ensureMailboxClient();
    const pair = this.gunService.getStoredPair();
    if (!pair?.priv) return;

    const responderIds = retractionRecipients(this.uiManager.getMyConversations() as Record<string, any>, talkId, authorId);
    if (responderIds.length === 0) return;

    const retractionPayload: P2PMeshTalkRetractedPayload = { talkId, authorId, retractedAt };
    for (const responderId of responderIds) {
      try {
        const responderEpub = await this.resolvePeerEpub(responderId);
        if (!responderEpub) continue;
        const ciphertext = await mailbox.encryptForRecipient(
          responderEpub,
          pair as import('../sea-gun').GunPair,
          retractionPayload,
        );
        const envelopeId = `mbx_retract_${talkId}_${authorId}_${responderId}`;
        await mailbox.postEnvelope({ id: envelopeId, recipientId: responderId, ciphertext });
        console.log('[Retraction] Posted mailbox envelope for responder', responderId);
      } catch (err) {
        console.warn('[Retraction] Failed to post mailbox envelope for', responderId, ':', err);
      }
    }
  }

  /**
   * Idempotent materialization of the deterministic TechSupport conversation record — the
   * one thing both the boot-time bootstrap below and a peer-detail-page "message TechSupport"
   * click (findOrCreateDirectConversation) need, and both need it to actually exist (not just
   * compute the id string) before showConversationDetail/sendMessage can use it. Pulled out so
   * a click that races ahead of ensureSupportBootstrapForCurrentUser's own boot-time call still
   * gets a real record instead of a "conversation not found" no-op or a fallback conv_pair_ id.
   * transportMode is always the literal 'star-gun' here (TechSupportConversationTransport.mode),
   * never this.conversationService.getTransportMode() — that reports the ORDINARY peer transport
   * ('direct-p2p'), which previously leaked into this record and showed as "Channel transport:
   * Direct P2P" on TechSupport's own peer-detail page.
   *
   * Symmetric by design: the conversation id must be identical on both sides regardless of who
   * calls this. An earlier version of this method always keyed the id off `this.currentUser.id`
   * — correct for an ordinary user opening a chat with TechSupport, but wrong when TechSupport's
   * OWN operator session calls it to reply to that user (peerId is the ordinary user's id here,
   * not TechSupport's). That mismatch used to leave the operator's `findOrCreateDirectConversation`
   * call falling through to the generic path, minting an unrelated conv_pair_ id on a different
   * transport (direct-p2p/WebRTC) — the operator's replies and the user's own messages ended up
   * in two disconnected conversations that neither side ever saw the other's side of. Deriving the
   * ordinary-side user id explicitly (from peerId when the caller IS TechSupport, from
   * currentUser.id otherwise) keeps the id — and therefore the transport, since
   * WebConversationService.isSupportConversation checks the id's conv_support_ prefix first —
   * identical on both sides.
   */
  private ensureSupportConversationRecord(peerId?: string, peerName?: string): string {
    if (!this.currentUser) throw new Error('Not logged in');
    const iAmTechSupport = this.currentUser.id === TECHSUPPORT_ROOT_USER_ID;
    const ordinaryUserId = iAmTechSupport ? (peerId || '') : this.currentUser.id;
    if (!ordinaryUserId) throw new Error('Missing peer id for TechSupport conversation');
    const conversationId = `conv_support_${TECHSUPPORT_ROOT_USER_ID}_${ordinaryUserId}`;
    const now = new Date().toISOString();
    const transportMode: ConversationTransportMode = 'star-gun';
    const gun = this.gunService.getGun();

    // Write the LOCAL contact record first, independent of the greeting below.
    // hasSupportContact()/Contacts read `myConversations` (via addNewConversation), so
    // the support contact must appear even if verification fails — the contact row is not
    // gated on the greeting (K1-3/K6: the support channel is a stuck user's only recourse).
    gun.get(`conversations/${conversationId}`).put({
      data: JSON.stringify({
        id: conversationId,
        participants: [TECHSUPPORT_ROOT_USER_ID, ordinaryUserId],
        createdAt: now,
        status: 'active',
        supportChannel: true,
      }),
    });
    gun.get(`users/${ordinaryUserId}`).get('conversations').get(conversationId).put({
      conversationId,
      otherUserId: TECHSUPPORT_ROOT_USER_ID,
      otherUserName: TECHSUPPORT_STAGE_NAME,
      createdAt: now,
      supportChannel: true,
      transportMode,
    });
    // My own local cache entry — from an ordinary user's perspective the other side is
    // TechSupport; from TechSupport's own operator session, the other side is this ordinary user.
    this.uiManager.addNewConversation({
      conversationId,
      otherUserId: iAmTechSupport ? ordinaryUserId : TECHSUPPORT_ROOT_USER_ID,
      otherUserName: iAmTechSupport ? (peerName || ordinaryUserId) : TECHSUPPORT_STAGE_NAME,
      supportChannel: true,
      transportMode,
    });
    return conversationId;
  }

  private async ensureSupportBootstrapForCurrentUser(): Promise<void> {
    if (!this.currentUser || this.supportBootstrapChecked || isTechSupportUser(this.currentUser)) return;
    // Idempotency no longer needs a localStorage gate: the greeting write below uses a
    // deterministic message id (a repeat call overwrites the same soul), so the in-memory
    // flag is a per-session no-op optimization, not persistence.
    this.supportBootstrapChecked = true;
    this.supportGreetingTimestamp = new Date().toISOString();
    await this.renderSupportGreeting({ notify: true });
  }

  /**
   * K2 (docs/TODO.md): render the pre-signed, per-locale welcome template — verify against the
   * compiled DM trust anchors BEFORE rendering. A client that cannot verify shows no greeting at
   * all (K2-3: silent suppression, never a fabricated/impersonated message). Also called after a
   * stage-name change so the welcome names the user as they now are (same message id, original
   * timestamp; the signature covers the template, not the rendered name).
   */
  private async renderSupportGreeting(options: { notify: boolean }): Promise<void> {
    if (!this.currentUser || isTechSupportUser(this.currentUser) || !this.supportGreetingTimestamp) return;
    const userId = this.currentUser.id;
    const conversationId = this.ensureSupportConversationRecord();
    const timestamp = this.supportGreetingTimestamp;
    const locale = getUiLanguagePreference(uiLanguageFromProfile(this.currentUser.languages)) as GreetingLocale;
    const bundle = techsupportGreetingBundle.greetings as SignedGreeting[];
    const entry = bundle.find((g) => g.locale === locale) ?? bundle.find((g) => g.locale === 'en');
    const verified = entry ? await verifyTechSupportGreeting(entry) : null;
    if (!verified) return;

    const rendered = renderGreeting(verified.template, this.currentUser.stageName);
    // Real message, persisted in the receiver's own local Gun only (K2-2) — never
    // transmitted. The greeting is authored by TechSupport, not "sent" by this device, so
    // this writes directly through the local-only primitive rather than sendMessage's
    // peer-notify path.
    await this.conversationService.upsertMessageRecord(
      conversationId,
      {
        id: `support_welcome_${userId}`,
        senderId: TECHSUPPORT_ROOT_USER_ID,
        text: rendered,
        timestamp,
        channel: 'public',
        transport: 'star-gun',
        greetingLocale: verified.locale,
        greetingSignature: verified.signature,
        greetingAuthorPub: verified.authorPub,
      },
      { otherUserId: userId },
    );
    // On a rename, only refresh the list preview while the greeting is still the latest message.
    const preview = (this.uiManager.getMyConversations() as Record<string, { lastMessage?: string }>)[conversationId]?.lastMessage;
    if (options.notify || !preview || preview === this.supportGreetingRendered) {
      this.uiManager.updateConversationMessage(conversationId, rendered, timestamp);
    }
    this.supportGreetingRendered = rendered;

    if (options.notify && !this.uiManager.isSupportNotificationsMuted()) {
      this.uiManager.showNotification(rendered, 'info');
    }

    // The verified greeting remains the one TechSupport-authored first contact. Product
    // education now lives in the local actionable guide and optional reference tour instead
    // of manufacturing five additional conversation messages for every new user. The signed
    // legacy tips bundle and verifier remain available for authenticating old stored messages.
  }

  /** docs/TODO.md OPEN-29: keeps the local recovery-anchor cache verified/fresh, the same way
   * the FAQ bundle and delegate-grant caches already do — a compromised-key incident needs this
   * device to learn about a revocation/next-anchor without waiting for a reload. */
  private subscribeToTechSupportRecoveryAnchor(): void {
    if (this.techSupportRecoveryAnchorUnsubscribe) return;
    // Seed instantly from the local monotonic cache (no Gun round trip needed) so the master's
    // own recovery banner (docs/TODO.md OPEN-29) doesn't wait on the live subscription below to
    // paint an incident that already happened in an earlier session. Routed through
    // refreshDelegateAdminPanel rather than a direct currentUser check here — this method runs
    // early in boot, often before `currentUser` itself has resolved, so a one-shot guarded push
    // right here can silently miss the only time Gun's `.on()` ever delivers an unchanged record.
    this.refreshDelegateAdminPanel();
    const gun = this.gunService.getGun();
    this.techSupportRecoveryAnchorUnsubscribe = subscribeToRecoveryAnchor(gun, (record) => {
      console.warn(
        `[TechSupport recovery] anchor updated: reason="${record.reason}" ` +
        `revokedDm=${record.revokedDmPubs.length} revokedAnnouncement=${record.revokedAnnouncementPubs.length} ` +
        `nextDm=${record.nextDmPub ?? 'unchanged'} nextAnnouncement=${record.nextAnnouncementPub ?? 'unchanged'}`,
      );
      // docs/TODO.md OPEN-29: surface this to the master operator, not just the console — a
      // compromised-key incident shouldn't depend on someone having devtools open.
      this.refreshDelegateAdminPanel();
      // A recovery update changes which authors verify as TechSupport — re-render so a
      // newly-trusted (or newly-untrusted) message reflects it without a reload.
      this.uiManager.rerenderOpenConversation();
    });
  }

  /**
   * docs/TODO.md K7, design note "Extending the trust check": keeps the local delegate-grant
   * cache verified/fresh for every session (master, delegates, and ordinary askers all need it —
   * an asker's client must be able to verify a delegate-signed FAQ bundle/answer, and the master
   * needs the live roster for its Delegates panel).
   */
  private subscribeToTechSupportDelegateGrants(): void {
    if (this.techSupportDelegateGrantsUnsubscribe) return;
    const gun = this.gunService.getGun();
    this.techSupportDelegateGrantsUnsubscribe = subscribeToDelegateGrants(gun, (grant) =>
      this.handleVerifiedDelegateGrant(grant),
    );
  }

  /** Shared by the live Gun subscription above and the HTTP relay poll below (K7 follow-on) —
   * either one learning about a grant update should have the exact same effect. */
  private handleVerifiedDelegateGrant(grant: TechSupportDelegateGrant): void {
    if (this.currentUser?.id === TECHSUPPORT_ROOT_USER_ID) {
      this.refreshDelegateAdminPanel();
      this.maybeWarnDelegateGrantExpiringSoon(grant, false);
      return;
    }
    // An ordinary user's OWN grant can arrive/change mid-session (issued or revoked while
    // they're already online) — re-derive eligibility immediately rather than requiring a
    // reload, matching the design note's "on boot (or on a slow poll)" allowance with the
    // stronger live version this subscription already gives every other session for free.
    const ownPub = this.gunService.getStoredPair()?.pub;
    if (ownPub && grant.delegatePub === ownPub) void this.checkOwnDelegateEligibility();
  }

  /**
   * docs/TODO.md K7 follow-on: `fetchGrantLive`'s whole point is "don't trust a stale cache,"
   * so it deliberately does a live Gun `.once()` read rather than consulting the cache the relay
   * poll populates. On a native (embedded-node) device that live Gun read has the exact same
   * "no generic peering to the hub" blind spot as everything else here — found the hard way via
   * a real-hardware test (09-android-techsupport-delegate-answers) where the relay poll had
   * already cached Honor's grant, yet the opt-in toggle still never appeared, because this
   * deliberately-live check ignored that cache. Falls back to the same explicit-HTTP relay,
   * still a live fetch (not the cache), just one that actually reaches a native device's session.
   */
  private async fetchOwnGrantLiveOrRelay(pub: string): Promise<TechSupportDelegateGrant | null> {
    const raw = await fetchGrantLive(this.gunService.getGun(), pub);
    const live = await verifyValidDelegateGrant(raw);
    if (live) return live;
    const grants = await fetchDelegateGrantsFromServer(this.getBackendApiBase());
    const relayed = grants.find((grant) => grant.delegatePub === pub) ?? null;
    return isValidDelegateGrant(relayed) ? relayed : null;
  }

  private techSupportRelayPollTimer: ReturnType<typeof setInterval> | undefined;
  private techSupportRelayPollInFlight = false;
  private techSupportTargetedInviteCheckedAt = 0;

  /**
   * docs/TODO.md K7 follow-on: a native (embedded-node) device has no generic Gun peering to the
   * hub (embedded-node.ts dials it "for discovery only"), so the two live Gun subscriptions above
   * never fire there — found via a real-hardware test (09-android-techsupport-delegate-answers)
   * where a real Honor phone's signed delegate request never reached the master at all. This
   * poll is the read-side half of the fix: every session (master, delegate, or ordinary asker;
   * browser or native) periodically pulls the delegate roster and FAQ bundle through the new
   * explicit-HTTP relay routes and feeds them through the exact same verify+cache+react path a
   * live Gun push would. Cheap and infrequent enough not to matter next to this app's existing
   * 10-30s presence heartbeats.
   */
  private startTechSupportDelegateRelayPolling(): void {
    if (this.techSupportRelayPollTimer) return;
    const tick = async (): Promise<void> => {
      if (this.techSupportRelayPollInFlight) return;
      this.techSupportRelayPollInFlight = true;
      try {
        const apiBase = this.getBackendApiBase();
        if (!apiBase) return;
        const grants = await fetchDelegateGrantsFromServer(apiBase);
        for (const grant of grants) this.handleVerifiedDelegateGrant(grant);
        // Targeted (remote) delegate invites: candidates look for one addressed to them, the
        // master looks for the resulting requests. Slower cadence (~30s) — invites last days.
        if (Date.now() - this.techSupportTargetedInviteCheckedAt >= 30_000) {
          this.techSupportTargetedInviteCheckedAt = Date.now();
          await this.checkIncomingTargetedDelegateInvite(apiBase);
          await this.pollTargetedDelegateInviteRequests(apiBase);
        }
        // docs/TODO.md OPEN-31: no whole-FAQ poll any more — an asker's answer message carries its
        // own signed record. Only operator sessions have FAQ state to refresh: hide pending rows a
        // competing delegate already answered, and (master) refresh the "Delegate activity" audit
        // list, which is a capped server listing so it runs on a slower cadence (~10s, master only).
        if (this.currentUser && this.isTechSupportOperatorSession()) {
          void this.refreshSupportAnsweredKeys();
          if (this.currentUser.id === TECHSUPPORT_ROOT_USER_ID && this.techSupportAdminPanelTick++ % 2 === 0) {
            this.refreshDelegateAdminPanel();
          }
        }
        // docs/TODO.md OPEN-29: same native-device gap, one layer up — a recovery anchor is
        // arguably the most urgent thing a stuck native client needs to learn during an incident.
        await fetchRecoveryAnchorFromServer(apiBase);
      } catch {
        /* best-effort — the live Gun subscriptions above still cover browser-to-browser sync */
      } finally {
        this.techSupportRelayPollInFlight = false;
      }
    };
    void tick();
    // Delegate grants and the recovery anchor change only during an incident (OPEN-73): an
    // ordinary session reads them every 15 s; operators, who answer and audit, every 5 s.
    this.techSupportRelayPollTimer = setInterval(throttleWhileHidden(() => void tick(), 120_000, {
      visibleMs: () => (this.currentUser && this.isTechSupportOperatorSession() ? 0 : 15_000),
    }), 5_000);
  }

  private stopTechSupportDelegateRelayPolling(): void {
    if (this.techSupportRelayPollTimer) clearInterval(this.techSupportRelayPollTimer);
    this.techSupportRelayPollTimer = undefined;
  }

  private static readonly DELEGATE_EXPIRY_WARNING_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

  /**
   * K7 follow-on (lifecycle): warns once per grant per session when it's within 7 days of expiry
   * and not revoked — `delegateExpiryWarned` keeps a re-render or the next live grant update from
   * re-firing the same warning. `forSelf` picks the delegate's own first-person message vs. the
   * master's per-delegate roster message.
   */
  private maybeWarnDelegateGrantExpiringSoon(grant: TechSupportDelegateGrant, forSelf: boolean): void {
    if (grant.revokedAt) return;
    const remaining = new Date(grant.expiresAt).getTime() - Date.now();
    if (remaining <= 0 || remaining > IinPublicApp.DELEGATE_EXPIRY_WARNING_WINDOW_MS) return;
    if (this.delegateExpiryWarned.has(grant.delegatePub)) return;
    this.delegateExpiryWarned.add(grant.delegatePub);
    const days = Math.max(1, Math.ceil(remaining / (24 * 60 * 60 * 1000)));
    const message = forSelf
      ? this.uiManager.formatDelegateGrantExpiringSoonSelf(days)
      : this.uiManager.formatDelegateGrantExpiringSoonOther(grant.label || grant.delegateUserId, days);
    this.uiManager.showNotification(message, 'warning', { persistent: true });
  }

  /**
   * docs/TODO.md K7: true for the master session (holds the DM pair, identity IS the root id) or
   * an ordinary user who has both a currently-valid grant for their OWN pub AND has explicitly
   * opted in (holding a grant never silently activates delegate mode — the person must accept).
   */
  private isTechSupportOperatorSession(): boolean {
    if (this.currentUser?.id === TECHSUPPORT_ROOT_USER_ID) return true;
    return this.techSupportDelegateOptedIn && isValidDelegateGrant(this.techSupportDelegateGrant);
  }

  private loadDelegateOptInFromStorage(): boolean {
    try {
      return settingsStorage.getItem(IinPublicApp.DELEGATE_OPT_IN_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private persistDelegateOptIn(optedIn: boolean): void {
    try {
      if (optedIn) settingsStorage.setItem(IinPublicApp.DELEGATE_OPT_IN_STORAGE_KEY, '1');
      else settingsStorage.removeItem(IinPublicApp.DELEGATE_OPT_IN_STORAGE_KEY);
    } catch {
      /* best-effort persistence only */
    }
  }

  /**
   * docs/TODO.md K7: an ordinary (non-master) user checks, on boot, whether the master has issued
   * THEM a currently-valid grant — a **live** Gun read (not the local cache), since this decides
   * whether to unlock a capability, not merely how to render a message. Holding a valid grant only
   * ever surfaces an opt-in prompt; it never silently enables delegate mode.
   */
  private async checkOwnDelegateEligibility(): Promise<void> {
    if (!this.currentUser || this.currentUser.id === TECHSUPPORT_ROOT_USER_ID) return;
    const pair = this.gunService.getStoredPair();
    if (!pair?.pub) return;
    const grant = await this.fetchOwnGrantLiveOrRelay(pair.pub);
    this.techSupportDelegateGrant = grant;
    this.techSupportDelegateOptedIn = !!grant && this.loadDelegateOptInFromStorage();
    this.uiManager.setTechSupportDelegateEligibility(!!grant, grant?.label || '', this.techSupportDelegateOptedIn);
    if (grant) this.maybeWarnDelegateGrantExpiringSoon(grant, true);
    if (this.isTechSupportOperatorSession()) this.subscribeToSupportInboxIfTechSupport();
  }

  /** docs/TODO.md K7: the Me-tab opt-in toggle — never auto-enabled by merely holding a grant. */
  private async handleToggleTechSupportDelegateOptIn(nextOptedIn: boolean): Promise<void> {
    if (nextOptedIn) {
      // Re-verify live rather than trusting the boot-time check — the grant could have been
      // revoked since (design note's "re-verified at answer time, not a stale cache" discipline).
      const pair = this.gunService.getStoredPair();
      const grant = pair?.pub ? await this.fetchOwnGrantLiveOrRelay(pair.pub) : null;
      if (!grant) {
        this.techSupportDelegateGrant = null;
        this.techSupportDelegateOptedIn = false;
        this.uiManager.setTechSupportDelegateEligibility(false, '', false);
        return;
      }
      this.techSupportDelegateGrant = grant;
    }
    this.techSupportDelegateOptedIn = nextOptedIn;
    this.persistDelegateOptIn(nextOptedIn);
    this.uiManager.setTechSupportDelegateEligibility(!!this.techSupportDelegateGrant, this.techSupportDelegateGrant?.label || '', nextOptedIn);
    if (nextOptedIn) this.subscribeToSupportInboxIfTechSupport();
  }

  /** docs/TODO.md K7: master-only — issue or re-issue a grant for the given delegate pub. */
  private async handleIssueTechSupportDelegate(input: { delegateUserId: string; label: string; ttlDays: number }): Promise<void> {
    if (this.currentUser?.id !== TECHSUPPORT_ROOT_USER_ID) return;
    const pair = this.gunService.getStoredPair();
    const delegateUserId = input.delegateUserId.trim();
    if (!pair?.priv || !delegateUserId) return;
    // Approving a pending request: its signed candidatePub is already in hand, so skip the public
    // user lookup (a network read that ran past the panel's refresh window under load).
    const delegatePub = this.delegateRequestsCache.find((r) => r.candidateUserId === delegateUserId)?.candidatePub
      || (await this.gunService.getPublicUser(delegateUserId))?.pub;
    if (!delegatePub) {
      console.warn('[Support] Cannot issue delegate grant — no such user id', delegateUserId);
      return;
    }
    const ttlMs = Math.min(Math.max(input.ttlDays, 1) * 24 * 60 * 60 * 1000, DELEGATE_GRANT_MAX_TTL_MS) || DELEGATE_GRANT_DEFAULT_TTL_MS;
    const grant = await signDelegateGrant(
      { delegatePub, delegateUserId, label: input.label, expiresAt: new Date(Date.now() + ttlMs).toISOString() },
      pair as import('../sea-gun').GunPair & { pub: string; priv: string },
    );
    this.writeDelegateGrant(grant);
    this.clearMatchingDelegateRequest(delegatePub);
  }

  /** K7 follow-on: once a pending request has been approved (issued), it no longer needs review —
   * drop it from the panel's local cache and best-effort clear its Gun soul. Never throws: a
   * failed cleanup just leaves a harmless already-approved request visible until it ages out. */
  private clearMatchingDelegateRequest(delegatePub: string): void {
    const match = this.delegateRequestsCache.find((r) => r.candidatePub === delegatePub);
    if (!match) return;
    this.delegateRequestsCache = this.delegateRequestsCache.filter((r) => r.requestId !== match.requestId);
    this.uiManager.updateTechSupportDelegateRequests(this.delegateRequestsCache);
    try {
      let ref = this.gunService.getGun().get(delegateRequestPath(match.requestId)[0]);
      for (const segment of delegateRequestPath(match.requestId).slice(1)) ref = ref.get(segment);
      ref.put(null);
    } catch {
      /* best-effort cleanup only */
    }
  }

  /** docs/TODO.md K7: master-only — revoke by republishing the same soul with `revokedAt` set. */
  private async handleRevokeTechSupportDelegate(delegatePub: string): Promise<void> {
    if (this.currentUser?.id !== TECHSUPPORT_ROOT_USER_ID) return;
    const pair = this.gunService.getStoredPair();
    if (!pair?.priv) return;
    const existingRaw = await fetchGrantLive(this.gunService.getGun(), delegatePub);
    const existing = await verifyValidDelegateGrant(existingRaw);
    if (!existing) return;
    const grant = await signDelegateGrant(
      {
        delegatePub,
        delegateUserId: existing.delegateUserId,
        label: existing.label,
        expiresAt: existing.expiresAt,
        issuedAt: existing.issuedAt,
        revokedAt: new Date().toISOString(),
      },
      pair as import('../sea-gun').GunPair & { pub: string; priv: string },
    );
    this.writeDelegateGrant(grant);
  }

  private writeDelegateGrant(grant: TechSupportDelegateGrant): void {
    let ref = this.gunService.getGun().get(delegateGrantPath(grant.delegatePub)[0]);
    for (const segment of delegateGrantPath(grant.delegatePub).slice(1)) ref = ref.get(segment);
    ref.put(grant);
    // Our own freshly signed grant: cache it now (same verify + rollback rules as any received
    // grant) so the roster shows it at once instead of after the Gun echo / 5 s relay poll.
    void applyRawDelegateGrant(grant).finally(() => this.refreshDelegateAdminPanel());
  }

  /** docs/TODO.md K7: pushes the current roster + delegate-answered FAQ entries to the master's Delegates panel.
   * Also pushes the latest known recovery-anchor record (OPEN-29) — called from every point in the
   * lifecycle where the master's own identity is confirmed, unlike the live Gun subscription
   * (subscribeToTechSupportRecoveryAnchor) alone, which can fire before `currentUser` resolves at
   * boot and would otherwise never re-deliver the same unchanged record afterward. */
  private refreshDelegateAdminPanel(): void {
    if (this.currentUser?.id !== TECHSUPPORT_ROOT_USER_ID) return;
    this.uiManager.updateTechSupportDelegates(readCachedDelegateGrants());
    this.uiManager.updateTechSupportRecoveryAnchor(readCachedRecoveryAnchor());
    const apiBase = this.getBackendApiBase();
    if (!apiBase) return;
    void fetchRecentFaqEntriesFromServer(apiBase, this.gunService.getGun(), 200).then((entries) => {
      this.uiManager.updateDelegateActivity(entries.filter((e) => !!e.answeredByDelegate).map(faqEntryOf));
    });
  }

  private randomSecretHex(): string {
    const bytes = new Uint8Array(18);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * K7 follow-on: master-only — generates a fresh 5-minute invite code/QR for the Delegates
   * panel's "Invite delegate" dialog and starts (or confirms) the live subscription that will
   * surface the resulting signed request once a candidate enters it. One outstanding invite at a
   * time keeps this simple — generating a new one replaces the last (unapproved requests against
   * an old invite are still shown if already received; only future ones need a live invite).
   */
  private handleCreateTechSupportDelegateInvite(): { code: string; expiresAt: number } | null {
    if (this.currentUser?.id !== TECHSUPPORT_ROOT_USER_ID) return null;
    const invite = createDelegateInvite(() => this.randomSecretHex());
    this.pendingDelegateInvite = invite;
    this.subscribeToTechSupportDelegateRequests();
    return { code: encodeDelegateInviteCode(invite), expiresAt: invite.expiresAt };
  }

  /**
   * Targeted (remote) invite — the redundant, no-proximity alternative to the code/QR dialog.
   * The master looks up a known user, confirms their name + fingerprint, and publishes an invite
   * addressed to that user's pub. The candidate's client discovers it (relay poll) and accepts
   * from anywhere; the request is only honored when signed by that exact pub.
   */
  private async previewTargetedDelegateInviteTarget(userId: string): Promise<{ stageName: string; pub: string } | null> {
    if (this.currentUser?.id !== TECHSUPPORT_ROOT_USER_ID) return null;
    const id = userId.trim();
    if (!id || id === TECHSUPPORT_ROOT_USER_ID) return null;
    const user = await this.gunService.getPublicUser(id).catch(() => null);
    if (!user?.pub) return null;
    return { stageName: String(user.stageName || id), pub: user.pub };
  }

  private async handleSendTargetedDelegateInvite(userId: string): Promise<'sent' | 'unknown-user' | 'unavailable'> {
    if (this.currentUser?.id !== TECHSUPPORT_ROOT_USER_ID) return 'unavailable';
    const target = await this.previewTargetedDelegateInviteTarget(userId);
    if (!target) return 'unknown-user';
    const pair = this.gunService.getStoredPair();
    if (!pair?.priv || !pair.pub) return 'unavailable';
    try {
      const invite = await signTargetedDelegateInvite(
        { targetPub: target.pub, targetUserId: userId.trim(), randomSecret: () => this.randomSecretHex() },
        pair as import('../sea-gun').GunPair & { pub: string; priv: string },
      );
      this.targetedDelegateInvites.set(invite.requestId, invite);
      this.persistTargetedDelegateInvites();
      this.subscribeToTechSupportDelegateRequests();
      let ref = this.gunService.getGun().get(targetedDelegateInvitePath(invite.targetPub)[0]);
      for (const segment of targetedDelegateInvitePath(invite.targetPub).slice(1)) ref = ref.get(segment);
      ref.put(invite as unknown as Record<string, unknown>);
      const apiBase = this.getBackendApiBase();
      if (!apiBase) return 'sent';
      const response = await fetch(`${apiBase}/api/support/delegate-invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(invite),
      });
      return response.ok ? 'sent' : 'unavailable';
    } catch {
      return 'unavailable';
    }
  }

  private loadTargetedDelegateInvites(): Map<string, TargetedDelegateInvite> {
    try {
      const raw = JSON.parse(settingsStorage.getItem(IinPublicApp.TARGETED_DELEGATE_INVITES_STORAGE_KEY) || '[]');
      const live = (Array.isArray(raw) ? raw : []).filter(
        (i: TargetedDelegateInvite) => i && typeof i.requestId === 'string' && !isDelegateInviteExpired(i),
      );
      return new Map(live.map((i: TargetedDelegateInvite) => [i.requestId, i]));
    } catch {
      return new Map();
    }
  }

  private persistTargetedDelegateInvites(): void {
    try {
      const live = [...this.targetedDelegateInvites.values()].filter((i) => !isDelegateInviteExpired(i));
      settingsStorage.setItem(IinPublicApp.TARGETED_DELEGATE_INVITES_STORAGE_KEY, JSON.stringify(live));
    } catch {
      /* best-effort persistence only */
    }
  }

  /** Master: native devices never get the live Gun push, so poll each outstanding targeted invite's request slot. */
  private async pollTargetedDelegateInviteRequests(apiBase: string): Promise<void> {
    if (this.currentUser?.id !== TECHSUPPORT_ROOT_USER_ID) return;
    for (const invite of this.targetedDelegateInvites.values()) {
      if (isDelegateInviteExpired(invite)) continue;
      if (this.delegateRequestsCache.some((r) => r.requestId === invite.requestId)) continue;
      try {
        const response = await fetch(`${apiBase}/api/support/delegate-requests/${encodeURIComponent(invite.requestId)}`);
        if (response.ok) await this.handleIncomingDelegateRequest(await response.json());
      } catch {
        /* best-effort */
      }
    }
  }

  private readHandledDelegateInvites(): string[] {
    try {
      const raw = JSON.parse(settingsStorage.getItem(IinPublicApp.HANDLED_DELEGATE_INVITES_STORAGE_KEY) || '[]');
      return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : [];
    } catch {
      return [];
    }
  }

  private markDelegateInviteHandled(requestId: string): void {
    try {
      const next = [...this.readHandledDelegateInvites().filter((id) => id !== requestId), requestId].slice(-20);
      settingsStorage.setItem(IinPublicApp.HANDLED_DELEGATE_INVITES_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* best-effort persistence only */
    }
  }

  /** Candidate: look for an invite addressed to this identity (relay first — it reaches native devices too). */
  private async checkIncomingTargetedDelegateInvite(apiBase: string): Promise<void> {
    if (!this.currentUser || this.currentUser.id === TECHSUPPORT_ROOT_USER_ID) return;
    if (isValidDelegateGrant(this.techSupportDelegateGrant)) return;
    const pub = this.gunService.getStoredPair()?.pub;
    if (!pub) return;
    let raw: unknown = null;
    try {
      const response = await fetch(`${apiBase}/api/support/delegate-invites/${encodeURIComponent(pub)}`);
      if (response.ok) raw = await response.json();
    } catch {
      /* best-effort */
    }
    const invite = await verifyTargetedDelegateInvite(raw);
    const usable =
      !!invite &&
      invite.targetPub === pub &&
      isTrustedDmPubWithRecovery(invite.masterPub, readCachedRecoveryAnchor()) &&
      !isDelegateInviteExpired(invite) &&
      !this.readHandledDelegateInvites().includes(invite.requestId);
    const next = usable ? invite : null;
    if (next?.requestId === this.incomingTargetedDelegateInvite?.requestId) return;
    this.incomingTargetedDelegateInvite = next;
    this.uiManager.setIncomingTargetedDelegateInvite(next ? { expiresAt: next.expiresAt } : null, { notify: !!next });
  }

  private async handleRespondToTargetedDelegateInvite(accept: boolean): Promise<'invalid' | 'expired' | 'unavailable' | null> {
    const invite = this.incomingTargetedDelegateInvite;
    if (!invite) return 'invalid';
    if (accept) {
      const err = await this.handleSubmitTechSupportDelegateRequest(
        encodeDelegateInviteCode({ version: invite.version, requestId: invite.requestId, secret: invite.secret, expiresAt: invite.expiresAt }),
      );
      if (err) return err;
    }
    this.markDelegateInviteHandled(invite.requestId);
    this.incomingTargetedDelegateInvite = null;
    this.uiManager.setIncomingTargetedDelegateInvite(null);
    return null;
  }

  /**
   * K7 follow-on: candidate-side — decode + validate the invite code, sign a request with the
   * candidate's OWN key (never the master's), and publish it for the master to review. Never
   * throws — a network/signing failure surfaces as 'unavailable' so the UI can show an inline
   * error rather than an unhandled rejection.
   */
  private async handleSubmitTechSupportDelegateRequest(code: string): Promise<'invalid' | 'expired' | 'unavailable' | null> {
    if (!this.currentUser || this.currentUser.id === TECHSUPPORT_ROOT_USER_ID) return 'invalid';
    const payload = decodeDelegateInviteCode(code);
    if (!payload) return 'invalid';
    if (isDelegateInviteExpired(payload)) return 'expired';
    const pair = this.gunService.getStoredPair();
    if (!pair?.priv) return 'unavailable';
    try {
      const request = await buildDelegateRequest(
        { requestId: payload.requestId, secret: payload.secret, candidateUserId: this.currentUser.id },
        pair as import('../sea-gun').GunPair & { pub: string; priv: string },
      );
      let ref = this.gunService.getGun().get(delegateRequestPath(request.requestId)[0]);
      for (const segment of delegateRequestPath(request.requestId).slice(1)) ref = ref.get(segment);
      ref.put(request);
      // K7 follow-on: the raw Gun write above never reaches the hub from a native (embedded-node)
      // device — found via a real-hardware test (09-android-techsupport-delegate-answers). The
      // explicit HTTP relay is the reliable path for every runtime; the Gun write above stays as
      // a harmless, lower-latency fast path for plain browsers already peered with the hub.
      const apiBase = this.getBackendApiBase();
      if (apiBase) {
        void fetch(`${apiBase}/api/support/delegate-requests`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(request),
        }).catch(() => undefined);
      }
      return null;
    } catch {
      return 'unavailable';
    }
  }

  /** K7 follow-on: master-only — live roster of candidate requests, filtered down to ones that
   * verify AND match this session's own outstanding invite (requestId + secretHash both bound to
   * the exact invite this session generated — a requestId collision alone is not enough). */
  private subscribeToTechSupportDelegateRequests(): void {
    if (this.currentUser?.id !== TECHSUPPORT_ROOT_USER_ID) return;
    if (this.techSupportDelegateRequestsUnsubscribe) return;
    const gun = this.gunService.getGun();
    const ref = gun.get(delegateRequestPath('')[0]).map();
    ref.on((raw: unknown) => {
      void this.handleIncomingDelegateRequest(raw);
    });
    this.techSupportDelegateRequestsUnsubscribe = () => ref.off();
  }

  private async handleIncomingDelegateRequest(raw: unknown): Promise<void> {
    const request = await verifyDelegateRequest(raw);
    if (!request) return;
    const invite = this.pendingDelegateInvite;
    const matchesCodeInvite = !!invite && !isDelegateInviteExpired(invite) && (await delegateRequestMatchesInvite(request, invite));
    const targeted = this.targetedDelegateInvites.get(request.requestId);
    const matchesTargetedInvite =
      !!targeted && !isDelegateInviteExpired(targeted) && (await delegateRequestMatchesTargetedInvite(request, targeted));
    if (!matchesCodeInvite && !matchesTargetedInvite) return;
    if (this.delegateRequestsCache.some((r) => r.requestId === request.requestId)) return;
    this.delegateRequestsCache = [...this.delegateRequestsCache, request];
    this.uiManager.updateTechSupportDelegateRequests(this.delegateRequestsCache);
  }

  /**
   * docs/TODO.md K5, design note §Item 2 — replaces the old blanket canned reply
   * (sendTechSupportAutoReply/supportReply) with a hit/miss branch, run on the ASKER'S OWN
   * client so a known question is answered even while TechSupport's device is offline:
   *
   *   - known  → render a signed auto-answer locally from the verified cached FAQ bundle
   *              (never `sendMessage` — authored by TechSupport, not transmitted, same trick
   *              as the K2 greeting).
   *   - new    → render the compiled, pre-signed acknowledgement locally, and deliver the
   *              question to the TechSupport device via the offline mailbox (§Item 3).
   *   - unanswerable (no real question text) → do nothing; no reply, no inbox entry.
   */
  private async handleSupportQuestion(
    conversationId: string,
    userMessageId: string,
    questionText: string,
  ): Promise<void> {
    if (!this.currentUser || isTechSupportUser(this.currentUser)) return;
    // docs/TODO.md OPEN-31: ONE targeted read for the ONE record this question hashes to, made
    // synchronously right before the lookup — a just-published answer is visible immediately
    // (no poll-interval race, the bug the old whole-bundle sync had), and the device downloads and
    // caches only what it actually asked about. Falls back to the local per-entry cache when the
    // relay is unreachable, so a previously-seen answer still auto-answers offline.
    const questionKey = supportQuestionKey(questionText);
    if (!questionKey) return;
    const apiBase = this.getBackendApiBase();
    const signedEntry: SignedFaqEntry | null = apiBase
      ? await fetchFaqEntryFromServer(apiBase, this.gunService.getGun(), questionKey)
      : null;
    let result = lookupSupportAnswer(questionText, signedEntry ? [faqEntryOf(signedEntry)] : []);
    const now = new Date().toISOString();

    if (result.status === 'unanswerable') return;

    // The live per-entry record missed — fall back to the curated starter FAQ
    // (docs/TODO.md K5, `techsupport-faq-seed.ts`) before queuing a human. A real human answer
    // for the same question, once one exists live, always wins; this only ever fires while that
    // question has never been answered live. `faqProvenance` is what the rendered answer carries
    // so a later render pass can re-verify it.
    let faqProvenance: {
      faqQuestionKey: string;
      faqAuthorPub: string;
      faqSignature: string;
      faqEntryJson?: string;
    } | null =
      result.status === 'known' && signedEntry
        ? {
            faqQuestionKey: result.questionKey,
            faqAuthorPub: signedEntry.authorPub,
            faqSignature: signedEntry.signature,
            faqEntryJson: JSON.stringify(signedEntry),
          }
        : null;
    if (result.status === 'new') {
      const seedBundle = await readSeedFaqBundle();
      if (seedBundle) {
        const seedResult = lookupSupportAnswer(questionText, seedBundle.entries);
        if (seedResult.status === 'known') {
          result = seedResult;
          faqProvenance = {
            faqQuestionKey: seedResult.questionKey,
            faqAuthorPub: seedBundle.authorPub,
            faqSignature: seedBundle.signature,
          };
        }
      }
    }

    if (result.status === 'known') {
      if (!faqProvenance) return;
      // Deliver like handleAnswerSupportQuestion does: via the resolved transport, which
      // persists to the DURABLE server store in addition to local Gun. The known-branch
      // historically used conversationService.upsertMessageRecord (local Gun only, no
      // server, "NOT used as a conversation transport") — reliable at conversation-open
      // time (the K2 greeting does this too) but racy when the conversation is already
      // open and depends solely on the live Gun subscription observing the put. Under
      // parallel test:all load (and under real-world Gun lag), that observation
      // intermittently failed and the answer bubble silently never rendered. Sending
      // through the techsupport transport adds the same 5s server-poll backstop the
      // human-answer path already has, so a missed Gun put is still recovered.
      //
      // The message is still TECHSUPPORT-authored (senderId = TECHSUPPORT_ROOT_USER_ID).
      // This is NOT a "send to peer" — the transport's sendMessage here is its Gun+server
      // persist, no WebRTC/direct-p2p hop (mode = star-gun, channel = public →
      // shouldEncrypt = false).
      //
      // The transport's server POST is awaited and can throw (e.g. server temporarily
      // unavailable). The question in this same handler is already rendered/sent at the
      // point we get here, so the answer's delivery path MUST NOT allow that to escape
      // into the caller (which would surface a send-failed toast for an already-sent
      // question). Fall back to the previous local-only render on any failure — the
      // subscription-based recovery is best-effort; the fallback preserves prior behavior
      // rather than regressing to a toast.
      let delivered = false;
      try {
        await this.conversationService.sendMessage(
          conversationId,
          TECHSUPPORT_ROOT_USER_ID,
          result.entry.answer,
          {
            otherUserId: this.currentUser.id,
            messageId: supportAutoAnswerMessageId(userMessageId),
            channel: 'public',
            isFromChatbot: true,
          },
        );
        delivered = true;
      } catch (err) {
        console.warn('[Support] known-branch transport send failed — falling back to local record', err);
      }
      // Belt: record the FAQ provenance fields on the local Gun message node (idempotent
      // under the same deterministic messageId; harmless when the transport path already
      // wrote it). When the transport path worked, this is a duplicate put that adds the
      // provenance fields; when it failed, it is the only local delivery the subscription
      // can recover from.
      try {
        const gun = this.gunService.getGun();
        gun
          .get(`conversations/${conversationId}`)
          .get('messages')
          .get(supportAutoAnswerMessageId(userMessageId))
          .put({
            ...(delivered ? {} : {
              id: supportAutoAnswerMessageId(userMessageId),
              senderId: TECHSUPPORT_ROOT_USER_ID,
              text: result.entry.answer,
              timestamp: now,
              channel: 'public',
              isFromChatbot: true,
            }),
            ...faqProvenance,
          });
      } catch {
        // local Gun write failure is non-fatal in both paths
      }
      // Same 🤖 convention a talk's own saved-answer chatbot already uses
      // (conversation-list-updates.ts's `respondedByBot`, sticky once true) — surface that this
      // support conversation has an auto-repeated answer in it, not only a human one. Merges
      // onto the existing support-conversation record (`ensureSupportConversationRecord`);
      // `addNewConversation` treats this as an update, not a fresh conversation.
      this.uiManager.addNewConversation({
        conversationId,
        otherUserId: TECHSUPPORT_ROOT_USER_ID,
        otherUserName: TECHSUPPORT_STAGE_NAME,
        supportChannel: true,
        respondedByBot: true,
      });
      this.uiManager.updateConversationMessage(conversationId, result.entry.answer, now);
      return;
    }

    // result.status === 'new' (MISS)
    const locale = getUiLanguagePreference(uiLanguageFromProfile(this.currentUser.languages)) as SupportAckLocale;
    const ackBundle = techsupportSupportAckBundle.acks as SignedSupportAck[];
    const ackEntry = ackBundle.find((a) => a.locale === locale) ?? ackBundle.find((a) => a.locale === 'en');
    const verifiedAck = ackEntry ? await verifySupportAck(ackEntry) : null;
    if (verifiedAck) {
      const renderedAck = renderGreeting(verifiedAck.template, this.currentUser.stageName);
      // Same delivery guarantee as the 'known' branch (see above): a local-
      // upsertMessageRecord render of a TECHSUPPORT-authored ack into an
      // already-open conversation can be dropped (the open subscription's
      // Gun-event delivery is the only recovery path for a local-only put, and
      // it intermittently misses — observed as 06-support-new-question-ack's
      // ack never rendering in a full test:all run). Route through the
      // support transport so the message is durable on the server too, and
      // the 5s server-poll in TechSupportConversationTransport.subscribe-
      // ToMessages will recover any missed Gun put. A verify failure suppresses
      // the ack but never blocks the mailbox delivery below (K2-3 discipline
      // unchanged).
      const ackProvenance = {
        ackLocale: verifiedAck.locale,
        ackSignature: verifiedAck.signature,
        ackAuthorPub: verifiedAck.authorPub,
      };
      const ackMessageId = `support_ack_${userMessageId}`;
      let ackDelivered = false;
      try {
        await this.conversationService.sendMessage(
          conversationId,
          TECHSUPPORT_ROOT_USER_ID,
          renderedAck,
          {
            otherUserId: this.currentUser.id,
            messageId: ackMessageId,
            channel: 'public',
            isFromChatbot: true,
          },
        );
        ackDelivered = true;
      } catch (err) {
        console.warn('[Support] miss-branch transport send failed — falling back to local record', err);
      }
      // Belt: ensure the local Gun message node carries the ack provenance + full
      // record as a fallback if the transport POST failed (or, when it succeeded,
      // just add the provenance fields as a harmless duplicate put under the same
      // deterministic messageId).
      try {
        const gun = this.gunService.getGun();
        gun
          .get(`conversations/${conversationId}`)
          .get('messages')
          .get(ackMessageId)
          .put({
            ...(ackDelivered ? {} : {
              id: ackMessageId,
              senderId: TECHSUPPORT_ROOT_USER_ID,
              text: renderedAck,
              timestamp: now,
              channel: 'public',
              isFromChatbot: true,
            }),
            ...ackProvenance,
          });
      } catch {
        // local Gun write failure is non-fatal in both paths
      }
      this.uiManager.updateConversationMessage(conversationId, renderedAck, now);
    }
    // A verify failure suppresses the ack (K2-3 discipline) but must never block delivery —
    // the question still needs to reach TechSupport either way.
    await this.postSupportQuestionToMailbox({
      questionKey: result.questionKey,
      question: questionText,
      askedBy: this.currentUser.id,
      conversationId,
      askedAt: now,
      status: 'pending',
    });
  }

  private loadTravelModeStateFromStorage(): void {
    const travel = settingsStorage.getItem('iinpublic_travel_room') || undefined;
    let nearbyPoint: (GPSCoordinate & { placeId: string; placeName: string }) | undefined;
    try {
      const raw = JSON.parse(settingsStorage.getItem('iinpublic_travel_nearby_point') || 'null') as any;
      const latitude = Number(raw?.latitude);
      const longitude = Number(raw?.longitude);
      if (Number.isFinite(latitude) && latitude >= -90 && latitude <= 90
        && Number.isFinite(longitude) && longitude >= -180 && longitude <= 180
        && typeof raw?.placeId === 'string' && raw.placeId) {
        nearbyPoint = {
          latitude,
          longitude,
          accuracy: 0,
          timestamp: new Date(),
          placeId: raw.placeId,
          placeName: String(raw.placeName || 'Place'),
        };
      }
    } catch {
      // Invalid legacy/session storage is ignored; automatic Nearby remains the safe home.
    }
    // A Place visit or a deliberate Nearby-at-a-pin visit survives restart. Arbitrary legacy
    // Nearby IDs do not: the public point is required to recompute and verify the travel scope.
    this.travelModeActive = !!travel && (
      isPlaceRoomId(travel)
      || (isNearbyRoomId(travel) && nearbyPoint != null)
    );
    this.travelHomeChatroomId = undefined;
    this.travelChatroomId = this.travelModeActive ? travel : undefined;
    this.travelNearbyPoint = this.travelModeActive && isNearbyRoomId(String(travel || ''))
      ? nearbyPoint
      : undefined;
    this.uiManager.setTravelModeState({
      active: this.travelModeActive,
      ...(this.travelHomeChatroomId ? { homeChatroomId: this.travelHomeChatroomId } : {}),
    });
    this.refreshNearbyPoint();
  }

  private persistTravelModeStateToStorage(): void {
    settingsStorage.setItem('iinpublic_travel_mode', this.travelModeActive ? '1' : '0');
    if (this.travelHomeChatroomId) settingsStorage.setItem('iinpublic_travel_home', this.travelHomeChatroomId);
    else settingsStorage.removeItem('iinpublic_travel_home');
    if (this.travelChatroomId) settingsStorage.setItem('iinpublic_travel_room', this.travelChatroomId);
    else settingsStorage.removeItem('iinpublic_travel_room');
    if (this.travelNearbyPoint) {
      settingsStorage.setItem('iinpublic_travel_nearby_point', JSON.stringify({
        latitude: this.travelNearbyPoint.latitude,
        longitude: this.travelNearbyPoint.longitude,
        placeId: this.travelNearbyPoint.placeId,
        placeName: this.travelNearbyPoint.placeName,
      }));
    } else {
      settingsStorage.removeItem('iinpublic_travel_nearby_point');
    }
  }

  /**
   * Get a user-friendly display name for a chatroom
   */
  private getChatroomDisplayName(chatroomId: string): string {
    return this.uiManager.resolveChatroomTitle(chatroomId);
  }

  /**
   * Fetch member counts for all chatrooms in the UI list
   * This allows showing headcount badges for rooms the user hasn't visited
   */
  private subscribeToMessages(chatroomId: string): void {
    this.placeChatUnsubscribe?.();
    this.placeChatUnsubscribe = null;
    // Only a Place keeps a shared chat feed (§3.7); Nearby and Contacts only have none.
    if (!hasRoomChat(chatroomId)) return;
    console.log('💬 Subscribing to chatroom messages:', chatroomId);
    this.placeChatUnsubscribe = this.placeChatClient.subscribe(
      chatroomId,
      (messages, dropped) => {
        if (dropped) console.warn('Place chat skipped older messages that exceeded one bounded page');
        for (const messageData of messages) {
          this.uiManager.displayChatroomMessage({
            id: messageData.id,
            text: messageData.text,
            senderName: messageData.senderName,
            timestamp: messageData.timestamp,
            isOwnMessage: messageData.senderId === this.currentUser?.id,
          });
        }
      },
      (error) => console.warn('Place chat poll failed:', error),
    );
  }

  private chatbotHasAnswersFor(talkId: string, contentId: string, talkData: any): boolean {
    return !!this.uiManager.getChatbotTemplate(talkId)
      || (!!contentId && contentId !== talkId && !!this.uiManager.getChatbotTemplate(contentId))
      || !!this.uiManager.tryBuildChatbotAnswersFromFlattened(talkData);
  }

  /**
   * OPEN-42: true when the chatbot is about to answer this talk on its own, so the user is told
   * "answered automatically" instead of a "New talk" notice that implies they must act.
   */
  private chatbotWillAnswer(talkId: string, talkData: any, authorId: string): boolean {
    return chatbotWillAct(this.chatbotReplyDecision(talkId, talkData, authorId));
  }

  /** The core's rule (src/core/chatbot-reply.ts) over this app's chatbot state. */
  private chatbotReplyDecision(talkId: string, talkData: any, authorId: string): ChatbotReplyDecision {
    return decideChatbotReply(authorId, this.currentUser?.id, {
      enabled: () => this.uiManager.getChatbotEnabled(),
      handled: () => this.chatbotAutoReplySentForPair.has(talkRevisionPairKey(talkId, authorId, talkData)),
      contentId: () => computeTalkIdFromTalkData(talkData),
      answeredBefore: (contentId) => answeredBeforeFor(contentId, authorId, getAnsweredTalkByContent(), getMyTalks()),
      hasAnswers: (contentId) => this.chatbotHasAnswersFor(talkId, contentId, talkData),
      passedTag: () => isPassedTag(talkData, getExactChatbotMemory(), Date.now()),
    });
  }

  /**
   * When chatbot is on and we have a saved template for this talk, reply once per announcer
   * (e.g. Bob re-broadcasts the same talk Tom created — Jerry auto-replies on first receipt, not only on Gun replay).
   */
  private maybeAutoChatbotReplyToAnnouncer(
    talkId: string,
    talkData: any,
    authorId: string,
    authorName: string,
  ): void {
    const pairKey = talkRevisionPairKey(talkId, authorId, talkData);
    const decision = this.chatbotReplyDecision(talkId, talkData, authorId);
    if (decision.act === 'skip') {
      if (decision.reason !== 'self') console.log(`🤖 Chatbot auto-reply skipped: ${decision.reason}`, { talkId, authorId });
      return;
    }
    const contentId = decision.contentId;
    if (decision.act === 'pass') {
      // A tag the user passed, sent again by someone: pass it again. A pass is a talk-level
      // opt-out, so nothing goes back to the sender — only the user's list records it (🤖).
      this.chatbotAutoReplySentForPair.add(pairKey);
      const question = talkData.questions[0];
      console.log('🤖 Chatbot passed a tag the user passed before', { talkId, authorId });
      this.uiManager.recordChatbotAnsweredTalk(
        { ...talkData, id: talkId, authorId },
        [{ questionId: question.id, answerId: 'ignore', answerText: 'ignore' }],
        'mismatch',
        true,
      );
      return;
    }
    if (decision.act === 'wait') {
      const retries = this.chatbotAutoReplyRetryCountByPair.get(pairKey) ?? 0;
      if (retries < 6) {
        this.chatbotAutoReplyRetryCountByPair.set(pairKey, retries + 1);
        setTimeout(() => {
          this.maybeAutoChatbotReplyToAnnouncer(talkId, talkData, authorId, authorName);
        }, 250);
      } else {
        this.chatbotAutoReplyRetryCountByPair.delete(pairKey);
        console.log('🤖 Chatbot auto-reply skipped: no reusable template', {
          talkId,
          contentId,
          authorId,
          retries,
        });
      }
      return;
    }
    this.chatbotAutoReplyRetryCountByPair.delete(pairKey);
    this.chatbotAutoReplySentForPair.add(pairKey);

    console.log('🤖 Chatbot auto-reply triggered', { talkId, contentId, authorId, authorName });
    this.tryChatbotReply(talkId, talkData, authorId, authorName);
  }

  /** Load full talk for an incoming mesh/local announcement. */
  public isDirectTalkDeliveryEnabled(): boolean {
    return usesMeshTalkDelivery(this.p2pRuntimeFlags);
  }

  public isMeshTalkDeliveryEnabled(): boolean {
    return usesMeshTalkDelivery(this.p2pRuntimeFlags);
  }

  public async getLocalIncomingClustersForE2e(): Promise<any[]> {
    if (!this.currentUser?.id) return [];
    // R-a step 7: syncRoomTalkBodyRendezvous removed (Gun p2pMeshTalkBodies/* path deleted).
    // Incoming clusters arrive exclusively via mesh DataChannel push or mailbox drain.
    const gunClusters = await collectLocalIncomingTalkClusters(this.gunService, this.currentUser.id, this.p2pRuntimeFlags);
    const uiClusters = Array.isArray((this.uiManager as any).incomingTalkClusters)
      ? (this.uiManager as any).incomingTalkClusters
      : [];
    const byKey = new Map<string, any>();
    for (const cluster of [...gunClusters, ...uiClusters, ...this.e2eSeededIncomingClusters]) {
      if (cluster?.identityKey) byKey.set(cluster.identityKey, cluster);
    }
    return [...byKey.values()];
  }

  private contactsPruneTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * OPEN-87 S5: people only exchanged Talks with are capped (Settings); beyond the cap the ones with
   * the fewest shared tags and the oldest exchange are pruned. Debounced: exchanges come in bursts.
   */
  private scheduleContactsPrune(delayMs = 3_000): void {
    if (this.contactsPruneTimer) clearTimeout(this.contactsPruneTimer);
    this.contactsPruneTimer = setTimeout(() => {
      this.contactsPruneTimer = null;
      const me = this.currentUser;
      if (!me) return;
      const conversations = this.uiManager.getMyConversations();
      const knownPeople = me.knownPeople ?? [];
      const blocked = new Set(me.blockedUserIds ?? []);
      const { drop, keepAlways } = planContactsPrune({
        peers: deriveLocalPeers({ currentUserId: me.id, conversations, knownPeople }),
        conversations,
        knownPeopleIds: new Set(knownPeople.map((person) => person.userId)),
        isBlocked: (peerId) => blocked.has(peerId),
        pinnedIds: new Set(getPinnedIds('contacts')),
        cap: getContactsCap(),
        sharedTags: sharedTagCounter(),
      });
      applyContactsPrune(drop, keepAlways);
      if (drop.length > 0) {
        console.log(`[contacts-prune] pruned ${drop.length} exchange-only contact(s) beyond the cap`);
        this.uiManager.displayContactsList();
      }
      // Me counts each answer's 👥 when it renders: once a burst of exchanges settles, show the new
      // counts if Me is on screen (graph-focus.ts).
      if (document.querySelector('.nav-btn[data-view="me"]')?.classList.contains('active')) this.uiManager.displayAnswersList();
    }, delayMs);
  }

  /**
   * Stores a delivery record in the device core (OPEN-91 P2). Never throws: the record is crash
   * recovery, and the response still goes out over the mesh or the mailbox without it.
   */
  private async coreDelivery(request: Extract<CoreRequest, { name: 'delivery.putSentResponse' | 'delivery.record' }>): Promise<void> {
    if (!this.coreRequest) {
      console.warn(`[delivery] device core unavailable, not stored: ${request.name}`);
      return;
    }
    try {
      await this.coreRequest(request);
    } catch (error) {
      console.warn(`[delivery] device core write failed: ${request.name}`, error);
    }
  }

  /**
   * Connects the GUI's exchange view to the device core's log (OPEN-88 part 2, OPEN-91 P1) and loads
   * it. Waits at most 5 s; a later snapshot still lands, and the open Contacts or Me tab redraws.
   * Without the worker the view keeps exchanges in memory for this session.
   */
  /**
   * Where this device's core runs (OPEN-91 P5): the app's embedded node when the shell provides its
   * secret and the node answers — copying the WebView worker's core over the first time — otherwise
   * the web worker. Resolved once at start-up, before any log loads.
   */
  private coreRequest: CoreRequester | null = null;

  private async resolveCoreHost(nativeNode: NativeNode | null): Promise<void> {
    const worker: CoreRequester | null = this.gunService.isBridgeReady()
      ? (request) => this.gunService.getBridge().core(request)
      : null;
    const node = nativeNode?.core ?? null;
    if (node && worker) {
      try {
        const result = await migrateWorkerCoreToNode(worker, node);
        if (result.migrated) console.log(`[core] moved to the app's node: ${result.records} record(s) copied from the WebView`);
      } catch (error) {
        // Not marked done: it runs again at the next start. The node still serves meanwhile.
        console.warn('[core] copying the WebView core to the node failed (retried next start):', error);
      }
    }
    this.coreRequest = node ?? worker;
    console.log(`[core] device core host: ${node ? 'app node' : worker ? 'web worker' : 'none'}`);
  }

  /**
   * The rest of the move to the app's node (OPEN-91 P5), once the owner's keypair is known: the
   * WebView worker's private records (re-encrypted here, stored on the node) and its device-local
   * mirrors. When everything is across, the worker is shut down for good — the WebView then runs no
   * Gun at all. A failure leaves the marks unset, so the next start tries again.
   */
  private async finishMoveToNativeNode(): Promise<void> {
    if (!this.gunService.isNativeNode() || !this.gunService.isBridgeReady() || !this.coreRequest) return;
    const core = this.coreRequest;
    try {
      const copied = await this.gunService.copyPrivateFromWorker();
      const bridge = this.gunService.getBridge();
      for (const path of [IinPublicApp.DEVICE_LOCAL_USER_ID_PATH, IinPublicApp.DEVICE_LOCAL_CACHED_USER_PATH]) {
        const value = await bridge.get(path).catch(() => null);
        if (!value || typeof value !== 'object' || await this.gunService.getDeviceLocal(path)) continue;
        const { _: _meta, ...rest } = value as Record<string, unknown>;
        await this.gunService.putDeviceLocal(path, rest);
      }
      await core({ name: 'collection.upsert', collection: 'appData', entries: { [MIGRATION_MARKS.privates]: new Date().toISOString() } });
      const marks = await core({ name: 'collection.snapshot', collection: 'appData' }) as Record<string, unknown>;
      if (marks[MIGRATION_MARKS.collections]) {
        this.gunService.retireWorker();
        console.log(`[core] the WebView keeps no Gun now (${copied} private record(s) moved to the app's node)`);
      }
    } catch (error) {
      console.warn('[core] moving private records to the app node failed (retried next start):', error);
    }
  }

  private async connectExchangeLog(): Promise<void> {
    const core = this.coreRequest;
    if (!core) return;
    const link = {
      snapshot: () => core({ name: 'exchanges.snapshot' }),
      upsert: (records: Parameters<typeof upsertExchange>[0][]) => core({ name: 'exchanges.upsert', records }),
      removePeers: (peerIds: string[]) => core({ name: 'exchanges.removePeers', peerIds }),
    };
    const collections = {
      snapshot: (collection: CoreCollection) => core({ name: 'collection.snapshot', collection }),
      upsert: (collection: CoreCollection, entries: Record<string, unknown>) => core({ name: 'collection.upsert', collection, entries }),
      remove: (collection: CoreCollection, keys: string[]) => core({ name: 'collection.remove', collection, keys }),
    };
    // Storage busy at start-up (a slow device, a loaded machine) is retried, never taken for "no exchanges".
    const loaded = (async () => {
      for (let attempt = 1; ; attempt++) {
        try {
          const bridge = this.gunService.isNativeNode() ? undefined : this.gunService.getBridge();
          await Promise.all([connectExchangeCore(link), connectCoreCollections(collections, bridge)]);
          connectTalkLedgerCore(core, bridge);
          return;
        } catch (error) {
          if (attempt >= 6) {
            console.warn('[exchanges] could not load the exchange log:', error);
            return;
          }
          await new Promise((resolve) => setTimeout(resolve, 2_000));
        }
      }
    })();
    const inTime = await Promise.race([loaded.then(() => true), new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 5_000))]);
    if (inTime) return;
    void loaded.then(() => {
      this.loadAttachmentShareSentIds();
      restoreCreatorReplyFilterState();
      if (document.querySelector('.nav-btn[data-view="contacts"]')?.classList.contains('active')) this.uiManager.displayContactsList();
      if (document.querySelector('.nav-btn[data-view="me"]')?.classList.contains('active')) this.uiManager.displayAnswersList();
    });
  }

  private recordLocalTalkExchange(
    peerId: string,
    peerName: string,
    talkId: string,
    talkData: any,
    outcome: 'match' | 'mismatch' | 'ignore',
    // R-2: forward-compat fields for steps 8–11 (responseId, version, respondedAt)
    meta?: {
      responseId?: string;
      version?: number;
      respondedAt?: string;
      answers?: any[];
      direction?: 'sent' | 'received';
    },
  ): void {
    if (!this.currentUser?.id || !peerId || peerId === this.currentUser.id || !talkId) return;
    console.log(`[RESP-RECORD] talk=${talkId.slice(-8)} peer=${peerId.slice(0, 8)} outcome=${outcome} dir=${meta?.direction || 'sent'}`);
    // A new exchange brings a pruned person back; the cap is checked again shortly after.
    unprunePeer(peerId);
    this.scheduleContactsPrune();
    const collectionTag = meta?.direction === 'received'
      ? null
      : recordMatchCollectionExchange(peerId, talkId, outcome);
    // What it records is the core's (src/core/exchange-recording.ts); this writes it. The exchange
    // log lives in the device core's Gun (OPEN-88, OPEN-91 P1) and the GUI's view updates at once.
    const input = {
      peerId,
      peerName,
      talkId,
      talkData,
      outcome,
      ...(meta ? { meta } : {}),
      ...(this.currentChatroomId ? { room: { roomId: this.currentChatroomId, roomName: this.getChatroomDisplayName(this.currentChatroomId) } } : {}),
      collectionTag,
      nowIso: new Date().toISOString(),
    };
    upsertExchange(exchangeRecordFor(input));
    const ledger = authorLedgerWritesFor(input, this.currentUser.id);
    if (!ledger) return;
    try {
      void recordAuthorAnswer(ledger.event, ledger.exchanged)
        .catch((error) => console.warn('[ledger] exchange commit failed:', error));
    } catch {
      // Ledger write failures are non-fatal — suppression misses cost one redundant send.
    }
  }

  private async syncDirectPairTalkExchangesForContacts(): Promise<void> {
    if (!usesMeshTalkDelivery(this.p2pRuntimeFlags) || !this.currentUser?.id) return;
    const myTalks: Record<string, any> = getMyTalks();
    const createdTalks = Object.entries(myTalks)
      .filter(([, entry]: [string, any]) => entry?.role === 'created')
      .map(([talkId, entry]: [string, any]) => ({
        talkId: String(entry?.fullTalk?.id || entry?.id || talkId),
        talkData: entry?.fullTalk || entry,
      }))
      .filter((entry) => entry.talkId && entry.talkData);
    if (createdTalks.length === 0) return;

    const chatroomId = this.chatroomService.getCurrentChatroomId?.() || this.currentChatroomId || CONTACTS_ONLY_SCOPE_ID;
    let peerIds: string[] = [];
    try {
      peerIds = (await this.chatroomService.getActiveMembers(chatroomId))
        .map((id: string) => String(id || ''))
        .filter((id: string) => id && id !== this.currentUser!.id);
    } catch {
      peerIds = [];
    }
    if (peerIds.length === 0) return;

    const gun = this.gunService.getGun();
    const collectPairResponses = (pairId: string, talkId: string) =>
      new Promise<any[]>((resolve) => {
        const rows: any[] = [];
        const ref = gun.get('pairTalkResponses').get(pairId).get(talkId).map();
        ref.once((raw: unknown, key: string) => {
          if (raw && key && !key.startsWith('_')) rows.push(raw);
        });
        window.setTimeout(() => {
          try {
            ref.off();
          } catch {
            /* ignore */
          }
          resolve(rows);
        }, 350);
      });

    await Promise.all(
      peerIds.map(async (peerId) => {
        let peerName = 'Unknown';
        try {
          const peer = await this.userService.getUser(peerId);
          peerName = peer?.stageName || peerName;
        } catch {
          /* keep fallback */
        }
        for (const { talkId, talkData } of createdTalks) {
          const pairId = this.pairIdForUsers(this.currentUser!.id, peerId);
          const rows = await collectPairResponses(pairId, talkId);
          for (const row of rows) {
            if (String(row?.authorId || '') !== this.currentUser!.id) continue;
            if (String(row?.responderId || '') !== peerId) continue;
            try {
              const decrypted = await this.decryptPairTalkResponsePayload(row);
              const isMatch = checkIfMatch(talkData, decrypted.answers);
              this.recordLocalTalkExchange(peerId, decrypted.responderName || peerName, talkId, talkData, isMatch ? 'match' : 'mismatch');
            } catch (error) {
              console.warn('Failed to sync pair talk response for contacts:', error);
            }
          }
        }
      }),
    );
  }

  private pairIdForUsers(userA: string, userB: string): string {
    return [String(userA || '').trim(), String(userB || '').trim()].sort().join('__');
  }

  /**
   * Roster records now carry each member's public encryption key (see the membership
   * heartbeat) — harvest them into the epub cache on every roster update so encrypting FOR
   * any room member (talk-body mailbox posting, pair offers) needs no network lookup.
   */
  private harvestRosterEpubs(members: Array<{ userId?: string; epub?: string }>): void {
    for (const member of members || []) {
      const id = String(member?.userId || '').trim();
      const epub = typeof member?.epub === 'string' ? member.epub.trim() : '';
      if (id && epub) {
        if (!this.peerEpubByUserId.has(id)) this.peerEpubByUserId.set(id, epub);
        // Also seed the Gun service's roster-epub cache so message decryption (which reads
        // getPublicUser) can resolve a peer's epub from the reliable heartbeat roster instead
        // of leaving received DMs as raw SEA ciphertext when the public-user lookup races.
        this.gunService.rememberRosterEpub(id, epub);
      }
    }
  }

  private async resolvePeerEpub(peerUserId: string, hint?: string): Promise<string> {
    const hinted = firstKeyHint(hint);
    if (hinted) {
      this.peerEpubByUserId.set(peerUserId, hinted);
      return hinted;
    }
    const cached = this.peerEpubByUserId.get(peerUserId);
    if (cached) return cached;

    for (let attempt = 0; attempt < 4; attempt += 1) {
      try {
        const presence = this.presenceClient ?? new P2PPresenceClient({
          apiBase: this.getBackendApiBase(),
        });
        const peers = await Promise.race([
          presence.fetchNearby(
            this.currentUser?.id,
            200,
            this.activeExchangeRoomController?.getScope() ?? undefined,
          ),
          new Promise<never>((_, reject) => setTimeout(
            () => reject(new Error('presence key lookup timeout')),
            1_500,
          )),
        ]);
        const peer = peers.find((candidate) => candidate.userId === peerUserId);
        const epub = typeof peer?.epub === 'string' ? peer.epub.trim() : '';
        if (epub) {
          this.peerEpubByUserId.set(peerUserId, epub);
          return epub;
        }
      } catch {
        /* bounded Gun fallback below */
      }

      try {
        // K7 follow-on: 900ms was tuned for a browser's direct Gun peer, which either has the
        // answer already or doesn't. `getPublicUser` also has an explicit-HTTP relay fallback
        // (`getPublicUserFromApi`) for an embedded caller resolving a DIFFERENT embedded peer's
        // epub (neither is Gun-peered to the other or to the hub) — found via a real-hardware
        // test (09-android-techsupport-delegate-answers, the MacMini-Electron-app leg) that
        // 900ms routinely killed that relay round-trip before it ever returned, silently
        // dropping the delegate from mailbox fan-out with no error anywhere. This path runs for
        // an async mailbox fan-out, not an interactive action, so a few extra seconds is fine.
        const peer = await Promise.race([
          this.gunService.getPublicUser(peerUserId),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 5_000)),
        ]);
        const epub = typeof peer?.epub === 'string' ? peer.epub.trim() : '';
        if (epub) {
          this.peerEpubByUserId.set(peerUserId, epub);
          return epub;
        }
      } catch {
        /* retry */
      }
      await new Promise((resolve) => setTimeout(resolve, 200 + attempt * 100));
    }
    return '';
  }

  private async getPairTalkResponseSecret(peerUserId: string, peerEpubHint?: string): Promise<string> {
    // The protocol is the core's (src/core/pair-response-crypto.ts).
    const pair = this.gunService.getStoredPair() as SeaSigningPair | null;
    return pairSecret(this.seaPort(), pair, peerUserId, await this.resolvePeerEpub(peerUserId, peerEpubHint));
  }

  private seaPort(): SeaPort {
    const sea = getSEA();
    return {
      secret: (epub, pair) => sea.secret(epub, pair as GunPair),
      encrypt: (data, secret) => sea.encrypt(data, secret),
      decrypt: (data, secret) => sea.decrypt(data, secret),
    };
  }

  private async encryptPairTalkResponsePayload(
    peerUserId: string,
    payload: Record<string, unknown>,
    peerEpubHint?: string,
  ): Promise<string> {
    const pair = this.gunService.getStoredPair() as SeaSigningPair | null;
    return encryptPairResponse(this.seaPort(), pair, peerUserId, await this.resolvePeerEpub(peerUserId, peerEpubHint), payload);
  }

  private async decryptPairTalkResponsePayload(responseData: any): Promise<PairResponseContent> {
    return decodePairResponse(
      this.seaPort(),
      this.gunService.getStoredPair() as SeaSigningPair | null,
      responseData,
      (peerId, hint) => this.resolvePeerEpub(peerId, hint),
      (peerId, epub) => this.peerEpubByUserId.set(peerId, epub),
    );
  }

  /**
   * Auto-reply to a talk using saved template (chatbot). Puts response to Gun and creates
   * responder's conversation so the author will receive the match and see bot icon.
   */
  private tryChatbotReply(
    talkId: string,
    talkData: any,
    authorId: string,
    authorName: string,
  ): void {
    let template = this.uiManager.getChatbotTemplate(talkId);
    if (!template && talkData) {
      const cid = computeTalkIdFromTalkData(talkData);
      if (cid && cid !== talkId) {
        template = this.uiManager.getChatbotTemplate(cid);
      }
    }
    if (!template && talkData) {
      const built = this.uiManager.tryBuildChatbotAnswersFromFlattened(talkData);
      if (built && built.length > 0) {
        template = { answers: built, talkData };
      }
    }
    if (!template || !this.currentUser?.id) {
      console.log('🤖 Chatbot reply aborted: missing template or user', {
        talkId,
        hasTemplate: !!template,
        hasUser: !!this.currentUser?.id,
      });
      return;
    }

    void this.submitTalkResponsePairDirect({
      talkId,
      talkData,
      answers: template.answers,
      isChatbotResponse: true,
      authorId,
      authorName,
      isAutoResponse: true,
    });
    // Keep a visible record: the talk leaves the unanswered inbox and shows as answered by 🤖,
    // reviewable and re-answerable later like any answered talk.
    const outcome = checkIfMatch(
      talkData,
      template.answers,
      this.resolveResponderSelfTagForAnswers(talkData, template.answers),
    ) ? 'match' : 'mismatch';
    this.uiManager.recordChatbotAnsweredTalk({ ...talkData, id: talkId, authorId }, template.answers, outcome);
  }


  /**
   * HTTP API lives on the Gun server (port 8080 in single-worker dev/e2e, 8080+N for parallel
   * Playwright worker N). Derive the port from the current page port so each worker's bundle
   * talks to its own backend.
   */
  private getBackendApiBase(): string {
    if (typeof window === 'undefined') {
      // Node/SSR fallback: honour PORT env var so callers running inside a parallel
      // worker process (web 3001+N ↔ gun 8080+N) still target their own Gun server.
      const envPort = typeof process !== 'undefined' && process.env && process.env.PORT
        ? parseInt(process.env.PORT, 10)
        : 8080;
      return `https://localhost:${Number.isFinite(envPort) ? envPort : 8080}`;
    }
    const { hostname, protocol, port } = window.location;
    return deriveBackendApiBaseFromLocation(protocol, hostname, port);
  }

  private async syncConversationTransportFromServer(): Promise<void> {
    try {
      const res = await fetch(`${this.getBackendApiBase()}/api/debug/storage`, { cache: 'no-store' });
      if (!res.ok) return;
      const payload = (await res.json()) as { flags?: P2PRuntimeFlags };
      if (payload.flags) {
        this.conversationService.applyRuntimeTransportFlags(payload.flags);
      }
    } catch (error) {
      console.warn('Could not sync conversation transport from server flags:', error);
    }
  }

  /**
   * Sender-driven mesh delivery for all resolved room receivers.
   */
  private async deliverTalkToReceiversOverMesh(
    talkId: string,
    talk: Talk,
    members: Array<{ userId: string; stageName: string }>,
    eligibleReceiverIds?: string[],
    deliveryOptions: { skipAcknowledgements?: boolean } = {},
  ): Promise<boolean> {
    const me = this.currentUser;
    if (!me?.id || members.length === 0) return false;
    if (this.isTalkExpiredForDelivery(talk)) return false;
    // Never offer a Talk to someone this user has blocked. The receiver's own block check used to
    // ask the hub on every Talk and hid this; it is now cached for a minute.
    const blocked = new Set(me.blockedUserIds ?? []);
    let receiverIds = members
      .map((m) => m.userId)
      .filter((id) => id !== me.id && id !== TECHSUPPORT_ROOT_USER_ID && !blocked.has(id));
    if (eligibleReceiverIds !== undefined) {
      const allowed = new Set(eligibleReceiverIds);
      receiverIds = receiverIds.filter((id) => allowed.has(id));
    }
    receiverIds = filterTalkPeersByContactPolicy(
      receiverIds,
      getTalkIntakeFilters(),
      me.knownPeople,
      this.roomTravelerIds,
    );
    if (receiverIds.length === 0) return false;

    // Step 8.2 / Step 11.3: sender-side per-identity suppression.
    // For tag talks: compute suppressed identity keys per recipient and deliver a
    // filtered talk body (only non-exchanged answers). For flow/survey/route: whole-talk
    // suppression unchanged (no independent atoms).
    // NOTE: flood frames reach everyone by design; suppression applies only to the
    // directed recipientUserIds list used for announce/body delivery.
    const wholeTalkIdentityKey = buildTalkIdentityKey(talk);
    const identityKeys = buildTagIdentityKeys(talk, wholeTalkIdentityKey);
    const isTagTalk = talk.type === 'tag' && identityKeys.length > 1;
    const nowMs = Date.now();
    const talkRevision = computeTalkRevisionHash(talk);

    // Build per-recipient delivery plan:
    //   null    → skip entirely (all identities suppressed or edge gated)
    //   talk    → deliver unmodified talk (no suppression)
    //   filtered → deliver tag-filtered version (partial suppression)
    // docs/TODO.md §W Gap 2 — `sentIdentityKeys` is the set actually delivered to this
    // recipient (identityKeys minus whatever was suppressed), recorded to the ledger's
    // `sent` set once the send actually happens below, so a future send suppresses on our
    // own outbound record, not just on the recipient's answer coming back.
    type DeliveryPlan = { recipientId: string; talkPayload: typeof talk; sentIdentityKeys: string[] };
    const plans: DeliveryPlan[] = [];

    for (const recipientId of receiverIds) {
      // Compute which identity keys are suppressed for this recipient.
      const suppressedSet = new Set<string>();
      if (!this.talkLedgerSuppressionDisabledForE2e) {
        for (const ik of identityKeys) {
          if (shouldSuppressForPeer(recipientId, ik)) suppressedSet.add(ik);
        }
      }

      // If ALL identity keys suppressed → skip recipient entirely — unless the peer holds an older
      // revision of the same content (title/routing edit, OPEN-45): then re-send it whole as an
      // in-place update.
      let revisionUpdate = false;
      if (suppressedSet.size === identityKeys.length) {
        if (!talkRevisionOwedToPeer(recipientId, talkId, talkRevision, { baselineIfUnknown: true })) {
          console.debug(`[Ledger] Suppressing talk deliver to ${recipientId} for all identityKey(s) ${identityKeys.join(',')} — already exchanged`);
          continue;
        }
        revisionUpdate = true;
        console.debug(`[Ledger] Re-sending ${talkId} to ${recipientId} — title/routing updated in place`);
      }

      // Step 8.3: client per-edge cooldown/quota gate.
      const gate = await applyEdgeGateForPeer(recipientId, nowMs, talkId);
      if (!gate.ok) {
        console.debug(`[Ledger] Edge gate rejected deliver to ${recipientId}: ${gate.rejectedBy.join(',')}`);
        continue;
      }

      const sentIdentityKeys = revisionUpdate ? identityKeys : identityKeys.filter((ik) => !suppressedSet.has(ik));

      if (isTagTalk && suppressedSet.size > 0 && !revisionUpdate) {
        // Partial suppression for tag talk: deliver filtered body.
        const filterResult = filterTalkForRecipient(talk, suppressedSet);
        if (!filterResult) {
          // filterTalkForRecipient returns null when all remaining are also suppressed (edge case).
          console.debug(`[Ledger] filterTalkForRecipient returned null for ${recipientId} — skipping`);
          continue;
        }
        const filteredTalk = { ...filterResult.filtered, id: talkId, authorId: me.id } as typeof talk;
        plans.push({ recipientId, talkPayload: filteredTalk, sentIdentityKeys });
        console.debug(`[Ledger] Partial tag suppression for ${recipientId}: delivering ${(filteredTalk.questions ?? []).length > 0 ? ((filteredTalk.questions as any[])[0]?.answers?.length ?? '?') : '?'} of ${identityKeys.length} tags`);
      } else {
        // No suppression (or non-tag talk with no suppression): deliver unmodified.
        plans.push({ recipientId, talkPayload: { ...talk, id: talkId, authorId: me.id }, sentIdentityKeys });
      }
    }

    if (plans.length === 0) {
      console.debug(`[Ledger] All recipients suppressed or gated for talkId=${talkId}, skipping broadcastTalk`);
      return true; // return true so the caller counts this as "sent" (suppression is silent)
    }

    const mesh = this.ensurePeerMeshService();
    if (!mesh) return false;
    const chatroomId = this.chatroomService.getCurrentChatroomId();
    // Nearby keeps its list as the roster: the broadcast loop opens links for queued Talks itself.
    if (chatroomId && !isPersonalListScope(chatroomId)) {
      await mesh.joinRoom(chatroomId, [
        { userId: me.id, stageName: me.stageName },
        ...members,
      ]);
    }
    // R-f step 7: author-side Gun talks/* mirror removed; body cached in PeerMeshService directly.
    // Cache the full (unfiltered) talk body so body-request fallbacks get the complete talk.
    mesh.cacheTalkBody(talkId, talk as unknown as Record<string, unknown>);

    // Group plans by talkPayload identity to minimise frame count:
    //   - Recipients that get the full unfiltered talk can share one broadcast call.
    //   - Recipients that get different filtered versions each need a directed send.
    const fullTalkPayload = JSON.stringify({ ...talk, id: talkId, authorId: me.id });
    const fullRecipients = plans
      .filter((p) => JSON.stringify(p.talkPayload) === fullTalkPayload)
      .map((p) => p.recipientId);
    const filteredPlans = plans.filter((p) => JSON.stringify(p.talkPayload) !== fullTalkPayload);

    let announceCount = 0;
    const acceptedRecipientIds = new Set<string>();
    // Deliver full-talk recipients in one broadcast (batched).
    if (fullRecipients.length > 0) {
      const acknowledged = await mesh.broadcastTalk({ ...talk, id: talkId, authorId: me.id }, {
        recipientUserIds: fullRecipients,
        roomBroadcast: true,
        ...(deliveryOptions.skipAcknowledgements ? { skipAcknowledgements: true } : {}),
      });
      for (const recipientId of acknowledged) acceptedRecipientIds.add(recipientId);
      announceCount += fullRecipients.length;
    }
    // Deliver per-recipient filtered talk (directed sends, one per distinct filtered body).
    for (const plan of filteredPlans) {
      const acknowledged = await mesh.broadcastTalk(plan.talkPayload, {
        recipientUserIds: [plan.recipientId],
        roomBroadcast: false,
      });
      for (const recipientId of acknowledged) acceptedRecipientIds.add(recipientId);
      announceCount += 1;
    }

    // Record only delivery that the recipient accepted and acknowledged. Transport attempts
    // rejected by intake policy (for example, an adult talk before age verification) deliberately
    // receive no ACK and must remain eligible after that policy changes. The accepted-delivery
    // record still suppresses repeats before an answer comes back.
    const sentAtIso = new Date(nowMs).toISOString();
    for (const plan of plans) {
      if (!acceptedRecipientIds.has(plan.recipientId)) continue;
      this.noteNearbyDelivery(talkId, plan.recipientId);
      for (const ik of plan.sentIdentityKeys) {
        await markTalkSentToPeer({ peerId: plan.recipientId, identityKey: ik, talkId, sentAt: sentAtIso });
      }
      recordTalkRevisionSent(plan.recipientId, talkId, talkRevision);
    }

    const suppressedCount = receiverIds.length - plans.length;
    console.log(`📡 Mesh talk announcement published: talkId=${talkId} receivers=${announceCount} (suppressed=${suppressedCount}, partialFiltered=${filteredPlans.length})`);
    return true;
  }

  /**
   * Other users who should receive direct offers or legacy server-side IN registration for a broadcast.
   * **Gun `chatrooms/<id>/users` is authoritative** for who is in the room (FR-BM-7: same node only,
   * no parent→child hierarchy fan-out). The UI member list supplies `stageName`s only — never adds
   * receiver ids when Gun reports an empty room.
   */
  private async resolveBroadcastReceivers(
    chatroomId: string,
    uiMembers: Array<{ userId: string; stageName: string }>,
  ): Promise<Array<{ userId: string; stageName: string }>> {
    const me = this.currentUser?.id;
    if (!me) return [];

    const uiNameById = new Map<string, string>();
    for (const m of uiMembers || []) {
      if (!m.userId || m.userId === me || m.userId === TECHSUPPORT_ROOT_USER_ID) continue;
      const name = String(m.stageName || m.userId).trim() || m.userId;
      uiNameById.set(m.userId, name);
    }

    let memberIds: string[] = [];
    const fromServer = await this.chatroomService.fetchMemberIdsFromServer(chatroomId);
    memberIds = [...new Set(fromServer.filter((id) => !!id && id !== me && id !== TECHSUPPORT_ROOT_USER_ID))];

    let gunMemberIds: string[] = [];
    const mergeGunOnce = async () => {
      const ids = await this.chatroomService.getActiveMembers(chatroomId);
      gunMemberIds = [...new Set(ids.filter((id) => !!id && id !== me && id !== TECHSUPPORT_ROOT_USER_ID))];
    };

    if (memberIds.length === 0) {
      await mergeGunOnce();
      memberIds = gunMemberIds;
    }

    if (memberIds.length === 0) {
      for (let i = 0; i < 4; i++) {
        await new Promise((r) => setTimeout(r, 150));
        await mergeGunOnce();
        memberIds = gunMemberIds;
        if (memberIds.length > 0) break;
      }
    }

    // Same-room UI list is authoritative for stage names when Gun/server snapshots lag after join.
    if (memberIds.length === 0 && uiNameById.size > 0) {
      memberIds = [...uiNameById.keys()];
    }

    // OPEN-38: keep queueing for members whose phone fell asleep (heartbeat lapsed, never left).
    // Direct delivery holds the body in the outbox; the wake ping lets their device notice it.
    // Bounded so the receiver list never exceeds the room capacity.
    if (this.p2pRuntimeFlags.p2pDirectTalkDelivery) {
      const lapsed = await this.chatroomService.observeLapsedMemberIds(chatroomId, LAPSED_MEMBER_DELIVERY_MS);
      const capacity = this.activeExchangeRoomController?.getActiveRoom()?.chatroomCapacity ?? CONFIG.CHATROOM_MAX_CAPACITY;
      const known = new Set(memberIds);
      for (const userId of lapsed) {
        if (known.size >= capacity - 1) break;
        if (!userId || userId === me || userId === TECHSUPPORT_ROOT_USER_ID || known.has(userId)) continue;
        known.add(userId);
      }
      memberIds = [...known];
    }

    const permittedMemberIds = filterTalkPeersByContactPolicy(
      memberIds,
      getTalkIntakeFilters(),
      this.currentUser?.knownPeople,
      this.roomTravelerIds,
    );
    return permittedMemberIds.map((userId) => ({
      userId,
      stageName: uiNameById.get(userId) || userId,
    }));
  }

  /**
   * Current user saw a talk announcement in their subscribed chatroom. Receiver-driven intake still
   * works when the sender's on-screen member list is wrong (e.g. a room mismatch).
   */
  private async ingestIncomingTalkAnnouncement(
    talkId: string,
    senderId: string,
    senderName: string,
    talkData: any,
    opts: { deliveryChatroomId?: string } = {},
  ): Promise<boolean> {
    if (!this.currentUser) return false;
    if (
      !(await this.shouldAcceptIncomingTalkAsync({
        senderId,
        talkData: talkData as Record<string, unknown>,
        ...(opts.deliveryChatroomId ? { deliveryChatroomId: opts.deliveryChatroomId } : {}),
      }))
    ) {
      return false;
    }

    const cluster = await upsertLocalIncomingTalkCluster(
      this.gunService,
      this.currentUser.id,
      {
        talkId,
        talkData: talkData as Record<string, unknown>,
        senderId,
        senderName,
      },
      this.p2pRuntimeFlags,
    );
    this.mergeIncomingClusterIntoUi([cluster]);

    // Step 9: write responder-side exchanged entry so change-of-mind fanout can
    // enumerate all senders of this identity.  Written here (on talk acceptance)
    // so the sender list is populated before any submitTalkResponsePairDirect call.
    // role:'responder' + version:0 means "received but not yet answered".
    // submitTalkResponsePairDirect will overwrite with the actual version on answer.
    try {
      const identityKey = buildTalkIdentityKey(talkData);
      const existingVersion = getResponderVersionForTalk(identityKey, senderId);
      if (existingVersion === 0) {
        // Only seed the sender record if not yet answered (version 0 = no prior answer)
        await writeResponderExchangedEntry({
          authorId: senderId,
          identityKey,
          talkId,
          authorName: senderName,
          authorEpub: String(talkData?.authorEpub || talkData?.senderEpub || ''),
          outcome: 'no-reply',
          version: 0,
          responseId: '',
          respondedAt: new Date().toISOString(),
        });
      }
    } catch {
      // Non-fatal: fanout may miss this sender on first change-of-mind attempt
    }

    return true;
  }

  /**
   * Full talk-completion flow. Three sequential steps:
   *
   * 1. Sync QA preferences — persist the user's chosen answers to their profile (Gun, SEA-signed).
   *
   * 2. Save the client-side chatbot template — stored in localStorage so the chatbot UI
   *    can use it for a rapid re-announce.
   *
   * 3. Submit the response via the direct mesh P2P path (P0 step 7: server talk delivery removed).
   *    Match/conversation creation happens fully client-side via submitTalkResponsePairDirect.
   */
  private async handleTalkCompleted(data: {
    talkId: string;
    answers: any[];
    talkData?: any;
    isChatbotResponse?: boolean;
    /** docs/TODO.md — the receiver picked the dedicated "Ignore" choice (a talk-level
     *  opt-out, distinct from any of the asker's own provided answers): the sender must
     *  receive nothing at all. Local bookkeeping above still runs as normal. */
    withholdFromSender?: boolean;
  }): Promise<void> {
    console.log('📝 User completed talk:', data);
    // Starter contacts are local demo senders. Their Talks exercise the normal Answer/Copy
    // UI and local Q&A memory, but there is no remote identity to deliver a response to.
    if (data.talkData?.starterContactId) return;
    const isChatbot = !!data.isChatbotResponse;
    const locallyLooksLikeMatch =
      !!data.talkData &&
      checkIfMatch(data.talkData, data.answers, this.resolveResponderSelfTagForAnswers(data.talkData, data.answers));
    const isE2eLocalOnlyReject =
      data.talkData?.e2eLocalOnlyReject === true &&
      !locallyLooksLikeMatch &&
      data.talkData?.authorId &&
      data.talkData.authorId !== this.currentUser?.id;

    if (isE2eLocalOnlyReject) {
      this.recordLocalTalkExchange(
        String(data.talkData.authorId),
        String(data.talkData.authorName || 'Unknown'),
        data.talkId,
        data.talkData,
        'mismatch',
        {
          answers: data.answers,
          direction: 'received',
          respondedAt: new Date().toISOString(),
        },
      );
      const pairKey = talkRevisionPairKey(data.talkId, String(data.talkData.authorId), data.talkData);
      this.chatbotAutoReplySentForPair.add(pairKey);
      this.chatbotAutoReplyRetryCountByPair.delete(pairKey);
      return;
    }

    // Step 1 — sync QA preferences (best-effort: a transient Gun SEA write failure
    // must not abort step 3 which creates the match/conversation/contact).
    if (data.talkData && !data.withholdFromSender) {
      const pair = this.gunService.getStoredPair();
      if (pair) {
        try {
          await this.userService.syncQuestionAnswersFromTalkCompletion(
            data.talkData,
            data.answers,
            this.uiManager.getAnswerPreferencesSnapshot(),
            pair,
          );
        } catch (syncErr) {
          console.warn('syncQuestionAnswersFromTalkCompletion failed (non-fatal):', syncErr);
        }
      }
    }

    const chatroomId = this.chatroomService.getCurrentChatroomId();
    if (!chatroomId) return;

    // Step 2 — save client-side chatbot template (localStorage, UI cache only)
    // Saved before the mesh response so a rapid re-announce can use it immediately.
    if (data.talkData && locallyLooksLikeMatch && !isChatbot) {
      this.uiManager.saveChatbotTemplate(data.talkId, {
        answers: data.answers,
        talkData: data.talkData,
      });
    }

    // A manual response already handled this sender/talk pair. Do not let later Gun replays
    // or duplicate room announcements trigger a chatbot follow-up to the same announcer.
    if (!isChatbot && this.currentUser?.id && data.talkData?.authorId) {
      const pairKey = talkRevisionPairKey(data.talkId, String(data.talkData.authorId), data.talkData);
      this.chatbotAutoReplySentForPair.add(pairKey);
      this.chatbotAutoReplyRetryCountByPair.delete(pairKey);
    }

    // The receiver picked the dedicated Ignore choice — a talk-level opt-out, not one of
    // the asker's own answers. Local bookkeeping (Steps 1-2 above) already ran; the sender
    // must receive nothing at all, so Step 3 never runs.
    if (data.withholdFromSender) {
      console.log('🙅 Talk withheld from sender (receiver ignored):', data.talkId);
      return;
    }

    // Step 3 — submit via direct mesh P2P path (P0 step 7: server delivery removed).
    // All match/conversation creation happens inside submitTalkResponsePairDirect.
    const localAuthorName =
      data.talkData?.authorName && data.talkData.authorName !== 'Unknown'
        ? data.talkData.authorName
        : undefined;
    void localAuthorName; // referenced in submitTalkResponsePairDirect indirectly via authorName param

    if (data.talkData?.authorId && data.talkData.authorId !== this.currentUser?.id) {
      await this.submitTalkResponsePairDirect({
        talkId: data.talkId,
        talkData: data.talkData,
        answers: data.answers,
        isChatbotResponse: isChatbot,
        authorId: String(data.talkData.authorId),
        authorName: (data.talkData?.authorName && data.talkData.authorName !== 'Unknown'
          ? data.talkData.authorName
          : undefined) || String(data.talkData.authorName || 'Unknown'),
        isAutoResponse: !data.answers.some((a: any) => a?.mode === 'manual'),
      });
    }
    // If no authorId (e.g. self-test or malformed talk), no-op: mesh delivery requires an author.
  }

  private async submitTalkResponsePairDirect(params: {
    talkId: string;
    talkData: any;
    answers: any[];
    isChatbotResponse: boolean;
    authorId: string;
    authorName: string;
    isAutoResponse: boolean;
  }): Promise<void> {
    if (!this.currentUser?.id) return;
    if (!talkContactPolicyAllowsPeer(
      params.authorId,
      getTalkIntakeFilters(),
      this.currentUser.knownPeople,
      this.roomTravelerIds,
    )) return;
    // Persist exact-answer memory and provenance privately before any chatbot/manual
    // response can leave this device. Public offers never subscribe to this path.
    try {
      await new GunChatbotMemoryRepository(this.gunService).saveState(
        getExactChatbotMemory(),
        params.isChatbotResponse ? 'chatbot-reuse' : 'human',
      );
    } catch (error) {
      // Exact-answer memory is already durable in the device-local store. A transient SEA
      // conflict while backing it up to the private Gun namespace must not suppress the talk
      // response itself; a later completion rewrites both per-question state and the snapshot.
      console.warn('Private chatbot-memory backup failed (non-fatal):', error);
    }
    // docs/TODO.md §W Gap 2 completeness note: a survey response that hasn't answered every
    // question is not yet "done" on the responder's side — the sender must not receive it and
    // must go on treating it as not-yet-answered (no ledger write, no mesh send). This is the
    // structural backstop at the single submission chokepoint (both the real completion path
    // via handleTalkCompleted and the chatbot-replay path via tryChatbotReply funnel through
    // here) — talk-response-dialog.ts's per-type flows are the primary fix and should never
    // actually reach this with a short answer set for survey anymore.
    if (!isResponseComplete(params.talkData, params.answers)) {
      console.log('⏸️ Talk response incomplete (survey, not all questions answered) — not submitting:', {
        talkId: params.talkId,
        answered: params.answers.length,
      });
      return;
    }
    // The canonical match decision (both the manual-click path via handleTalkCompleted and
    // the chatbot's auto-reply path via tryChatbotReply funnel through here) — resolving my
    // own selfTag here is what makes the preference-set veto authoritative regardless of how
    // the answers were chosen, not just an optimization the chatbot's findAutoAnswer short-circuits.
    const isMatch = checkIfMatch(
      params.talkData,
      params.answers,
      this.resolveResponderSelfTagForAnswers(params.talkData, params.answers),
    );
    const isIgnore = isIgnoreAnswerSet(params.answers);
    // R-1: CIDv1 response id — REQ-LEDGER-04/12 (replaces resp_<ts>_<rand> non-deterministic id)
    const responseContentJson = canonicalSerialize(params.answers);
    let responseId = await computeResponseId({
      talkId: params.talkId,
      responderId: this.currentUser.id,
      responseContentJson,
    });
    const submittedAt = new Date().toISOString();

    // Step 9: monotonic version bump (REQ-LEDGER-04).
    // The identityKey is the content-hash of the talk (same for all senders of identical content).
    // We look up the prior version we sent for this (identityKey, authorId) pair.
    const wholeTalkIdentityKey = buildTalkIdentityKey(params.talkData);
    const priorVersion = getResponderVersionForTalk(wholeTalkIdentityKey, params.authorId);
    const priorResponseId = getResponderLastResponseId(wholeTalkIdentityKey, params.authorId);
    let isContentChange = priorResponseId !== null && priorResponseId !== responseId;
    let newVersion = nextResponseVersion(priorResponseId, priorVersion, responseId);
    const respondedAt = submittedAt; // first answer: respondedAt == submittedAt; supersession: now

    const skipE2eLocalOnlyReject =
      process.env.DISABLE_HMR === 'true' &&
      params.talkData?.e2eLocalOnlyReject === true &&
      !isMatch;

    // Helper: send mesh+mailbox to a single target authorId
    const sentResponseByAuthor = new Map<string, { talkId: string; responseId: string; version: number }>();
    const sendToAuthor = async (
      targetAuthorId: string,
      targetAuthorName: string,
      targetEpub?: string,
      targetTalkId = params.talkId,
    ): Promise<void> => {
      // Change-of-mind fanout can include every prior sender of identical content.
      // Re-check each target so a historic stranger edge cannot bypass the current
      // contacts-only preference through that secondary delivery path.
      if (!talkContactPolicyAllowsPeer(
        targetAuthorId,
        getTalkIntakeFilters(),
        this.currentUser?.knownPeople,
        this.roomTravelerIds,
      )) return;
      const authorEpub = targetEpub ?? firstKeyHint(
        targetAuthorId === params.authorId ? params.talkData?.authorEpub : undefined,
        targetAuthorId === params.authorId ? params.talkData?.senderEpub : undefined,
      );
      let payloadCiphertext: string;
      try {
        payloadCiphertext = await this.encryptPairTalkResponsePayload(targetAuthorId, {
          responderName: this.currentUser!.stageName,
          authorName: targetAuthorName,
          answers: params.answers,
          isChatbotResponse: params.isChatbotResponse,
          transportMode: 'mesh-p2p',
        }, authorEpub);
      } catch (err) {
        console.warn(`[Step9] Failed to encrypt response for ${targetAuthorId}:`, err);
        return;
      }
      const committed = await commitTalkResponse({
        talkId: targetTalkId, talkData: params.talkData, answers: params.answers,
        authorId: targetAuthorId, authorName: targetAuthorName, authorEpub,
        responderId: this.currentUser!.id, responderEpub: this.gunService.getStoredPair()?.epub,
        payloadCiphertext, submittedAt,
        selfTag: this.resolveResponderSelfTagForAnswers(params.talkData, params.answers),
      });
      const meshPayload = committed.payload;
      const targetResponseId = meshPayload.responseId;
      const targetVersion = meshPayload.version!;
      if (targetAuthorId === params.authorId) {
        responseId = targetResponseId;
        newVersion = targetVersion;
        isContentChange = committed.isContentChange;
      }
      const mesh = this.ensurePeerMeshService();
      let sent = false;
      if (mesh) {
        try {
          sent = await mesh.sendTalkResponse(meshPayload);
        } catch (err) {
          console.warn('[MeshResponse] sendTalkResponse failed, falling back to mailbox:', err);
        }
      }
      // Step 6: when unicast cannot be delivered (author offline or send failed),
      // post a ciphertext envelope to the server mailbox.
      let mailboxPosted: boolean | null = null;
      let queued = false;
      if (!sent) {
        mailboxPosted = await this.postToMailbox(meshPayload);
        if (!mailboxPosted) {
          this.enqueueFailedMailboxPost(meshPayload);
          queued = true;
        }
      }
      if (sent || mailboxPosted || queued) {
        await this.coreDelivery({ name: 'delivery.record', input: {
          objectId: targetResponseId,
          recipientId: targetAuthorId,
          objectKind: 'talk-response',
          state: 'sent',
        } });
      }
      // Delivery-matrix diagnostic: exactly one line per response send with every stage's
      // outcome — the silent-success pipeline made every saturation post-mortem archaeology.
      console.log(`[RESP-SEND] talk=${targetTalkId.slice(-8)} to=${targetAuthorId.slice(0, 8)} v=${targetVersion} mesh=${sent} mailbox=${mailboxPosted} queued=${queued}`);
      sentResponseByAuthor.set(targetAuthorId, {
        talkId: targetTalkId,
        responseId: targetResponseId,
        version: targetVersion,
      });
    };

    if (!skipE2eLocalOnlyReject) {
      // Step 10: check dead inbox — if the talk has been retracted by the author, skip delivery.
      const ledgerDocForPrimary = getTalkLedgerDoc();
      const primaryRetractKey = ledgerRetractedKey(params.talkId, params.authorId);
      const talkRetractedForPrimary = !!ledgerDocForPrimary.retracted[primaryRetractKey];
      if (talkRetractedForPrimary) {
        console.debug(`[Step10] Skipping response send — talk ${params.talkId} retracted by ${params.authorId}`);
      } else {
        // Send to the primary author
        await sendToAuthor(params.authorId, params.authorName);
      }

      // Step 9.1: change-of-mind fanout — if this is a version bump (content change),
      // propagate to ALL original senders of this identity (not just the current author).
      // The responder's exchanged section records every author who sent them this identity.
      if (isContentChange) {
        const allSenders = getResponderTargetsForIdentity(wholeTalkIdentityKey);
        const ledgerDocForFanout = getTalkLedgerDoc();
        for (const sender of allSenders) {
          const senderId = sender.peerId;
          if (senderId === params.authorId) continue; // already sent above
          // Step 10: skip senders whose talk has been retracted (dead inbox).
          const rKey = ledgerRetractedKey(params.talkId, senderId);
          if (ledgerDocForFanout.retracted[rKey]) {
            console.debug(`[Step10] Skipping fanout to retracted-talk sender ${senderId}`);
            continue;
          }
          console.log(`[Step9] Change-of-mind fanout to additional sender ${senderId}`);
          try {
            await sendToAuthor(
              senderId,
              sender.peerName || 'Unknown',
              sender.peerEpub,
              sender.talkId || params.talkId,
            );
          } catch (err) {
            console.warn(`[Step9] Fanout to ${senderId} failed:`, err);
          }
        }
      }
    }

    // Write/update the responder-side exchanged entry to track this response.
    // This persists version + responseId for future version-bump decisions.
    try {
      const ledgerOutcomeVal = isMatch ? 'matched' as const : 'ignored' as const;
      await writeResponderExchangedEntry({
        authorId: params.authorId,
        identityKey: wholeTalkIdentityKey,
        talkId: params.talkId,
        authorName: params.authorName,
        authorEpub: String(params.talkData?.authorEpub || params.talkData?.senderEpub || ''),
        outcome: ledgerOutcomeVal,
        version: newVersion,
        responseId,
        respondedAt,
      });
      for (const identityKey of selectedTalkIdentityKeys(params.talkData, params.answers)) {
        if (identityKey === wholeTalkIdentityKey) continue;
        await writeResponderExchangedEntry({
          authorId: params.authorId,
          identityKey,
          talkId: params.talkId,
          authorName: params.authorName,
          authorEpub: String(params.talkData?.authorEpub || params.talkData?.senderEpub || ''),
          outcome: ledgerOutcomeVal,
          version: newVersion,
          responseId,
          respondedAt,
        });
      }
      // Also update for any other senders of the same identity (fanout case)
      if (isContentChange) {
        const allSenders = getResponderTargetsForIdentity(wholeTalkIdentityKey);
        for (const sender of allSenders) {
          const senderId = sender.peerId;
          if (senderId === params.authorId) continue;
          if (!talkContactPolicyAllowsPeer(
            senderId,
            getTalkIntakeFilters(),
            this.currentUser.knownPeople,
            this.roomTravelerIds,
          )) continue;
          const sentResponse = sentResponseByAuthor.get(senderId);
          await writeResponderExchangedEntry({
            authorId: senderId,
            identityKey: wholeTalkIdentityKey,
            ...(sentResponse?.talkId || sender.talkId
              ? { talkId: sentResponse?.talkId || sender.talkId }
              : {}),
            ...(sender.peerName ? { authorName: sender.peerName } : {}),
            ...(sender.peerEpub ? { authorEpub: sender.peerEpub } : {}),
            outcome: ledgerOutcomeVal,
            version: sentResponse?.version ?? sender.version,
            responseId: sentResponse?.responseId || String((sender as any).responseId || ''),
            respondedAt,
          });
        }
      }
    } catch {
      // Non-fatal
    }

    // Step 7: client-side stats POST removed (server route dies in phase B).
    this.ledgerEmit(InteractionKind.TALK_ANSWERED, {
      talkId: params.talkId,
      responseId,
      outcome: isMatch ? 'match' : isIgnore ? 'ignore' : 'mismatch',
    });

    this.recordLocalTalkExchange(
      params.authorId,
      params.authorName,
      params.talkId,
      params.talkData,
      isMatch ? 'match' : 'mismatch',
      {
        responseId,
        version: newVersion,
        respondedAt,
        answers: params.answers,
        direction: 'received',
      },
    );

    if (!isMatch) return;
    const conversationId = await this.conversationService.createConversation({
      userId1: this.currentUser.id,
      userName1: this.currentUser.stageName,
      userId2: params.authorId,
      userName2: params.authorName,
      talkId: params.talkId,
      respondedByBotForUser1: false,
      respondedByBotForUser2: params.isChatbotResponse,
      dealEligible: isDealEligibleTalk(params.talkData),
      ...matchScoreParamsFor(params.talkData, params.answers),
    });
    this.recordConversationNeedKey(conversationId, params.talkData);
    // docs/TODO.md §JJ residual gap: on THIS (responder) side of the match, params.talkData is
    // the OTHER side's talk, not mine — recordMyDealTalkForConversation needs my own talkId,
    // resolved from chatbot memory rather than known directly the way the author-side call
    // sites above already know it from payload.talkId.
    const responderSourceTalkId = this.resolveResponderSourceTalkIdForAnswers(params.talkData, params.answers);
    if (responderSourceTalkId) {
      this.recordMyDealTalkForConversation(conversationId, responderSourceTalkId, params.talkData);
    }
    this.uiManager.maybeShowMatchSafetyToast();
    this.uiManager.addNewConversation({
      conversationId,
      otherUserId: params.authorId,
      otherUserName: params.authorName,
      talkId: params.talkId,
      respondedByBot: false,
      transportMode: this.conversationService.getTransportMode(),
      dealEligible: isDealEligibleTalk(params.talkData),
      ...matchScoreParamsFor(params.talkData, params.answers),
    });
    this.uiManager.setMemberMatched(params.authorId);
    this.ledgerEmit(InteractionKind.MATCH_CREATED, {
      talkId: params.talkId,
      conversationId,
      otherUserId: params.authorId,
    });
    // No auto-withdraw here — a match doesn't close the listing anymore; only a mutually
    // confirmed Deal does (see the deal-confirmation flow).
  }

  /**
   * My own self-tag for the last question of a would-be match, if I've ever recorded one for
   * this exact question text (see ui-manager.ts's getMySelfTagForQuestionText /
   * exact-chatbot-memory.ts) — passed into checkIfMatch's Pair-tag-ancestor veto so a manual
   * answer gets the same protection the chatbot's auto-reply already has via findAutoAnswer.
   * Undefined (no veto) if I have no self-tag history for this question or the talk declares no
   * qualifying Pair-tag ancestor.
   */
  private resolveResponderSelfTagForAnswers(talkData: any, answers: any[]): string | undefined {
    const lastAnswer = answers?.[answers.length - 1];
    const question = talkData?.questions?.find((q: any) => q.id === lastAnswer?.questionId);
    if (!question?.text) return undefined;
    return this.uiManager.getMySelfTagForQuestionText(question.text, talkData?.language);
  }

  /**
   * docs/TODO.md §JJ residual gap: the talkId of MY OWN talk that taught the memorized answer
   * to this would-be match's last question, if any — mirrors `resolveResponderSelfTagForAnswers`
   * above exactly (same "last question" heuristic, same chatbot-memory lookup shape). Lets
   * `submitTalkResponsePairDirect` narrow `recordMyDealTalkForConversation` to a specific one
   * of my own deal-eligible talks even when THIS match was formed by answering someone ELSE's
   * incoming talk (as a responder) rather than by someone answering my own talk (as an author) —
   * the case `recordMyDealTalkForConversation`'s other call sites already cover. Undefined when
   * I have no such memory (e.g. I've never self-answered a talk of my own with this exact
   * question text), in which case `maybeFinalizeConfirmedDeal` keeps its existing "disable every
   * active listing" fallback.
   */
  private resolveResponderSourceTalkIdForAnswers(talkData: any, answers: any[]): string | undefined {
    const lastAnswer = answers?.[answers.length - 1];
    const question = talkData?.questions?.find((q: any) => q.id === lastAnswer?.questionId);
    if (!question?.text) return undefined;
    return this.uiManager.getMySourceTalkIdForQuestionText(question.text, talkData?.language);
  }

  /** Resolve full talk using the receiver-owned local incoming-talk index. */
  private async loadFullTalkViaIncomingIdentity(identityKey: string): Promise<Talk | null> {
    if (!this.currentUser?.id) return null;
    try {
      const clusters = await collectLocalIncomingTalkClusters(this.gunService, this.currentUser.id, this.p2pRuntimeFlags);
      if (!Array.isArray(clusters)) return null;
      const cluster = clusters.find(
        (c: any) =>
          c &&
          (c.identityKey === identityKey ||
            (c.identityAliases &&
              typeof c.identityAliases === 'object' &&
              c.identityAliases[identityKey])),
      );
      if (!cluster) return null;
      const latestTalkId = pickLatestTalkIdFromIncomingCluster(cluster);
      if (!latestTalkId) return null;
      const cached = this.peerMeshService?.getCachedTalkBody(latestTalkId);
      if (cached) return cached as unknown as Talk;
      if (cluster.questionsJson) {
        try {
          const sender = Object.values(cluster.senders || {}).find(
            (candidate: any) => candidate?.lastTalkId === latestTalkId,
          ) as any;
          return {
            id: latestTalkId,
            title: cluster.title,
            type: cluster.type,
            language: cluster.language,
            authorId: sender?.senderId || '',
            authorName: sender?.senderName || 'Unknown',
            questions: JSON.parse(cluster.questionsJson),
            isAdult: false,
            tags: [],
            createdAt: new Date(cluster.updatedAt),
            isTemplate: false,
            usageCount: 0,
          } as unknown as Talk;
        } catch {
          /* retry the legacy lookup below */
        }
      }
      return this.talkService.getTalkWithRetry(latestTalkId);
    } catch {
      return null;
    }
  }

  private subscribeToIncomingTalks(): void {
    if (!this.currentUser) return;
    console.log('👂 Subscribing to incoming talk clusters for:', this.currentUser.id);

    this.incomingTalkClusterUnsubscribe?.();
    this.incomingTalkClusterUnsubscribe = subscribeLocalIncomingTalkClusters(
      this.gunService,
      this.currentUser.id,
      this.p2pRuntimeFlags,
      (cluster: any, id: string) => {
        if (!cluster || !id || id.startsWith('_')) return;
        if (this.incomingApiRefreshTimer) clearTimeout(this.incomingApiRefreshTimer);
        this.incomingApiRefreshTimer = setTimeout(() => {
          void this.refreshIncomingTalkClustersFromLocalGun();
        }, 120);
      },
    );
  }

  /**
   * E2E: deliver pending OUT talks using the same register + Gun path as Broadcast, without the audience modal.
   */
  /** "Sent to N of M" (§3.7): count delivery acks from the people on my list as the loop reaches them. */
  private startNearbyBroadcastProgress(chatroomId: string, talkIds: readonly string[], total: number, recipientIds: readonly string[]): void {
    // Nearby and Places are both my list (§3.7 "Places in v3"): the same progress applies.
    if (!isPersonalListScope(chatroomId)) return;
    if (total > 0) this.uiManager.animateMapBroadcast('outgoing', this.currentUser?.id, chatroomId, recipientIds);
    const previous = this.nearbyBroadcast;
    // The same Talks again (a retried send of the same broadcast) keep the deliveries counted so far.
    const same = previous && previous.talkIds.size === talkIds.length && talkIds.every((id) => previous.talkIds.has(id));
    this.nearbyBroadcast = same ? { ...previous, total } : { talkIds: new Set(talkIds), total, delivered: new Set() };
    const delivered = this.nearbyBroadcast.delivered.size;
    this.uiManager.setNearbyBroadcastProgress({ sent: Math.min(delivered, total), total });
  }

  /** One person acknowledged a Talk of the latest Nearby broadcast (outbox ack or a live send's ack). */
  private noteNearbyDelivery(talkId: string, recipientUserId: string): void {
    const progress = this.nearbyBroadcast;
    if (!progress?.talkIds.has(talkId) || progress.delivered.has(recipientUserId)) return;
    progress.delivered.add(recipientUserId);
    this.uiManager.setNearbyBroadcastProgress({ sent: Math.min(progress.delivered.size, progress.total), total: progress.total });
  }

  public async deliverPendingBroadcastTalksForE2e(
    minReceivers = 1,
    opts: {
      skipAudiencePreview?: boolean;
      skipDeliveryAcks?: boolean;
      receiverUsers?: Array<{ userId: string; stageName?: string }>;
    } = {},
  ): Promise<{ talksSent: number; receivers: number }> {
    if (!this.currentUser) throw new Error('App not ready for E2E broadcast delivery');
    const chatroomId = this.chatroomService.getCurrentChatroomId();
    if (!chatroomId) throw new Error('No current chatroom for E2E broadcast delivery');
    const members = this.uiManager.getCurrentChatroomMembers();
    let broadcastableIds = this.uiManager.getBroadcastableTalkIds();
    if (broadcastableIds.length === 0) {
      broadcastableIds = this.uiManager.getPendingBroadcastTalkIds();
    }
    const receivers = Array.isArray(opts.receiverUsers) && opts.receiverUsers.length > 0
      ? opts.receiverUsers
          .filter((user) => user.userId && user.userId !== this.currentUser!.id && user.userId !== TECHSUPPORT_ROOT_USER_ID)
          .map((user) => ({ userId: user.userId, stageName: user.stageName || user.userId }))
      : await this.resolveBroadcastReceivers(chatroomId, members);
    if (receivers.length < minReceivers && !(minReceivers === 0 && receivers.length === 0)) {
      throw new Error(`receiverIds=${receivers.length} room=${chatroomId}`);
    }
    const targetCount = receivers.length;
    if (!Array.isArray(broadcastableIds) || broadcastableIds.length === 0) {
      this.uiManager.setBroadcastBulkAck(0, targetCount);
      return { talksSent: 0, receivers: targetCount };
    }
    // Same as the Broadcast button: these Talks are now owed to anyone who links into this room.
    recordTalksBroadcastInRoom(chatroomId, broadcastableIds, Object.keys(getMyTalks()));
    const activeCollection = this.ensureMatchCollectionSession(chatroomId);
    const collectionLocation = activeCollection
      ? await this.matchCollectionSendLocation(chatroomId)
      : undefined;
    const peerPoints = activeCollection
      ? Object.fromEntries(this.nearbyV3?.service.entries().map((entry) => [entry.card.userId, entry.card.point]) ?? [])
      : undefined;
    recordMatchCollectionDelivery({
      talkIds: broadcastableIds,
      peerIds: receivers.map((receiver) => receiver.userId),
      ...(collectionLocation ? { location: collectionLocation } : {}),
      ...(peerPoints ? { peerPoints } : {}),
    });
    this.syncNativeBackgroundAgent();
    this.startNearbyBroadcastProgress(chatroomId, broadcastableIds, targetCount, receivers.map((receiver) => receiver.userId));
    const releaseInFlight = this.markTalkBroadcastsInFlight(broadcastableIds);
    try {
      let sent = 0;
      const talkPayloads: Array<{ tid: string; talk: Talk }> = [];
      for (const talkId of broadcastableIds) {
        let talk = this.uiManager.getBroadcastTalkPayload(talkId);
        if (!talk) {
          talk = await this.talkService.getTalkWithRetry(talkId, { attempts: 15, gapMs: 100 });
        }
        if (!talk) continue;
        const tid = String(talk.id || talkId);
        talk = { ...talk, id: tid, authorId: talk.authorId || this.currentUser.id };
        if (this.isTalkExpiredForDelivery(talk)) continue;
        talkPayloads.push({ tid, talk: talk as Talk });
      }
      if (talkPayloads.length === 0) {
        throw new Error('no talk payloads for E2E broadcast delivery');
      }
      if (receivers.length === 0) {
        const attempted = talkPayloads
          .filter(({ tid }) => this.uiManager.getBroadcastableTalkIds().includes(tid))
          .length;
        this.uiManager.setBroadcastBulkAck(attempted, 0);
        return { talksSent: attempted, receivers: 0 };
      }
      // Mesh delivery fans out to resolved receivers directly; no server preview needed.
      const previews: BroadcastAudiencePreview[] = [];
      const previewByTalkId = new Map(previews.map((p) => [p.talkId, p]));
      const REGISTER_BATCH = 5;
      for (let i = 0; i < talkPayloads.length; i += REGISTER_BATCH) {
        const batch = talkPayloads.slice(i, i + REGISTER_BATCH);
        const batchResults = await Promise.all(
          batch.map(async ({ tid, talk }) => {
            const preview = previewByTalkId.get(tid);
            const eligibleIds =
              usesMeshTalkDelivery(this.p2pRuntimeFlags) || preview?.previewUnavailable || !Array.isArray(preview?.eligibleReceiverIds)
                ? undefined
                : preview.eligibleReceiverIds;
            return this.deliverTalkToReceiversOverMesh(
              tid,
              talk,
              receivers,
              eligibleIds,
              { skipAcknowledgements: opts.skipDeliveryAcks === true },
            );
          }),
        );
        sent += batchResults.filter(Boolean).length;
      }
      this.uiManager.setBroadcastBulkAck(sent, targetCount);
      return { talksSent: sent, receivers: targetCount };
    } finally {
      releaseInFlight();
    }
  }

  /**
   * E2E: await the same IN-list merge as the Talks tab. The tab emits
   * `needIncomingTalkClusters` without awaiting; Playwright needs a promise-bound sync.
   */
  public async syncIncomingClustersFromServer(): Promise<void> {
    // R-a step 7: Gun p2pMeshTalkBodies/* rendezvous path removed; clusters arrive via mesh or mailbox drain.
    // A mesh send can fall back to the encrypted mailbox when the receiver's DataChannel
    // is not ready yet.  The periodic drain is deliberately asynchronous, so an explicit
    // E2E sync must drain first; otherwise it can repeatedly observe the old local Gun
    // snapshot while the correctly delivered envelope is still waiting on the server.
    await this.drainMailbox();
    await this.refreshIncomingTalkClustersFromLocalGun();
    if (this.e2eSeededIncomingClusters.length > 0) {
      this.mergeIncomingClusterIntoUi(this.e2eSeededIncomingClusters);
    }
  }

  /**
   * Step 7: setSkipDirectTalkStatsForE2e removed — client stats POST deleted.
   * Step 8 E2E hook: set quota unlimited for high-fanout rebroadcast specs.
   */
  public setTalkLedgerQuotaUnlimitedForE2e(unlimited: boolean): void {
    setTalkLedgerQuotaUnlimited(unlimited === true);
  }

  public setTalkLedgerSuppressionDisabledForE2e(disabled: boolean): void {
    this.talkLedgerSuppressionDisabledForE2e = disabled === true;
  }

  public setMailboxFallbackDisabledForE2e(disabled: boolean): void {
    this.mailboxFallbackDisabledForE2e = disabled === true;
  }

  public getFetchedAttachmentBytesLengthForE2e(cid: string): number {
    const bytes = this.fetchedAttachmentBytesByCid.get(String(cid || '').trim());
    return bytes ? bytes.length : 0;
  }

  /**
   * Step 8 E2E read helper: return the current talkLedger doc for assertions.
   */
  public getTalkLedgerDocForE2e(): unknown {
    return getTalkLedgerDoc();
  }

  /**
   * docs/TODO.md §W Gap 2 E2E hook: write a `sent` ledger entry directly, for tests that need
   * to simulate "a send already happened" without driving a full mesh delivery. Computes the
   * identityKey the same way `deliverTalkToReceiversOverMesh` does (`buildTalkIdentityKey`) so
   * a test-written entry suppresses exactly what a real send would.
   */
  public async markTalkSentToPeerForE2e(params: { peerId: string; talkId: string; talkData: unknown; sentAt?: string }): Promise<void> {
    await markTalkSentToPeer({
      peerId: params.peerId,
      talkId: params.talkId,
      identityKey: buildTalkIdentityKey(params.talkData),
      ...(params.sentAt ? { sentAt: params.sentAt } : {}),
    });
  }

  /**
   * TODO §S Item 7 E2E hooks: drive real interaction-ledger + message activity fast
   * enough (via direct service calls, not hundreds of real UI actions) to actually cross
   * LEDGER_CHECKPOINT_INTERVAL/LEDGER_RETENTION_WINDOW and MESSAGE_CHECKPOINT_INTERVAL/
   * MESSAGE_RETENTION_WINDOW in a real browser, so the design note's own numbered
   * requirements (checkpoint+prune actually fires; old nodes gone + checkpoint valid; an
   * offline-during-pruning peer still catches up; message history still renders correctly
   * up to the retention window) can be proven end to end rather than only at the unit
   * level. Mirrors the existing setTalkLedgerQuotaUnlimitedForE2e-style narrow test hooks
   * above — never a raw exposure of the whole service instance.
   */
  public async appendLedgerEventsForE2e(count: number): Promise<void> {
    if (!this.ledgerService) return;
    for (let i = 0; i < count; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await this.ledgerService.appendEvent(InteractionKind.TALK_CREATED, {
        talkId: `e2e-ledger-fill-${i}-${Date.now()}`,
        title: `E2E ledger fill ${i}`,
        type: 'flow',
        language: 'en',
      });
    }
  }

  public getLedgerStateForE2e(): Record<string, number> {
    return this.ledgerService?.getState() ?? {};
  }

  public async isLedgerRawEventPresentForE2e(seq: number): Promise<boolean> {
    if (!this.ledgerService || !this.currentUser?.id) return false;
    const event = await this.ledgerService.getEventBySeq(this.currentUser.id, seq);
    return event !== null;
  }

  public async pushLedgerSyncToPeerForE2e(peerId: string): Promise<void> {
    const session = listP2PSessions().find(
      (candidate) => candidate.getOtherUserId() === peerId && candidate.hasLedgerHooks(),
    );
    session?.synchronizeLedger();
  }

  /**
   * Reads the checkpoint event at `checkpointEventSeq` (the caller computes this from the
   * known LEDGER_CHECKPOINT_INTERVAL cadence — a checkpoint covering seqs 1..N lands at
   * seq N+1) and confirms it verifies via the service's own verifyEvent, not a
   * re-implementation. Returns null if no such event exists yet.
   */
  public async getLedgerCheckpointVerifiedForE2e(
    checkpointEventSeq: number,
  ): Promise<{ verified: boolean; count: number; rangeStart: number; rangeEnd: number } | null> {
    if (!this.ledgerService || !this.currentUser?.id) return null;
    const event = await this.ledgerService.getEventBySeq(this.currentUser.id, checkpointEventSeq);
    if (!event || event.kind !== InteractionKind.CHECKPOINT_CREATED) return null;
    const verified = await this.ledgerService.verifyEvent(event);
    const content = event.content as { count: number; rangeStart: number; rangeEnd: number };
    return { verified, count: content.count, rangeStart: content.rangeStart, rangeEnd: content.rangeEnd };
  }

  public async sendConversationMessagesForE2e(
    conversationId: string,
    otherUserId: string,
    count: number,
    textPrefix: string,
  ): Promise<void> {
    if (!this.currentUser?.id) return;
    for (let i = 0; i < count; i += 1) {
      // Explicit, predictable messageId — the wire's own Gun key is otherwise a random/
      // timestamp id unrelated to send order, and its `text` field is SEA ciphertext for
      // direct-p2p, so a caller has no other reliable way to identify "the Nth message
      // sent" from outside the running app (e.g. via a raw Gun snapshot in a test).
      // eslint-disable-next-line no-await-in-loop
      await this.conversationService.sendMessage(conversationId, this.currentUser.id, `${textPrefix}-${i}`, {
        otherUserId,
        messageId: `${textPrefix}-${i}`,
      });
    }
  }

  public async seedIncomingTagTalkForE2e(params: {
    keyword: string;
    senderId: string;
    senderName: string;
  }): Promise<void> {
    await this.seedIncomingTagTalksForE2e([params]);
  }

  public async seedIncomingTagTalksForE2e(paramsList: Array<{
    keyword: string;
    senderId: string;
    senderName: string;
  }>): Promise<void> {
    if (!this.currentUser?.id) return;
    const items = paramsList.flatMap((params) => {
      const keyword = String(params.keyword || '').trim();
      const senderId = String(params.senderId || '').trim();
      if (!keyword || !senderId || senderId === this.currentUser?.id) return [];
      const talkId = `e2e-tag-${senderId}-${keyword.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
      const talkData = {
        id: talkId,
        title: keyword,
        type: 'tag',
        language: 'en',
        authorId: senderId,
        authorName: params.senderName || senderId,
        e2eLocalOnlyReject: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        questions: [
          {
            id: `${talkId}-q`,
            text: keyword,
            answers: [
              { id: 'match', text: 'Match.', isMatch: true },
              { id: 'ignore', text: 'Ignore.', isIgnore: true },
            ],
          },
        ],
      };
      this.e2eSeededTagTalks.set(keyword.toLowerCase(), talkData);
      return [{ talkId, talkData, senderId, senderName: params.senderName || senderId }];
    });
    const clusters = await upsertLocalIncomingTalkClusters(
      this.gunService,
      this.currentUser.id,
      items,
      this.p2pRuntimeFlags,
    );
    this.e2eSeededIncomingClusters.push(...clusters);
    await this.refreshIncomingTalkClustersFromLocalGun();
    this.mergeIncomingClusterIntoUi(clusters);
  }

  /**
   * Generic incoming-talk seed for E2E specs that need a real IN row + real response-modal
   * UI without waiting on a live mesh/WebRTC round trip (e.g. mobile-viewport specs where the
   * point under test is the response dialog, not delivery). Mirrors {@link seedIncomingTagTalkForE2e}
   * but accepts any talk type/shape as-is (flow/route/survey/tag) instead of hardcoding a tag talk.
   * Also caches the talk body on the local mesh service so {@link resolveMeshTalkData} (used by the
   * "View" button's demandFullTalk handler) resolves it without a network round trip.
   */
  public async seedIncomingTalkForE2e(params: {
    talkData: Record<string, unknown>;
    senderId: string;
    senderName: string;
  }): Promise<void> {
    if (!this.currentUser?.id) return;
    const senderId = String(params.senderId || '').trim();
    const talkId = String(params.talkData?.id || '').trim();
    if (!talkId || !senderId || senderId === this.currentUser.id) return;
    const talkData = { ...params.talkData, authorId: senderId, authorName: params.senderName || senderId };
    this.ensurePeerMeshService()?.cacheTalkBody?.(talkId, talkData);
    const cluster = await upsertLocalIncomingTalkCluster(
      this.gunService,
      this.currentUser.id,
      {
        talkId,
        talkData,
        senderId,
        senderName: params.senderName || senderId,
      },
      this.p2pRuntimeFlags,
    );
    this.e2eSeededIncomingClusters.push(cluster);
    await this.refreshIncomingTalkClustersFromLocalGun();
    this.mergeIncomingClusterIntoUi([cluster]);
  }

  public openSeededTagResponseForE2e(keyword: string): boolean {
    const talk = this.e2eSeededTagTalks.get(String(keyword || '').trim().toLowerCase());
    if (!talk) return false;
    this.uiManager.showTalkResponseDialog(talk as Talk, { skipAutoAnswer: true });
    return true;
  }

  private async refreshIncomingTalkClustersFromLocalGun(): Promise<void> {
    if (!this.currentUser?.id) return;
    const clusters = await collectLocalIncomingTalkClusters(this.gunService, this.currentUser.id, this.p2pRuntimeFlags);
    // Just read from local Gun: render only. Mirroring it straight back re-wrote the whole owner
    // envelope once per cluster, which re-fired this refresh's own subscription — a feedback loop
    // measured on hardware at >1 write/s (5.5 KB each, ~16 KB/s of Gun traffic sent to every
    // connected peer, ~10 %/h battery on the Wi-Fi radio).
    this.mergeIncomingClusterIntoUi(clusters, { persist: false });
  }

  private mergeIncomingClusterIntoUi(clusters: any[], options: { persist?: boolean } = {}): void {
    this.applyIncomingTalkClusters(clusters, options);
  }

  private applyIncomingTalkClusters(clusters: any[], options: { persist?: boolean } = {}): void {
    // Replace the UI snapshot with the current local/direct or legacy server cluster list.
    // Spreading incomingClustersMap left stale/non-identity entries and could prevent IN rows from matching.
    const next: Record<string, any> = {};
    for (const c of clusters) {
      if (c?.identityKey) {
        next[c.identityKey] = c;
      }
    }
    const list = Object.values(next).filter((c: any) => c && c.identityKey);
    if (this.currentUser?.id && options.persist !== false) {
      mirrorIncomingTalkClustersToLocalGun(
        this.gunService,
        this.currentUser.id,
        list,
        this.p2pRuntimeFlags,
      );
    }
    this.uiManager.setIncomingTalkClusters(list);
    this.uiManager.displayTalksList();
  }

  /**
   * The talk-independent DM thread with a peer (redesign §5): one conversation per
   * pair with `talkId: 'direct'`, kept separate from per-matched-talk threads so DM
   * and thread messages never leak into each other. The TechSupport pair reuses its
   * dedicated support channel.
   */
  private async findOrCreateDirectConversation(peerId: string, peerName: string): Promise<string> {
    if (!this.currentUser) throw new Error('Not logged in');
    // TechSupport's conversation id is a deterministic constant (conv_support_<root>_<userId>),
    // never the generic conv_pair_ id `createConversation` below would mint. Routing through
    // ensureSupportConversationRecord() (rather than just computing the id string) also avoids
    // a real startup race: the localStorage-lookup fallback below only finds the support
    // conversation once ensureSupportBootstrapForCurrentUser's own boot-time call has finished
    // writing it, and a click on the TechSupport contact card before that write lands used to
    // fall through and create an ordinary conv_pair_ conversation instead — which the server's
    // POST /api/support/messages/:conversationId route always 400s (it only accepts ids that
    // start with conv_support_), even though the message itself still lands fine in Gun. Just
    // returning the computed id string without materializing the record was not enough either:
    // showConversationDetail requires an existing getMyConversations() entry for the id it's
    // given, so a click that outraced the boot-time bootstrap still hit "conversation not
    // found" and silently did nothing. ensureSupportConversationRecord() is idempotent
    // (addNewConversation merges), so calling it again here is a safe no-op once boot has
    // already written the record. This must also cover TechSupport's OWN operator session
    // replying to an ordinary user (this.currentUser.id === TECHSUPPORT_ROOT_USER_ID, peerId is
    // the ordinary user) — not just the ordinary user's own side (peerId === TECHSUPPORT_ROOT_
    // USER_ID) — otherwise the operator's own findOrCreateDirectConversation call falls through
    // to the generic conv_pair_/direct-p2p path below and the two sides end up in two different,
    // disconnected conversations that neither one ever sees the other's messages in.
    if (peerId === TECHSUPPORT_ROOT_USER_ID || this.currentUser.id === TECHSUPPORT_ROOT_USER_ID) {
      return this.ensureSupportConversationRecord(peerId, peerName);
    }
    const conversations = getMyConversations();
    const existing = Object.entries(conversations).find(
      ([, conv]: [string, any]) =>
        conv?.otherUserId === peerId && (conv?.supportChannel === true || conv?.talkId === 'direct'),
    );
    if (existing) return existing[0];
    const conversationId = await this.conversationService.createConversation({
      userId1: this.currentUser.id,
      userName1: this.currentUser.stageName,
      userId2: peerId,
      userName2: peerName,
      talkId: 'direct',
    });
    await this.ingestConversationRecords([{
      conversationId,
      otherUserId: peerId,
      otherUserName: peerName,
      talkId: 'direct',
      createdAt: new Date().toISOString(),
    }]);
    return conversationId;
  }

  private async ingestConversationRecords(conversations: any[]): Promise<void> {
    if (!this.currentUser) return;

    for (const conversationData of conversations) {
      // Gun stores otherUserId/otherUserName; legacy or other sources may use userId1/userId2
      const otherUserId =
        conversationData.otherUserId ??
        (conversationData.userId1 === this.currentUser.id
          ? conversationData.userId2
          : conversationData.userId1);
      const otherUserName =
        conversationData.otherUserName ??
        (conversationData.userId1 === this.currentUser.id
          ? conversationData.userName2
          : conversationData.userName1);

      // Never ingest a conversation whose other participant resolves to the local user.
      // A content-collision (two authors, identical content-addressed talkId) can produce
      // a `conv_<a>_<b>_<talkId>` record whose participants array, read back from the
      // shared Gun node, leaves otherUserId ambiguous; guarding self here keeps the user
      // out of their own contacts list.
      if (!otherUserId || otherUserId === this.currentUser.id) continue;

      let resolvedOtherUserName = otherUserName ?? 'Unknown';
      if (
        !resolvedOtherUserName ||
        resolvedOtherUserName === 'Unknown' ||
        resolvedOtherUserName === 'Someone'
      ) {
        try {
          const publicUser = await this.gunService.getPublicUser(otherUserId);
          if (publicUser?.stageName) {
            resolvedOtherUserName = publicUser.stageName;
          }
        } catch {
          /* keep placeholder */
        }
        if (
          !resolvedOtherUserName ||
          resolvedOtherUserName === 'Unknown' ||
          resolvedOtherUserName === 'Someone'
        ) {
          try {
            const fullUser = await this.userService.getUser(otherUserId);
            if (fullUser?.stageName) {
              resolvedOtherUserName = fullUser.stageName;
            }
          } catch {
            /* keep placeholder */
          }
        }
      }

      this.uiManager.addNewConversation({
        conversationId: conversationData.conversationId,
        otherUserId,
        otherUserName: resolvedOtherUserName,
        talkId: conversationData.talkId,
        relatedTalkIds: conversationData.relatedTalkIds,
        relatedTalkIdsJson: conversationData.relatedTalkIdsJson,
        respondedByBot: conversationData.respondedByBot,
        supportChannel: conversationData.supportChannel,
        transportMode:
          conversationData.transportMode ?? this.conversationService.getTransportMode(),
        transportFallbackReason: conversationData.transportFallbackReason,
        dealEligible: conversationData.dealEligible === true,
        ...(conversationData.matchScore !== undefined ? { matchScore: conversationData.matchScore } : {}),
        ...(conversationData.matchTotal !== undefined ? { matchTotal: conversationData.matchTotal } : {}),
      });

      if (typeof conversationData.dealConfirmedByJson === 'string') {
        try {
          const dealConfirmedBy = JSON.parse(conversationData.dealConfirmedByJson);
          if (Array.isArray(dealConfirmedBy)) {
            this.uiManager.applyDealConfirmedBy(conversationData.conversationId, dealConfirmedBy);
            this.maybeFinalizeConfirmedDeal(dealConfirmedBy, otherUserId, conversationData.conversationId);
          }
        } catch {
          /* malformed sync payload — ignore, next sync tick will retry */
        }
      }
    }
  }

  private subscribeToUserConversations(): void {
    if (!this.currentUser) return;

    console.log('💬 Subscribing to user conversations for:', this.currentUser.id);

    this.conversationService.subscribeToUserConversations(
      this.currentUser.id,
      async (conversations) => {
        console.log('📨 New conversations detected:', conversations);
        await this.ingestConversationRecords(conversations);
      },
    );
  }

  private ensureConversationPreviewSubscription(conversationId: string): void {
    if (!this.currentUser || this.conversationPreviewUnsubscribers.has(conversationId)) return;
    const conversation = this.uiManager.getMyConversations()[conversationId];
    if (!conversation?.otherUserId) return;
    const unsubscribe = this.conversationService.subscribeToMessages(
      conversationId,
      (messages) => {
        this.uiManager.syncConversationMessageSummary(conversationId, messages, this.currentUser!.id);
        // Runs continuously in the background for every conversation (not just the currently
        // open thread), so this is the one place that reliably observes every message this
        // device ever decrypts for any of its own conversations — see WebDeviceSyncService's
        // own doc comment for why the `messages` device-sync category needs plaintext (already
        // decrypted here) rather than raw Gun ciphertext.
        void this.deviceSyncService.enqueueMessagesForConversation(
          conversationId,
          this.currentUser!.pub || this.currentUser!.id,
          messages.map((message: any): SyncedMessage => ({
            id: String(message.id),
            senderId: String(message.senderId),
            text: String(message.text ?? ''),
            timestamp: (message.timestamp instanceof Date ? message.timestamp : new Date(message.timestamp)).toISOString(),
            ...(message.talkId ? { talkId: String(message.talkId) } : {}),
            ...(message.channel ? { channel: message.channel } : {}),
          })),
        ).catch(() => {});
      },
      this.currentUser.id,
      conversation.otherUserId,
    );
    this.conversationPreviewUnsubscribers.set(conversationId, unsubscribe);
  }

  /**
   * One-shot bounded read of a conversation's currently-decrypted local messages, for
   * WebDeviceSyncService's one-time backfill at link/sync-activation time (see
   * `backfillExistingDataToPeer`). Wraps the ongoing `subscribeToMessages` API — the only message
   * read surface `WebConversationService` exposes — in a promise that resolves on its first
   * callback firing rather than duplicating GunMessageStore's decrypt logic here.
   */
  private snapshotLocalMessagesForSync(conversationId: string, otherUserId: string): Promise<SyncedMessage[]> {
    return new Promise((resolve) => {
      if (!this.currentUser) {
        resolve([]);
        return;
      }
      let settled = false;
      const finish = (messages: SyncedMessage[]) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        unsubscribe();
        resolve(messages);
      };
      const timer = setTimeout(() => finish([]), 5_000);
      const unsubscribe = this.conversationService.subscribeToMessages(
        conversationId,
        (messages) => finish(messages.map((message: any): SyncedMessage => ({
          id: String(message.id),
          senderId: String(message.senderId),
          text: String(message.text ?? ''),
          timestamp: (message.timestamp instanceof Date ? message.timestamp : new Date(message.timestamp)).toISOString(),
          ...(message.talkId ? { talkId: String(message.talkId) } : {}),
          ...(message.channel ? { channel: message.channel } : {}),
        }))),
        this.currentUser.id,
        otherUserId,
      );
    });
  }

  /**
   * Online dots for Contacts, conversations and room members (§3.7 "Contacts online"): the people
   * whose card the hub saw in the last two reissue periods, asked by id, so a contact online
   * anywhere shows online. TechSupport has no Nearby card; it keeps the presence registry.
   */
  private async refreshConversationPresence(): Promise<void> {
    if (!this.currentUser) return;
    const presence = this.presenceClient ?? new P2PPresenceClient({ apiBase: this.getBackendApiBase() });
    try {
      const ids = new Set<string>();
      for (const conversation of Object.values(this.uiManager.getMyConversations() || {})) {
        const otherUserId = String((conversation as { otherUserId?: unknown })?.otherUserId || '');
        if (otherUserId) ids.add(otherUserId);
      }
      for (const known of this.currentUser.knownPeople ?? []) if (known.userId) ids.add(known.userId);
      for (const member of this.uiManager.getCurrentChatroomMembers()) ids.add(member.userId);
      ids.delete(this.currentUser.id);
      ids.delete(TECHSUPPORT_ROOT_USER_ID);
      const [online, peers] = await Promise.all([
        new FetchNearbyV3Hub(() => this.getBackendApiBase()).online([...ids]).catch(() => new Set<string>()),
        presence.fetchNearby(
          this.currentUser.id,
          200,
          this.activeExchangeRoomController?.getScope() ?? undefined,
        ).catch(() => []),
      ]);
      if (peers.some((peer) => peer.userId === TECHSUPPORT_ROOT_USER_ID)) online.add(TECHSUPPORT_ROOT_USER_ID);
      this.uiManager.setConversationOnlineStatus(online);
      // K1 item 3: same real-presence signal, kept fresh whenever we already refresh for
      // conversations rather than a separate poll loop.
      this.uiManager.setTechSupportOnlineStatus(online.has(TECHSUPPORT_ROOT_USER_ID));
    } catch {
      // Presence is supplemental list metadata; leave the existing state intact on failure.
    }
  }

  private refreshStatusBar(): void {
    const chatroomId = this.currentChatroomId || this.chatroomService.getCurrentChatroomId();
    if (!chatroomId || !this.currentUser) return;
    const chatroomName = this.getChatroomDisplayName(chatroomId);
    const memberCount = this.uiManager.getChatroomMemberCount(chatroomId) || 0;
    this.uiManager.updateStatusBar(
      this.currentUser.stageName,
      chatroomName,
      memberCount,
      this.uiManager.getTotalMatches(),
    );
  }

  private setupEventHandlers(): void {
    // Handle UI events

    this.uiManager.on('contactsCapChanged', () => this.scheduleContactsPrune(0));
    this.uiManager.on('connectivitySettingsChanged', (settings: ConnectivitySettings) => {
      const mesh = this.ensurePeerMeshService();
      mesh?.updateForwardingSettings(settings.forwarding);
      if (mesh) this.uiManager.updateConnectivityDiagnostics(mesh.getForwardingDiagnostics());
      const bridge = readNearbyOfflineBridge();
      const scope = this.activeExchangeRoomController?.getScope();
      if (bridge && scope) bridge.setNearbyExchangeMode?.(settings.nearbyMode, scope.roomId);
      if (settings.nearbyMode === 'off') {
        this.nearbyOfflineService?.dispose();
        this.nearbyOfflineService = null;
      } else if (!this.nearbyOfflineService) {
        const pub = this.gunService.getStoredPair()?.pub;
        if (pub) this.initNearbyOffline(String(pub));
      }
      void this.applyNearbyPrivacyMode(settings).catch((error) => {
        this.uiManager.showNotification(`Could not change nearby audience: ${(error as Error).message}`, 'error');
      });
      this.syncNativeBackgroundAgent();
    });

    this.uiManager.on('conversationAdded', (data: { conversationId: string }) => {
      this.refreshStatusBar();
      this.ensureConversationPreviewSubscription(data.conversationId);
      void this.refreshConversationPresence();
      const conversation = this.uiManager.getMyConversations()[data.conversationId];
      // supportChannel excluded — see getLocalConversationsSnapshot's identical filter above for why.
      if (conversation && conversation.supportChannel !== true && this.currentUser) {
        void this.deviceSyncService.enqueueConversationChange(
          this.currentUser.pub || this.currentUser.id,
          { ...conversation, conversationId: data.conversationId },
        ).catch(() => {});
      }
    });

    // Spec §30.2 deal confirmation: a match on a deal-eligible (Pair-tag) talk isn't exclusive
    // on its own (several compatible candidates can each hold an open conversation) — the
    // talk only disables, and other open candidates get marked "no longer available" on this
    // device, once BOTH participants have confirmed here.
    this.uiManager.on('confirmDeal', async (data: { conversationId: string }) => {
      if (!this.currentUser?.id) return;
      const conversation = this.uiManager.getMyConversations()[data.conversationId] as any;
      if (!conversation) return;
      const dealConfirmedBy = await this.conversationService.confirmDeal(data.conversationId, this.currentUser.id);
      this.uiManager.applyDealConfirmedBy(data.conversationId, dealConfirmedBy);
      this.maybeFinalizeConfirmedDeal(dealConfirmedBy, conversation.otherUserId, data.conversationId);
    });

    this.uiManager.on('updateTalkFilters', async (filters: any) => {
      if (!this.currentUser) return;
      this.currentUser.talkFilters = filters;
      this.writeCachedUser(this.currentUser);
      try {
        await this.userService.updateTalkFilters(this.currentUser.id, filters);
      } catch (error) {
        console.warn('Failed to persist talk filters:', error);
      }
    });

    this.uiManager.on('setCreditVisibility', async (data: { visible: boolean }) => {
      if (!this.currentUser) return;
      this.currentUser.reputation.isHidden = !data.visible;
      try {
        await this.userService.updateReputationVisibility(this.currentUser.id, !data.visible);
      } catch (error) {
        console.warn('Failed to persist credit visibility:', error);
      }
    });

    this.uiManager.on(
      'saveKnownPerson',
      async (data: { userId: string; labels: any[]; nickname?: string; customLabel?: string; rating?: number; notes?: string }) => {
        if (!this.currentUser) return;
        try {
          const extras = {
            ...(data.customLabel ? { customLabel: data.customLabel } : {}),
            ...(typeof data.rating === 'number' ? { rating: data.rating } : {}),
            ...(data.notes ? { notes: data.notes } : {}),
          };
          const existing = this.currentUser.knownPeople?.find((known) => known.userId === data.userId);
          const target = await this.userService.getUser(data.userId);
          if (existing?.identityPub && target.pub && existing.identityPub !== target.pub) {
            throw new Error('Contact identity key changed; remove and verify the contact before adding again');
          }
          const liveIdentityPub = listP2PSessions().find((session) => session.getOtherUserId() === data.userId)?.otherPub;
          const identityPub = existing?.identityPub ?? target.pub ?? liveIdentityPub;
          if (!identityPub) throw new Error('Cannot add Contact until the peer identity is verified');
          const identityEpub = existing?.identityEpub ?? target.epub;
          await this.userService.addKnownPerson(
            this.currentUser.id,
            data.userId,
            data.labels,
            data.nickname,
            {
              ...extras,
              identityPub,
              ...(identityEpub ? { identityEpub } : {}),
            },
          );
          const pair = this.gunService.getStoredPair();
          if (pair?.pub && pair?.priv && target.pub) {
            const friendMark = await createSignedFriendMark({
              markerUserId: this.currentUser.id,
              targetUserId: data.userId,
              targetPub: target.pub,
              active: data.labels.includes('friend'),
              pair,
            });
            await this.userService.setFriendMark(friendMark);
          }
          const currentEntry = this.currentUser.knownPeople?.find((known) => known.userId === data.userId);
          if (currentEntry) {
            currentEntry.identityPub = identityPub;
            if (identityEpub) currentEntry.identityEpub = identityEpub;
            currentEntry.identityPinnedAt ??= new Date();
          }
          // "Block is a status, not a one-time notification" — a contact added or relabeled
          // into a group scope this account already shared a block with catches up now.
          void this.resendSharedBlockSignalsToNewContact({
            userId: data.userId,
            labels: data.labels,
            addedAt: new Date(),
            ...extras,
          }).catch((err) => console.warn('[BlockSignal] catch-up on saveKnownPerson failed:', err));
        } catch (error) {
          console.warn('Failed to save known person:', error);
        }
      },
    );

    this.uiManager.on('submitPeerReview', async (data: { userId: string; rating: number }) => {
      try {
        await this.userService.submitPeerReview(data.userId, data.rating);
      } catch (error) {
        console.warn('Failed to submit peer review:', error);
      }
    });

    this.uiManager.on('vouchAgeVerified', async (data: { userId: string }) => {
      try {
        await this.userService.vouchAgeVerified(data.userId);
        this.uiManager.showNotification(this.uiManager.formatAgeVoteSubmitted(), 'success');
      } catch (error) {
        console.warn('Failed to vouch age:', error);
      }
    });

    this.uiManager.on(
      'setUserBlocked',
      async (data: {
        userId: string;
        blocked: boolean;
        notify?: { groupId: string; recipientUserIds: string[] };
      }) => {
        if (!this.currentUser) return;
        try {
          const blockedUserIds = data.blocked
            ? await this.userService.blockUser(this.currentUser.id, data.userId)
            : await this.userService.unblockUser(this.currentUser.id, data.userId);
          this.blockStatusCache.invalidate(data.userId);
          this.currentUser.blockedUserIds = blockedUserIds;
          this.writeCachedUser(this.currentUser);
          this.uiManager.adoptSessionUser(this.currentUser);
          this.uiManager.showNotification(
            this.uiManager.formatUserBlockChanged(data.blocked),
            'success',
          );
          // Opt-in friend-circle notification (src/shared/block-signal.ts) — the UI layer
          // already resolved which group and current members to notify; skipped entirely
          // when the blocker declined or when unblocking.
          if (data.blocked && data.notify && data.notify.recipientUserIds.length > 0) {
            const { groupId, recipientUserIds } = data.notify;
            void this.fanOutBlockSignal(data.userId, groupId, recipientUserIds).catch((err) => {
              console.warn('[BlockSignal] fan-out failed:', err);
            });
          }
        } catch (error) {
          console.warn('Failed to update block state:', error);
        }
      },
    );

    // Handle stage name changes
    this.uiManager.onStageNameChange = async (userId: string, newStageName: string) => {
      try {
        await this.userService.updateStageName(userId, newStageName);

        // Update current user object
        if (this.currentUser && this.currentUser.id === userId) {
          this.currentUser.stageName = newStageName;
          this.writeCachedUser(this.currentUser);
          const pair = this.gunService.getStoredPair();
          await this.userService.syncPublicUserForRelay({
            ...this.currentUser,
            ...(pair?.pub ? { pub: pair.pub } : {}),
            ...(pair?.epub ? { epub: pair.epub } : {}),
          });
          // Refresh the UI to show the new name
          this.uiManager.showMainInterface(this.currentUser);
          await this.renderSupportGreeting({ notify: false });

          // Nearby: my card carries my name; reissue it and tell the nearest person now.
          this.nearbyV3?.refresh();
          // Update the stage name in the current Place so others can see it
          if (this.currentChatroomId && !hasNoHubRoster(this.currentChatroomId)) {
            const gun = this.gunService.getGun();
            gun.get('chatrooms').get(this.currentChatroomId).get('users').get(userId).put({
              stageName: newStageName,
            });
            // Restart the membership heartbeat with the new name — its ~30s beats re-put the
            // member record with the name captured at heartbeat start and would otherwise
            // clobber this write back to the old name (peers' rosters then stay stale forever).
            this.chatroomService.refreshMembershipStageName(this.currentChatroomId, userId, newStageName);
            await fetch(
              `${this.getBackendApiBase()}/api/chatrooms/${encodeURIComponent(this.currentChatroomId)}/members/${encodeURIComponent(userId)}`,
              {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ stageName: newStageName, lastSeen: new Date().toISOString() }),
              },
            ).catch(() => undefined);
          }
          this.refreshStatusBar();
        }

        this.uiManager.showNotification(this.uiManager.formatStageNameUpdated(), 'success');
      } catch (error) {
        console.error('Failed to update stage name:', error);
        throw error;
      }
    };

    this.uiManager.onProfileChange = async (
      userId: string,
      updates: {
        headshot?: string;
        languages: string[];
        profile: Array<{ id: string; question: string; answer: string; isAuto: boolean; answeredAt: Date }>;
        interests: Tag[];
      },
    ) => {
      try {
        const previousTalkFilters = this.currentUser?.talkFilters;
        const updatedUser = await this.userService.updateProfileFoundation(userId, updates);
        if (this.currentUser && this.currentUser.id === userId) {
          this.currentUser = {
            ...updatedUser,
            ...(previousTalkFilters ? { talkFilters: previousTalkFilters } : {}),
          };
          this.writeCachedUser(this.currentUser);
          this.uiManager.showMainInterface(this.currentUser);
          this.refreshStatusBar();
        }
        this.uiManager.showNotification(this.uiManager.formatProfileUpdated(), 'success');
      } catch (error) {
        console.error('Failed to update profile foundation:', error);
        throw error;
      }
    };

    // Handle "Send Talk" button click on user - now opens conversation if exists
    this.uiManager.on('sendTalkToUser', async (data: { userId: string }) => {
      console.log('👤 User clicked:', data.userId);

      // Check if conversation exists with this user
      const conversations = getMyConversations();
      const existingEntry = Object.entries(conversations).find(
        ([, conv]: [string, any]) => conv?.otherUserId === data.userId,
      );

      if (existingEntry) {
        const [conversationId] = existingEntry;
        console.log('💬 Opening existing conversation:', conversationId);
        this.uiManager.showConversationDetail(conversationId);
      } else {
        // No conversation yet - show notification
        this.uiManager.showNotification(
          this.uiManager.formatMatchToStartConversation(),
          'info',
        );
      }
    });

    this.uiManager.on(
      'createTalk',
      async (
        talkData: Partial<Talk> & {
          selfAnswers?: Array<{ questionId: string; answerId: string }>;
          mediaFile?: File;
          // docs/TODO.md §Y1 — set only when the Talk Editor was opened on a copied-but-not-
          // yet-owned talk and the user made a real content edit. Routes through the same
          // revise-mints-new-id + originalAuthorId-transfer path §V built for DM shorthand,
          // instead of an ordinary from-scratch talk.
          reviseSourceTalk?: Talk;
        },
      ) => {
      try {
        console.log('📝 Creating talk:', talkData);

        // Optional media: upload the file to IPFS and attach it so the link is auto-shared
        // to each matcher. Bytes never ride the talk broadcast — only the link does.
        const { mediaFile, reviseSourceTalk, ...talkFields } = talkData;
        let ipfsAttachments = talkFields.ipfsAttachments;
        if (mediaFile) {
          this.uiManager.showNotification(this.uiManager.formatMediaShareUploading(mediaFile.name), 'info');
          const pendingTalkId = `talk-media:${this.currentUser!.id}:${Date.now()}`;
          const attachment = await this.publishMediaFileToIpfs(mediaFile, pendingTalkId);
          ipfsAttachments = [...(ipfsAttachments ?? []), attachment];
        }

        // TODO §X: blurred by default (docs/TODO.md) — never the precise coordinate.
        const authorLocation = this.confirmedLocationForTalks();

        // Create the talk
        const talk = reviseSourceTalk
          ? await this.talkService.createTalk({
              ...buildRevisedTalkDraft(reviseSourceTalk, talkFields.questions ?? [], this.currentUser!.id, {
                ...(talkFields.title !== undefined ? { title: talkFields.title } : {}),
                ...(talkFields.type !== undefined ? { type: talkFields.type } : {}),
                ...(talkFields.language !== undefined ? { language: talkFields.language } : {}),
                ...(talkFields.tags !== undefined ? { tags: talkFields.tags } : {}),
                ...(talkFields.isAdult !== undefined ? { isAdult: talkFields.isAdult } : {}),
                ...(talkFields.expiresAt !== undefined ? { expiresAt: talkFields.expiresAt } : {}),
                ...(talkFields.locationRadiusMiles !== undefined ? { locationRadiusMiles: talkFields.locationRadiusMiles } : {}),
                ...(talkFields.matchThreshold !== undefined ? { matchThreshold: talkFields.matchThreshold } : {}),
                ...(talkFields.collectionImportance !== undefined ? { collectionImportance: talkFields.collectionImportance } : {}),
              }),
              ...(ipfsAttachments ? { ipfsAttachments } : {}),
              ...(authorLocation ? { authorLocation } : {}),
            })
          : await this.talkService.createTalk({
              ...talkFields,
              ...(ipfsAttachments ? { ipfsAttachments } : {}),
              authorId: this.currentUser!.id,
              ...(authorLocation ? { authorLocation } : {}),
            });

        if (reviseSourceTalk) {
          this.uiManager.applyTalkRevisionPolicy(reviseSourceTalk.id);
        }

        if (Array.isArray(talk.ipfsAttachments) && talk.ipfsAttachments.length > 0) {
          await this.ensureContentNodeInitialized();
          this.contentNodeService.pinTalkAttachments(talk.id, talk.ipfsAttachments);
        }

        console.log('✅ Talk created:', talk);

        // Phase E: ledger hook — TALK_CREATED
        this.ledgerEmit(InteractionKind.TALK_CREATED, {
          talkId: talk.id,
          title: talk.title,
          type: talk.type,
          language: talk.language,
        });

        this.uiManager.saveCreatedTalk(talk, {
          selfAnswers: talkData.selfAnswers ?? [],
        });

        // Deliver to the current room over mesh — fire-and-forget. deliverTalkToReceiversOverMesh
        // already treats delivery as eventually-consistent (its own flood/ack retry loop falls
        // back to an encrypted mailbox for any recipient that doesn't ACK in time — see its own
        // doc comments), so awaiting it here only meant the "talk created" success notification
        // sat blocked for however long that retry loop took, up to ~9s+ of pure ack-waiting per
        // room member before this even reaches the mailbox fallback. The talk is already
        // genuinely created and saved locally the moment createTalk() above resolved; delivering
        // it to the room is a background concern, not a precondition for reporting success.
        const wantSendToChatroom = (talkData as { sendToChatroom?: boolean }).sendToChatroom !== false;
        const chatroomId = this.chatroomService.getCurrentChatroomId();
        if (chatroomId && wantSendToChatroom) {
          recordTalksBroadcastInRoom(chatroomId, [talk.id], Object.keys(getMyTalks()));
          const releaseInFlight = this.markTalkBroadcastsInFlight([talk.id]);
          void (async () => {
            const receivers = await this.resolveBroadcastReceivers(
              chatroomId,
              this.uiManager.getCurrentChatroomMembers(),
            );
            await this.deliverTalkToReceiversOverMesh(talk.id, talk, receivers);
            console.log('📢 Talk broadcasted to chatroom:', chatroomId);
          })().catch((error) => {
            console.warn('Background talk broadcast failed (non-fatal — talk is already saved):', error);
          }).finally(releaseInFlight);
        }

        let createdMode: 'sent' | 'saved-only' | 'needs-room' = 'sent';
        if (!wantSendToChatroom) {
          createdMode = 'saved-only';
        } else if (!chatroomId) {
          createdMode = 'needs-room';
        }
        this.uiManager.showNotification(this.uiManager.formatTalkCreated(createdMode), 'success');
      } catch (error) {
        console.error('Failed to create talk:', error);
        this.uiManager.showNotification(this.uiManager.formatTalkCreateFailed((error as Error).message), 'error');
      }
    });

    // docs/TODO.md §V — Auto Linear Capture: a DM capture session just closed (terminator
    // line, or any non-captured message sent while one was active). Assemble whatever was
    // gathered into a real Talk — a new draft in the plain DM thread, or an appended question
    // on the existing talk if this session started inside that talk's thread. Deliberately
    // does NOT auto-broadcast to the current chatroom (unlike the Talk Editor's `createTalk`
    // handler above) — FR-TK-7's own wording is "a linear talk draft ... to reuse and
    // broadcast later," not immediately, and a DM capture has no chatroom context to begin
    // with.
    this.uiManager.on(
      'finalizeCaptureSession',
      async (data: { conversationId: string; scopeTalkId: string | undefined; lines: string[] }) => {
        try {
          const authorLocation = this.confirmedLocationForTalks();

          if (data.scopeTalkId) {
            // Append case: extend the existing talk's question chain, then mint a new talk
            // (editing-mints-a-new-id policy) and retire the predecessor per the user's
            // delete/keep-disabled preference.
            const oldTalk = await this.talkService.getTalk(data.scopeTalkId);
            if (!oldTalk) return;
            const appended = FlowCapture.buildCapturedQuestions(data.lines, oldTalk.questions.length);
            if (appended.length === 0) return;
            const mergedQuestions = TalkAutofix.fix({
              ...oldTalk,
              questions: [...oldTalk.questions, ...appended],
            }).talk.questions;
            const draft = buildRevisedTalkDraft(oldTalk, mergedQuestions, this.currentUser!.id);
            const newTalk = await this.talkService.createTalk({
              ...draft,
              ...(authorLocation ? { authorLocation } : {}),
            });
            this.uiManager.saveCreatedTalk(newTalk, { selfAnswers: [] });
            this.uiManager.applyTalkRevisionPolicy(data.scopeTalkId);
            this.uiManager.showNotification(this.uiManager.formatCaptureTalkAppended(), 'success');
          } else {
            // New-draft case: the plain DM thread.
            const assembled = FlowCapture.assembleCapturedTalk(data.lines);
            if (!assembled) return;
            const newTalk = await this.talkService.createTalk({
              ...assembled,
              authorId: this.currentUser!.id,
              ...(authorLocation ? { authorLocation } : {}),
            });
            this.uiManager.saveCreatedTalk(newTalk, { selfAnswers: [] });
            this.uiManager.showNotification(this.uiManager.formatCaptureTalkCreated(), 'success');
          }
        } catch (error) {
          console.error('Failed to finalize captured talk:', error);
          this.uiManager.showNotification(this.uiManager.formatTalkCreateFailed((error as Error).message), 'error');
        }
      },
    );

    this.uiManager.on(
      'startMatchCollection',
      (data: { label?: string; scopeId?: string }) => {
        const scopeId = String(data.scopeId || this.chatroomService.getCurrentChatroomId() || '').trim();
        if (!scopeId) return;
        setMatchCollectionEnabled(true);
        const point = this.nearbyPublicPoint();
        startMatchCollectionSession({
          label: String(data.label || this.uiText('matchCollectionDefaultLabel')),
          scopeId,
          talkIds: this.uiManager.getBroadcastableTalkIds(),
          ...(point ? { point } : {}),
        });
        this.syncNativeBackgroundAgent();
        this.uiManager.showChatroomList();
        this.uiManager.showNotification(this.uiText('matchCollectionStarted'), 'success');
      },
    );
    this.uiManager.on('endMatchCollection', () => {
      setMatchCollectionEnabled(false);
      if (!endMatchCollectionSession()) return;
      this.syncNativeBackgroundAgent();
      this.uiManager.showChatroomList();
      this.uiManager.showNotification(this.uiText('matchCollectionEnded'), 'success');
    });
    this.uiManager.on('matchCollectionSettingChanged', (data: { enabled?: boolean }) => {
      const enabled = data.enabled === true;
      setMatchCollectionEnabled(enabled);
      if (enabled) this.ensureMatchCollectionSession(this.chatroomService.getCurrentChatroomId());
      else endMatchCollectionSession();
      this.syncNativeBackgroundAgent();
    });
    this.uiManager.on('broadcastMatchCollection', () => {
      this.uiManager.broadcastTalksFromCurrentRoom();
    });
    this.uiManager.on('reviewMatchCollection', (data: { sessionId?: string }) => {
      const sessionId = String(data.sessionId || '').trim();
      if (!sessionId) return;
      showMatchCollectionReviewDialog({
        sessionId,
        text: (key) => this.uiText(key),
        openPerson: (peerId) => this.uiManager.openUserConversationFirst(peerId, peerId),
      });
    });

    // Broadcast broadcastable OUT talks: server register + Gun announce (current chatroom only)
    this.uiManager.on(
      'broadcastTalk',
      async (data: {
        chatroomId: string;
        members: Array<{ userId: string; stageName: string }>;
        talkIds?: string[];
        /** docs/TODO.md §W Gap 1: per-talk narrower receiver list, when the caller has one. */
        talkReceiverIds?: Record<string, string[]>;
        broadcastTargetTags?: string[];
        broadcastMaxDistanceMiles?: number;
        automatic?: boolean;
        /** Background catch-up to a newly linked same-room peer: no toasts, no bulk-ack change. */
        catchUp?: boolean;
      }) => {
        let releaseInFlight = (): void => {};
        try {
          const chatroomId = data.chatroomId || this.chatroomService.getCurrentChatroomId();
          if (!chatroomId || !this.currentUser) {
            this.uiManager.showNotification(this.uiManager.formatBroadcastNoChatroom(), 'error');
            return;
          }
          const broadcastableIds = Array.isArray(data.talkIds) && data.talkIds.length > 0
            ? data.talkIds
            : this.uiManager.getBroadcastableTalkIds();
          console.log(`📢 broadcastTalk: ${broadcastableIds.length} broadcastable ids, members=${data.members?.length ?? 0}`);
          if (broadcastableIds.length === 0) {
            // UI already shows this notification when broadcastableCount === 0; skip duplicate to avoid double toast
            const receivers = await this.resolveBroadcastReceivers(chatroomId, data.members ?? []);
            this.uiManager.setBroadcastBulkAck(0, receivers.length);
            return;
          }

          // Show the audience-confirmation modal immediately: its content (sender-omitted
          // previews) is built entirely from localStorage/in-memory state and doesn't need
          // receivers or talk payloads resolved first — both of those can take several
          // seconds on a slow/real device (resolveBroadcastReceivers does a network fetch
          // with up to a 5s timeout plus Gun-observation retries; getTalkWithRetry below is
          // a per-talk retry loop up to 1.5s each). Blocking the modal on that work made
          // every broadcast tap feel unresponsive for no reason — confirm first, only pay
          // the slow cost after the user has actually said yes.
          const previews: BroadcastAudiencePreview[] = [];
          const previewTalkIds = new Set(broadcastableIds);
          const senderOmittedPreviews = this.uiManager
            .getSenderOmittedBroadcastPreviews()
            .filter((preview) => !previewTalkIds.has(preview.talkId));
          const audiencePreviews = [...previews, ...senderOmittedPreviews];
          if (!data.automatic && !(await this.uiManager.confirmBroadcastAudience(audiencePreviews))) {
            this.uiManager.showNotification(this.uiManager.formatBroadcastCancelled(), 'info');
            return;
          }
          // A deliberate broadcast is what makes these Talks owed to anyone who links into this
          // room later (catchUpPeerInActiveRoom); it never carries them into another room.
          // (A plain Broadcast tap arrives as `automatic` — it only skips the confirm dialog.)
          if (!data.catchUp) {
            recordTalksBroadcastInRoom(chatroomId, broadcastableIds, Object.keys(getMyTalks()));
            releaseInFlight = this.markTalkBroadcastsInFlight(broadcastableIds);
          }

          // Immediate feedback before the slow work below (resolving receivers, fetching
          // talk payloads) — without this, a tap that shows nothing for a few seconds reads
          // as "nothing happened," and the user taps Broadcast again.
          if (!data.catchUp) this.uiManager.showNotification(this.uiManager.formatBroadcastInProgress(), 'info');

          // A catch-up is owed only to the peer that just linked; the room roster would re-send the
          // same Talk to everyone on every reconnect (OPEN-50).
          const receivers = data.catchUp
            ? (data.members ?? []).filter((member) =>
              !!member.userId && member.userId !== this.currentUser!.id && member.userId !== TECHSUPPORT_ROOT_USER_ID)
            : await this.resolveBroadcastReceivers(chatroomId, data.members ?? []);
          const activeCollection = this.ensureMatchCollectionSession(chatroomId);
          const collectionLocation = activeCollection
            ? await this.matchCollectionSendLocation(chatroomId)
            : undefined;
          const peerPoints = activeCollection
            ? Object.fromEntries(this.nearbyV3?.service.entries().map((entry) => [entry.card.userId, entry.card.point]) ?? [])
            : undefined;
          recordMatchCollectionDelivery({
            talkIds: broadcastableIds,
            peerIds: receivers.map((receiver) => receiver.userId),
            ...(data.talkReceiverIds ? { talkReceiverIds: data.talkReceiverIds } : {}),
            ...(collectionLocation ? { location: collectionLocation } : {}),
            ...(peerPoints ? { peerPoints } : {}),
          });
          this.syncNativeBackgroundAgent();
          const targetCount = receivers.length;
          console.log(`📢 broadcastTalk: ${targetCount} receivers resolved`);
          if (!data.catchUp) this.startNearbyBroadcastProgress(chatroomId, broadcastableIds, targetCount, receivers.map((receiver) => receiver.userId));
          if (targetCount === 0) {
            console.warn(
              '⚠️ broadcastTalk: no receivers resolved (no other active members in this chatroom).',
            );
          }
          let sent = 0;
          // Resolve all talk payloads first (sync from localStorage, no awaits)
          const talkPayloads: Array<{ tid: string; talk: Talk }> = [];
          for (const talkId of broadcastableIds) {
            // Prefer OUT row fullTalk (localStorage) — Gun getTalk often lags for 20 talks; skipping
            // register + Gun put for most ids leaves Tom with a single IN cluster (e2e poll sees 1).
            let talk = this.uiManager.getBroadcastTalkPayload(talkId);
            if (!talk) {
              talk = await this.talkService.getTalkWithRetry(talkId, { attempts: 15, gapMs: 100 });
            }
            if (!talk) { console.warn(`📢 broadcastTalk: skipping ${talkId} (no talk data)`); continue; }
            const tid = String(talk.id || talkId);
            talk = { ...talk, id: tid, authorId: talk.authorId || this.currentUser!.id };
            if (this.isTalkExpiredForDelivery(talk)) {
              console.warn(`📢 broadcastTalk: skipping ${talkId} (expired)`);
              continue;
            }
            talkPayloads.push({ tid, talk: talk as Talk });
          }
          // Phase 1: deliver in small parallel batches (direct mesh in P2P mode, legacy HTTP in star mode).
          // Fully sequential was very slow (20 round-trips); full parallel can spike the server.
          const REGISTER_BATCH = 5;
          const registeredTalkIds: string[] = [];
          const previewByTalkId = new Map(audiencePreviews.map((p) => [p.talkId, p]));
          const broadcastableSnapshot = new Set(broadcastableIds);
          for (let i = 0; i < talkPayloads.length; i += REGISTER_BATCH) {
            const batch = talkPayloads.slice(i, i + REGISTER_BATCH);
            const batchResults = await Promise.all(
              batch.map(async ({ tid, talk }) => {
                if (!broadcastableSnapshot.has(tid)) return false;
                const preview = previewByTalkId.get(tid);
                let eligibleIds =
                  usesMeshTalkDelivery(this.p2pRuntimeFlags) &&
                  !preview?.previewUnavailable &&
                  Array.isArray(preview?.eligibleReceiverIds)
                    ? preview.eligibleReceiverIds
                    : undefined;
                // docs/TODO.md §W Gap 1: further narrow to receivers who don't already have
                // this specific talk, when the caller supplied that per-talk information —
                // fixes the room-wide "any member still needs it" batch re-attempting
                // delivery to a receiver who already has it.
                const perTalkReceiverIds = data.talkReceiverIds?.[tid];
                if (perTalkReceiverIds !== undefined) {
                  const allowed = new Set(perTalkReceiverIds);
                  eligibleIds = (eligibleIds ?? receivers.map((r) => r.userId)).filter((id) => allowed.has(id));
                }
                const ok = await this.deliverTalkToReceiversOverMesh(
                  tid,
                  talk,
                  receivers,
                  eligibleIds,
                );
                if (ok) registeredTalkIds.push(tid);
                return ok;
              }),
            );
            sent += batchResults.filter(Boolean).length;
          }
          const broadcastableNowForGun = new Set(registeredTalkIds);
          // Phase E: ledger hook — TALK_BROADCAST (one event per broadcasted talk)
          for (const { tid } of talkPayloads.filter(({ tid }) => broadcastableNowForGun.has(tid))) {
            this.ledgerEmit(InteractionKind.TALK_BROADCAST, {
              talkId: tid,
              recipientCount: sent,
              chatroomIds: [chatroomId],
            });
          }

          if (data.catchUp) {
            console.info(`[catch-up] sent ${broadcastableIds.length} talk(s) to ${sent}/${targetCount} newly linked peer(s) in ${chatroomId}`);
          } else {
            this.uiManager.setBroadcastBulkAck(sent, targetCount);
            this.uiManager.showNotification(this.uiManager.formatBroadcastSent(sent, targetCount), 'success');
          }
        } catch (error) {
          console.error('Broadcast talks failed:', error);
          this.uiManager.showNotification(this.uiManager.formatBroadcastFailed((error as Error).message), 'error');
        } finally {
          releaseInFlight();
        }
      },
    );

    // docs/TODO.md §U — broadcast a single talk to a contact group, online or not. The
    // group-picker dialog already resolved *who* (client-side only, per-recipient); delivery
    // reuses the exact same deliverTalkToReceiversOverMesh path every other broadcast already
    // uses — mesh-flood to whoever's online, mailbox fallback (48h/72h TTL) for the rest —
    // nothing new to build for the "online or not, deferred, dropped after timeout" half of
    // the ask (docs/TODO.md §U's own research finding). No chatroomId/eligibleIds narrowing:
    // the resolved member list already IS the intended recipient set.
    this.uiManager.on(
      'broadcastToContactGroup',
      async (data: { talkId: string; members: Array<{ userId: string; stageName: string }> }) => {
        try {
          if (data.members.length === 0) return;
          let talk = this.uiManager.getBroadcastTalkPayload(data.talkId);
          if (!talk) {
            talk = await this.talkService.getTalkWithRetry(data.talkId, { attempts: 15, gapMs: 100 });
          }
          if (!talk) {
            this.uiManager.showNotification(this.uiManager.formatBroadcastFailed('talk not found'), 'error');
            return;
          }
          talk = { ...talk, authorId: talk.authorId || this.currentUser!.id };
          if (this.isTalkExpiredForDelivery(talk)) {
            this.uiManager.showNotification(this.uiManager.formatBroadcastFailed('talk expired'), 'error');
            return;
          }
          const ok = await this.deliverTalkToReceiversOverMesh(data.talkId, talk, data.members);
          if (ok) {
            this.ledgerEmit(InteractionKind.TALK_BROADCAST, {
              talkId: data.talkId,
              recipientCount: data.members.length,
              chatroomIds: [],
            });
          }
          this.uiManager.showNotification(
            ok ? this.uiManager.formatBroadcastGroupSent() : this.uiManager.formatBroadcastFailed('delivery failed'),
            ok ? 'success' : 'error',
          );
        } catch (error) {
          console.error('Broadcast to contact group failed:', error);
          this.uiManager.showNotification(this.uiManager.formatBroadcastFailed((error as Error).message), 'error');
        }
      },
    );

    this.uiManager.on('updateTalk', async (data: Partial<Talk> & {
      id: string;
      title: string;
      type: string;
      questions: any[];
      selfAnswers?: Array<{ questionId: string; answerId: string }>;
    }) => {
      try {
        const updatedTalk = await this.talkService.updateTalk(data.id, {
          title: data.title,
          type: data.type as Talk['type'],
          questions: data.questions,
          language: data.language || 'en',
          tags: data.tags || [],
          ...(data.isAdult !== undefined ? { isAdult: data.isAdult } : {}),
          ...(data.expiresAt !== undefined ? { expiresAt: data.expiresAt } : {}),
          ...(data.locationRadiusMiles !== undefined ? { locationRadiusMiles: data.locationRadiusMiles } : {}),
          ...(data.matchThreshold !== undefined ? { matchThreshold: data.matchThreshold } : {}),
        });
        if (Array.isArray(updatedTalk.ipfsAttachments) && updatedTalk.ipfsAttachments.length > 0) {
          await this.ensureContentNodeInitialized();
          this.contentNodeService.pinTalkAttachments(updatedTalk.id, updatedTalk.ipfsAttachments);
        }
        this.uiManager.saveCreatedTalk(updatedTalk, { selfAnswers: data.selfAnswers ?? [] });
        this.uiManager.showNotification(this.uiManager.formatTalkUpdated(), 'success');
        this.uiManager.displayTalksList();
        // Phase F: emit TALK_SUPERSEDED so peers know this talk was revised.
        // Since we update in-place (same ID), oldTalkId === newTalkId; the ledger
        // captures the edit event for chatbot differential answering (REQ-CHATBOT-03).
        this.ledgerEmit(InteractionKind.TALK_SUPERSEDED, {
          oldTalkId: data.id,
          newTalkId: data.id,
        });
      } catch (error) {
        console.error('Failed to update talk:', error);
        this.uiManager.showNotification(this.uiManager.formatTalkUpdateFailed((error as Error).message), 'error');
      }
    });

    // Phase F: TALK_WITHDRAWN — emitted when user deletes or disables a talk (REQ-LEDGER-13)
    // Grace window default: 24h (configurable via TALK_WITHDRAWN_GRACE_MS env var if available)
    this.uiManager.on('withdrawTalk', (data: { talkId: string }) => {
      const gracePeriodMs =
        (typeof process !== 'undefined' && process.env && Number(process.env['TALK_WITHDRAWN_GRACE_MS']) > 0)
          ? Number(process.env['TALK_WITHDRAWN_GRACE_MS'])
          : 24 * 60 * 60 * 1000; // 24 hours default
      this.ledgerEmit(InteractionKind.TALK_WITHDRAWN, {
        talkId: data.talkId,
        gracePeriodMs,
      });
    });

    // Step 10: TALK_RETRACTED — hard retraction on delete or tag-uncheck.
    // Emits the audit-feed event, writes the local tombstone, floods the mesh frame,
    // and posts mailbox envelopes to all known responders (outcomes + exchanged).
    this.uiManager.on('retractTalk', (data: { talkId: string; retractedAt: number }) => {
      void this.handleRetractTalk(data.talkId, data.retractedAt);
    });

    this.uiManager.on(
      'demandFullTalk',
      async (data: {
        talkId: string;
        identityKeyFallback?: string;
        callback: (fullTalk: any) => void;
      }) => {
        try {
          let talk: Talk | null = null;
          const id = (data.talkId || '').trim();
          if (id) talk = await this.resolveMeshTalkData(id);
          if (!talk && data.identityKeyFallback) {
            talk = await this.loadFullTalkViaIncomingIdentity(data.identityKeyFallback);
          }
          data.callback(talk);
        } catch (error) {
          console.error('Failed to get full talk:', error);
          data.callback(null);
        }
      },
    );

    this.uiManager.on(
      'demandFullTalkByIdentity',
      async (data: { identityKey: string; callback: (fullTalk: any) => void }) => {
        if (!this.currentUser?.id) {
          data.callback(null);
          return;
        }
        try {
          const talk = await this.loadFullTalkViaIncomingIdentity(data.identityKey);
          data.callback(talk);
        } catch (error) {
          console.error('demandFullTalkByIdentity failed:', error);
          data.callback(null);
        }
      },
    );

    this.uiManager.on('loadTalkForEdit', async (data: { talkId: string }) => {
      try {
        const talk = await this.talkService.getTalk(data.talkId);
        if (talk) {
          this.uiManager.showTalkEditorDialog(talk);
        } else {
          this.uiManager.showNotification(this.uiManager.formatTalkNotFound(), 'error');
        }
      } catch (error) {
        console.error('Failed to load talk:', error);
        this.uiManager.showNotification(this.uiManager.formatTalkLoadFailed((error as Error).message), 'error');
      }
    });

    this.uiManager.on('needIncomingTalkClusters', () => {
      void this.refreshIncomingTalkClustersFromLocalGun();
    });

    this.uiManager.on('needTalkStats', async (data: { talkIds: string[] }) => {
      if (data.talkIds.length === 0) {
        this.uiManager.setTalkStats({});
        this.uiManager.displayTalksList();
        return;
      }
      const statsMap: Record<string, { responses: number; matches: number; ignores: number }> = {};
      const outcomes = Object.values(getTalkLedgerDoc().outcomes);
      for (const talkId of data.talkIds) {
        const rows = outcomes.filter(
          (entry) => entry.talkId === talkId && entry.authorId === this.currentUser?.id,
        );
        statsMap[talkId] = {
          responses: rows.length,
          matches: rows.filter((entry) => entry.outcome === 'matched').length,
          ignores: rows.filter((entry) => entry.outcome === 'ignored').length,
        };
      }
      this.uiManager.setTalkStats(statsMap);
      // Refresh status bar so match count is shown
      this.refreshStatusBar();
    });

    this.uiManager.on(
      'talkCompleted',
      async (data: { talkId: string; answers: any[]; talkData?: any; isChatbotResponse?: boolean }) => {
        const completion = this.talkCompletionQueue
          .catch(() => undefined)
          .then(() => this.handleTalkCompleted(data));
        this.talkCompletionQueue = completion;
        (globalThis as any).__iinpublic_lastTalkCompletion = completion;
        try {
          await completion;
          this.syncNativeBackgroundAgent();
        } catch (error) {
          console.error('Failed to handle talk completion:', error);
        }
      },
    );

    this.uiManager.on(
      'answerQuestion',
      async (data: { conversationId: string; questionId: string; answerId: string }) => {
        try {
          const result = await this.talkService.processAnswer(
            data.conversationId,
            data.questionId,
            data.answerId,
            this.currentUser!.id,
          );

          this.uiManager.updateConversation(data.conversationId, result);

          if (result.isComplete) {
            this.uiManager.showTalkCompletion(data.conversationId, result.outcome);
          }
        } catch (error) {
          this.uiManager.showNotification(
            this.uiManager.formatAnswerProcessFailed((error as Error).message),
            'error',
          );
        }
      },
    );

    this.uiManager.on('sendMessage', async (data: { conversationId: string; message: string }) => {
      try {
        console.log('📤 Sending message:', data.message);

        // For now, broadcast message to the chatroom
        const chatroomId = this.chatroomService.getCurrentChatroomId();
        if (!chatroomId) {
          this.uiManager.showNotification(this.uiManager.formatNotInChatroom(), 'error');
          return;
        }

        // Create a simple message object
        const message = {
          id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          text: data.message,
          senderId: this.currentUser!.id,
          senderName: this.currentUser!.stageName,
          timestamp: new Date().toISOString(),
        };

        const stored = await this.placeChatClient.send(chatroomId, message);
        // The next bounded poll dedupes this id; render immediately for responsive sending.
        this.uiManager.displayChatroomMessage({ ...stored, isOwnMessage: true });

        console.log('✅ Message sent:', stored);
        this.uiManager.showNotification(this.uiManager.formatMessageSent(), 'success');
      } catch (error) {
        console.error('Failed to send message:', error);
        this.uiManager.showNotification(
          this.uiManager.formatMessageSendFailed((error as Error).message),
          'error',
        );
      }
    });

    // Handle conversation message loading
    this.uiManager.on('loadConversation', async (data: { conversationId: string }) => {
      try {
        console.log('📖 Loading conversation:', data.conversationId);
        const conversation = this.uiManager.getMyConversations()[data.conversationId] as any;
        const otherUserId = conversation?.otherUserId ? String(conversation.otherUserId) : undefined;

        // Subscribe to messages for this conversation (pass myUserId for prevSeen DAG tracking)
        this.conversationService.subscribeToMessages(data.conversationId, (liveMessages) => {
          console.log('📨 Received conversation messages:', liveMessages);
          // A conversation this device only knows about via device-sync (see
          // WebDeviceSyncService's `messages` category) has no live Gun messages at all — the
          // pair path is keyed by the two ORIGINAL participant ids, not this device's — so its
          // history lives only in the synced-plaintext cache. Live copies win any id collision.
          const messages = mergeMessagesWithSynced(data.conversationId, liveMessages);
          this.uiManager.displayConversationMessages(data.conversationId, messages);
          for (const message of messages) {
            const sharePayload = parseAttachmentShareMessageText(String(message.text || ''));
            if (!sharePayload) continue;
            if (!message.senderId || message.senderId === this.currentUser?.id) continue;
            void this.maybeFetchSharedAttachmentBytes(sharePayload, String(message.senderId)).catch((err) => {
              console.warn('[L5] conversation attachment fetch failed:', err);
            });
          }
          // Reconcile the preview through the durable read cursor. This subscription can replay
          // an already-read batch after the conversation closes; blindly marking its last message
          // unread would resurrect a badge that the user just cleared.
          const currentUserId = this.currentUser?.id;
          if (currentUserId) {
            this.uiManager.syncConversationMessageSummary(
              data.conversationId,
              messages,
              currentUserId,
            );
          }
        }, this.currentUser?.id, otherUserId);
      } catch (error) {
        console.error('Failed to load conversation:', error);
        this.uiManager.showNotification(
          this.uiManager.formatConversationLoadFailed((error as Error).message),
          'error',
        );
      }
    });

    // Handle sending conversation messages
    this.uiManager.on(
      'sendConversationMessage',
      async (data: { conversationId: string; message: string; talkId?: string }) => {
        try {
          console.log('📤 Sending conversation message:', data.message);
          const conversation = this.uiManager.getMyConversations()[data.conversationId];
          const otherUserId = conversation?.otherUserId
            ? String(conversation.otherUserId)
            : undefined;
          const messageId = `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

          await this.conversationService.sendMessage(
            data.conversationId,
            this.currentUser!.id,
            data.message,
            {
              messageId,
              ...(otherUserId ? { otherUserId } : {}),
              // Per-talk thread scope (redesign §5) — omitted for the DM thread.
              ...(data.talkId ? { talkId: data.talkId } : {}),
            },
          );

          console.log('✅ Conversation message sent');
          if (conversation?.supportChannel === true || otherUserId === TECHSUPPORT_ROOT_USER_ID) {
            await this.handleSupportQuestion(data.conversationId, messageId, data.message);
          }

          // Phase E: ledger hook — CONVERSATION_MSG
          this.ledgerEmit(InteractionKind.CONVERSATION_MSG, {
            conversationId: data.conversationId,
            messageId,
            seq: Date.now(),
          });
        } catch (error) {
          console.error('Failed to send conversation message:', error);
          this.uiManager.showNotification(
            this.uiManager.formatMessageSendFailed((error as Error).message),
            'error',
          );
        }
      },
    );

    // docs/TODO.md K5, design note §Item 5 — the TechSupport-inbox answer control (only ever
    // rendered inside a TechSupport-root session, ui-manager.ts's renderSettingsView).
    this.uiManager.on(
      'answerSupportQuestion',
      async (data: { questionKey: string; question: string; answer: string; conversationId: string; askedBy: string }) => {
        try {
          await this.handleAnswerSupportQuestion(data);
        } catch (error) {
          console.error('Failed to answer support question:', error);
          this.uiManager.showNotification(
            this.uiManager.formatMessageSendFailed((error as Error).message),
            'error',
          );
        }
      },
    );

    // docs/TODO.md K7 — master's Delegates panel: issue/revoke a co-operator grant.
    this.uiManager.on(
      'issueTechSupportDelegate',
      async (data: { delegateUserId: string; label: string; ttlDays: number }) => {
        try {
          await this.handleIssueTechSupportDelegate(data);
        } catch (error) {
          console.error('Failed to issue TechSupport delegate grant:', error);
          this.uiManager.showNotification(this.uiManager.formatMessageSendFailed((error as Error).message), 'error');
        }
      },
    );
    this.uiManager.on('revokeTechSupportDelegate', async (delegatePub: string) => {
      try {
        await this.handleRevokeTechSupportDelegate(delegatePub);
      } catch (error) {
        console.error('Failed to revoke TechSupport delegate grant:', error);
        this.uiManager.showNotification(this.uiManager.formatMessageSendFailed((error as Error).message), 'error');
      }
    });
    // docs/TODO.md K7 — an eligible ordinary user's own opt-in toggle in the Me tab.
    this.uiManager.on('toggleTechSupportDelegateOptIn', (nextOptedIn: boolean) => {
      void this.handleToggleTechSupportDelegateOptIn(nextOptedIn);
    });

    // Share a link to any media file in a DM: upload the bytes to IPFS and send only the
    // link (an IPFS_SHARE card), never the raw bytes over the DM channel.
    this.uiManager.on(
      'shareConversationMedia',
      async (data: { conversationId: string; file: File }) => {
        try {
          if (!this.currentUser) throw new Error('Not logged in');
          const conversation = this.uiManager.getMyConversations()[data.conversationId];
          const otherUserId = conversation?.otherUserId ? String(conversation.otherUserId) : undefined;
          // Courtesy/efficiency check (the real enforcement boundary is receive-side, see
          // maybeFetchSharedAttachmentBytes/ingestAttachmentShareFromMailbox) — don't even
          // upload to IPFS for a peer we already know is blocked either-way.
          if (otherUserId && await this.resolveBlockStatusEitherWay(otherUserId)) {
            this.uiManager.showNotification(this.uiManager.formatMediaShareBlockedRecipient(), 'error');
            return;
          }
          this.uiManager.showNotification(this.uiManager.formatMediaShareUploading(data.file.name), 'info');
          const attachment = await this.publishMediaFileToIpfs(data.file, data.conversationId);
          const payload: AttachmentShareMessagePayload = {
            kind: 'ipfs-auto-share-v1',
            conversationId: data.conversationId,
            talkId: `dm-media:${data.conversationId}`,
            authorId: this.currentUser.id,
            cid: attachment.cid,
            link: `ipfs://${attachment.cid}`,
            name: attachment.name,
            mimeType: attachment.mimeType,
            sizeBytes: attachment.sizeBytes,
            enc: attachment.enc,
            keyCiphertext: 'public',
            sharedAt: new Date().toISOString(),
          };
          await this.conversationService.sendMessage(
            data.conversationId,
            this.currentUser.id,
            this.formatAttachmentShareMessageText(payload),
            {
              messageId: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
              ...(otherUserId ? { otherUserId } : {}),
            },
          );
        } catch (error) {
          console.error('Failed to share media link:', error);
          this.uiManager.showNotification(
            this.uiManager.formatMessageSendFailed((error as Error).message),
            'error',
          );
        }
      },
    );

    // Direct message from peer detail overlay — find or create a conversation then send
    this.uiManager.on(
      'sendDirectMessage',
      async (data: { peerId: string; peerName: string; text: string; resolve: () => void; reject: (e: unknown) => void }) => {
        try {
          if (!this.currentUser) throw new Error('Not logged in');
          const conversationId = await this.findOrCreateDirectConversation(data.peerId, data.peerName);
          await this.conversationService.sendMessage(conversationId, this.currentUser.id, data.text, {
            otherUserId: data.peerId,
          });
          data.resolve();
        } catch (error) {
          console.error('Failed to send direct message:', error);
          this.uiManager.showNotification(this.uiManager.formatMessageSendFailed((error as Error).message), 'error');
          data.reject(error);
        }
      },
    );

    // Conversation-first user click (rule N2a): resolve the peer's default DM
    // conversation, creating it on first contact.
    this.uiManager.on(
      'openDirectConversation',
      async (data: { peerId: string; peerName: string; resolve: (id: string) => void; reject: (e: unknown) => void }) => {
        try {
          if (!this.currentUser) throw new Error('Not logged in');
          data.resolve(await this.findOrCreateDirectConversation(data.peerId, data.peerName));
        } catch (error) {
          console.warn('openDirectConversation failed:', error);
          data.reject(error);
        }
      },
    );

    this.uiManager.on('needConversationSync', async () => {
      if (!this.currentUser) return;
      try {
        const snapshot = await this.conversationService.getUserConversationsSnapshot(this.currentUser.id);
        await this.ingestConversationRecords(snapshot);
      } catch (error) {
        console.warn('Conversation sync snapshot failed:', error);
      }
    });

    this.uiManager.on('requestLocationUpdate', async () => {
      await this.updateLocationAndMaybeSwitch();
    });
    // Android: the location sheet (first launch, or "Use my location") was answered with a grant.
    window.addEventListener('iinpublic-location-granted', () => {
      void readDeviceLocation()
        .then((fix) => this.applyRealGpsFix(fix))
        .catch(() => this.handleGpsFixUnavailable())
        .finally(() => { if (this.currentUser) this.uiManager.refreshSettingsViewIfActive(this.currentUser); });
    });
    // OPEN-54: Settings → "Use my location". The Android shell asks the OS again (or opens the
    // app's settings once Android stops showing the dialog); a browser re-asks through geolocation.
    this.uiManager.on('requestLocationPermission', async () => {
      const bridge = (window as unknown as { IinPublicNearby?: { requestLocationPermission?: () => void } }).IinPublicNearby;
      if (typeof bridge?.requestLocationPermission === 'function') {
        bridge.requestLocationPermission();
        return;
      }
      await this.updateLocationAndMaybeSwitch();
      if (this.currentUser) this.uiManager.refreshSettingsViewIfActive(this.currentUser);
    });

    // §3.7 "Meeting new people in groups": swap the whole group for the next one, or go back.
    this.uiManager.on('nearbyMeetNewPeople', () => {
      void this.nearbyV3?.service.meetNewPeople().catch((error: unknown) => console.warn('Meet new people failed:', error));
    });
    this.uiManager.on('nearbyBackToFirstGroup', () => {
      void this.nearbyV3?.service.backToFirstGroup().catch((error: unknown) => console.warn('Back to group 1 failed:', error));
    });

    this.uiManager.on('travelNearbyAtPoint', (point: { latitude: number; longitude: number }) => {
      const latitude = Number(point?.latitude);
      const longitude = Number(point?.longitude);
      if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90
        || !Number.isFinite(longitude) || longitude < -180 || longitude > 180) return;
      this.beginNearbyTravel(
        { latitude, longitude, accuracy: 0, timestamp: new Date() },
        'map-point',
        'Selected map point',
      );
    });

    this.uiManager.on('returnHomeFromTravel', async () => {
      if (!this.currentUser) return;
      const home = await this.resolveAutomaticNearbyRoom();
      this.travelModeActive = false;
      this.travelChatroomId = undefined;
      this.travelNearbyPoint = undefined;
      this.pendingNearbyTravel = undefined;
      this.travelHomeChatroomId = home;
      this.persistTravelModeStateToStorage();
      this.uiManager.setTravelModeState({ active: false });
      this.refreshNearbyPoint();
      if (this.currentChatroomId !== home) {
        await this.chatroomService.switchChatroom(this.currentUser.id, home, this.currentUser.stageName);
        this.currentChatroomId = home;
        settingsStorage.setItem('iinpublic_last_chatroom', home);
        this.subscribeToMessages(home);
        this.uiManager.setCurrentChatroomId(home);
        this.chatroomService.subscribeToMembers(home, (members) => {
          this.harvestRosterEpubs(members);
          this.observeNearbyRoomRoster(home, members);
          this.uiManager.updateChatroomMembers(members, this.currentUser!.id);
          // Like every other room switch: without it the mesh stayed in the Place, so Nearby
          // (personal mode) never came back and the list never searched again (native-app/38).
          this.syncPeerMeshRoom(home, members);
          const chatroomName = this.getChatroomDisplayName(home);
          this.uiManager.updateStatusBar(
            this.currentUser!.stageName,
            chatroomName,
            this.countRoomMembers(members),
            this.uiManager.getTotalMatches(),
          );
        });
        if (!isLocalOnlyRoomScope(home)) await this.activateRoomExchange(home);
      }
      this.uiManager.showNotification(this.uiManager.formatTravelReturnedHome(), 'success');
      this.chatroomService.announceMembershipNow();
    });

    this.uiManager.on(
      'createCustomChatroom',
      async (payload: {
        type: 'business' | 'custom';
        name: string;
        description?: string;
        businessInfo?: { headline?: string };
      }) => {
        if (!this.currentUser) return;
        // Custom rooms are local: anchored to the creator's blurred ~1 km cell (confirmed GPS only).
        if (!this.locationConfirmed || !this.currentLocation) {
          this.uiManager.showNotification(this.uiManager.formatChatroomCreateFailed('location required'), 'error');
          return;
        }
        const anchorCell = LocationPrivacy.blurLocation(this.currentLocation).region;
        const anchor = parseAnchorCell(anchorCell);
        const pair = this.gunService.getStoredPair();
        if (!anchor || !pair?.pub || !pair?.priv) {
          this.uiManager.showNotification(this.uiManager.formatChatroomCreateFailed('signing identity unavailable'), 'error');
          return;
        }
        const base = this.getBackendApiBase();
        try {
          const descriptor = await createSignedPlaceDescriptor({
            name: payload.name,
            placeType: payload.type,
            ...(payload.description != null ? { description: payload.description } : {}),
            location: { latitude: anchor.latitude, longitude: anchor.longitude },
            pair,
          });
          const res = await fetch(`${base}/api/chatrooms`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: payload.name,
              type: payload.type,
              createdBy: this.currentUser.id,
              anchorCell,
              descriptor,
              ...(payload.description != null ? { description: payload.description } : {}),
              ...(payload.businessInfo != null ? { businessInfo: payload.businessInfo } : {}),
            }),
          });
          const text = await res.text();
          let created: {
            id?: string;
            name?: string;
            type?: string;
            description?: string;
            createdBy?: string;
            createdAt?: string;
            businessInfo?: { headline?: string };
            location?: { latitude: number; longitude: number };
          } | null = null;
          if (text) {
            try {
              created = JSON.parse(text) as {
                id?: string;
                name?: string;
                type?: string;
                description?: string;
                createdBy?: string;
                createdAt?: string;
                businessInfo?: { headline?: string };
                location?: { latitude: number; longitude: number };
              };
            } catch {
              created = null;
            }
          }
          if (!res.ok) {
            this.showPlaceCreateRefusal(res.status, text);
            return;
          }
          const createdId = String(created?.id || '').trim();
          if (createdId) {
            this.uiManager.upsertCustomChatroomFromServer({
              id: createdId,
              name: String(created?.name || payload.name),
              type: created?.type === 'business' ? 'business' : 'custom',
              description: String(created?.description || payload.description || ''),
              createdBy: String(created?.createdBy || this.currentUser.id),
              ...(created?.createdAt != null ? { createdAt: created.createdAt } : {}),
              ...(created?.businessInfo != null || payload.businessInfo != null
                ? { businessInfo: created?.businessInfo ?? payload.businessInfo! }
                : {}),
              ...(created?.location != null ? { location: created.location } : {}),
            });
            this.uiManager.showChatroomDetail(createdId);
          }
          void this.refreshCustomChatroomsFromServer().then(() => {
            this.subscribeToAllChatroomMemberCounts();
          });
          this.uiManager.showNotification(this.uiManager.formatChatroomCreated(created?.name || payload.name), 'success');
        } catch (e) {
          this.uiManager.showNotification(this.uiManager.formatChatroomCreateFailed((e as Error).message), 'error');
        }
      },
    );

    this.uiManager.on('chatroomChanged', async (requestedChatroomId: string) => {
      if (!this.currentUser) {
        return;
      }
      const nearbyTravelRequest = this.pendingNearbyTravel && isNearbyRoomId(requestedChatroomId)
        ? this.pendingNearbyTravel
        : undefined;
      const chatroomId = requestedChatroomId;
      // Contacts only is a fallback, never a room a tap enters (OPEN-53): a click on the row
      // rendered before Nearby resolved must not pull the user back out of their real room.
      if (chatroomId === CONTACTS_ONLY_SCOPE_ID
        && this.currentChatroomId
        && this.currentChatroomId !== CONTACTS_ONLY_SCOPE_ID) {
        this.uiManager.showChatroomDetail(this.currentChatroomId);
        return;
      }
      const previousChatroomId = this.currentChatroomId;
      const isSameRoom = previousChatroomId === chatroomId;

      if (!isSameRoom) {
        try {
          if (this.currentChatroomId) {
            console.log(`🔄 User switching from chatroom ${this.currentChatroomId} to ${chatroomId}`);

            await this.chatroomService.switchChatroom(
              this.currentUser.id,
              chatroomId,
              this.currentUser.stageName,
            );
          } else {
            // App lost track of room (race / fresh UI) while user opened a room — align service with UI.
            const svcId = this.chatroomService.getCurrentChatroomId();
            if (svcId && svcId !== chatroomId) {
              await this.chatroomService.switchChatroom(
                this.currentUser.id,
                chatroomId,
                this.currentUser.stageName,
              );
            } else if (!svcId) {
              await this.chatroomService.joinChatroom(
                chatroomId,
                this.currentUser.id,
                this.currentUser.stageName,
              );
            }
          }
        } catch (error) {
          if (previousChatroomId) this.uiManager.setCurrentChatroomId(previousChatroomId);
          const language = getUiLanguagePreference(uiLanguageFromProfile(this.currentUser.languages));
          this.uiManager.showNotification(uiText(language, 'chatroomSwitchFailed'), 'warning');
          if (nearbyTravelRequest) this.pendingNearbyTravel = undefined;
          console.warn(`Chatroom switch to ${chatroomId} was rejected:`, error);
          return;
        }

        this.currentChatroomId = chatroomId;
        settingsStorage.setItem('iinpublic_last_chatroom', chatroomId);

        this.subscribeToMessages(chatroomId);
            console.log(`✅ Switched to ${chatroomId}`);
        // Switching rooms changes the audience only. Existing Talks never follow the user or
        // auto-send to newcomers; broadcasting in the new room is always a deliberate action.
      } else {
        // Same room: ensure app id matches (e.g. first time opening detail after join)
        this.currentChatroomId = chatroomId;
      }

      this.uiManager.setCurrentChatroomId(chatroomId);
      // A deliberate Place or Nearby-at-a-pin selection is travel; Nearby at my own point is
      // home. Commit only after the switch succeeds, so a rejected destination cannot leave
      // navigation or the Return-to-Nearby target pointing at a false room.
      const continuingNearbyTravel = this.travelModeActive && this.travelNearbyPoint != null && isNearbyRoomId(chatroomId);
      if (nearbyTravelRequest || continuingNearbyTravel) {
        if (!this.travelModeActive && previousChatroomId && !isPlaceRoomId(previousChatroomId)) {
          this.travelHomeChatroomId = previousChatroomId;
        }
        this.travelModeActive = true;
        this.travelChatroomId = chatroomId;
        if (nearbyTravelRequest) {
          this.travelNearbyPoint = {
            ...nearbyTravelRequest.location,
            placeId: nearbyTravelRequest.placeId,
            placeName: nearbyTravelRequest.placeName,
          };
        }
        this.pendingNearbyTravel = undefined;
      } else if (isPlaceRoomId(chatroomId)) {
        if (!this.travelModeActive && previousChatroomId && !isPlaceRoomId(previousChatroomId)) {
          this.travelHomeChatroomId = previousChatroomId;
        }
        this.travelModeActive = true;
        this.travelChatroomId = chatroomId;
        this.travelNearbyPoint = undefined;
      } else {
        this.travelModeActive = false;
        this.travelHomeChatroomId = chatroomId;
        this.travelChatroomId = undefined;
        this.travelNearbyPoint = undefined;
      }
      this.persistTravelModeStateToStorage();
      this.uiManager.setTravelModeState({
        active: this.travelModeActive,
        ...(this.travelHomeChatroomId ? { homeChatroomId: this.travelHomeChatroomId } : {}),
      });
      this.refreshNearbyPoint();

      // subscribeToMembers reuses the Gun listener when chatroomId is unchanged (see WebChatroomService)
      this.chatroomService.subscribeToMembers(chatroomId, (members) => {
        this.harvestRosterEpubs(members);
        this.observeNearbyRoomRoster(chatroomId, members);
        this.uiManager.updateChatroomMembers(members, this.currentUser!.id);
        this.syncPeerMeshRoom(chatroomId, members);
        const chatroomName = this.getChatroomDisplayName(chatroomId);
        this.uiManager.updateStatusBar(
          this.currentUser!.stageName,
          chatroomName,
          this.countRoomMembers(members),
          this.uiManager.getTotalMatches(),
        );
      });
      // A fresh install starts in the local Contacts-only scope, so no active-room controller
      // exists yet. The first manual Place/Nearby move must create and start the exchange runtime;
      // otherwise the UI and server roster show the new room while presence, mailbox scoping,
      // LAN discovery, and Wi-Fi Direct remain silently bound to no public room.
      if (!isLocalOnlyRoomScope(chatroomId)) await this.activateRoomExchange(chatroomId);
      this.syncNativeBackgroundAgent();
    });

    // Handle Gun.js real-time updates
    this.gunService.on('newMessage', (message: any) => {
      this.uiManager.displayNewMessage(message);
    });

    this.gunService.on('newTalk', (conversation: any) => {
      this.uiManager.displayIncomingTalk(conversation);
    });

    this.gunService.on('chatroomUpdate', (update: any) => {
      this.uiManager.updateChatroomInfo(update);
    });

    // Handle visibility changes (for offline/online status). Android's Activity lifecycle also
    // dispatches `iinpublic-native-resume`: several WebViews do not emit visibilitychange after
    // HOME even though onResume ran, which otherwise leaves stale WebRTC sessions untouched.
    let lastForegroundRecoveryAt = 0;
    let foregroundRecoveryRunning = false;
    let foregroundRecoveryQueued = false;
    const recoverAfterForeground = () => {
      if (!this.currentUser) return;
      if (foregroundRecoveryRunning) {
        foregroundRecoveryQueued = true;
        return;
      }
      const now = Date.now();
      if (now - lastForegroundRecoveryAt < 500) {
        foregroundRecoveryQueued = true;
        return;
      }
      lastForegroundRecoveryAt = now;
      foregroundRecoveryRunning = true;
      try {
        this.userService.setUserStatus(this.currentUser.id, 'online');
        const settings = loadConnectivitySettings();
        if (settings.nearbyMode === 'while-open' && !this.nearbyOfflineService) {
          const pub = this.gunService.getStoredPair()?.pub;
          if (pub) this.initNearbyOffline(String(pub));
        }
        // Android can freeze a background WebView while its WebRTC objects still report
        // "connected". Rebuild only the bounded room transports on foreground and drain any
        // encrypted fallback that accumulated meanwhile. Without this, an author's durable
        // direct-Talk outbox can keep retrying a stale session indefinitely after the receiver
        // returns, even though both phones still show the same room.
        if (this.currentChatroomId && !isLocalOnlyRoomScope(this.currentChatroomId)) {
          void this.peerMeshService?.resumeRoomConnections().catch((error) => {
            console.warn('Could not refresh room connections after foregrounding:', error);
          });
          this.chatroomService.announceMembershipNow();
          this.chatroomService.pullMembersFromPeers(this.currentChatroomId);
        }
        void this.drainMailbox().catch(() => {});
        void this.retryFailedMailboxPosts().catch(() => {});
        this.syncNativeBackgroundAgent();
        void this.replayNativeBackgroundEvents();
      } finally {
        foregroundRecoveryRunning = false;
        if (foregroundRecoveryQueued) {
          foregroundRecoveryQueued = false;
          queueMicrotask(() => recoverAfterForeground());
        }
      }
    };
    window.addEventListener('iinpublic-native-resume', recoverAfterForeground);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.syncNativeBackgroundAgent();
        this.userService.setUserStatus(this.currentUser!.id, 'away');
        if (loadConnectivitySettings().nearbyMode === 'while-open') {
          this.nearbyOfflineService?.dispose();
          this.nearbyOfflineService = null;
        }
      } else {
        recoverAfterForeground();
      }
    });

    // Handle beforeunload to cleanup
    window.addEventListener('beforeunload', () => {
      // A protected identity must not remain reachable from service references during a
      // normal navigation/close boundary. JavaScript cannot await unload work reliably,
      // but lockIdentityNow clears the pair and direct Gun authentication synchronously
      // before its best-effort worker logout. Only do this when an identity password is
      // actually configured: beforeunload can fire without the page truly being torn down
      // (bfcache restores, some browsers' speculative unload timing) and unconditionally
      // nulling out the ordinary (non-password-protected) identity's in-memory SEA pair on
      // every such event — with nothing re-establishing it afterward — left the app running
      // with no keypair and every send failing with "No SEA keypair — call
      // ensureKeypairAndAuth first" until an actual full reload.
      if (this.gunService.isIdentityPasswordActive()) {
        void this.gunService.lockIdentityNow();
      }
      if (this.mailboxPollTimer) clearInterval(this.mailboxPollTimer);
      this.mailboxPollTimer = undefined;
      if (this.deviceSyncTimer) clearInterval(this.deviceSyncTimer);
      this.deviceSyncTimer = undefined;
      this.peerMeshService?.leaveRoom();
      if (this.currentUser && this.currentChatroomId) {
        // Mark user as inactive in current chatroom (for member count)
        this.chatroomService.leaveChatroom(this.currentChatroomId, this.currentUser.id);
        // Set user status to offline
        this.userService.setUserStatus(this.currentUser.id, 'offline');
      }
    });
  }

  // Public methods for UI interaction
  public getCurrentUser(): User | undefined {
    return this.currentUser;
  }

  public getCurrentLocation(): GPSCoordinate | undefined {
    return this.currentLocation;
  }

  /**
   * Content layer bootstrap entry point. Helia/libp2p initializes on first use,
   * never on first paint.
   */
  public async ensureContentNodeInitialized(): Promise<WebContentNode> {
    return this.contentNodeService.ensureNode();
  }

  public async ensureContentLibp2pInitialized(): Promise<unknown> {
    return this.contentNodeService.ensureLibp2p();
  }

  public async refreshUserData(): Promise<void> {
    if (this.currentUser) {
      this.currentUser = await this.userService.getUser(this.currentUser.id);
      this.uiManager.updateUserInfo(this.currentUser);
    }
  }

  /** OPEN-91 merge seam: connect the authenticated core's match-count view after startup. */
  public setMapMatchSource(source: MapMatchSource | null): void {
    this.mapMatchView.setSource(source);
  }

  public async logout(): Promise<void> {
    this.mapMatchView.reset();
    if (this.currentUser) {
      await this.userService.setUserStatus(this.currentUser.id, 'offline');
      localStorage.removeItem('iinpublic_user_id');
      this.currentUser = undefined as any; // Type assertion for now

      // Reload the page to restart
      window.location.reload();
    }
  }

  // E2E Testing helpers - expose private state for manual cleanup in tests
  public getCurrentChatroomId(): string | undefined {
    return this.currentChatroomId;
  }

  public async updateLocationAndMaybeSwitch(nextLocation?: GPSCoordinate): Promise<void> {
    try {
      // A phone that is a Traveler only for lack of GPS tries the real fix again first.
      if (!nextLocation && isGpsFixUnavailable() && deviceHasGps()) {
        const fix = await readDeviceLocation().catch(() => null);
        if (fix) {
          this.applyRealGpsFix(fix);
          return;
        }
      }
      // A Traveler (by choice or for lack of GPS): a refresh re-applies the encounter location.
      const encounter = !nextLocation && usesSelfDefinedEncounterLocation() ? getEncounterLocation() : null;
      if (!nextLocation && usesSelfDefinedEncounterLocation() && !encounter) {
        // Just switched to Traveler without a point: stop using GPS and appear at a random point
        // (with the picker) until one is chosen — never off the map (OPEN-53).
        this.locationConfirmed = false;
        this.placeLocationlessDeviceOnMap();
        return;
      }
      let gpsFix: GPSCoordinate | null = null;
      if (!nextLocation && !encounter) {
        gpsFix = await readDeviceLocation().catch(() => null);
        if (!gpsFix) {
          // Location refused or unavailable (e.g. the browser prompt was denied): a Resident with
          // no fix in hand appears as a Traveler at their encounter point instead.
          this.uiManager.showNotification(this.uiText('locationPermissionUnavailable'), 'warning');
          if (!isGpsFixUnavailable()) this.handleGpsFixUnavailable({ force: !this.hasRealGpsFix() });
          return;
        }
        setCachedLocation(gpsFix);
      }
      const newLocation = nextLocation || (encounter ? encounterLocationCoordinate(encounter) : gpsFix!);
      this.currentLocation = newLocation;
      this.locationConfirmed = true;

      if (this.currentUser) {
        await this.userService.updateUserLocation(this.currentUser.id, this.publicHomeLocation());

        if (this.travelModeActive) {
          this.uiManager.showNotification(this.uiManager.formatTravelLocationHeld(), 'info');
          return;
        }

        const newChatroomId = await this.resolveAutomaticNearbyRoom();
        const currentChatroomId = this.chatroomService.getCurrentChatroomId();

        if (newChatroomId !== currentChatroomId) {
          await this.chatroomService.switchChatroom(
            this.currentUser.id,
            newChatroomId,
            this.currentUser.stageName,
          );
          this.currentChatroomId = newChatroomId;
          settingsStorage.setItem('iinpublic_last_chatroom', newChatroomId);
          this.uiManager.setCurrentChatroomId(newChatroomId);
          this.subscribeToMessages(newChatroomId);
          this.chatroomService.subscribeToMembers(newChatroomId, (members) => {
            this.harvestRosterEpubs(members);
            this.observeNearbyRoomRoster(newChatroomId, members);
            this.uiManager.updateChatroomMembers(members, this.currentUser!.id);
            this.syncPeerMeshRoom(newChatroomId, members);
            this.uiManager.updateStatusBar(
              this.currentUser!.stageName,
              this.getChatroomDisplayName(newChatroomId),
              this.countRoomMembers(members),
              this.uiManager.getTotalMatches(),
            );
          });
          if (!isLocalOnlyRoomScope(newChatroomId)) await this.activateRoomExchange(newChatroomId);
          this.uiManager.showNotification(this.uiManager.formatTravelMovedLocation(), 'info');
        }
      }
    } catch (error) {
      this.uiManager.showNotification(this.uiManager.formatLocationUpdateFailed((error as Error).message), 'error');
      throw error;
    }
  }

  /**
   * Re-announce a talk to the current room as the current user (for E2E: Bob "sends same talk" to trigger chatbot).
   */
  public async announceTalkToRoom(talkId: string, localTalkData?: any): Promise<void> {
    const chatroomId = this.chatroomService.getCurrentChatroomId();
    if (!chatroomId || !this.currentUser) return Promise.reject(new Error('No chatroom or user'));
    const talkData = localTalkData || await this.resolveMeshTalkData(talkId);
    if (!talkData) throw new Error(`Talk not found: ${talkId}`);
    const receivers = await this.resolveBroadcastReceivers(
      chatroomId,
      this.uiManager.getCurrentChatroomMembers(),
    );
    await this.deliverTalkToReceiversOverMesh(talkId, {
      ...talkData,
      id: talkId,
      authorId: this.currentUser.id,
      authorName: this.currentUser.stageName,
    } as Talk, receivers);
  }

  /**
   * E2E / advanced: open response dialog with flattened / saved auto-answers applied.
   * The normal IN-row "View" path uses skipAutoAnswer so opening the list does not instantly complete a match.
   */
  public async openTalkResponseDialogWithAuto(talkId: string): Promise<void> {
    const talk = await this.resolveMeshTalkData(talkId);
    if (!talk) {
      this.uiManager.showNotification(this.uiManager.formatTalkCouldNotLoad(), 'error');
      return;
    }
    this.uiManager.showTalkResponseDialog(talk, { skipAutoAnswer: false });
  }

  private applyChatroomMemberCount(chatroomId: string, count: number): void {
    if (isDevStageZeroSelfHeal()) {
      const prev = this.stageZeroLastMemberCounts.get(chatroomId) ?? 0;
      this.stageZeroLastMemberCounts.set(chatroomId, count);
      const pastBootGrace = Date.now() - this.stageZeroBootedAt > 30_000;
      const maxGlobalMembers = getDevStageZeroMaxGlobalMembers();
      if (
        pastBootGrace
        && !this.stageZeroRepairInFlight
        && prev <= 1
        && count > maxGlobalMembers
        && (chatroomId === 'global' || count > 8)
      ) {
        void this.repairStageZeroGunGraph(`headcount:${chatroomId}:${prev}->${count}`);
        return;
      }
    }
    this.uiManager.setChatroomMemberCount(chatroomId, count);
  }

  private async repairStageZeroGunGraph(reason: string): Promise<void> {
    if (this.stageZeroRepairInFlight) return;
    this.stageZeroRepairInFlight = true;
    console.warn(`[stage-zero] repairing Gun graph (${reason}) — close other localhost tabs`);
    try {
      const gun = this.gunService.getGun();
      const bootGraceMs = 30_000;
      const clearServer = Date.now() - this.stageZeroBootedAt > bootGraceMs;
      await purgeDevStageZeroGraph(this.getBackendApiBase(), gun, { clearServer });
      if (clearServer) {
        await this.gunService.ensureKeypairAndAuth();
      }
      await this.reloadChatroomMemberCountsAfterStageClear();
      this.stageZeroLastMemberCounts.clear();
    } catch (err) {
      console.warn('[stage-zero] repair failed (non-fatal):', err);
    } finally {
      this.stageZeroRepairInFlight = false;
    }
  }

  private startStageZeroHeadcountWatchdog(): void {
    if (this.stageZeroWatchdogTimer) clearInterval(this.stageZeroWatchdogTimer);
    let ticks = 0;
    this.stageZeroWatchdogTimer = setInterval(() => {
      ticks += 1;
      const globalN = this.uiManager.getChatroomMemberCount('global');
      const maxGlobalMembers = getDevStageZeroMaxGlobalMembers();
      if (globalN > maxGlobalMembers) {
        void this.repairStageZeroGunGraph(`watchdog:global=${globalN}`);
      }
      if (ticks >= 12) {
        clearInterval(this.stageZeroWatchdogTimer);
        this.stageZeroWatchdogTimer = undefined;
      }
    }, 5000);
  }

  /**
   * After stage-zero wipes Gun, re-join the current room and refresh list headcounts
   * so Global and other rooms do not show stale members from a previous session.
   */
  public async reloadChatroomMemberCountsAfterStageClear(): Promise<void> {
    this.chatroomService.unsubscribeAllMemberCounts();
    this.subscribedMemberCountRoomIds.clear();

    const gun = this.gunService.getGun();
    gun.get('chatrooms').put({});

    for (const chatroomId of getAllChatroomIds()) {
      this.uiManager.setChatroomMemberCount(chatroomId, 0);
    }
    for (const chatroomId of this.uiManager.getCustomChatroomIds()) {
      if (chatroomId) {
        this.uiManager.setChatroomMemberCount(chatroomId, 0);
      }
    }

    if (this.currentUser && this.currentChatroomId) {
      await this.chatroomService.joinChatroom(
        this.currentChatroomId,
        this.currentUser.id,
        this.currentUser.stageName,
      );
    }

    this.subscribeToAllChatroomMemberCounts();
  }

  public async manualCleanup(): Promise<void> {
    // Manually trigger cleanup (for E2E tests where beforeunload may not fire)
    console.log('🧹 Manual cleanup called');
    this.stopNearbyV3();
    if (this.activeRoomsTimer) clearInterval(this.activeRoomsTimer);
    this.activeRoomsTimer = undefined;
    if (this.quietTimer) clearInterval(this.quietTimer);
    if (this.quietGpsTimer) clearInterval(this.quietGpsTimer);
    this.quietTimer = undefined;
    this.quietGpsTimer = undefined;
    if (this.incomingApiRefreshTimer) {
      clearTimeout(this.incomingApiRefreshTimer);
      this.incomingApiRefreshTimer = undefined;
    }
    if (this.mailboxPollTimer) {
      clearInterval(this.mailboxPollTimer);
      this.mailboxPollTimer = undefined;
    }
    if (this.deviceSyncTimer) {
      clearInterval(this.deviceSyncTimer);
      this.deviceSyncTimer = undefined;
    }
    this.stopTechSupportDelegateRelayPolling();
    this.peerMeshService?.leaveRoom();
    for (const unsubscribe of this.conversationPreviewUnsubscribers.values()) unsubscribe();
    this.conversationPreviewUnsubscribers.clear();
    if (this.currentUser && this.currentChatroomId) {
      console.log(`🧹 Cleanup: user=${this.currentUser.id}, chatroom=${this.currentChatroomId}`);
      this.chatroomService.unsubscribeAllMemberCounts();
      // Leave current chatroom
      await this.chatroomService.leaveChatroom(this.currentChatroomId, this.currentUser.id);
      await this.userService.setUserStatus(this.currentUser.id, 'offline');
      console.log('✅ Manual cleanup complete');
    } else {
      console.log('⚠️ Manual cleanup skipped - no user or chatroom');
    }
  }
}

/** Fields of `fresh` that are actually set win; anything it lacks keeps the known value. */
function mergeUserRecord(known: User, fresh: User): User {
  const defined = Object.fromEntries(Object.entries(fresh ?? {}).filter(([key, value]) => key !== '_' && value !== undefined && value !== null));
  return { ...known, ...defined, id: known.id } as User;
}

/**
 * A user whose record never synced its stage name (offline, partial local copy) still needs one to
 * render and to write a room membership. Use a temporary in-memory name; the next full refresh
 * (mergeUserRecord) replaces it, and writeCachedUser never caches a record without a real one.
 */
function withUsableStageName(user: User): User {
  if (typeof user.stageName === 'string' && user.stageName) return user;
  return { ...user, stageName: `User${String(user.id || '').replace(/-/g, '').slice(0, 6)}` };
}
